<template>
  <div class="work-calendar">
    <div class="studio-cockpit-wrapper fill-height d-flex flex-column" :class="themeClasses" @dragend="onDragEndGlobal">
      <!-- Week View -->
      <work-calendar-week
        v-if="viewMode === 'week'"
        class="flex-grow-1 overflow-hidden"
        style="height: 100%; min-height: 0; display: flex; flex-direction: column"
        :initial-date="currentDate"
        :focused-employee-id="focusedEmployeeId"
        :show-last-name-initial="showLastNameInitial"
        :user-allowed-studios="userAllowedStudios"
        @back="$emit('back')"
        @switch-view="viewMode = 'day'"
        @open-day="openDayFromWeek"
        @clear-focus="focusedEmployeeId = null" />

      <!-- Day View (existing) -->
      <template v-if="viewMode === 'day'">
        <!-- Premium Header -->
        <div class="wc-header" :class="$vuetify.breakpoint.smAndDown ? 'wc-header-mobile' : ''">
          <!-- 1. Left Section: Date & Back button -->
          <div class="wc-header-left">
            <v-btn icon x-large dark class="wc-back-btn mr-3" @click="$router.push('/dashboard/shift-management')">
              <v-icon x-large>mdi-arrow-left</v-icon>
            </v-btn>
            <div class="wc-date-block">
              <div class="wc-day-name">{{ $moment(currentDate).format("dddd") }}</div>
              <div class="wc-full-date">{{ $moment(currentDate).format("DD. MMMM YYYY") }}</div>
            </div>
            <div v-if="isToday" class="wc-today-badge ml-2">Heute</div>

            <!-- Studio Switcher -->
            <v-hover v-slot="{ hover }" v-if="userAllowedStudios && userAllowedStudios.length > 1">
              <div
                class="wc-studio-switcher-chip d-flex align-center ml-4 px-3 py-1.5 rounded-pill"
                :class="{ hovered: hover }"
                @click="changeStudio"
                title="Studio wechseln">
                <v-icon left size="16" class="wc-studio-icon mr-1.5">mdi-storefront-outline</v-icon>
                <span class="wc-studio-name font-weight-bold mr-1">
                  {{ selectedStudio ? selectedStudio.name : "Studio wechseln" }}
                </span>
                <v-icon right size="14" class="wc-studio-chevron">mdi-chevron-down</v-icon>
              </div>
            </v-hover>

            <v-chip
              v-if="focusedEmployee"
              class="wc-focus-chip ml-3 font-weight-bold"
              color="primary"
              outlined
              label
              close
              small
              @click:close="focusedEmployeeId = null">
              <v-icon left small>mdi-account-star</v-icon>
              Fokus: {{ focusedEmployee.firstName }}
            </v-chip>
          </div>

          <!-- 2. Center Section: Navigation & Toggles -->
          <div class="wc-header-center-controls">
            <!-- Date Navigation Group -->
            <div class="wc-nav-group">
              <v-btn icon @click="changeDate(-1)" class="wc-nav-btn"><v-icon>mdi-chevron-left</v-icon></v-btn>
              <v-btn text class="wc-today-btn" :class="{ 'is-today': isToday }" @click="jumpToToday">Heute</v-btn>
              <v-btn icon @click="changeDate(1)" class="wc-nav-btn"><v-icon>mdi-chevron-right</v-icon></v-btn>
            </div>

            <!-- Calendar Picker Menu -->
            <v-menu
              v-model="datePickerMenu"
              offset-y
              :close-on-content-click="false"
              transition="scale-transition"
              content-class="wc-date-picker-menu">
              <template v-slot:activator="{ on, attrs }">
                <v-btn icon v-bind="attrs" v-on="on" class="wc-icon-btn mr-2" title="Datum auswählen">
                  <v-icon>mdi-calendar-search</v-icon>
                </v-btn>
              </template>
              <v-date-picker v-model="currentDate" no-title scrollable :dark="isDarkMode" color="primary" @input="selectCalendarDate"></v-date-picker>
            </v-menu>

            <v-btn
              icon
              class="wc-icon-btn mr-2"
              :class="{ active: appointmentSearchDialog }"
              :disabled="loading"
              title="Kalender durchsuchen"
              @click="appointmentSearchDialog = true">
              <v-icon>mdi-magnify</v-icon>
            </v-btn>

            <!-- Trainerpoint Live: Daten werden erst beim Öffnen geladen. -->
            <v-btn icon @click="triggerSearch" class="wc-icon-btn mr-2" :class="{ active: customerSearchDialog }" title="Trainerpoint Live öffnen">
              <v-icon>mdi-account-group-outline</v-icon>
            </v-btn>

            <v-btn v-if="false" icon @click="openAppointmentHistory" class="wc-icon-btn mr-2" title="Termin-Änderungsverlauf">
              <v-icon>mdi-history</v-icon>
            </v-btn>

            <v-chip
              v-if="appointmentClipboard && !$vuetify.breakpoint.smAndDown"
              small
              outlined
              close
              color="primary"
              class="wc-clipboard-chip mr-2 font-weight-bold"
              @click:close="clearAppointmentClipboard"
              title="Freien Zielslot antippen oder per Rechtsklick öffnen">
              <v-icon left small>{{ appointmentClipboard.mode === "move" ? "mdi-content-cut" : "mdi-content-copy" }}</v-icon>
              <span class="wc-clipboard-mode">{{ appointmentClipboard.mode === "move" ? "Verschieben" : "Kopieren" }}</span>
              <span class="wc-clipboard-title">· {{ appointmentClipboard.title }}</span>
            </v-chip>

            <!-- Sidebar Toggle -->
            <v-btn
              icon
              @click="toggleSidebar"
              class="wc-icon-btn mr-2"
              :class="{ active: sidebarOpen || mobileTemplateSheet }"
              title="Vorlagen-Panel">
              <v-icon>mdi-dock-right</v-icon>
            </v-btn>
          </div>

          <!-- 3. Right Section: Trainer Hub Trigger Menu -->
          <div class="wc-trainer-hub-wrapper">
            <v-menu
              v-model="trainerControlMenuOpen"
              offset-y
              left
              transition="scale-transition"
              :close-on-content-click="false"
              content-class="wc-trainer-hub-menu">
              <template v-slot:activator="{ on, attrs }">
                <!-- Desktop Hub Button -->
                <div
                  v-if="!$vuetify.breakpoint.smAndDown && getSelectedEmployee"
                  class="wc-trainer-hub-trigger d-flex align-center"
                  v-bind="attrs"
                  v-on="on"
                  title="Trainer Kontrollzentrum">
                  <v-avatar size="24" class="mr-2" style="border: 1px solid rgba(var(--rgb-color), 0.15)">
                    <img :src="employeePicture" alt="Mitarbeiter" style="object-fit: cover" />
                  </v-avatar>
                  <div class="text-left mr-1 d-flex flex-column justify-center" style="line-height: 1.1">
                    <div class="body-2 font-weight-bold text-truncate" style="max-width: 110px">
                      {{ getSelectedEmployee.vorname || getSelectedEmployee.fullName.split(" ")[0] }}
                    </div>
                    <div class="caption text-truncate" style="max-width: 110px; font-size: 0.72rem !important; opacity: 0.8">
                      {{ getSelectedEmployeeLastName }}
                    </div>
                  </div>
                  <v-icon small color="grey" style="margin-left: 2px">mdi-chevron-down</v-icon>
                </div>
                <!-- Mobile Hub Avatar -->
                <div
                  v-else-if="getSelectedEmployee"
                  class="wc-trainer-hub-trigger-mobile d-flex align-center justify-center mr-1"
                  v-bind="attrs"
                  v-on="on"
                  title="Trainer Kontrollzentrum">
                  <v-badge bottom overlap dot color="orange" :value="showMobileEmployeeWarning" offset-x="4" offset-y="4">
                    <v-avatar size="30" style="border: 2px solid rgba(var(--rgb-color), 0.15)">
                      <img :src="employeePicture" alt="Mitarbeiter" style="object-fit: cover" />
                    </v-avatar>
                  </v-badge>
                </div>
              </template>

              <!-- Unified Control Center Card (No backdrop-filter to prevent iPad crashes) -->
              <v-card width="330" class="wc-control-center-card pa-1" :dark="isDarkMode">
                <!-- 1. Profile Section -->
                <div class="px-4 py-3 d-flex align-center">
                  <v-avatar size="36" class="mr-3" style="border: 1px solid rgba(var(--rgb-color), 0.15)">
                    <img :src="employeePicture" alt="Mitarbeiter" style="object-fit: cover" />
                  </v-avatar>
                  <div class="text-left flex-grow-1 min-width-0">
                    <div class="caption grey--text font-weight-medium" style="font-size: 0.7rem">Aktiver Trainer</div>
                    <div class="body-2 font-weight-black text-truncate">{{ getSelectedEmployee.fullName }}</div>
                  </div>
                  <v-btn
                    v-if="canSwitchEmployee"
                    x-small
                    depressed
                    outlined
                    color="primary"
                    class="font-weight-bold rounded-lg px-2 ml-2"
                    style="height: 24px; text-transform: none"
                    @click="onEmployeeChipClick">
                    Wechseln
                  </v-btn>
                </div>

                <v-divider class="my-1" style="opacity: 0.1"></v-divider>

                <!-- 2. View Toggle -->
                <div class="px-4 py-2">
                  <div class="caption font-weight-bold text-uppercase grey--text mb-2" style="letter-spacing: 0.5px; font-size: 0.65rem">Ansicht</div>
                  <v-btn-toggle v-model="viewMode" mandatory dense color="primary" class="w-100" style="display: flex">
                    <v-btn value="day" style="flex: 1; text-transform: none; font-weight: 600; font-size: 0.75rem" class="rounded-l-lg">
                      <v-icon left small>mdi-view-day</v-icon> Tag
                    </v-btn>
                    <v-btn value="week" style="flex: 1; text-transform: none; font-weight: 600; font-size: 0.75rem" class="rounded-r-lg">
                      <v-icon left small>mdi-view-week</v-icon> Woche
                    </v-btn>
                  </v-btn-toggle>
                </div>

                <v-divider class="my-1" style="opacity: 0.1"></v-divider>

                <!-- 3. Schnellzugriff Grid -->
                <div class="px-4 py-2">
                  <div
                    class="caption font-weight-bold text-uppercase grey--text mb-2 d-flex align-center"
                    style="letter-spacing: 0.5px; font-size: 0.65rem">
                    <v-icon small color="orange" class="mr-1">mdi-flash</v-icon> Blitz-Aktionen
                  </div>
                  <v-row dense>
                    <!-- Trainerpoint Live -->
                    <v-col cols="6">
                      <div class="wc-action-tile d-flex align-center pa-2 rounded-xl" @click="triggerSearch">
                        <div class="wc-action-icon-box blue-theme mr-2">
                          <v-icon small color="blue">mdi-account-group-outline</v-icon>
                        </div>
                        <div class="wc-action-text">Trainerpoint Live</div>
                      </div>
                    </v-col>
                    <!-- Weiterempfehlung -->
                    <v-col cols="6">
                      <div class="wc-action-tile d-flex align-center pa-2 rounded-xl" @click="triggerLeads">
                        <div class="wc-action-icon-box orange-theme mr-2">
                          <v-icon small color="orange darken-2">mdi-account-heart</v-icon>
                        </div>
                        <div class="wc-action-text">Empfehlung</div>
                      </div>
                    </v-col>
                    <!-- 11-Tage Aktionen -->
                    <v-col cols="6">
                      <div class="wc-action-tile d-flex align-center pa-2 rounded-xl" @click="trigger11Days">
                        <div class="wc-action-icon-box purple-theme mr-2">
                          <v-icon small color="purple">mdi-card-account-details-star-outline</v-icon>
                        </div>
                        <div class="wc-action-text">11-Tage Aktion</div>
                      </div>
                    </v-col>
                    <!-- WM Trikot -->
                    <v-col cols="6">
                      <div class="wc-action-tile d-flex align-center pa-2 rounded-xl" @click="triggerTowel">
                        <div class="wc-action-icon-box red-theme mr-2">
                          <v-icon small color="red">mdi-tshirt-crew</v-icon>
                        </div>
                        <div class="wc-action-text">WM Trikot</div>
                      </div>
                    </v-col>
                    <!-- Kennzeichen -->
                    <v-col cols="6" v-if="selectedStudio && selectedStudio.parkingAvailable">
                      <div class="wc-action-tile d-flex align-center pa-2 rounded-xl" @click="triggerLicensePlate">
                        <div class="wc-action-icon-box grey-theme mr-2">
                          <v-icon small color="grey darken-3">mdi-car-info</v-icon>
                        </div>
                        <div class="wc-action-text">Kennzeichen</div>
                      </div>
                    </v-col>
                    <v-col cols="6">
                      <div class="wc-action-tile d-flex align-center pa-2 rounded-xl" @click="openSeriesManager">
                        <div class="wc-action-icon-box grey-theme mr-2">
                          <v-icon small color="blue-grey">mdi-calendar-sync-outline</v-icon>
                        </div>
                        <div class="wc-action-text">Serientermine</div>
                      </div>
                    </v-col>
                  </v-row>
                </div>

                <v-divider class="my-1" style="opacity: 0.1"></v-divider>

                <!-- 4. Focus Selector (only in day view) -->
                <div class="px-4 py-2" v-if="viewMode === 'day'">
                  <div class="caption font-weight-bold text-uppercase grey--text mb-2" style="letter-spacing: 0.5px; font-size: 0.65rem">
                    Mitarbeiter Fokus
                  </div>
                  <select v-model="focusedEmployeeId" class="wc-focus-select w-100">
                    <option :value="null">Alle Mitarbeiter</option>
                    <option v-for="item in focusEmployeeItems.slice(1)" :key="item.value" :value="item.value">
                      {{ item.text }}
                    </option>
                  </select>
                </div>

                <v-divider class="my-1" style="opacity: 0.1" v-if="viewMode === 'day'"></v-divider>

                <!-- 5. Settings / Display -->
                <div class="px-4 py-2">
                  <div class="caption font-weight-bold text-uppercase grey--text mb-1" style="letter-spacing: 0.5px; font-size: 0.65rem">
                    Optionen
                  </div>

                  <v-list dense class="pa-0 transparent" style="background: transparent !important">
                    <!-- Light / Dark (temporarily locked while the dark theme is revised) -->
                    <v-list-item
                      disabled
                      title="Der Darkmode wird aktuell überarbeitet und ist vorübergehend deaktiviert."
                      class="wc-theme-option-disabled px-0 py-1"
                      style="min-height: 42px">
                      <v-list-item-icon class="mr-2 my-auto" style="min-width: 20px">
                        <v-icon small>mdi-weather-night</v-icon>
                      </v-list-item-icon>
                      <v-list-item-content class="py-0 pr-2">
                        <v-list-item-title class="caption">Dunkles Design</v-list-item-title>
                        <v-list-item-subtitle class="wc-theme-option-note">Wird aktuell überarbeitet</v-list-item-subtitle>
                      </v-list-item-content>
                      <v-list-item-action class="my-0 py-0">
                        <v-switch :input-value="false" disabled inset dense hide-details color="primary" style="margin: 0"></v-switch>
                      </v-list-item-action>
                    </v-list-item>

                    <!-- Monochrome Shifts -->
                    <v-list-item @click="monochromeShifts = !monochromeShifts" class="px-0 py-0" style="min-height: 32px">
                      <v-list-item-icon class="mr-2 my-auto" style="min-width: 20px">
                        <v-icon small>mdi-palette-outline</v-icon>
                      </v-list-item-icon>
                      <v-list-item-content class="py-0">
                        <v-list-item-title class="caption">Einheitliche Farben</v-list-item-title>
                      </v-list-item-content>
                      <v-list-item-action class="my-0 py-0">
                        <v-switch
                          :input-value="monochromeShifts"
                          @click.stop="monochromeShifts = !monochromeShifts"
                          inset
                          dense
                          hide-details
                          color="primary"
                          style="margin: 0"></v-switch>
                      </v-list-item-action>
                    </v-list-item>

                    <!-- Monochrome Color Picker (Indented/Only when monochromeShifts is active) -->
                    <v-list-item v-if="monochromeShifts" class="px-0 py-0 pl-6" style="min-height: 32px">
                      <v-list-item-icon class="mr-2 my-auto" style="min-width: 20px">
                        <v-icon small>mdi-format-color-fill</v-icon>
                      </v-list-item-icon>
                      <v-list-item-content class="py-0">
                        <v-list-item-title class="caption">Einheitliche Schichtfarbe</v-list-item-title>
                      </v-list-item-content>
                      <v-list-item-action class="my-0 py-0">
                        <input
                          type="color"
                          :value="monochromeShiftColor || (isDarkMode ? '#2e2e2e' : '#f1f1f1')"
                          @input="monochromeShiftColor = $event.target.value"
                          style="
                            width: 24px;
                            height: 24px;
                            border: 1px solid rgba(128, 128, 128, 0.3);
                            padding: 0;
                            cursor: pointer;
                            border-radius: 4px;
                            background: transparent;
                            outline: none;
                          "
                          @click.stop />
                      </v-list-item-action>
                    </v-list-item>

                    <!-- Monochrome shift background opacity -->
                    <v-list-item v-if="monochromeShifts" class="px-0 py-1 pl-6" style="min-height: 48px">
                      <v-list-item-content class="py-0">
                        <v-list-item-title class="caption mb-1">Deckkraft Schichtfarbe</v-list-item-title>
                        <div class="d-flex align-center" style="gap: 8px">
                          <input
                            v-model.number="monochromeShiftOpacity"
                            type="range"
                            min="10"
                            max="100"
                            step="5"
                            aria-label="Deckkraft der einheitlichen Schichtfarbe"
                            style="width: 100%; accent-color: var(--v-primary-base, #4caf50); cursor: pointer"
                            @click.stop
                            @input.stop />
                          <span class="caption font-weight-bold" style="min-width: 34px; text-align: right">{{ monochromeShiftOpacity }}%</span>
                        </div>
                        <v-list-item-subtitle class="grey--text mt-1" style="font-size: 0.65rem !important">
                          Weniger Deckkraft macht das Zeitraster sichtbar
                        </v-list-item-subtitle>
                      </v-list-item-content>
                    </v-list-item>

                    <!-- Color No Shift Switch -->
                    <v-list-item @click="colorNoShift = !colorNoShift" class="px-0 py-0" style="min-height: 32px">
                      <v-list-item-icon class="mr-2 my-auto" style="min-width: 20px">
                        <v-icon small>mdi-image-filter-none</v-icon>
                      </v-list-item-icon>
                      <v-list-item-content class="py-0">
                        <v-list-item-title class="caption">Farbe ohne Schichten</v-list-item-title>
                      </v-list-item-content>
                      <v-list-item-action class="my-0 py-0">
                        <v-switch
                          :input-value="colorNoShift"
                          @click.stop="colorNoShift = !colorNoShift"
                          inset
                          dense
                          hide-details
                          color="primary"
                          style="margin: 0"></v-switch>
                      </v-list-item-action>
                    </v-list-item>

                    <!-- Color No Shift Picker (Only when colorNoShift is active) -->
                    <v-list-item v-if="colorNoShift" class="px-0 py-0 pl-6" style="min-height: 32px">
                      <v-list-item-icon class="mr-2 my-auto" style="min-width: 20px">
                        <v-icon small>mdi-format-color-fill</v-icon>
                      </v-list-item-icon>
                      <v-list-item-content class="py-0">
                        <v-list-item-title class="caption">Hintergrund ohne Schicht</v-list-item-title>
                      </v-list-item-content>
                      <v-list-item-action class="my-0 py-0">
                        <input
                          type="color"
                          v-model="noShiftBgColor"
                          style="
                            width: 24px;
                            height: 24px;
                            border: 1px solid rgba(128, 128, 128, 0.3);
                            padding: 0;
                            cursor: pointer;
                            border-radius: 4px;
                            background: transparent;
                            outline: none;
                          "
                          @click.stop />
                      </v-list-item-action>
                    </v-list-item>

                    <!-- Performance Mode -->
                    <v-list-item @click="isPerformanceMode = !isPerformanceMode" class="px-0 py-0" style="min-height: 32px">
                      <v-list-item-icon class="mr-2 my-auto" style="min-width: 20px">
                        <v-icon small>mdi-speedometer</v-icon>
                      </v-list-item-icon>
                      <v-list-item-content class="py-0">
                        <v-list-item-title class="caption">Performance Modus</v-list-item-title>
                      </v-list-item-content>
                      <v-list-item-action class="my-0 py-0">
                        <v-switch
                          :input-value="isPerformanceMode"
                          @click.stop="isPerformanceMode = !isPerformanceMode"
                          inset
                          dense
                          hide-details
                          color="primary"
                          style="margin: 0"></v-switch>
                      </v-list-item-action>
                    </v-list-item>

                    <!-- Compact Mode -->
                    <v-list-item @click="isCompactMode = !isCompactMode" class="px-0 py-0" style="min-height: 32px">
                      <v-list-item-icon class="mr-2 my-auto" style="min-width: 20px">
                        <v-icon small>mdi-arrow-collapse-horizontal</v-icon>
                      </v-list-item-icon>
                      <v-list-item-content class="py-0">
                        <v-list-item-title class="caption">Kompakte Ansicht</v-list-item-title>
                      </v-list-item-content>
                      <v-list-item-action class="my-0 py-0">
                        <v-switch
                          :input-value="isCompactMode"
                          @click.stop="isCompactMode = !isCompactMode"
                          inset
                          dense
                          hide-details
                          color="primary"
                          style="margin: 0"></v-switch>
                      </v-list-item-action>
                    </v-list-item>

                    <!-- Thicker Lines -->
                    <v-list-item @click="isThickerLines = !isThickerLines" class="px-0 py-0" style="min-height: 32px">
                      <v-list-item-icon class="mr-2 my-auto" style="min-width: 20px">
                        <v-icon small>mdi-border-left</v-icon>
                      </v-list-item-icon>
                      <v-list-item-content class="py-0">
                        <v-list-item-title class="caption">Dicke Linien</v-list-item-title>
                      </v-list-item-content>
                      <v-list-item-action class="my-0 py-0">
                        <v-switch
                          :input-value="isThickerLines"
                          @click.stop="isThickerLines = !isThickerLines"
                          inset
                          dense
                          hide-details
                          color="primary"
                          style="margin: 0"></v-switch>
                      </v-list-item-action>
                    </v-list-item>

                    <!-- Current time line width -->
                    <v-list-item class="px-0 py-0" style="min-height: 32px">
                      <v-list-item-icon class="mr-2 my-auto" style="min-width: 20px">
                        <v-icon small color="red lighten-1">mdi-clock-outline</v-icon>
                      </v-list-item-icon>
                      <v-list-item-content class="py-0">
                        <v-list-item-title class="caption">Rote Zeitlinie</v-list-item-title>
                      </v-list-item-content>
                      <v-list-item-action class="my-0 py-0">
                        <select
                          v-model.number="currentTimeLineWidth"
                          class="caption rounded px-2 py-0.5"
                          aria-label="Dicke der roten Zeitlinie"
                          style="background: rgba(0, 0, 0, 0.3); border: 1px solid rgba(255, 255, 255, 0.1); color: white; outline: none"
                          @click.stop>
                          <option :value="1">Fein</option>
                          <option :value="2">Deutlich</option>
                          <option :value="3">Stark</option>
                          <option :value="4">Extra stark</option>
                        </select>
                      </v-list-item-action>
                    </v-list-item>

                    <!-- Past time overlay -->
                    <v-list-item @click="showPastTimeOverlay = !showPastTimeOverlay" class="px-0 py-0" style="min-height: 32px">
                      <v-list-item-icon class="mr-2 my-auto" style="min-width: 20px">
                        <v-icon small>mdi-eye-outline</v-icon>
                      </v-list-item-icon>
                      <v-list-item-content class="py-0">
                        <v-list-item-title class="caption">Vergangene Zeit abdunkeln</v-list-item-title>
                      </v-list-item-content>
                      <v-list-item-action class="my-0 py-0">
                        <v-switch
                          :input-value="showPastTimeOverlay"
                          @click.stop="showPastTimeOverlay = !showPastTimeOverlay"
                          inset
                          dense
                          hide-details
                          color="primary"
                          style="margin: 0"></v-switch>
                      </v-list-item-action>
                    </v-list-item>

                    <!-- Past time overlay opacity -->
                    <v-list-item v-if="showPastTimeOverlay" class="px-0 py-1 pl-6" style="min-height: 48px">
                      <v-list-item-content class="py-0">
                        <v-list-item-title class="caption mb-1">Overlay-Stärke</v-list-item-title>
                        <div class="d-flex align-center" style="gap: 8px">
                          <input
                            v-model.number="pastTimeOverlayOpacity"
                            type="range"
                            min="4"
                            max="40"
                            step="2"
                            aria-label="Stärke des Overlays für vergangene Zeit"
                            style="width: 100%; accent-color: var(--v-primary-base, #4caf50); cursor: pointer"
                            @click.stop
                            @input.stop />
                          <span class="caption font-weight-bold" style="min-width: 34px; text-align: right">{{ pastTimeOverlayOpacity }}%</span>
                        </div>
                      </v-list-item-content>
                    </v-list-item>

                    <!-- Show Last Name Initial -->
                    <v-list-item @click="showLastNameInitial = !showLastNameInitial" class="px-0 py-0" style="min-height: 32px">
                      <v-list-item-icon class="mr-2 my-auto" style="min-width: 20px">
                        <v-icon small>mdi-format-list-bulleted-type</v-icon>
                      </v-list-item-icon>
                      <v-list-item-content class="py-0">
                        <v-list-item-title class="caption">1. Buchstabe Nachname</v-list-item-title>
                      </v-list-item-content>
                      <v-list-item-action class="my-0 py-0">
                        <v-switch
                          :input-value="showLastNameInitial"
                          @click.stop="showLastNameInitial = !showLastNameInitial"
                          inset
                          dense
                          hide-details
                          color="primary"
                          style="margin: 0"></v-switch>
                      </v-list-item-action>
                    </v-list-item>

                    <!-- Helper Columns (if focused) -->
                    <v-list-item
                      v-if="focusedEmployeeId"
                      @click="showHelperColumnsDuringFocus = !showHelperColumnsDuringFocus"
                      class="px-0 py-0"
                      style="min-height: 32px">
                      <v-list-item-icon class="mr-2 my-auto" style="min-width: 20px">
                        <v-icon small>mdi-information-outline</v-icon>
                      </v-list-item-icon>
                      <v-list-item-content class="py-0">
                        <v-list-item-title class="caption">Hilfsspalten</v-list-item-title>
                      </v-list-item-content>
                      <v-list-item-action class="my-0 py-0">
                        <v-switch
                          :input-value="showHelperColumnsDuringFocus"
                          @click.stop="showHelperColumnsDuringFocus = !showHelperColumnsDuringFocus"
                          inset
                          dense
                          hide-details
                          color="primary"
                          style="margin: 0"></v-switch>
                      </v-list-item-action>
                    </v-list-item>

                    <!-- Custom Employee Sorting -->
                    <v-list-item @click="openSortDialog" class="px-0 py-0 mt-1" style="min-height: 32px">
                      <v-list-item-icon class="mr-2 my-auto" style="min-width: 20px">
                        <v-icon small color="primary">mdi-sort</v-icon>
                      </v-list-item-icon>
                      <v-list-item-content class="py-0">
                        <v-list-item-title class="caption font-weight-bold primary--text">Sortierung Mitarbeiter</v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>

                    <!-- Template access rules -->
                    <v-list-item
                      v-if="hasEmployeeManagerRight"
                      @click="openAppointmentRestrictionsFromOptions"
                      class="px-0 py-1 mt-1 wc-option-action"
                      style="min-height: 42px">
                      <v-list-item-icon class="mr-2 my-auto" style="min-width: 20px">
                        <v-icon small color="primary">mdi-shield-account-outline</v-icon>
                      </v-list-item-icon>
                      <v-list-item-content class="py-0">
                        <v-list-item-title class="caption font-weight-bold primary--text">Vorlagen-Zugriff</v-list-item-title>
                        <v-list-item-subtitle class="grey--text" style="font-size: 0.65rem !important">
                          Termine, Ämtli, Aufgaben & Info
                        </v-list-item-subtitle>
                      </v-list-item-content>
                      <v-list-item-action class="my-0">
                        <v-icon small color="primary">mdi-chevron-right</v-icon>
                      </v-list-item-action>
                    </v-list-item>

                    <v-divider class="my-2" style="opacity: 0.1"></v-divider>

                    <!-- SECTION 1: ZEITBEREICH -->
                    <div class="px-2 py-1 caption font-weight-bold text-uppercase grey--text" style="letter-spacing: 0.5px; font-size: 0.65rem">
                      Zeitbereich
                    </div>

                    <!-- Auto Adjust to Shifts -->
                    <v-list-item @click="calendarAutoAdjust = !calendarAutoAdjust" class="px-0 py-0" style="min-height: 32px">
                      <v-list-item-icon class="mr-2 my-auto" style="min-width: 20px">
                        <v-icon small>mdi-fit-to-page-outline</v-icon>
                      </v-list-item-icon>
                      <v-list-item-content class="py-0">
                        <v-list-item-title class="caption">Automatisch anpassen</v-list-item-title>
                        <v-list-item-subtitle class="grey--text" style="font-size: 0.65rem !important">An Schichten ausrichten</v-list-item-subtitle>
                      </v-list-item-content>
                      <v-list-item-action class="my-0 py-0">
                        <v-switch
                          :input-value="calendarAutoAdjust"
                          @click.stop="calendarAutoAdjust = !calendarAutoAdjust"
                          inset
                          dense
                          hide-details
                          color="primary"
                          style="margin: 0"></v-switch>
                      </v-list-item-action>
                    </v-list-item>

                    <!-- Start time -->
                    <div
                      v-if="!calendarAutoAdjust"
                      class="d-flex align-center justify-space-between py-1"
                      style="padding-left: 28px; padding-right: 8px">
                      <span class="caption grey--text">Startzeit:</span>
                      <select
                        :value="calendarStart"
                        @change="calendarStart = $event.target.value"
                        class="caption rounded px-2 py-0.5"
                        style="background: rgba(0, 0, 0, 0.3); border: 1px solid rgba(255, 255, 255, 0.1); color: white !important; outline: none">
                        <option v-for="t in ['05:00', '05:30', '06:00', '06:30', '07:00', '07:30', '08:00', '08:30', '09:00']" :key="t" :value="t">
                          {{ t }}
                        </option>
                      </select>
                    </div>

                    <!-- End time -->
                    <div
                      v-if="!calendarAutoAdjust"
                      class="d-flex align-center justify-space-between py-1"
                      style="padding-left: 28px; padding-right: 8px">
                      <span class="caption grey--text">Endzeit:</span>
                      <select
                        :value="calendarEnd"
                        @change="calendarEnd = $event.target.value"
                        class="caption rounded px-2 py-0.5"
                        style="background: rgba(0, 0, 0, 0.3); border: 1px solid rgba(255, 255, 255, 0.1); color: white !important; outline: none">
                        <option
                          v-for="t in [
                            '18:00',
                            '18:30',
                            '19:00',
                            '19:30',
                            '20:00',
                            '20:30',
                            '21:00',
                            '21:30',
                            '22:00',
                            '22:30',
                            '23:00',
                            '23:30',
                            '23:59',
                          ]"
                          :key="t"
                          :value="t">
                          {{ t }}
                        </option>
                      </select>
                    </div>

                    <!-- Buffer time selector -->
                    <div v-else class="d-flex align-center justify-space-between py-1" style="padding-left: 28px; padding-right: 8px">
                      <span class="caption grey--text">Pufferzeit:</span>
                      <select
                        v-model.number="calendarBufferTime"
                        class="caption rounded px-2 py-0.5"
                        style="background: rgba(0, 0, 0, 0.3); border: 1px solid rgba(255, 255, 255, 0.1); color: white !important; outline: none">
                        <option :value="0">Kein Puffer</option>
                        <option :value="30">30 Min</option>
                        <option :value="60">60 Min</option>
                        <option :value="90">90 Min</option>
                        <option :value="120">120 Min</option>
                      </select>
                    </div>

                    <v-divider class="my-2" style="opacity: 0.1"></v-divider>

                    <!-- SECTION 2: ANZEIGE & RASTER -->
                    <div class="px-2 py-1 caption font-weight-bold text-uppercase grey--text" style="letter-spacing: 0.5px; font-size: 0.65rem">
                      Darstellung & Raster
                    </div>

                    <!-- Grid Time step (15m, 30m, 60m) -->
                    <div class="d-flex align-center justify-space-between py-1" style="padding-left: 28px; padding-right: 8px">
                      <span class="caption grey--text">Zeit-Intervall:</span>
                      <select
                        v-model.number="calendarTimeStep"
                        class="caption white--text bg-grey-darken-4 rounded px-2 py-0.5"
                        style="background: rgba(0, 0, 0, 0.3); border: 1px solid rgba(255, 255, 255, 0.1); outline: none">
                        <option :value="15">15 Min</option>
                        <option :value="30">30 Min</option>
                        <option :value="60">60 Min</option>
                      </select>
                    </div>

                    <!-- Studio Opening/Closing Offset (Saved in DB) -->
                    <div class="d-flex align-center justify-space-between py-1" style="padding-left: 28px; padding-right: 8px">
                      <span class="caption grey--text">Vorlauf Öffnung/Schliessung:</span>
                      <select
                        :value="getStudioOffset(selectedStudio)"
                        @change="changeStudioOffset($event.target.value)"
                        class="caption white--text bg-grey-darken-4 rounded px-2 py-0.5"
                        style="background: rgba(0, 0, 0, 0.3); border: 1px solid rgba(255, 255, 255, 0.1); outline: none">
                        <option :value="10">10 Min</option>
                        <option :value="15">15 Min</option>
                        <option :value="20">20 Min</option>
                        <option :value="25">25 Min</option>
                        <option :value="30">30 Min</option>
                      </select>
                    </div>

                    <!-- Hour height scale (Stunden-Höhe) -->
                    <div class="d-flex align-center justify-space-between py-1" style="padding-left: 28px; padding-right: 8px">
                      <span class="caption grey--text">Stunden-Höhe:</span>
                      <select
                        v-model="customHourHeight"
                        class="caption white--text bg-grey-darken-4 rounded px-2 py-0.5"
                        style="background: rgba(0, 0, 0, 0.3); border: 1px solid rgba(255, 255, 255, 0.1); outline: none">
                        <option :value="null">Normal (Automatisch)</option>
                        <option :value="60">Kompakt</option>
                        <option :value="140">Gross</option>
                        <option value="fit">An Bildschirm anpassen</option>
                      </select>
                    </div>
                  </v-list>
                </div>
              </v-card>
            </v-menu>
          </div>
        </div>

        <!-- Mobile active employee/column sync warning banner -->
        <div
          v-if="$vuetify.breakpoint.smAndDown && showMobileEmployeeWarning"
          class="wc-mobile-sync-banner px-4 py-2 d-flex align-center justify-space-between"
          style="background: rgba(255, 152, 0, 0.15); border-bottom: 1px solid rgba(255, 152, 0, 0.3); z-index: 100">
          <v-icon color="orange darken-2" class="mr-2" small>mdi-alert-circle-outline</v-icon>
          <span class="caption text-truncate orange--text text--darken-3 font-weight-medium">
            Spalte von {{ currentColumnEmployeeName }} aktiv. Als {{ getSelectedEmployee.vorname }} erfassen?
          </span>
          <v-spacer></v-spacer>
          <v-btn x-small depressed color="orange darken-2" class="white--text font-weight-bold ml-2" @click="syncEmployeeToActiveColumn">
            Wechseln
          </v-btn>
        </div>

        <div v-show="!mobileDialogActive" class="flex-grow-1 d-flex overflow-hidden relative">
          <div class="flex-grow-1 d-flex flex-column relative" style="min-width: 0; border-right: 1px solid var(--border-light)">
            <div class="d-flex flex-grow-1 overflow-hidden">
              <!-- Time Axis -->
              <div
                class="time-axis custom-scrollbar"
                :class="{ 'wc-thick-lines': isThickerLines }"
                style="
                  width: 56px;
                  flex-shrink: 0;
                  overflow-y: hidden;
                  background: var(--bg-time-axis);
                  border-right: 1px solid var(--border-light);
                  position: relative;
                "
                ref="timeAxis">
                <div :style="{ height: headerOffset + 'px' }" style="flex-shrink: 0"></div>
                <div v-for="slot in timeSlots" :key="slot.time" class="time-slot-label relative" :style="{ height: slot.height + 'px' }">
                  <span class="wc-hour-label" v-if="slot.showLabel">{{ slot.label }}</span>
                  <div class="wc-hour-tick" :class="{ 'hour-tick-major': slot.minutes % 60 === 0 }"></div>
                </div>
                <div :style="{ height: (customHourHeight === 'fit' ? 0 : headerOffset) + 'px' }" style="flex-shrink: 0"></div>
                <div
                  v-if="isToday && currentTimeTop > 0"
                  class="time-axis-current-badge"
                  :style="{ top: currentTimeTop + headerOffset + 'px' }"
                  style="z-index: 5">
                  {{ $moment().format("HH:mm") }}
                </div>
              </div>
              <!-- Column Scroller -->
              <div class="wc-scroller custom-scrollbar" ref="scroller" @scroll="syncScroll">
                <!-- Loading Skeleton -->
                <template v-if="loading">
                  <div v-for="n in 5" :key="'skel' + n" class="wc-skeleton-col">
                    <div class="wc-skeleton-header">
                      <div class="wc-skeleton-avatar wc-pulse"></div>
                      <div style="flex: 1">
                        <div class="wc-skeleton-line wc-pulse" style="width: 70%; height: 12px"></div>
                        <div class="wc-skeleton-line wc-pulse" style="width: 50%; height: 8px; margin-top: 6px"></div>
                      </div>
                    </div>
                    <div class="wc-skeleton-body">
                      <div
                        v-for="b in 3"
                        :key="'sb' + b"
                        class="wc-skeleton-block wc-pulse"
                        :style="{ top: 60 + b * 120 + 'px', height: 50 + Math.random() * 60 + 'px' }"></div>
                    </div>
                  </div>
                </template>

                <div
                  v-if="isToday && currentTimeTop > 0 && showPastTimeOverlay"
                  class="past-time-overlay"
                  :style="{
                    top: headerOffset + 'px',
                    left: 0,
                    height: currentTimeTop + 'px',
                    minWidth: '100%',
                    width: mobileViewColumns.length * (isCompactMode ? 150 : 240) + 'px',
                    opacity: pastTimeOverlayOpacity / 100,
                  }"
                  aria-hidden="true"></div>

                <div
                  v-if="isToday && currentTimeTop > 0"
                  class="current-time-line"
                  :style="{
                    top: currentTimeTop + headerOffset + 'px',
                    left: 0,
                    minWidth: '100%',
                    width: mobileViewColumns.length * (isCompactMode ? 150 : 240) + 'px',
                    '--current-time-line-width': currentTimeLineWidth + 'px',
                  }"
                  aria-hidden="true"></div>

                <!-- Unauthorized Studio Access State -->
                <div
                  v-if="!loading && !hasStudioAccess"
                  class="fill-height w-100 d-flex flex-column align-center justify-center pa-6"
                  style="min-height: 450px; text-align: center">
                  <div class="pa-4 rounded-circle mb-3" style="background: rgba(255, 152, 0, 0.12)">
                    <v-icon size="56" color="warning">mdi-shield-lock-outline</v-icon>
                  </div>
                  <div style="color: var(--text-main); font-size: 1.2rem; font-weight: 700">Zugriff verweigert</div>
                  <div style="color: var(--text-muted); max-width: 420px; font-size: 0.9rem; line-height: 1.4" class="mt-2">
                    Sie befinden sich wahrscheinlich im falschen Center oder haben keinen Zugriff auf die Schichtplanung bzw. den Terminkalender
                    dieses Studios.
                  </div>
                </div>

                <!-- Empty State -->
                <div
                  v-else-if="!loading && hasStudioAccess && viewColumns.length === 0"
                  class="fill-height w-100 d-flex flex-column align-center justify-center"
                  style="min-height: 400px">
                  <div class="wc-empty-icon">
                    <v-icon size="40" color="var(--icon-color)">mdi-calendar-blank-outline</v-icon>
                  </div>
                  <div style="color: var(--text-main); font-size: 0.95rem; font-weight: 600; margin-top: 16px">Keine Schichten geplant</div>
                  <div style="color: var(--text-muted); font-size: 0.8rem; margin-top: 4px">Erstelle einen Einsatzplan für diesen Tag</div>
                </div>

                <!-- Columns -->
                <template v-if="!loading && hasStudioAccess">
                  <work-calendar-column
                    v-for="(col, index) in mobileViewColumns"
                    :key="col.id"
                    :col="col"
                    :is-last-column="index === mobileViewColumns.length - 1"
                    :context-menu-active="contextMenu.show || emptyContextMenu.show"
                    @contextmenu-item="showContextMenu"
                    :show-last-name-initial="showLastNameInitial"
                    :focused-employee-id="focusedEmployeeId"
                    :sick-employees-today="sickEmployeesToday"
                    :hours-count="hoursCount"
                    :time-slots="timeSlots"
                    :hour-height="hourHeight"
                    :is-fit-mode="customHourHeight === 'fit'"
                    :header-offset="headerOffset"
                    :hover-state="hoverState && hoverState.colId === col.id ? hoverState : null"
                    :isDarkMode="isDarkMode"
                    :isPerformanceMode="isPerformanceMode"
                    :monochromeShifts="monochromeShifts"
                    :color-no-shift="colorNoShift"
                    :no-shift-bg-color="noShiftBgColor"
                    :monochrome-shift-color="monochromeShiftColor"
                    :monochrome-shift-opacity="monochromeShiftOpacity"
                    :isCompactMode="isCompactMode"
                    :isThickerLines="isThickerLines"
                    :editing-users="editingUsers"
                    :current-date="currentDate"
                    :is-today="currentDate === $moment().format('YYYY-MM-DD')"
                    :hidden-sick-trainer-ids="hiddenSickTrainerIds"
                    @drag-over="handleDragOver"
                    @drop="handleDrop"
                    @block-drag-start="onBlockDragStart"
                    @block-drag-end="onDragEndGlobal"
                    @pointer-block-drop="handlePointerBlockDrop"
                    @click-item="handleBlockClick"
                    @click-empty="handleEmptyClick"
                    @contextmenu-empty="showEmptyContextMenu"
                    @click-header="openEmployeeSwitcher"
                    @open-note="openNoteDialog"
                    @edit-shift="openEditShiftDialog"
                    @transfer-shift="openTransferShiftDialog"
                    @swap-shift="openSwapShiftDialog"
                    @toggle-sick="toggleSickStatus"
                    @quick-check="handleQuickCheck"
                    @resize-block="handleBlockResize"
                    @resize-shift="handleShiftResize"
                    @open-customer-profile="openCustomerProfile"
                    @set-focus="toggleEmployeeFocus"
                    @hide-sick-column="hideSickColumn"
                    @show-sick-column="showSickColumn" />
                </template>
              </div>
            </div>
          </div>
          <!-- Desktop: Sidebar angedockt -->
          <transition name="slide-x-reverse-transition">
            <work-calendar-sidebar
              v-if="sidebarOpen && !$vuetify.breakpoint.mdAndDown"
              :templates="templates"
              :daily-tasks="dailyTasks"
              :completions="completions"
              :current-date="currentDate"
              :is-thicker-lines="isThickerLines"
              :is-dark-mode="isDarkMode"
              :has-appointment-management-right="hasAppointmentManagementRight"
              :has-task-management-right="hasTaskManagementRight"
              :has-guestbook-entry-right="hasGuestbookEntryRight"
              @create="openCreateTemplate"
              @drag-start="handleDragStart"
              @template-click="handleTemplateClick"
              @task-assign="openTaskAssignment"
              @delete-template="deleteTemplate"
              @edit-template="editTemplate"
              @mark-done="markTaskAsDone"
              @unmark-done="unmarkTaskAsDone"
              @auto-assign-tasks-click="openAmtliConfigDialog"
              @open-tasks-dashboard-click="openTasksDashboard"
              @open-guestbook-entry="openGuestbookEntry"
              style="width: 320px; flex-shrink: 0; z-index: 5" />
          </transition>

          <!-- Mobile/Tablet: Bottom-Sheet Dialog (Icon-on-Top Grid) -->
          <v-dialog
            v-model="mobileTemplateSheet"
            :fullscreen="$vuetify.breakpoint.xsOnly"
            :max-width="$vuetify.breakpoint.xsOnly ? undefined : 520"
            transition="dialog-bottom-transition"
            :content-class="$vuetify.breakpoint.xsOnly ? 'wc-mobile-sheet-fullscreen' : 'wc-mobile-sheet-dialog'">
            <v-card dark class="wc-mobile-sheet-card" :class="{ 'wc-thick-lines': isThickerLines }">
              <!-- Sheet Header -->
              <div class="wc-sheet-header pb-0" style="flex-direction: column; align-items: stretch">
                <div class="d-flex align-center justify-space-between mb-4 w-100">
                  <div class="d-flex align-center">
                    <div
                      class="wc-sheet-icon-box mr-3"
                      style="background: linear-gradient(135deg, rgba(76, 175, 80, 0.2), rgba(0, 0, 0, 0)); border: 1px solid rgba(76, 175, 80, 0.3)">
                      <v-icon small color="primary">mdi-view-dashboard-outline</v-icon>
                    </div>
                    <div>
                      <div class="wc-sheet-title" style="font-size: 1.25rem; font-weight: 800; letter-spacing: 0.5px">Vorlagen</div>
                      <div style="font-size: 0.75rem; color: rgba(255, 255, 255, 0.4); font-weight: 500; margin-top: 2px">
                        Wähle oder bearbeite Vorlagen
                      </div>
                    </div>
                  </div>
                  <div class="d-flex" style="gap: 8px">
                    <v-btn
                      v-if="mobileActiveTab !== 0"
                      icon
                      small
                      @click="mobileEditMode = !mobileEditMode"
                      :color="mobileEditMode ? 'primary' : ''"
                      class="wc-sheet-close"
                      style="background: rgba(255, 255, 255, 0.05); border: 1px solid rgba(255, 255, 255, 0.1)">
                      <v-icon small>{{ mobileEditMode ? "mdi-check" : "mdi-pencil-outline" }}</v-icon>
                    </v-btn>
                    <v-btn
                      icon
                      small
                      @click="mobileTemplateSheet = false"
                      class="wc-sheet-close"
                      style="background: rgba(255, 255, 255, 0.05); border: 1px solid rgba(255, 255, 255, 0.1)">
                      <v-icon small>mdi-close</v-icon>
                    </v-btn>
                  </div>
                </div>
                <button v-if="hasGuestbookEntryRight" type="button" class="wc-mobile-guestbook-action mb-2" @click="openGuestbookEntry">
                  <span class="wc-mobile-guestbook-icon">
                    <v-icon color="white" size="22">mdi-book-open-page-variant-outline</v-icon>
                  </span>
                  <span class="wc-mobile-guestbook-copy">
                    <span class="wc-mobile-guestbook-title">Gästebuch Eintrag</span>
                    <span class="wc-mobile-guestbook-subtitle">Gastdaten direkt auf diesem iPad erfassen</span>
                  </span>
                  <v-icon class="wc-mobile-guestbook-arrow">mdi-chevron-right</v-icon>
                </button>
                <!-- Premium Segmented Tab Control (2x2 Grid) -->
                <div class="wc-premium-tabs mb-2 mt-2">
                  <div class="wc-premium-tab" :class="{ active: mobileActiveTab === 0 }" @click="mobileActiveTab = 0">
                    <v-icon small class="wc-premium-tab-icon">mdi-checkbox-marked-circle-outline</v-icon>
                    <span class="wc-premium-tab-text">Ämtli</span>
                  </div>
                  <div class="wc-premium-tab" :class="{ active: mobileActiveTab === 1 }" @click="mobileActiveTab = 1">
                    <v-icon small class="wc-premium-tab-icon">mdi-clipboard-text</v-icon>
                    <span class="wc-premium-tab-text">Aufgabe</span>
                  </div>
                  <div class="wc-premium-tab" :class="{ active: mobileActiveTab === 2 }" @click="mobileActiveTab = 2">
                    <v-icon small class="wc-premium-tab-icon">mdi-calendar-star</v-icon>
                    <span class="wc-premium-tab-text">Termin</span>
                  </div>
                  <div class="wc-premium-tab" :class="{ active: mobileActiveTab === 3 }" @click="mobileActiveTab = 3">
                    <v-icon small class="wc-premium-tab-icon">mdi-account-clock</v-icon>
                    <span class="wc-premium-tab-text">Info</span>
                  </div>
                </div>
              </div>

              <!-- Suchfeld -->
              <div class="px-5 mt-2 pb-2">
                <v-text-field
                  v-model="mobileSearchQuery"
                  placeholder="Suchen…"
                  dense
                  solo
                  flat
                  :dark="isDarkMode"
                  :background-color="isDarkMode ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)'"
                  hide-details
                  clearable
                  prepend-inner-icon="mdi-magnify"
                  class="wc-mobile-search"
                  @click:clear="mobileSearchQuery = ''"></v-text-field>
              </div>

              <!-- Sheet Body -->
              <div class="wc-sheet-body custom-scrollbar pt-2">
                <!-- Kontext-Banner: Zeigt die geklickte Uhrzeit an -->
                <div
                  v-if="mobileClickContext"
                  class="mx-4 mb-3 pa-3 d-flex align-center justify-space-between rounded-lg"
                  style="background: rgba(76, 175, 80, 0.1); border: 1px solid rgba(76, 175, 80, 0.25)">
                  <div class="d-flex align-center">
                    <v-icon small color="#66bb6a" class="mr-2">mdi-clock-check-outline</v-icon>
                    <span style="font-size: 0.8rem; font-weight: 600; color: #a5d6a7">
                      Termin um <strong style="color: #66bb6a">{{ mobileClickContext.time }}</strong> Uhr einfügen
                    </span>
                  </div>
                  <v-btn icon x-small @click="mobileClickContext = null" style="color: rgba(255, 255, 255, 0.3)">
                    <v-icon x-small>mdi-close</v-icon>
                  </v-btn>
                </div>
                <!-- TAB 0: ÄMTLI -->
                <div v-show="mobileActiveTab === 0">
                  <!-- Auto-assign Button on Mobile -->
                  <div v-if="hasTaskManagementRight" class="px-5 mb-4">
                    <v-btn
                      block
                      color="primary"
                      class="rounded-lg"
                      depressed
                      @click="openAmtliConfigDialog"
                      style="text-transform: none; letter-spacing: 0; font-weight: 600">
                      <v-icon left>mdi-cog-outline</v-icon>
                      Ämtli-Einstellungen
                    </v-btn>
                  </div>

                  <!-- Manuelle Ämtli: konfiguriert für die Sidebar, ohne automatische Wiederholung -->
                  <div class="wc-sheet-section" v-if="mobileManualTasks.length > 0">
                    <div class="wc-sheet-section-label" style="color: #29b6f6">Bei Bedarf</div>
                    <div class="wc-sheet-grid-icons">
                      <div
                        v-for="tpl in mobileManualTasks"
                        :key="tpl.id"
                        class="wc-sheet-tile"
                        :style="{
                          opacity: tpl.isAssigned ? 0.55 : 1,
                          '--tile-color': tpl.color || '#29B6F6',
                          position: 'relative',
                        }"
                        @click="!mobileEditMode && !tpl.isAssigned && handleMobileTemplateClick(tpl)">
                        <div class="wc-sheet-tile-icon" :style="{ background: tpl.color || '#29B6F6' }">
                          <v-icon color="white" size="20">{{ tpl.icon || "mdi-hand-pointing-right" }}</v-icon>
                        </div>
                        <div class="wc-sheet-tile-title" :style="{ textDecoration: tpl.isAssigned ? 'line-through' : 'none' }" :title="tpl.title">
                          {{ taskVariantLabels(tpl).title }}
                        </div>
                        <div class="wc-sheet-tile-meta font-weight-bold" style="color: #29b6f6"><span v-if="taskVariantLabels(tpl).group" class="wc-sheet-tile-group">{{ taskVariantLabels(tpl).group }} · </span>
                          {{ tpl.isAssigned ? "Heute eingeplant" : "Manuell einfügen" }}
                        </div>
                        <work-calendar-task-assignment-info
                          :template-id="tpl._id || tpl.templateId || tpl.id"
                          :completions="completions"
                          :current-date="currentDate"
                          frequency="once"
                          :is-dark-mode="isDarkMode" />
                        <v-btn
                          v-if="mobileEditMode && hasAppointmentManagementRight"
                          icon
                          small
                          color="white"
                          style="position: absolute; top: 4px; right: 4px; background: #e53935; width: 24px; height: 24px"
                          @click.stop="deleteTemplate(tpl._id || tpl.templateId)"
                          ><v-icon size="14">mdi-delete</v-icon></v-btn
                        >
                        <v-btn
                          v-if="mobileEditMode"
                          icon
                          small
                          color="white"
                          style="position: absolute; top: 4px; left: 4px; background: var(--v-primary-base); width: 24px; height: 24px"
                          @click.stop="editTemplate(tpl)"
                          ><v-icon size="14">mdi-pencil</v-icon></v-btn
                        >
                      </div>
                    </div>
                  </div>

                  <!-- Tägliche Ämtli (Daily Tasks) -->
                  <div class="wc-sheet-section" v-if="dailyTasks.length > 0">
                    <div class="wc-sheet-section-label" style="color: #66bb6a">Tägliche Ämtli</div>
                    <div class="wc-sheet-grid-icons">
                      <div
                        v-for="tpl in dailyTasks"
                        :key="tpl.id"
                        class="wc-sheet-tile"
                        :class="{ 'wc-task-highlight': !tpl.isAssigned }"
                        :style="{
                          opacity: tpl.isAssigned ? 0.55 : 1,
                          '--tile-color': tpl.color || '#4CAF50',
                          position: 'relative',
                        }"
                        @click="!mobileEditMode && handleMobileTemplateClick(tpl)">
                        <div class="wc-sheet-tile-icon" :style="{ background: tpl.color || '#4CAF50' }">
                          <v-icon color="white" size="20">{{ tpl.icon || "mdi-checkbox-marked" }}</v-icon>
                        </div>
                        <div class="wc-sheet-tile-title" :style="{ textDecoration: tpl.isAssigned ? 'line-through' : 'none' }" :title="tpl.title">
                          {{ taskVariantLabels(tpl).title }}
                        </div>
                        <div class="wc-sheet-tile-meta font-weight-bold" style="color: #66bb6a"><span v-if="taskVariantLabels(tpl).group" class="wc-sheet-tile-group">{{ taskVariantLabels(tpl).group }} · </span>
                          <v-icon v-if="!tpl.isManualDaily" x-small color="#66bb6a" class="mr-1">mdi-clock-outline</v-icon>{{ tpl.time }}
                        </div>
                        <work-calendar-task-assignment-info
                          :template-id="tpl._id || tpl.templateId || tpl.id"
                          :completions="completions"
                          :current-date="currentDate"
                          :frequency="tpl.frequency || 'daily'"
                          :exact-time="tpl.assignedTime || (tpl.isManualDaily ? '' : tpl.time || '')"
                          :is-dark-mode="isDarkMode" />
                        <v-btn
                          v-if="mobileEditMode && hasAppointmentManagementRight"
                          icon
                          small
                          color="white"
                          style="position: absolute; top: 4px; right: 4px; background: #e53935; width: 24px; height: 24px"
                          @click.stop="deleteTemplate(tpl._id || tpl.id)"
                          ><v-icon size="14">mdi-delete</v-icon></v-btn
                        >
                        <v-btn
                          v-if="mobileEditMode"
                          icon
                          small
                          color="white"
                          style="position: absolute; top: 4px; left: 4px; background: var(--v-primary-base); width: 24px; height: 24px"
                          @click.stop="editTemplate(tpl)"
                          ><v-icon size="14">mdi-pencil</v-icon></v-btn
                        >
                      </div>
                    </div>
                  </div>
                  <div
                    v-if="
                      dailyTasks.length === 0 &&
                      mobileManualTasks.length === 0 &&
                      mobileWeeklyTaskTemplates.length === 0 &&
                      mobileMonthlyTaskTemplates.length === 0
                    "
                    class="text-center mt-10 opacity-40 mb-6">
                    <v-icon large class="mb-3" color="rgba(255,255,255,0.3)">mdi-calendar-blank-outline</v-icon>
                    <div style="font-size: 0.85rem">Keine Ämtli vorhanden.</div>
                  </div>

                  <!-- Wöchentliche Aufgaben -->
                  <div class="wc-sheet-section mt-6" v-if="mobileWeeklyTaskTemplates.length > 0">
                    <div class="wc-sheet-section-label" style="color: #ab47bc">Wöchentliche Ämtli</div>
                    <div class="wc-sheet-grid-icons">
                      <div
                        v-for="tpl in mobileWeeklyTaskTemplates"
                        :key="tpl._id || tpl.id"
                        class="wc-sheet-tile"
                        :class="{ 'wc-sheet-tile-urgent': getTaskUrgencyLevel(tpl) === 2, 'wc-sheet-tile-warning': getTaskUrgencyLevel(tpl) === 1 }"
                        :style="{
                          '--tile-color': tpl.color || '#AB47BC',
                          position: 'relative',
                          opacity: isTaskTargetPlanned(tpl) ? 0.4 : 1,
                          flexWrap: 'wrap',
                        }"
                        @click="!mobileEditMode && !isTaskTargetPlanned(tpl) && handleMobileTemplateClick(tpl)">
                        <div class="wc-sheet-tile-icon" :style="{ background: tpl.color || '#AB47BC' }">
                          <v-icon color="white" size="20">{{ tpl.icon || "mdi-calendar-week" }}</v-icon>
                        </div>
                        <div
                          class="wc-sheet-tile-title text-truncate"
                          :style="{ textDecoration: isTaskTargetPlanned(tpl) ? 'line-through' : 'none', flex: 1, paddingRight: '8px' }" :title="tpl.title">
                          {{ taskVariantLabels(tpl).title }}
                        </div>
                        <div v-if="getTaskUrgencyLevel(tpl) === 2" class="wc-sheet-urgency-badge urgent">FÄLLIG</div>
                        <div v-else-if="getTaskUrgencyLevel(tpl) === 1" class="wc-sheet-urgency-badge warning">BALD</div>
                        <div class="wc-sheet-tile-meta" style="width: 100%; margin-top: 4px"><span v-if="taskVariantLabels(tpl).group" class="wc-sheet-tile-group">{{ taskVariantLabels(tpl).group }} · </span>{{ getTaskCountString(tpl, "weekly") }}</div>
                        <work-calendar-task-assignment-info
                          :template-id="tpl._id || tpl.id"
                          :completions="completions"
                          :current-date="currentDate"
                          frequency="weekly"
                          :is-dark-mode="isDarkMode" />
                        <v-btn
                          v-if="mobileEditMode && hasAppointmentManagementRight"
                          icon
                          small
                          color="white"
                          style="position: absolute; top: 4px; right: 4px; background: #e53935; width: 24px; height: 24px"
                          @click.stop="deleteTemplate(tpl._id || tpl.id)"
                          ><v-icon size="14">mdi-delete</v-icon></v-btn
                        >
                        <v-btn
                          v-if="mobileEditMode"
                          icon
                          small
                          color="white"
                          style="position: absolute; top: 4px; left: 4px; background: var(--v-primary-base); width: 24px; height: 24px"
                          @click.stop="editTemplate(tpl)"
                          ><v-icon size="14">mdi-pencil</v-icon></v-btn
                        >
                      </div>
                    </div>
                  </div>

                  <!-- Monatliche Aufgaben -->
                  <div class="wc-sheet-section mt-6" v-if="mobileMonthlyTaskTemplates.length > 0">
                    <div class="wc-sheet-section-label" style="color: #00897b">Monatliche Ämtli</div>
                    <div class="wc-sheet-grid-icons">
                      <div
                        v-for="tpl in mobileMonthlyTaskTemplates"
                        :key="tpl._id || tpl.id"
                        class="wc-sheet-tile"
                        :class="{ 'wc-sheet-tile-urgent': getTaskUrgencyLevel(tpl) === 2, 'wc-sheet-tile-warning': getTaskUrgencyLevel(tpl) === 1 }"
                        :style="{
                          '--tile-color': tpl.color || '#00897B',
                          position: 'relative',
                          opacity: isTaskTargetPlanned(tpl) ? 0.4 : 1,
                          flexWrap: 'wrap',
                        }"
                        @click="!mobileEditMode && !isTaskTargetPlanned(tpl) && handleMobileTemplateClick(tpl)">
                        <div class="wc-sheet-tile-icon" :style="{ background: tpl.color || '#00897B' }">
                          <v-icon color="white" size="20">{{ tpl.icon || "mdi-calendar-month" }}</v-icon>
                        </div>
                        <div
                          class="wc-sheet-tile-title text-truncate"
                          :style="{ textDecoration: isTaskTargetPlanned(tpl) ? 'line-through' : 'none', flex: 1, paddingRight: '8px' }" :title="tpl.title">
                          {{ taskVariantLabels(tpl).title }}
                        </div>
                        <div v-if="getTaskUrgencyLevel(tpl) === 2" class="wc-sheet-urgency-badge urgent">FÄLLIG</div>
                        <div v-else-if="getTaskUrgencyLevel(tpl) === 1" class="wc-sheet-urgency-badge warning">BALD</div>
                        <div class="wc-sheet-tile-meta" style="width: 100%; margin-top: 4px"><span v-if="taskVariantLabels(tpl).group" class="wc-sheet-tile-group">{{ taskVariantLabels(tpl).group }} · </span>{{ getTaskCountString(tpl, "monthly") }}</div>
                        <work-calendar-task-assignment-info
                          :template-id="tpl._id || tpl.id"
                          :completions="completions"
                          :current-date="currentDate"
                          frequency="monthly"
                          :is-dark-mode="isDarkMode" />
                        <v-btn
                          v-if="mobileEditMode && hasAppointmentManagementRight"
                          icon
                          small
                          color="white"
                          style="position: absolute; top: 4px; right: 4px; background: #e53935; width: 24px; height: 24px"
                          @click.stop="deleteTemplate(tpl._id || tpl.id)"
                          ><v-icon size="14">mdi-delete</v-icon></v-btn
                        >
                        <v-btn
                          v-if="mobileEditMode"
                          icon
                          small
                          color="white"
                          style="position: absolute; top: 4px; left: 4px; background: var(--v-primary-base); width: 24px; height: 24px"
                          @click.stop="editTemplate(tpl)"
                          ><v-icon size="14">mdi-pencil</v-icon></v-btn
                        >
                      </div>
                    </div>
                  </div>
                </div>

                <!-- TAB 1: AUFGABE -->
                <div v-show="mobileActiveTab === 1">
                  <div class="wc-sheet-section mt-4" v-if="mobileTaskTemplates.length > 0">
                    <div class="wc-sheet-section-label" style="color: #ff9800">Aufgaben Vorlagen</div>
                    <div class="wc-sheet-grid-icons">
                      <div
                        v-for="tpl in mobileTaskTemplates"
                        :key="tpl._id || tpl.id"
                        class="wc-sheet-tile"
                        :style="{ '--tile-color': tpl.color || '#FF9800', position: 'relative' }"
                        @click="!mobileEditMode && handleMobileTemplateClick(tpl)">
                        <div class="wc-sheet-tile-icon" :style="{ background: tpl.color || '#FF9800' }">
                          <v-icon color="white" size="20">{{ tpl.icon || "mdi-clipboard-text" }}</v-icon>
                        </div>
                        <div class="wc-sheet-tile-title">{{ tpl.title }}</div>
                        <div class="wc-sheet-tile-meta">Aufgabe</div>
                        <v-btn
                          v-if="mobileEditMode && hasAppointmentManagementRight"
                          icon
                          small
                          color="white"
                          style="position: absolute; top: 4px; right: 4px; background: #e53935; width: 24px; height: 24px"
                          @click.stop="deleteTemplate(tpl._id || tpl.id)"
                          ><v-icon size="14">mdi-delete</v-icon></v-btn
                        >
                        <v-btn
                          v-if="mobileEditMode && hasAppointmentManagementRight"
                          icon
                          small
                          color="white"
                          style="position: absolute; top: 4px; left: 4px; background: var(--v-primary-base); width: 24px; height: 24px"
                          @click.stop="editTemplate(tpl)"
                          ><v-icon size="14">mdi-pencil</v-icon></v-btn
                        >
                      </div>
                    </div>
                  </div>
                  <div v-else class="text-center mt-10 opacity-40 mb-6">
                    <v-icon large class="mb-3" color="rgba(255,255,255,0.3)">mdi-clipboard-text</v-icon>
                    <div style="font-size: 0.85rem">Keine Aufgaben Vorlagen.</div>
                  </div>
                </div>

                <!-- TAB 2: TERMINE -->
                <div v-show="mobileActiveTab === 2">
                  <!-- Trainings & Checkups -->
                  <div class="wc-sheet-section">
                    <div class="wc-sheet-section-label">Trainings & Checkups</div>
                    <div class="wc-sheet-grid-icons">
                      <div
                        v-for="tpl in mobileTrainingTemplates"
                        :key="tpl.id"
                        class="wc-sheet-tile"
                        :style="{ '--tile-color': tpl.color }"
                        @click="handleMobileTemplateClick(tpl)">
                        <div class="wc-sheet-tile-icon" :style="{ background: tpl.color }">
                          <v-icon color="white" size="20">{{ tpl.icon }}</v-icon>
                        </div>
                        <div class="wc-sheet-tile-title">{{ tpl.title }}</div>
                        <div class="wc-sheet-tile-meta">{{ tpl.duration }} Min.</div>
                      </div>
                    </div>
                  </div>

                  <!-- Verträge & Management -->
                  <div class="wc-sheet-section">
                    <div class="wc-sheet-section-label">Verträge & Management</div>
                    <div class="wc-sheet-grid-icons">
                      <div
                        v-for="tpl in mobileContractTemplates"
                        :key="tpl.id"
                        class="wc-sheet-tile"
                        :style="{ '--tile-color': tpl.color }"
                        @click="handleMobileTemplateClick(tpl)">
                        <div class="wc-sheet-tile-icon" :style="{ background: tpl.color }">
                          <v-icon color="white" size="20">{{ tpl.icon }}</v-icon>
                        </div>
                        <div class="wc-sheet-tile-title">{{ tpl.title }}</div>
                        <div class="wc-sheet-tile-meta">Aktion</div>
                      </div>
                    </div>
                  </div>

                  <!-- Schulung & Teamsitzung -->
                  <div class="wc-sheet-section">
                    <div class="wc-sheet-section-label">Schulung & Teamsitzung</div>
                    <div class="wc-sheet-grid-icons">
                      <div
                        v-for="tpl in mobileSchulungTemplates"
                        :key="tpl.id"
                        class="wc-sheet-tile"
                        :style="{ '--tile-color': tpl.color }"
                        @click="handleMobileTemplateClick(tpl)">
                        <div class="wc-sheet-tile-icon" :style="{ background: tpl.color }">
                          <v-icon color="white" size="20">{{ tpl.icon }}</v-icon>
                        </div>
                        <div class="wc-sheet-tile-title">{{ tpl.title }}</div>
                        <div class="wc-sheet-tile-meta">{{ tpl.duration }} Min.</div>
                      </div>
                    </div>
                  </div>

                  <!-- Termine -->
                  <div class="wc-sheet-section">
                    <div class="wc-sheet-section-label">Termine</div>
                    <div class="wc-sheet-grid-icons">
                      <div
                        v-for="tpl in mobileStandardTemplates"
                        :key="tpl.id"
                        class="wc-sheet-tile"
                        :style="{ '--tile-color': tpl.color }"
                        @click="handleMobileTemplateClick(tpl)">
                        <div class="wc-sheet-tile-icon" :style="{ background: tpl.color }">
                          <v-icon color="white" size="20">{{ tpl.icon }}</v-icon>
                        </div>
                        <div class="wc-sheet-tile-title">{{ tpl.title }}</div>
                        <div class="wc-sheet-tile-meta">{{ tpl.duration }} Min.</div>
                      </div>
                    </div>
                  </div>

                  <!-- Eigene Vorlagen -->
                  <div class="wc-sheet-section" v-if="mobileCustomAppointmentTemplates.length > 0">
                    <div class="wc-sheet-section-label" style="color: var(--v-primary-base)">Eigene Vorlagen</div>
                    <div class="wc-sheet-grid-icons">
                      <div
                        v-for="tpl in mobileCustomAppointmentTemplates"
                        :key="tpl._id || tpl.id"
                        class="wc-sheet-tile"
                        :style="{ '--tile-color': tpl.color || 'var(--v-primary-base)', position: 'relative' }"
                        @click="!mobileEditMode && handleMobileTemplateClick(tpl)">
                        <div class="wc-sheet-tile-icon" :style="{ background: tpl.color || 'var(--v-primary-base)' }">
                          <v-icon color="white" size="20">{{ tpl.icon || "mdi-calendar" }}</v-icon>
                        </div>
                        <div class="wc-sheet-tile-title">{{ tpl.title }}</div>
                        <div class="wc-sheet-tile-meta">{{ tpl.duration }} Min.</div>
                        <v-btn
                          v-if="mobileEditMode && hasAppointmentManagementRight"
                          icon
                          small
                          color="white"
                          style="position: absolute; top: 4px; right: 4px; background: #e53935; width: 24px; height: 24px"
                          @click.stop="deleteTemplate(tpl._id || tpl.id)">
                          <v-icon size="14">mdi-delete</v-icon>
                        </v-btn>
                        <v-btn
                          v-if="mobileEditMode && hasAppointmentManagementRight"
                          icon
                          small
                          color="white"
                          style="position: absolute; top: 4px; left: 4px; background: var(--v-primary-base); width: 24px; height: 24px"
                          @click.stop="editTemplate(tpl)">
                          <v-icon size="14">mdi-pencil</v-icon>
                        </v-btn>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- TAB 3: UNBETREUT -->
                <div v-show="mobileActiveTab === 3">
                  <div class="wc-sheet-section mt-4" v-if="mobileUnsupervisedTemplates.length > 0">
                    <div class="wc-sheet-section-label" style="color: #9c27b0">Info Vorlagen</div>
                    <div class="wc-sheet-grid-icons">
                      <div
                        v-for="tpl in mobileUnsupervisedTemplates"
                        :key="tpl._id || tpl.id"
                        class="wc-sheet-tile"
                        :style="{ '--tile-color': tpl.color || '#9C27B0', position: 'relative' }"
                        @click="!mobileEditMode && handleMobileTemplateClick(tpl)">
                        <div class="wc-sheet-tile-icon" :style="{ background: tpl.color || '#9C27B0' }">
                          <v-icon color="white" size="20">{{ tpl.icon || "mdi-account-clock" }}</v-icon>
                        </div>
                        <div class="wc-sheet-tile-title">{{ tpl.title }}</div>
                        <div class="wc-sheet-tile-meta">Info</div>
                        <v-btn
                          v-if="mobileEditMode && hasAppointmentManagementRight"
                          icon
                          small
                          color="white"
                          style="position: absolute; top: 4px; right: 4px; background: #e53935; width: 24px; height: 24px"
                          @click.stop="deleteTemplate(tpl._id || tpl.id)"
                          ><v-icon size="14">mdi-delete</v-icon></v-btn
                        >
                        <v-btn
                          v-if="mobileEditMode && hasAppointmentManagementRight"
                          icon
                          small
                          color="white"
                          style="position: absolute; top: 4px; left: 4px; background: var(--v-primary-base); width: 24px; height: 24px"
                          @click.stop="editTemplate(tpl)"
                          ><v-icon size="14">mdi-pencil</v-icon></v-btn
                        >
                      </div>
                    </div>
                  </div>
                  <div v-else class="text-center mt-10 opacity-40 mb-6">
                    <v-icon large class="mb-3" color="rgba(255,255,255,0.3)">mdi-account-clock</v-icon>
                    <div style="font-size: 0.85rem">Keine Info Vorlagen.</div>
                  </div>
                </div>
              </div>

              <!-- Sheet Footer -->
              <div class="wc-sheet-footer">
                <v-btn
                  block
                  depressed
                  class="wc-sheet-create-btn"
                  @click="
                    mobileTemplateSheet = false
                    openCreateTemplate()
                  ">
                  <v-icon left small>mdi-plus-circle</v-icon>
                  Neue Vorlage erstellen
                </v-btn>
              </div>
            </v-card>
          </v-dialog>

          <!-- Ämtli Smart-Assignment Dialog -->
          <v-dialog
            v-model="taskAssignDialog"
            :persistent="taskAssignSaving"
            :fullscreen="$vuetify.breakpoint.xsOnly"
            :max-width="$vuetify.breakpoint.xsOnly ? undefined : 520"
            transition="dialog-bottom-transition"
            :content-class="$vuetify.breakpoint.xsOnly ? 'wc-mobile-sheet-fullscreen' : 'wc-task-assign-dialog'">
            <v-card dark :class="$vuetify.breakpoint.xsOnly ? 'wc-mobile-sheet-card' : 'wc-task-assign-card'">
              <!-- Header -->
              <div class="wc-sheet-header">
                <div class="d-flex align-center" style="gap: 10px">
                  <div
                    class="wc-sheet-tile-icon"
                    :style="{ background: taskAssignData.color || '#9C27B0', width: '36px', height: '36px', borderRadius: '10px' }">
                    <v-icon small color="white">{{ taskAssignData.icon || "mdi-checkbox-marked" }}</v-icon>
                  </div>
                  <div>
                    <div class="wc-sheet-title">{{ taskAssignData.title || "Ämtli zuweisen" }}</div>
                    <div class="wc-sheet-subtitle">Wähle Zeit & Mitarbeiter</div>
                  </div>
                </div>
                <v-btn icon small :disabled="taskAssignSaving" @click="taskAssignDialog = false" class="wc-sheet-close">
                  <v-icon small>mdi-close</v-icon>
                </v-btn>
              </div>

              <!-- Time & Date Picker -->
              <div class="wc-task-time-picker pa-4 mx-0" style="gap: 12px; display: flex; flex-direction: column">
                <div v-if="taskAssignData.frequency !== 'daily'" style="width: 100%">
                  <div class="wc-task-time-label mb-1">
                    <v-icon x-small color="rgba(255,255,255,0.4)" class="mr-1">mdi-calendar</v-icon>
                    Datum
                  </div>
                  <input type="date" v-model="taskAssignDate" :disabled="taskAssignSaving" class="wc-task-time-input" style="width: 100%" @change="computeTaskAvailability({ suggestTime: true })" />
                </div>
                <div class="d-flex w-100" style="gap: 12px">
                  <div style="flex: 1">
                    <div class="wc-task-time-label mb-1">
                      <v-icon x-small color="rgba(255,255,255,0.4)" class="mr-1">mdi-clock-outline</v-icon>
                      Uhrzeit
                    </div>
                    <input type="time" v-model="taskAssignTime" :disabled="taskAssignSaving" class="wc-task-time-input" style="width: 100%" @change="computeTaskAvailability()" />
                  </div>
                  <div style="flex: 1">
                    <div class="wc-task-time-label mb-1">
                      <v-icon x-small color="rgba(255,255,255,0.4)" class="mr-1">mdi-timer-outline</v-icon>
                      Dauer (Min)
                    </div>
                    <input
                      type="number"
                      v-model.number="taskAssignDuration"
                      :disabled="taskAssignSaving"
                      min="1"
                      max="1440"
                      class="wc-task-time-input"
                      style="width: 100%"
                      @change="computeTaskAvailability({ suggestTime: true })" />
                  </div>
                </div>
              </div>

              <!-- Employee Availability Grid -->
              <div class="wc-sheet-body custom-scrollbar" style="padding-top: 8px">
                <div class="wc-sheet-section-label mb-3">Verfügbarkeit</div>

                <v-progress-linear v-if="taskAvailabilityLoading || taskAssignSaving" indeterminate color="primary" class="mb-3" />
                <div v-if="taskAvailabilityNotice" role="status" class="text-body-2 mb-3">{{ taskAvailabilityNotice }}</div>

                <div v-if="!taskAvailabilityLoading && !taskAvailabilityNotice && taskAvailability.length === 0" class="text-center py-6" style="color: rgba(255, 255, 255, 0.3)">
                  <v-icon size="36" color="rgba(255,255,255,0.15)" class="mb-2">mdi-account-off</v-icon>
                  <div style="font-size: 0.8rem">Keine Mitarbeiter eingeplant</div>
                </div>

                <div
                  class="wc-task-emp-card"
                  v-for="emp in taskAvailability"
                  :key="emp.id"
                  :class="{
                    'wc-task-emp-free': emp.status === 'free',
                    'wc-task-emp-busy': emp.status === 'busy',
                    'wc-task-emp-noshift': emp.status === 'noshift',
                    'wc-task-emp-restricted': emp.status === 'restricted',
                    'disabled-card': emp.status !== 'free' || taskAvailabilityLoading || taskAssignSaving,
                  }"
                  @click="emp.status === 'free' && assignTask(emp)">
                  <v-avatar size="36" class="wc-task-emp-avatar">
                    <img :src="emp.picture ? '/img/profilbilder/' + emp.picture : '/img/default-avatar.png'" style="object-fit: cover" />
                  </v-avatar>
                  <div class="wc-task-emp-info">
                    <div class="wc-task-emp-name">{{ emp.firstName }}</div>
                    <div class="wc-task-emp-shift">
                      <v-icon x-small style="font-size: 10px" class="mr-1">mdi-clock-outline</v-icon>
                      {{ emp.shiftRange }}
                    </div>
                  </div>
                  <div class="wc-task-emp-status">
                    <template v-if="emp.status === 'free'">
                      <div class="wc-task-badge wc-task-badge-free">
                        <v-icon x-small color="white" style="font-size: 10px">mdi-check</v-icon>
                        Frei
                      </div>
                    </template>
                    <template v-else-if="emp.status === 'busy'">
                      <div class="wc-task-badge wc-task-badge-busy">
                        <v-icon x-small style="font-size: 10px">mdi-clock-alert</v-icon>
                        Belegt
                      </div>
                    </template>
                    <template v-else-if="emp.status === 'restricted'">
                      <div class="wc-task-badge wc-task-badge-restricted">
                        <v-icon x-small style="font-size: 10px">mdi-shield-lock-outline</v-icon>
                        Gesperrt
                      </div>
                    </template>
                    <template v-else-if="emp.status === 'outside_window'">
                      <div class="wc-task-badge wc-task-badge-off">Ausserhalb Zeitfenster</div>
                    </template>
                    <template v-else>
                      <div class="wc-task-badge wc-task-badge-off">Keine Schicht</div>
                    </template>
                    <div v-if="emp.nextFree" class="wc-task-alt">Frei ab {{ emp.nextFree }}</div>
                  </div>
                </div>
              </div>
            </v-card>
          </v-dialog>
        </div>

        <!-- Mitarbeiter Sortierung Dialog -->
        <v-dialog v-model="sortEmployeesDialog" max-width="650" persistent :dark="isDarkMode">
          <v-card
            class="rounded-xl overflow-hidden"
            :style="`background: ${isDarkMode ? '#1e1e1e' : '#ffffff'}; border: 1px solid ${
              isDarkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)'
            }`">
            <v-card-title class="px-6 pt-6 pb-2 d-flex align-center justify-space-between">
              <span class="text-h6 font-weight-bold" :style="`color: ${isDarkMode ? '#ffffff' : '#1e1e1e'}`">Mitarbeiter sortieren</span>
              <v-btn icon @click="sortEmployeesDialog = false" :dark="isDarkMode">
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </v-card-title>

            <v-card-text class="px-6 pb-2">
              <p class="caption mb-1" :style="`color: ${isDarkMode ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.6)'}`">
                Bewege die Mitarbeiter per Drag & Drop in die gewünschte Reihenfolge. Diese Reihenfolge wird für alle geplanten und ungeplanten Tage
                übernommen.
              </p>

              <!-- Drag and Drop Grid -->
              <div class="employee-sort-grid mt-4 custom-scrollbar">
                <div
                  v-for="(emp, idx) in sortedEmployeeList"
                  :key="emp._id"
                  draggable="true"
                  @dragstart="onSortDragStart($event, idx)"
                  @dragover="onSortDragOver($event, idx)"
                  @drop="onSortDrop($event, idx)"
                  @dragend="onSortDragEnd"
                  class="d-flex align-center pa-2 rounded-lg sort-item"
                  :style="sortItemStyle(idx)">
                  <v-icon class="mr-2 drag-handle" small>mdi-drag</v-icon>

                  <!-- Avatar -->
                  <v-avatar size="28" class="mr-2" style="border: 1px solid rgba(255, 255, 255, 0.1); flex-shrink: 0">
                    <v-img v-if="emp.picture" :src="emp.picture" />
                    <span
                      v-else
                      class="white--text font-weight-bold d-flex align-center justify-center fill-height w-100"
                      style="font-size: 0.75rem; background-color: var(--primary)">
                      {{ getEmpInitials(emp) }}
                    </span>
                  </v-avatar>

                  <!-- Name -->
                  <span
                    class="font-weight-medium text-caption text-truncate"
                    :style="`color: ${isDarkMode ? '#e0e0e0' : '#333333'}`"
                    :title="emp.fullName || emp.vorname + ' ' + emp.nachname">
                    {{ emp.fullName || emp.vorname + " " + emp.nachname }}
                  </span>
                </div>
              </div>
            </v-card-text>

            <v-card-actions class="px-6 pb-6 pt-2 d-flex justify-end" style="gap: 8px">
              <v-btn text rounded depressed @click="sortEmployeesDialog = false" :dark="isDarkMode"> Abbrechen </v-btn>
              <v-btn color="primary" rounded depressed :loading="savingSortOrder" @click="saveSortOrder"> Speichern </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

        <v-dialog v-model="customerAppointmentLauncherDialog" max-width="760" :fullscreen="$vuetify.breakpoint.xsOnly" :dark="isDarkMode">
          <v-card :dark="isDarkMode" class="wc-customer-appointment-launcher" :class="{ 'wc-customer-appointment-launcher--light': !isDarkMode }">
            <header class="wc-customer-appointment-launcher__header">
              <div class="wc-customer-appointment-launcher__identity">
                <div class="wc-customer-appointment-launcher__icon">
                  <v-icon color="white">mdi-calendar-plus</v-icon>
                </div>
                <div>
                  <span class="wc-customer-appointment-launcher__eyebrow">TERMIN DIREKT PLANEN</span>
                  <h2>Terminart auswählen</h2>
                  <p v-if="customerAppointmentLauncherCustomer">
                    {{ customerAppointmentLauncherCustomer.vorname }}
                    {{ customerAppointmentLauncherCustomer.nachname }}
                  </p>
                </div>
              </div>
              <v-btn icon aria-label="Terminauswahl schliessen" @click="closeCustomerAppointmentLauncher">
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </header>

            <v-card-text class="wc-customer-appointment-launcher__body">
              <p class="wc-customer-appointment-launcher__hint">
                Der Kunde wird automatisch in den bestehenden Termin-Dialog übernommen. Zeit, Mitarbeiter und weitere Details kannst du dort wie
                gewohnt festlegen.
              </p>
              <div class="wc-customer-appointment-launcher__grid">
                <button
                  v-for="(template, index) in customerAppointmentLauncherTemplates"
                  :key="`${template.id || template._id || 'appointment'}-${index}`"
                  type="button"
                  class="wc-customer-appointment-launcher__template"
                  @click="startCustomerAppointment(template)">
                  <span class="wc-customer-appointment-launcher__template-icon" :style="{ backgroundColor: template.color || '#009640' }">
                    <v-icon color="white">{{ template.icon || "mdi-calendar" }}</v-icon>
                  </span>
                  <span class="wc-customer-appointment-launcher__template-copy">
                    <strong>{{ template.title }}</strong>
                    <small>{{ template.duration || 60 }} Minuten</small>
                  </span>
                  <v-icon>mdi-chevron-right</v-icon>
                </button>
              </div>
            </v-card-text>
          </v-card>
        </v-dialog>

        <work-calendar-drop-dialog
          v-model="dropDialog"
          :data="pendingDrop"
          :employees="allStudioEmployees"
          :current-date="currentDate"
          :is-dark-mode="isDarkMode"
          :tac-appointment-billing-active="tacAppointmentBillingActive"
          :saving="dropDialogSaving"
          @save="confirmDrop" />

        <!-- Kollisions-Dialog für Serien-Termine -->
        <v-dialog v-model="collisionDialog" max-width="500" persistent :dark="isDarkMode">
          <v-card
            :dark="isDarkMode"
            class="rounded-xl overflow-hidden"
            :style="{
              background: isDarkMode ? '#1e1e1e' : 'white',
              border: isDarkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(0,0,0,0.08)',
            }">
            <div class="pa-5">
              <div class="d-flex align-center mb-4" style="gap: 12px">
                <div
                  style="
                    width: 40px;
                    height: 40px;
                    border-radius: 10px;
                    background: rgba(255, 152, 0, 0.15);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                  ">
                  <v-icon color="orange darken-2">mdi-alert-decagram-outline</v-icon>
                </div>
                <div>
                  <div style="font-size: 1.05rem; font-weight: 800" :style="{ color: isDarkMode ? 'white' : '#1e293b' }">Termin-Kollisionen</div>
                  <div style="font-size: 0.75rem; color: grey">Folgende Tage überschneiden sich mit Terminen:</div>
                </div>
              </div>

              <!-- List of conflicts -->
              <div class="overflow-y-auto pr-1 mb-2" style="max-height: 200px; scrollbar-width: thin">
                <div
                  v-for="(collision, idx) in collisionsList"
                  :key="idx"
                  class="mb-2 pa-3 rounded-lg"
                  :style="{
                    background: isDarkMode ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)',
                    border: isDarkMode ? '1px solid rgba(255,255,255,0.05)' : '1px solid rgba(0,0,0,0.05)',
                  }">
                  <div class="font-weight-bold" style="font-size: 0.8rem" :style="{ color: isDarkMode ? 'white' : '#1e293b' }">
                    {{ formatDateGerman(collision.date) }}
                  </div>
                  <div v-for="(conf, cIdx) in collision.conflicts" :key="cIdx" class="caption mt-1 d-flex align-center red--text text--lighten-1">
                    <v-icon x-small color="red lighten-1" class="mr-1">mdi-clock-outline</v-icon>
                    <span>{{ conf.start }} - {{ conf.end }}: {{ conf.title }}</span>
                  </div>
                </div>
              </div>

              <div class="d-flex flex-column mt-4" style="gap: 8px">
                <v-btn
                  block
                  depressed
                  color="primary"
                  class="text-none font-weight-bold"
                  style="border-radius: 10px; height: 38px; text-transform: none"
                  @click="resolveCollision('skip')">
                  Kollidierende Tage überspringen (Empfohlen)
                </v-btn>
                <v-btn
                  block
                  outlined
                  class="text-none font-weight-bold"
                  style="border-radius: 10px; height: 38px; text-transform: none"
                  :style="{ borderColor: isDarkMode ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.15)', color: isDarkMode ? 'white' : '#1e293b' }"
                  @click="resolveCollision('ignore')">
                  Trotzdem alle Termine erstellen
                </v-btn>
                <v-btn
                  block
                  text
                  class="text-none font-weight-bold"
                  style="border-radius: 10px; height: 38px; color: grey; text-transform: none"
                  @click="resolveCollision('cancel')">
                  Abbrechen
                </v-btn>
              </div>
            </div>
          </v-card>
        </v-dialog>

        <!-- Dialog zum Löschen von Serienterminen/Ämtlis -->
        <v-dialog v-model="deleteSeriesDialog" max-width="440" persistent :dark="isDarkMode">
          <v-card
            :dark="isDarkMode"
            class="rounded-xl overflow-hidden"
            :style="{
              background: isDarkMode ? 'rgba(22, 22, 22, 0.98)' : 'white',
              border: isDarkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(0,0,0,0.08)',
            }">
            <div class="pa-5">
              <div class="d-flex align-center mb-4" style="gap: 12px">
                <div
                  style="
                    width: 40px;
                    height: 40px;
                    border-radius: 10px;
                    background: rgba(244, 67, 54, 0.15);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                  ">
                  <v-icon color="error">mdi-trash-can</v-icon>
                </div>
                <div>
                  <div style="font-size: 1rem; font-weight: 700">Serie löschen</div>
                  <div style="font-size: 0.75rem; opacity: 0.7">{{ deleteSeriesBlock?.title || "Eintrag" }}</div>
                </div>
              </div>

              <div class="mb-4">
                <v-radio-group v-model="deleteSeriesScope" dense :dark="isDarkMode" hide-details class="mt-0 pt-0">
                  <v-radio value="single" color="error">
                    <template v-slot:label>
                      <span style="font-size: 0.8rem">
                        Nur {{ deleteSeriesBlock?.isTask || deleteSeriesBlock?.type === "task" ? "dieses Ämtli" : "diesen Termin" }} löschen
                      </span>
                    </template>
                  </v-radio>
                  <v-radio value="future" color="error" class="mt-2">
                    <template v-slot:label>
                      <span style="font-size: 0.8rem">
                        {{
                          deleteSeriesBlock?.isTask || deleteSeriesBlock?.type === "task"
                            ? "Dieses und alle zukünftigen Ämtli"
                            : "Diesen und alle zukünftigen Termine"
                        }}
                        löschen
                      </span>
                    </template>
                  </v-radio>
                  <v-radio value="all" color="error" class="mt-2">
                    <template v-slot:label>
                      <span style="font-size: 0.8rem">
                        Alle {{ deleteSeriesBlock?.isTask || deleteSeriesBlock?.type === "task" ? "Ämtli" : "Termine" }} der Serie löschen
                      </span>
                    </template>
                  </v-radio>
                </v-radio-group>
              </div>

              <div v-if="!deleteSeriesBlock?.isTask && deleteSeriesBlock?.type !== 'task'" class="mb-4">
                <v-textarea
                  v-model="deleteSeriesReason"
                  placeholder="Löschgrund angeben (erforderlich)..."
                  outlined
                  dense
                  rows="2"
                  :dark="isDarkMode"
                  hide-details
                  style="font-size: 0.8rem"></v-textarea>
              </div>

              <div class="d-flex justify-end" style="gap: 8px">
                <v-btn text style="text-transform: none; font-size: 0.8rem" @click="closeDeleteSeriesDialog">Abbrechen</v-btn>
                <v-btn
                  depressed
                  color="error"
                  :disabled="!deleteSeriesBlock?.isTask && deleteSeriesBlock?.type !== 'task' && !deleteSeriesReason.trim()"
                  style="text-transform: none; font-weight: 600; font-size: 0.8rem; border-radius: 8px"
                  @click="confirmDeleteSeries">
                  Löschen
                </v-btn>
              </div>
            </div>
          </v-card>
        </v-dialog>

        <!-- Dialog zur Verwaltung von Serienterminen -->
        <v-dialog v-model="manageSeriesDialog" max-width="700" persistent :dark="isDarkMode">
          <v-card
            :dark="isDarkMode"
            class="rounded-xl overflow-hidden d-flex flex-column"
            :style="{
              maxHeight: '80vh',
              background: isDarkMode ? 'rgba(22, 22, 22, 0.98)' : 'white',
              border: isDarkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(0,0,0,0.08)',
            }">
            <v-card-title class="px-6 pt-5 pb-3 d-flex align-center justify-space-between" style="border-bottom: 1px solid rgba(255, 255, 255, 0.05)">
              <div class="d-flex align-center" style="gap: 10px">
                <v-icon color="primary">mdi-calendar-sync</v-icon>
                <span class="text-h6 font-weight-bold">Serientermine verwalten</span>
              </div>
              <v-btn icon @click="manageSeriesDialog = false" :dark="isDarkMode">
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </v-card-title>

            <v-card-text class="pa-6 overflow-y-auto flex-grow-1" style="scrollbar-width: thin">
              <v-progress-linear v-if="loadingActiveSeries" indeterminate color="primary" class="mb-4"></v-progress-linear>

              <div v-if="!activeSeries || !activeSeries.length" class="text-center py-12 grey--text" style="font-size: 0.85rem">
                Keine aktiven Serientermine oder Serien-Ämtlis vorhanden.
              </div>

              <v-expansion-panels v-else v-model="expandedSeriesPanel" accordion class="wcd-series-panels">
                <v-expansion-panel
                  v-for="series in activeSeries"
                  :key="series.seriesId"
                  class="mb-3 rounded-lg overflow-hidden"
                  :style="{
                    background: isDarkMode ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.01)',
                    border: isDarkMode ? '1px solid rgba(255, 255, 255, 0.05)' : '1px solid rgba(0, 0, 0, 0.05)',
                  }">
                  <v-expansion-panel-header class="py-3 px-4">
                    <div class="d-flex align-center justify-space-between flex-grow-1 pr-3" style="gap: 12px; min-width: 0">
                      <div class="d-flex align-center" style="gap: 8px; min-width: 0">
                        <v-avatar size="24" :color="series.color || 'primary'" class="white--text">
                          <v-icon size="14" color="white">{{
                            series.icon || (series.type === "task" ? "mdi-checkbox-marked" : "mdi-calendar")
                          }}</v-icon>
                        </v-avatar>
                        <div class="text-truncate" style="min-width: 0">
                          <div class="font-weight-bold text-truncate" style="font-size: 0.85rem; line-height: 1.2">{{ series.title }}</div>
                          <div class="caption grey--text text-truncate" style="font-size: 0.7rem">
                            {{ series.type === "task" ? "Ämtli" : "Termin" }} • {{ series.assignedEmployee || "Unzugeordnet" }}
                          </div>
                        </div>
                      </div>
                      <div class="text-right flex-shrink-0 font-weight-bold primary--text" style="font-size: 0.75rem">
                        {{ series.dateRange }}
                      </div>
                    </div>
                  </v-expansion-panel-header>

                  <v-expansion-panel-content class="px-4 pb-4">
                    <v-divider class="mb-4" style="opacity: 0.05"></v-divider>

                    <div class="d-flex justify-end mb-4" style="gap: 8px">
                      <v-btn small depressed class="series-action-btn extend-btn" @click="extendSeries(series)">
                        <v-icon left size="16">mdi-calendar-plus</v-icon>
                        Serie fortführen
                      </v-btn>
                      <v-btn small depressed class="series-action-btn delete-btn" @click="deleteWholeSeries(series)">
                        <v-icon left size="16">mdi-trash-can-outline</v-icon>
                        Ganze Serie löschen
                      </v-btn>
                    </div>

                    <div style="max-height: 250px; overflow-y: auto; scrollbar-width: thin" class="pr-2">
                      <div
                        v-for="occ in series.occurrences"
                        :key="occ._id"
                        class="d-flex align-center justify-space-between py-2 px-3 rounded mb-2 occ-row"
                        :style="{
                          background: isDarkMode ? 'rgba(255,255,255,0.015)' : 'rgba(0,0,0,0.01)',
                          border: isDarkMode ? '1px solid rgba(255,255,255,0.05)' : '1px solid rgba(0,0,0,0.04)',
                          borderLeft: `3px solid ${series.color || '#4caf50'}`,
                        }">
                        <div style="font-size: 0.75rem">
                          <span class="font-weight-bold">{{ formatSeriesOccDate(occ.date) }}</span>
                          <span class="ml-2 grey--text">{{ occ.time }}{{ occ.end ? " - " + occ.end : "" }}</span>
                        </div>
                        <div class="d-flex align-center" style="gap: 6px">
                          <v-btn icon x-small color="primary" title="Im Kalender bearbeiten" @click="editOccurrence(occ, series.type)">
                            <v-icon size="14">mdi-pencil-outline</v-icon>
                          </v-btn>
                          <v-btn icon x-small color="error" title="Diesen Termin löschen" @click="deleteSingleOcc(series, occ)">
                            <v-icon size="14">mdi-trash-can-outline</v-icon>
                          </v-btn>
                        </div>
                      </div>
                    </div>
                  </v-expansion-panel-content>
                </v-expansion-panel>
              </v-expansion-panels>
            </v-card-text>
          </v-card>
        </v-dialog>

        <work-calendar-edit-dialog
          ref="workCalendarEditDialog"
          v-model="editDialog"
          :edit-data="editData"
          :customer-details="customerDetails"
          :loading="customerLoading"
          :customer-details-error="customerDetailsError"
          :employees="employeeList"
          :finder-employees="allStudioEmployees"
          :current-date="currentDate"
          :isDarkMode="isDarkMode"
          :appointment-templates="allAppointmentTemplates"
          @save="saveEdit"
          @delete="deleteItem"
          @action="triggerSpecialAction"
          @load-customer-details="loadCustomerDetailsById"
          @customer-assigned="onCustomerAssigned"
          @appointment-updated="fetchData(true)" />

        <work-calendar-guestbook-dialog
          v-model="guestbookDialog"
          :entry="activeGuestbookEntry"
          :is-dark-mode="isDarkMode"
          :assigned-employee-id="selectedGuestbookTrainerId"
          :assigned-employee-name="selectedGuestbookTrainerName"
          :confirm-appointment-creation="confirmPastAppointmentCreation"
          @resolved="handleGuestbookResolved"
          @refresh="fetchData(true)" />

        <work-calendar-appointment-search
          v-model="appointmentSearchDialog"
          :current-date="currentDate"
          :appointments="appointmentSearchDayItems"
          :is-dark-mode="isDarkMode"
          :day-loading="loading"
          :opening="appointmentSearchOpening"
          @select="openAppointmentSearchResult" />

        <v-snackbar
          :value="!!appointmentClipboard && $vuetify.breakpoint.smAndDown"
          :timeout="-1"
          bottom
          color="primary"
          class="wc-mobile-clipboard-snackbar">
          <div class="d-flex align-center" style="min-width: 0">
            <v-icon color="white" small class="mr-2">
              {{ appointmentClipboard && appointmentClipboard.mode === "move" ? "mdi-content-cut" : "mdi-content-copy" }}
            </v-icon>
            <span class="text-truncate">
              {{ appointmentClipboard && appointmentClipboard.mode === "move" ? "Verschieben" : "Kopieren" }}:
              {{ appointmentClipboard ? appointmentClipboard.title : "" }} · Zielslot antippen
            </span>
            <v-btn icon small dark class="ml-2 flex-shrink-0" @click="clearAppointmentClipboard"><v-icon small>mdi-close</v-icon></v-btn>
          </div>
        </v-snackbar>

        <!-- CUSTOM RIGHT-CLICK CONTEXT MENU -->
        <v-menu
          v-model="contextMenu.show"
          :position-x="contextMenu.x"
          :position-y="contextMenu.y"
          absolute
          offset-y
          :dark="isDarkMode"
          content-class="wc-calendar-context-menu rounded-xl overflow-hidden elevation-10">
          <v-list dense class="py-1" :dark="isDarkMode" :light="!isDarkMode" style="min-width: 270px; max-width: calc(100vw - 24px)">
            <div class="wc-context-menu-heading px-4 pt-2 pb-1">
              <div class="font-weight-bold text-truncate">{{ calendarClipboardTypeLabel(contextMenu.block) }}</div>
              <div class="caption grey--text">Antippen öffnet · Langdruck zeigt Aktionen</div>
            </div>
            <v-divider class="mb-1" />

            <v-list-item v-if="isCalendarAppointmentBlock(contextMenu.block)" @click="handleContextMenuFinderMove">
              <v-list-item-icon class="mr-2 my-2">
                <v-icon small color="primary">mdi-calendar-search</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title class="font-weight-medium">Mit Termin-Finder verschieben…</v-list-item-title>
              </v-list-item-content>
            </v-list-item>

            <v-list-item v-if="isCalendarClipboardBlock(contextMenu.block)" @click="handleContextMenuMove">
              <v-list-item-icon class="mr-2 my-2">
                <v-icon small color="warning">mdi-content-cut</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title class="font-weight-medium">Verschieben</v-list-item-title>
                <v-list-item-subtitle>Zielslot danach antippen</v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>

            <v-list-item v-if="isCalendarClipboardBlock(contextMenu.block)" @click="handleAppointmentClipboardCopy">
              <v-list-item-icon class="mr-2 my-2">
                <v-icon small color="success">mdi-content-copy</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title class="font-weight-medium">Kopieren</v-list-item-title>
                <v-list-item-subtitle>Zielslot danach antippen</v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>

            <v-divider v-if="isCalendarClipboardBlock(contextMenu.block)" />

            <v-list-item @click="handleContextMenuCopy">
              <v-list-item-icon class="mr-2 my-2">
                <v-icon small color="primary">mdi-account-multiple-plus-outline</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title class="font-weight-medium">Für Mitarbeiter duplizieren…</v-list-item-title>
              </v-list-item-content>
            </v-list-item>

            <v-list-item @click="handleContextMenuDelete">
              <v-list-item-icon class="mr-2 my-2">
                <v-icon small color="error">mdi-delete-outline</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title class="font-weight-medium red--text">Löschen</v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </v-menu>

        <!-- RIGHT-CLICK MENU FOR AN EMPTY TARGET SLOT -->
        <v-menu
          v-model="emptyContextMenu.show"
          :position-x="emptyContextMenu.x"
          :position-y="emptyContextMenu.y"
          absolute
          offset-y
          :dark="isDarkMode"
          content-class="wc-calendar-context-menu rounded-xl overflow-hidden elevation-10">
          <v-list dense class="py-1" :dark="isDarkMode" :light="!isDarkMode" style="min-width: 230px">
            <v-subheader class="text-uppercase font-weight-bold" style="height: 32px; font-size: 0.68rem">
              Zielzeit {{ emptyContextMenu.time || "--:--" }}
            </v-subheader>
            <v-list-item :disabled="!appointmentClipboard || appointmentClipboardBusy" @click="pasteAppointmentClipboard">
              <v-list-item-icon class="mr-2 my-2">
                <v-progress-circular v-if="appointmentClipboardBusy" indeterminate size="16" width="2" color="primary" />
                <v-icon v-else small color="primary">mdi-content-paste</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title class="font-weight-medium">Eintrag einfügen</v-list-item-title>
                <v-list-item-subtitle v-if="appointmentClipboard" class="text-truncate">
                  {{ appointmentClipboard.mode === "move" ? "Verschieben" : "Kopieren" }}: {{ appointmentClipboard.title }}
                </v-list-item-subtitle>
                <v-list-item-subtitle v-else>Zuerst einen Termin oder ein Ämtli kopieren bzw. ausschneiden</v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
            <v-list-item v-if="appointmentClipboard" @click="clearAppointmentClipboard">
              <v-list-item-icon class="mr-2 my-2"><v-icon small>mdi-close</v-icon></v-list-item-icon>
              <v-list-item-content><v-list-item-title>Zwischenablage leeren</v-list-item-title></v-list-item-content>
            </v-list-item>
          </v-list>
        </v-menu>

        <!-- COPY EMPLOYEE SELECTION DIALOG -->
        <v-dialog v-model="copyDialog" max-width="420" rounded="xl" :dark="isDarkMode">
          <v-card class="rounded-xl pa-4" :dark="isDarkMode" :light="!isDarkMode" style="border: 1px solid rgba(255, 255, 255, 0.05)">
            <v-card-title class="headline font-weight-black d-flex align-center pb-2 px-2" style="font-size: 1.25rem !important">
              <v-icon color="success" class="mr-2" size="24">mdi-content-copy</v-icon>
              Eintrag kopieren
            </v-card-title>
            <v-card-text class="pt-2 px-2">
              <div class="mb-4 text-body-2" :style="isDarkMode ? 'color: rgba(255,255,255,0.7)' : 'color: rgba(0,0,0,0.6)'">
                Wählen Sie den Mitarbeiter aus, für den dieser Eintrag kopiert werden soll (Gleiche Zeit, gleiche Notizen).
              </div>

              <v-list class="pa-0 overflow-y-auto" max-height="300" style="scrollbar-width: thin; background: transparent">
                <v-list-item
                  v-for="emp in copyTargetEmployees"
                  :key="emp.id"
                  @click="selectEmployeeForCopy(emp)"
                  class="rounded-lg mb-2 px-4"
                  :style="
                    isDarkMode
                      ? 'background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.05)'
                      : 'background: rgba(0,0,0,0.02); border: 1px solid rgba(0,0,0,0.03)'
                  "
                  link>
                  <v-list-item-avatar size="36" color="primary">
                    <v-img v-if="emp.picture" :src="'/img/profilbilder/' + emp.picture" />
                    <span v-else class="white--text font-weight-bold" style="font-size: 0.85rem">
                      {{ getInitials(emp.name) }}
                    </span>
                  </v-list-item-avatar>

                  <v-list-item-content class="text-left py-2">
                    <v-list-item-title class="font-weight-bold text-body-2" :style="{ color: isDarkMode ? '#ffffff' : '#1e293b' }">
                      {{ emp.name }}
                    </v-list-item-title>
                    <v-list-item-subtitle class="caption font-weight-medium mt-0.5" :style="{ color: emp.isAvailable ? '#4CAF50' : '#F57C00' }">
                      <v-icon x-small :color="emp.isAvailable ? 'success' : 'warning'" class="mr-1">
                        {{ emp.isAvailable ? "mdi-check-circle-outline" : "mdi-alert-circle-outline" }}
                      </v-icon>
                      {{ emp.statusText }}
                    </v-list-item-subtitle>
                  </v-list-item-content>

                  <v-list-item-action class="my-0">
                    <v-icon small color="success">mdi-chevron-right</v-icon>
                  </v-list-item-action>
                </v-list-item>
              </v-list>
            </v-card-text>
            <v-card-actions class="pt-2 px-2 justify-end" style="gap: 8px">
              <v-btn text rounded depressed @click="copyDialog = false" :dark="isDarkMode">Abbrechen</v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

        <!-- BOUNDED APPOINTMENT CHANGE HISTORY -->
        <v-dialog v-model="appointmentHistoryDialog" max-width="680" scrollable :dark="isDarkMode">
          <v-card class="rounded-xl overflow-hidden" :dark="isDarkMode" :light="!isDarkMode">
            <v-card-title class="px-5 py-4 d-flex align-center" style="border-bottom: 1px solid rgba(128, 128, 128, 0.18)">
              <v-icon color="primary" class="mr-3">mdi-history</v-icon>
              <div>
                <div class="font-weight-black" style="line-height: 1.2">Termin-Änderungsverlauf</div>
                <div class="caption grey--text">
                  {{ $moment(currentDate).format("MMMM YYYY") }} · letzte {{ appointmentHistoryEntries.length }} Aktionen
                </div>
              </div>
              <v-spacer />
              <v-btn icon :loading="appointmentHistoryLoading" @click="loadAppointmentHistory" title="Aktualisieren"
                ><v-icon>mdi-refresh</v-icon></v-btn
              >
              <v-btn icon @click="appointmentHistoryDialog = false"><v-icon>mdi-close</v-icon></v-btn>
            </v-card-title>
            <v-progress-linear v-if="appointmentHistoryLoading" indeterminate color="primary" />
            <v-card-text class="pa-0" style="min-height: 180px; max-height: 65vh">
              <div v-if="!appointmentHistoryLoading && !appointmentHistoryEntries.length" class="text-center grey--text py-12 px-5">
                <v-icon size="42" color="grey" class="mb-3">mdi-history</v-icon>
                <div class="font-weight-medium">Für diesen Monat sind noch keine protokollierten Änderungen vorhanden.</div>
              </div>
              <v-list v-else two-line class="py-0" :dark="isDarkMode" :light="!isDarkMode">
                <v-list-item
                  v-for="(entry, index) in appointmentHistoryEntries"
                  :key="entry.eventId || index"
                  :class="{ 'border-bottom': index < appointmentHistoryEntries.length - 1 }">
                  <v-list-item-avatar size="36" :color="appointmentHistoryColor(entry.action)" class="white--text">
                    <v-icon small color="white">{{ appointmentHistoryIcon(entry.action) }}</v-icon>
                  </v-list-item-avatar>
                  <v-list-item-content>
                    <v-list-item-title class="font-weight-medium" style="white-space: normal">{{ entry.summary }}</v-list-item-title>
                    <v-list-item-subtitle>
                      {{ entry.actorName || "Unbekannt" }} · {{ formatAppointmentHistoryTime(entry.createdAt) }}
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </v-card-text>
          </v-card>
        </v-dialog>

        <!-- Zuweisungs-Dialog: Auto-Zuweisung -->
        <v-dialog v-if="hasTaskManagementRight" v-model="autoAssignTasksDialog" fullscreen persistent transition="dialog-bottom-transition" :dark="isDarkMode">
          <v-card
            class="amtli-journey-card"
            :dark="isDarkMode"
            :style="{
              background: isDarkMode ? '#121212' : '#f4f6f9',
              color: isDarkMode ? '#ffffff' : '#1e293b',
              display: 'flex',
              flexDirection: 'column',
            }">
            <work-calendar-amtli-journey-header
              :step="assignStep === 1 ? 2 : 3"
              :title="assignStep === 1 ? 'Planung festlegen' : 'Automatische Zuweisung'"
              :subtitle="
                assignStep === 1 ? 'Zeitraum, Regeln und Vorlagen für diesen Lauf auswählen' : 'Schichten, Kalender und Regeln werden abgeglichen'
              "
              :is-dark-mode="isDarkMode"
              @back="handleAutoAssignBack"
              @close="closeAutoAssignJourney">
              <template #actions>
                <v-btn
                  v-if="assignStep === 1"
                  depressed
                  color="success"
                  class="text-none font-weight-bold"
                  :disabled="!canExecuteAutoAssign"
                  :loading="autoAssignLoading"
                  @click="executeAutoAssign">
                  <v-icon left>mdi-auto-fix</v-icon> Vorschläge erstellen
                </v-btn>
              </template>
            </work-calendar-amtli-journey-header>

            <v-card-text class="pa-0 flex-grow-1 amtli-journey-body" :style="{ background: isDarkMode ? '#111111' : '#f8fafc', overflow: 'hidden' }">
              <v-row no-gutters class="fill-height amtli-plan-layout">
                <!-- STEP 1: CONFIG & TEMPLATE LIST -->
                <template v-if="assignStep === 1">
                  <!-- LEFT COLUMN: SETTINGS PANEL (REDESIGNED LUXURY SIDEBAR) -->
                  <v-col
                    cols="12"
                    lg="3"
                    class="pa-5 custom-scrollbar amtli-plan-aside"
                    :style="{
                      overflowY: 'auto',
                      height: '100%',
                      borderRight: isDarkMode ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(0, 0, 0, 0.06)',
                      background: isDarkMode ? '#16181d' : '#f8fafc',
                    }">
                    <!-- SECTION 1: ZEITRAUM & ALLGEMEIN -->
                    <div
                      class="pa-4 rounded-xl mb-4 transition-all"
                      :style="{
                        background: isDarkMode ? 'rgba(255,255,255,0.02)' : '#ffffff',
                        border: isDarkMode ? '1px solid rgba(255,255,255,0.05)' : '1px solid rgba(0,0,0,0.05)',
                        boxShadow: isDarkMode ? 'none' : '0 2px 10px rgba(0,0,0,0.02)',
                      }">
                      <div class="d-flex align-center mb-3" style="gap: 10px">
                        <div class="pa-2 rounded-lg" style="background: rgba(33, 150, 243, 0.15)">
                          <v-icon small color="primary">mdi-calendar-range</v-icon>
                        </div>
                        <div style="font-size: 0.95rem; font-weight: 800" :style="{ color: isDarkMode ? 'white' : '#1e293b' }">Planungszeitraum</div>
                      </div>

                      <div class="d-flex flex-column" style="gap: 12px">
                        <div class="d-flex flex-wrap" style="gap: 6px">
                          <v-btn x-small outlined color="primary" class="text-none" @click="setAutoAssignRange('thisMonth')">Dieser Monat</v-btn>
                          <v-btn x-small outlined color="primary" class="text-none" @click="setAutoAssignRange('nextMonth')">Nächster Monat</v-btn>
                        </div>
                        <v-text-field
                          v-model="autoAssignConfig.startDate"
                          label="Startdatum"
                          type="date"
                          outlined
                          dense
                          rounded="lg"
                          :dark="isDarkMode"
                          hide-details></v-text-field>
                        <v-text-field
                          v-model="autoAssignConfig.endDate"
                          label="Enddatum"
                          type="date"
                          outlined
                          dense
                          rounded="lg"
                          :dark="isDarkMode"
                          hide-details></v-text-field>

                        <div
                          class="pa-3 rounded-lg cursor-pointer d-flex align-center justify-space-between mt-1"
                          :style="{
                            background: autoAssignConfig.overwriteExisting
                              ? isDarkMode
                                ? 'rgba(33,150,243,0.1)'
                                : 'rgba(33,150,243,0.05)'
                              : isDarkMode
                              ? 'rgba(255,255,255,0.02)'
                              : '#f1f5f9',
                            border: autoAssignConfig.overwriteExisting ? '1px solid rgba(33,150,243,0.3)' : '1px solid transparent',
                          }"
                          @click="
                            autoAssignConfig.overwriteExisting = !autoAssignConfig.overwriteExisting
                            overwriteAcknowledged = false
                          ">
                          <span style="font-size: 0.75rem; font-weight: 600" :style="{ color: isDarkMode ? '#e2e8f0' : '#475569' }"
                            >Unveröffentlichte Vorschläge neu berechnen</span
                          >
                          <v-switch
                            v-model="autoAssignConfig.overwriteExisting"
                            dense
                            hide-details
                            color="primary"
                            class="mt-0 pt-0"
                            @change="overwriteAcknowledged = false"
                            @click.stop></v-switch>
                        </div>
                        <div v-if="autoAssignConfig.overwriteExisting" class="text-caption red--text text--lighten-1 mt-1">
                          Noch nicht veröffentlichte Auto-Vorschläge der ausgewählten Ämtli im Zeitraum werden nach erfolgreicher Neuberechnung
                          ersetzt. Bereits gespeicherte, manuelle oder bewusst gelöschte Einträge bleiben unverändert.
                        </div>
                        <v-checkbox
                          v-if="autoAssignConfig.overwriteExisting"
                          v-model="overwriteAcknowledged"
                          dense
                          hide-details
                          color="warning"
                          class="mt-0"
                          label="Ich habe die Auswirkungen verstanden" />
                      </div>
                    </div>

                    <!-- SECTION 2: GLOBALE ZUWEISUNGSREGELN -->
                    <div
                      class="pa-4 rounded-xl mb-4 transition-all"
                      :style="{
                        background: isDarkMode ? 'rgba(255,255,255,0.02)' : '#ffffff',
                        border: isDarkMode ? '1px solid rgba(255,255,255,0.05)' : '1px solid rgba(0,0,0,0.05)',
                        boxShadow: isDarkMode ? 'none' : '0 2px 10px rgba(0,0,0,0.02)',
                      }">
                      <div class="d-flex align-center mb-1" style="gap: 10px">
                        <div class="pa-2 rounded-lg" style="background: rgba(76, 175, 80, 0.15)">
                          <v-icon small color="success">mdi-lightning-bolt-outline</v-icon>
                        </div>
                        <div style="font-size: 0.95rem; font-weight: 800" :style="{ color: isDarkMode ? 'white' : '#1e293b' }">Zuweisungsregeln</div>
                      </div>
                      <div style="font-size: 0.7rem; color: grey" class="mb-3 pl-1">Steuert die Logik der automatischen Aufgaben-Verteilung.</div>

                      <div class="d-flex flex-column" style="gap: 14px">
                        <div>
                          <div style="font-size: 0.75rem; font-weight: 700" :style="{ color: isDarkMode ? '#e2e8f0' : '#334155' }" class="mb-1">
                            Zuweisungs-Modus
                          </div>
                          <v-select
                            v-model="autoAssignConfig.distributionMode"
                            :items="[
                              { text: 'Pool bevorzugen, sonst Fallback (Standard)', value: 'prefer_pool' },
                              { text: 'Gleichmässige Lastverteilung (Pool ignorieren)', value: 'balanced' },
                              { text: 'Strikter Pool (Niemals Fallback-Zuweisung)', value: 'strict_pool' },
                            ]"
                            outlined
                            dense
                            rounded="lg"
                            :dark="isDarkMode"
                            hide-details></v-select>
                          <div style="font-size: 0.68rem; color: grey; line-height: 1.3" class="mt-1">
                            <span v-if="autoAssignConfig.distributionMode === 'prefer_pool'"
                              >Versucht zuerst bevorzugte Mitarbeiter einzuteilen. Nutzen im Notfall Ersatzkräfte.</span
                            >
                            <span v-else-if="autoAssignConfig.distributionMode === 'balanced'"
                              >Verteilt die Last möglichst gerecht auf alle anwesenden Mitarbeiter im Studio.</span
                            >
                            <span v-else
                              >Teilt Ämtli nur an hinterlegte Mitarbeiter ein. Findet sich niemand, bleibt ein unsichtbarer Planungsvorschlag im
                              Ämtli-Manager.</span
                            >
                          </div>
                        </div>

                        <!-- Option Switches with Detail Descriptions -->
                        <div class="d-flex flex-column gap-2 mt-1">
                          <div
                            class="pa-3 rounded-lg transition-all"
                            :style="{
                              background: isDarkMode ? 'rgba(255,255,255,0.02)' : '#f8fafc',
                              border: isDarkMode ? '1px solid rgba(255,255,255,0.04)' : '1px solid rgba(0,0,0,0.04)',
                            }">
                            <div class="d-flex align-center justify-space-between mb-1">
                              <span style="font-size: 0.75rem; font-weight: 700" :style="{ color: isDarkMode ? '#cbd5e1' : '#334155' }"
                                >Keine Doppelbuchungen</span
                              >
                              <v-icon small color="success">mdi-shield-check-outline</v-icon>
                            </div>
                            <div style="font-size: 0.68rem; color: grey; line-height: 1.2">
                              Ämtli werden nie parallel zu Terminen oder anderen Ämtli derselben Person eingeplant.
                            </div>
                          </div>

                          <div
                            class="pa-3 rounded-lg transition-all"
                            :style="{
                              background: isDarkMode ? 'rgba(255,255,255,0.02)' : '#f8fafc',
                              border: isDarkMode ? '1px solid rgba(255,255,255,0.04)' : '1px solid rgba(0,0,0,0.04)',
                            }">
                            <div class="d-flex align-center justify-space-between mb-1">
                              <span style="font-size: 0.75rem; font-weight: 700" :style="{ color: isDarkMode ? '#cbd5e1' : '#334155' }"
                                >Verkettungen zusammenhalten</span
                              >
                              <v-icon small color="success">mdi-link-variant</v-icon>
                            </div>
                            <div style="font-size: 0.68rem; color: grey; line-height: 1.2">
                              Verknüpfte Ämtli bleiben am selben Tag bei derselben Person direkt hintereinander.
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- SECTION 3: MITARBEITER AUSSCHLIESSEN -->
                    <div
                      class="pa-4 rounded-xl transition-all"
                      :style="{
                        background: isDarkMode ? 'rgba(255,255,255,0.02)' : '#ffffff',
                        border: isDarkMode ? '1px solid rgba(255,255,255,0.05)' : '1px solid rgba(0,0,0,0.05)',
                        boxShadow: isDarkMode ? 'none' : '0 2px 10px rgba(0,0,0,0.02)',
                      }">
                      <div class="d-flex align-center justify-space-between mb-3">
                        <div class="d-flex align-center" style="gap: 10px">
                          <div class="pa-2 rounded-lg" style="background: rgba(244, 67, 54, 0.15)">
                            <v-icon small color="red">mdi-account-cancel-outline</v-icon>
                          </div>
                          <div style="font-size: 0.95rem; font-weight: 800" :style="{ color: isDarkMode ? 'white' : '#1e293b' }">Ausschliessen</div>
                        </div>
                        <v-chip x-small color="red" outlined class="font-weight-bold" v-if="autoAssignConfig.excludedEmployeeIds.length > 0">
                          {{ autoAssignConfig.excludedEmployeeIds.length }} pausiert
                        </v-chip>
                      </div>

                      <v-autocomplete
                        v-model="autoAssignConfig.excludedEmployeeIds"
                        :items="employeeOptions"
                        multiple
                        chips
                        deletable-chips
                        small-chips
                        outlined
                        dense
                        hide-details
                        clearable
                        label="Mitarbeitende auswählen"
                        prepend-inner-icon="mdi-account-search-outline" />
                      <div class="mt-2 grey--text" style="font-size: 0.7rem; line-height: 1.35">
                        Nur für diesen Planungslauf. Die Mitarbeitenden bleiben in der manuellen Sidebar verfügbar.
                      </div>
                    </div>
                  </v-col>

                  <!-- RIGHT COLUMN: TARGET TEMPLATE SELECTION CHECKLIST -->
                  <v-col cols="12" lg="9" class="pa-6 custom-scrollbar amtli-plan-main" style="overflow-y: auto; height: 100%">
                    <div class="mb-6 d-flex align-center justify-space-between flex-wrap" style="gap: 12px">
                      <div>
                        <div style="font-size: 1.3rem; font-weight: 700" :style="{ color: isDarkMode ? 'white' : '#1e293b' }">
                          Zu planende Ämtli auswählen
                        </div>
                        <div style="font-size: 0.85rem; margin-top: 4px" :style="{ color: isDarkMode ? 'rgba(255,255,255,0.5)' : '#64748b' }">
                          Wähle aus, welche der konfigurierten Ämtli in diesem Zeitraum automatisch zugewiesen werden sollen.
                        </div>
                      </div>
                      <div class="d-flex align-center flex-wrap" style="gap: 8px">
                        <v-chip small outlined color="success" class="font-weight-bold">
                          <v-icon left small>mdi-check-circle-outline</v-icon>{{ selectedAutoAssignTemplates.length }} ausgewählt
                        </v-chip>
                        <v-chip small outlined :color="autoAssignRunIssues.length ? 'warning' : 'success'" class="font-weight-bold">
                          <v-icon left small>{{ autoAssignRunIssues.length ? "mdi-alert-outline" : "mdi-shield-check-outline" }}</v-icon>
                          {{ autoAssignRunIssues.length ? autoAssignRunIssues.length + " Hinweis(e)" : "Startbereit" }}
                        </v-chip>
                      </div>
                    </div>

                    <v-alert v-if="autoAssignRunIssues.length" type="warning" outlined dense class="mb-4 text-caption">
                      <strong>Vor dem Start bitte prüfen:</strong>
                      <ul class="amtli-run-issues mt-2 mb-0">
                        <li v-for="issue in autoAssignRunIssues" :key="issue">{{ issue }}</li>
                      </ul>
                    </v-alert>
                    <v-alert v-else type="success" text dense class="mb-4 text-caption">
                      Alles bereit. Der Assistent erzeugt zuerst unsichtbare Vorschläge – sichtbar werden sie erst nach deiner Prüfung.
                    </v-alert>

                    <!-- Empty State View -->
                    <div
                      v-if="autoAssignConfig.templates.length === 0"
                      class="d-flex flex-column align-center justify-center pa-10 text-center rounded-xl my-4"
                      :style="{
                        background: isDarkMode ? 'rgba(255,255,255,0.02)' : '#ffffff',
                        border: isDarkMode ? '2px dashed rgba(255,255,255,0.08)' : '2px dashed rgba(0,0,0,0.08)',
                        minHeight: '360px',
                      }">
                      <div
                        class="d-flex align-center justify-center rounded-circle mb-4"
                        style="width: 80px; height: 80px; background: rgba(244, 67, 54, 0.1); border: 1px solid rgba(244, 67, 54, 0.2)">
                        <v-icon size="42" color="red">mdi-alert-circle-outline</v-icon>
                      </div>
                      <div style="font-size: 1.3rem; font-weight: 800" :style="{ color: isDarkMode ? 'white' : '#1e293b' }">
                        Keine konfigurierten Ämtli vorhanden
                      </div>
                      <div class="text-subtitle-2 mt-2 mb-6" style="max-width: 440px; color: grey">
                        Bitte füge zuerst im Einstellungs-Dialog Ämtli hinzu und konfiguriere sie.
                      </div>
                    </div>

                    <div v-else class="d-flex flex-column" style="gap: 10px">
                      <!-- Select All / Deselect All Controls -->
                      <div class="d-flex align-center justify-space-between mb-2 px-1">
                        <div class="d-flex align-center" style="gap: 12px">
                          <v-btn
                            small
                            text
                            class="font-weight-bold text-none px-2"
                            color="primary"
                            @click="
                              selectedTemplateIds = autoAssignConfig.templates
                                .filter((t) => getAmtliTemplateReadiness(t).ready)
                                .map((t) => String(t._id || t.id))
                            ">
                            <v-icon left small>mdi-checkbox-multiple-marked-outline</v-icon> Alle startbereiten
                          </v-btn>
                          <v-btn small text class="font-weight-bold text-none px-2" color="grey" @click="selectedTemplateIds = []">
                            <v-icon left small>mdi-checkbox-multiple-blank-outline</v-icon> Alle abwählen
                          </v-btn>
                        </div>
                        <span class="text-caption grey--text font-weight-bold mr-2">
                          {{ selectedTemplateIds.length }} von {{ amtliTemplateReadinessSummary.ready }} startbereiten ausgewählt
                        </span>
                      </div>

                      <!-- High Density Checklist Grid -->
                      <div
                        v-for="tpl in autoAssignConfig.templates"
                        :key="tpl._id || tpl.id"
                        @click="toggleTemplateSelection(tpl)"
                        class="assign-template-item amtli-auto-template-item pa-3 px-4 rounded-xl transition-all position-relative"
                        :class="{
                          'cursor-pointer': getAmtliTemplateReadiness(tpl).ready,
                          'amtli-auto-template-disabled': !getAmtliTemplateReadiness(tpl).ready,
                        }"
                        :style="{
                          background: isDarkMode ? 'linear-gradient(135deg, rgba(255,255,255,0.03), rgba(255,255,255,0.01))' : '#ffffff',
                          border: isDarkMode ? '1px solid rgba(255,255,255,0.07)' : '1px solid rgba(0,0,0,0.06)',
                          boxShadow: isDarkMode ? 'none' : '0 2px 10px rgba(0,0,0,0.02)',
                          opacity: getAmtliTemplateReadiness(tpl).ready ? 1 : 0.74,
                          display: $vuetify.breakpoint.lgAndUp ? 'grid' : 'flex',
                          gridTemplateColumns: '40px 1fr 150px 150px 150px',
                        }">
                        <!-- Col 0: Checkbox -->
                        <div class="amtli-auto-template-check d-flex align-center justify-center mr-1" @click.stop style="width: 32px; height: 32px">
                          <v-checkbox
                            v-model="selectedTemplateIds"
                            :value="String(tpl._id || tpl.id)"
                            color="primary"
                            hide-details
                            dense
                            class="ma-0 pa-0"
                            :disabled="!getAmtliTemplateReadiness(tpl).ready"
                            :dark="isDarkMode"></v-checkbox>
                        </div>

                        <!-- Col 1: Icon & Title & Frequency -->
                        <div class="amtli-auto-template-main d-flex align-center" style="gap: 12px; min-width: 0">
                          <div
                            class="d-flex align-center justify-center rounded-xl elevation-1"
                            :style="{ background: tpl.color || '#2196F3', width: '36px', height: '36px', flexShrink: 0 }">
                            <v-icon color="white" small>{{ tpl.icon || "mdi-calendar" }}</v-icon>
                          </div>
                          <div style="min-width: 0; overflow: hidden">
                            <div
                              style="font-weight: 800; font-size: 0.95rem; line-height: 1.2"
                              :style="{ color: isDarkMode ? 'white' : '#1e293b' }"
                              class="text-truncate"
                              :title="tpl.title">
                              {{ tpl.title }}
                            </div>
                            <div class="d-flex align-center flex-wrap mt-1" style="gap: 6px">
                              <v-chip
                                x-small
                                outlined
                                :color="tpl.frequency === 'daily' ? 'blue' : tpl.frequency === 'weekly' ? 'green' : 'purple'"
                                class="font-weight-bold"
                                style="height: 18px; font-size: 0.65rem">
                                {{ getAmtliFrequencyLabel(tpl) }}
                              </v-chip>
                              <span style="font-size: 0.7rem; color: grey">{{ tpl.duration || 30 }} Min</span>
                              <v-chip
                                x-small
                                outlined
                                :color="getAmtliTemplateReadiness(tpl).color"
                                class="font-weight-bold"
                                :title="getAmtliTemplateReadiness(tpl).message">
                                <v-icon left x-small>{{ getAmtliTemplateReadiness(tpl).icon }}</v-icon>
                                {{ getAmtliTemplateReadiness(tpl).label }}
                              </v-chip>
                              <v-btn
                                v-if="!getAmtliTemplateReadiness(tpl).ready"
                                x-small
                                text
                                color="primary"
                                class="text-none font-weight-bold"
                                @click.stop="openTemplateConfigDrawer(tpl, 'auto')">
                                <v-icon left x-small>mdi-cog-outline</v-icon>
                                {{ getAmtliTemplateReadiness(tpl).manualOnly ? "Automatik einrichten" : "Anpassen" }}
                              </v-btn>
                            </div>
                            <div class="amtli-template-summary text-truncate" :title="getAmtliTemplatePlainSummary(tpl)">
                              {{ getAmtliTemplatePlainSummary(tpl) }}
                            </div>
                          </div>
                        </div>

                        <!-- Col 2: Zuweisung Pill -->
                        <div
                          class="amtli-auto-template-detail pa-2 px-3 rounded-lg d-flex align-center"
                          style="gap: 8px"
                          :style="{
                            background: isDarkMode ? 'rgba(255,255,255,0.03)' : '#f8fafc',
                            border: isDarkMode ? '1px solid rgba(255,255,255,0.04)' : '1px solid rgba(0,0,0,0.04)',
                          }">
                          <v-icon x-small color="primary">mdi-account-outline</v-icon>
                          <div class="d-flex flex-column" style="overflow: hidden">
                            <span style="font-size: 0.6rem; color: grey; text-transform: uppercase; font-weight: 700">Zuweisung</span>
                            <span
                              style="font-size: 0.75rem; font-weight: 700"
                              :style="{ color: isDarkMode ? '#e2e8f0' : '#334155' }"
                              class="text-truncate">
                              {{
                                tpl.fixedEmployeeId
                                  ? getEmployeeNameById(tpl.fixedEmployeeId)
                                  : tpl.assignedEmployees && tpl.assignedEmployees.length > 0
                                  ? "Pool (" + tpl.assignedEmployees.length + ")"
                                  : "Alle Kräfte"
                              }}
                            </span>
                          </div>
                        </div>

                        <!-- Col 3: Uhrzeiten Pill -->
                        <div
                          class="amtli-auto-template-detail pa-2 px-3 rounded-lg d-flex align-center"
                          style="gap: 8px"
                          :style="{
                            background: isDarkMode ? 'rgba(255,255,255,0.03)' : '#f8fafc',
                            border: isDarkMode ? '1px solid rgba(255,255,255,0.04)' : '1px solid rgba(0,0,0,0.04)',
                          }">
                          <v-icon x-small color="amber darken-2">mdi-clock-outline</v-icon>
                          <div class="d-flex flex-column" style="overflow: hidden">
                            <span style="font-size: 0.6rem; color: grey; text-transform: uppercase; font-weight: 700">Uhrzeiten</span>
                            <span
                              style="font-size: 0.75rem; font-weight: 700"
                              :style="{ color: isDarkMode ? '#e2e8f0' : '#334155' }"
                              class="text-truncate">
                              {{ getTemplateTimeSummary(tpl) }}
                            </span>
                          </div>
                        </div>

                        <!-- Col 4: Planungstage Pill -->
                        <div
                          class="amtli-auto-template-detail pa-2 px-3 rounded-lg d-flex align-center"
                          style="gap: 8px"
                          :style="{
                            background: isDarkMode ? 'rgba(255,255,255,0.03)' : '#f8fafc',
                            border: isDarkMode ? '1px solid rgba(255,255,255,0.04)' : '1px solid rgba(0,0,0,0.04)',
                          }">
                          <v-icon x-small color="indigo">mdi-calendar-multiselect</v-icon>
                          <div class="d-flex flex-column" style="overflow: hidden">
                            <span style="font-size: 0.6rem; color: grey; text-transform: uppercase; font-weight: 700">Planungstage</span>
                            <span
                              style="font-size: 0.75rem; font-weight: 700"
                              :style="{ color: isDarkMode ? '#e2e8f0' : '#334155' }"
                              class="text-truncate">
                              {{ getTemplatePlanningDaysSummary(tpl) }}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </v-col>
                </template>

                <!-- STEP 2: ANIMATED LOADER -->
                <v-col
                  cols="12"
                  v-else-if="assignStep === 2"
                  class="d-flex flex-column align-center justify-center fill-height py-10"
                  style="height: 100%"
                  :style="{ background: isDarkMode ? '#121212' : '#f8fafc' }">
                  <v-progress-circular indeterminate color="primary" size="72" width="4" class="mb-6"></v-progress-circular>
                  <div class="text-h5 font-weight-bold" :style="{ color: isDarkMode ? 'white' : 'black' }">Ämtli werden zugeordnet</div>
                  <div
                    class="text-subtitle-1 mt-2 text-center"
                    style="max-width: 520px"
                    :style="{ color: isDarkMode ? 'rgba(255,255,255,0.5)' : '#64748b' }">
                    Der Assistent berechnet die anwesenden Mitarbeiter und verteilt die Last optimal unter Berücksichtigung aller Verkettungen,
                    Kollisionen und Poolregeln...
                  </div>
                  <div class="amtli-loader-steps mt-7">
                    <div
                      v-for="(label, index) in ['Schichten prüfen', 'Kalender abgleichen', 'Regeln anwenden', 'Vorschläge vorbereiten']"
                      :key="label">
                      <span>{{ index + 1 }}</span
                      ><strong>{{ label }}</strong>
                    </div>
                  </div>
                </v-col>

                <!-- Alte Zwischenvorschau bleibt deaktiviert; erfolgreiche Läufe öffnen direkt den Ämtli-Manager. -->
                <v-col
                  cols="12"
                  v-else-if="false"
                  class="pa-8 custom-scrollbar"
                  style="overflow-y: auto; height: 100%"
                  :style="{ background: isDarkMode ? '#121212' : '#f8fafc' }">
                  <div class="d-flex align-center mb-6">
                    <div
                      class="d-flex align-center justify-center mr-4"
                      style="
                        width: 48px;
                        height: 48px;
                        background: rgba(76, 175, 80, 0.15);
                        border-radius: 12px;
                        border: 1px solid rgba(76, 175, 80, 0.25);
                      ">
                      <v-icon color="success">mdi-check-decagram</v-icon>
                    </div>
                    <div>
                      <div style="font-size: 1.4rem; font-weight: 800" :style="{ color: isDarkMode ? 'white' : '#1e293b' }">Planungs-Ergebnisse</div>
                      <div style="font-size: 0.85rem" :style="{ color: isDarkMode ? 'rgba(255, 255, 255, 0.5)' : '#64748b' }">
                        {{ autoAssignResultSummary.total }} Vorschläge wurden vorbereitet. Noch nichts davon ist ohne deine Prüfung im Kalender
                        sichtbar.
                      </div>
                    </div>
                  </div>

                  <!-- Stats Dashboard Grid -->
                  <v-row class="mb-6">
                    <v-col cols="6" sm="3" v-if="autoAssignStats">
                      <div
                        class="pa-4 rounded-xl text-center"
                        :style="{
                          background: isDarkMode ? 'rgba(33, 150, 243, 0.08)' : '#ffffff',
                          border: isDarkMode ? '1px solid rgba(33, 150, 243, 0.15)' : '1px solid rgba(33, 150, 243, 0.1)',
                        }">
                        <div style="font-size: 0.75rem; text-transform: uppercase; font-weight: 700; color: grey">Erstellt</div>
                        <div style="font-size: 2.2rem; font-weight: 900; color: #2196f3" class="my-1">{{ autoAssignStats.totalCreated }}</div>
                        <div style="font-size: 0.75rem; color: grey">Ämtli-Einträge</div>
                      </div>
                    </v-col>
                    <v-col cols="6" sm="3" v-if="autoAssignStats">
                      <div
                        class="pa-4 rounded-xl text-center"
                        :style="{
                          background: isDarkMode ? 'rgba(76, 175, 80, 0.08)' : '#ffffff',
                          border: isDarkMode ? '1px solid rgba(76, 175, 80, 0.15)' : '1px solid rgba(76, 175, 80, 0.1)',
                        }">
                        <div style="font-size: 0.75rem; text-transform: uppercase; font-weight: 700; color: grey">Aus Pool / Fest</div>
                        <div style="font-size: 2.2rem; font-weight: 900; color: #4caf50" class="my-1">{{ autoAssignStats.assignedFromPool }}</div>
                        <div style="font-size: 0.75rem; color: grey">Bevorzugte Kräfte</div>
                      </div>
                    </v-col>
                    <v-col cols="6" sm="3" v-if="autoAssignStats">
                      <div
                        class="pa-4 rounded-xl text-center"
                        :style="{
                          background: isDarkMode ? 'rgba(255, 152, 0, 0.08)' : '#ffffff',
                          border: isDarkMode ? '1px solid rgba(255, 152, 0, 0.15)' : '1px solid rgba(255, 152, 0, 0.1)',
                        }">
                        <div style="font-size: 0.75rem; text-transform: uppercase; font-weight: 700; color: grey">Fallbacks</div>
                        <div style="font-size: 2.2rem; font-weight: 900; color: #ff9800" class="my-1">{{ autoAssignStats.assignedFallback }}</div>
                        <div style="font-size: 0.75rem; color: grey">Andere verfügbare Kräfte</div>
                      </div>
                    </v-col>
                    <v-col cols="6" sm="3" v-if="autoAssignStats">
                      <div
                        class="pa-4 rounded-xl text-center"
                        :style="{
                          background: isDarkMode ? 'rgba(244, 67, 54, 0.08)' : '#ffffff',
                          border: isDarkMode ? '1px solid rgba(244, 67, 54, 0.15)' : '1px solid rgba(244, 67, 54, 0.1)',
                        }">
                        <div style="font-size: 0.75rem; text-transform: uppercase; font-weight: 700; color: grey">Zu prüfen</div>
                        <div style="font-size: 2.2rem; font-weight: 900; color: #f44336" class="my-1">{{ autoAssignStats.unassigned }}</div>
                        <div style="font-size: 0.75rem; color: grey">Zuweisung erforderlich</div>
                      </div>
                    </v-col>
                  </v-row>

                  <!-- Header -->
                  <div class="text-subtitle-1 font-weight-bold mb-3" :style="{ color: isDarkMode ? 'white' : '#1e293b' }">
                    Detaillierte Einteilungs-Vorschau
                  </div>

                  <!-- LIST VIEW MODE -->
                  <div
                    :style="{
                      border: isDarkMode ? '1px solid rgba(255, 255, 255, 0.06)' : '1px solid rgba(0, 0, 0, 0.06)',
                      background: isDarkMode ? 'rgba(0,0,0,0.15)' : '#ffffff',
                    }"
                    class="pa-4 rounded-xl custom-scrollbar mb-6">
                    <div v-for="group in autoAssignResultsGrouped" :key="group.date" class="mb-6">
                      <!-- Date Header -->
                      <div
                        class="text-subtitle-2 font-weight-bold mb-3 pb-1 d-flex align-center justify-space-between"
                        :style="{
                          color: isDarkMode ? '#2196F3' : '#0284c7',
                          borderBottom: isDarkMode ? '1px solid rgba(33, 150, 243, 0.2)' : '1px solid rgba(2, 132, 199, 0.15)',
                        }">
                        <span>
                          <v-icon small :color="isDarkMode ? '#2196F3' : '#0284c7'" class="mr-1">mdi-calendar-range</v-icon>
                          {{ group.formattedDate }}
                        </span>
                        <v-chip x-small outlined :color="isDarkMode ? '#2196F3' : '#0284c7'"> {{ group.items.length }} Ämtli </v-chip>
                      </div>

                      <!-- Employee Groups -->
                      <div class="pl-2" style="display: flex; flex-direction: column; gap: 12px">
                        <div
                          v-for="empGroup in group.employeeGroups"
                          :key="empGroup.employeeName"
                          class="pl-2"
                          style="border-left: 2px solid rgba(128, 128, 128, 0.2)">
                          <!-- Employee/Unassigned Subheader -->
                          <div class="d-flex align-center mb-2" style="gap: 6px">
                            <v-icon small :color="empGroup.isUnassigned ? 'red' : 'green'">
                              {{ empGroup.isUnassigned ? "mdi-alert-circle-outline" : "mdi-account" }}
                            </v-icon>
                            <span
                              style="font-size: 0.85rem; font-weight: 700"
                              :style="{ color: empGroup.isUnassigned ? '#F44336' : isDarkMode ? '#e2e8f0' : '#334155' }">
                              {{ empGroup.employeeName }}
                            </span>
                          </div>

                          <!-- Tasks list for this employee -->
                          <div style="display: flex; flex-direction: column; gap: 6px">
                            <div
                              v-for="(task, tIdx) in empGroup.tasks"
                              :key="tIdx"
                              class="d-flex align-center justify-space-between pa-2 rounded-lg"
                              :style="{
                                background: isDarkMode ? 'rgba(255,255,255,0.02)' : '#f8fafc',
                                border: isDarkMode ? '1px solid rgba(255,255,255,0.03)' : '1px solid rgba(0,0,0,0.03)',
                              }">
                              <div class="d-flex align-center" style="gap: 10px">
                                <div
                                  class="d-flex align-center justify-center rounded"
                                  :style="{ background: task.color || 'blue', width: '28px', height: '28px' }">
                                  <v-icon small color="white">{{ task.icon || "mdi-calendar" }}</v-icon>
                                </div>
                                <div>
                                  <div style="font-size: 0.8rem; font-weight: 600" :style="{ color: isDarkMode ? 'white' : '#2c3e50' }">
                                    {{ task.title }}
                                  </div>
                                  <div style="font-size: 0.65rem; color: grey">{{ task.time }} Uhr</div>
                                </div>
                              </div>

                              <div>
                                <v-chip
                                  v-if="task.type === 'pool'"
                                  color="rgba(76, 175, 80, 0.15)"
                                  text-color="green"
                                  x-small
                                  outlined
                                  style="font-size: 0.65rem; font-weight: 600">
                                  Pool/Fest
                                </v-chip>
                                <v-chip
                                  v-else-if="task.type === 'fallback'"
                                  color="rgba(255, 152, 0, 0.15)"
                                  text-color="orange"
                                  x-small
                                  outlined
                                  style="font-size: 0.65rem; font-weight: 600">
                                  Ersatz
                                </v-chip>
                                <v-chip
                                  v-else
                                  color="rgba(244, 67, 54, 0.15)"
                                  text-color="red"
                                  x-small
                                  outlined
                                  style="font-size: 0.65rem; font-weight: 600">
                                  {{ task.employeeName }}
                                </v-chip>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <v-alert text dense type="info" class="mt-4 mb-0 text-caption">
                    Über <strong>„Vorschläge prüfen“</strong> im Kopfbereich öffnest du die Freigabe. Mit Zurück kannst du einen weiteren Lauf
                    vorbereiten; die bestehenden Vorschläge bleiben erhalten.
                  </v-alert>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-dialog>

        <!-- Ämtli-Einstellungen Dialog -->
        <v-dialog v-if="hasTaskManagementRight" v-model="amtliConfigDialog" fullscreen persistent transition="dialog-bottom-transition" :dark="isDarkMode">
          <v-card
            class="amtli-journey-card"
            :dark="isDarkMode"
            :style="{
              background: isDarkMode ? '#121212' : '#f4f6f9',
              color: isDarkMode ? '#ffffff' : '#1e293b',
              display: 'flex',
              flexDirection: 'column',
            }">
            <work-calendar-amtli-journey-header
              :step="1"
              title="Ämtli konfigurieren"
              :is-dark-mode="isDarkMode"
              @back="finishAmtliConfiguration"
              @close="finishAmtliConfiguration">
              <template #actions>
                <v-menu offset-y left :close-on-content-click="false" max-width="360">
                  <template #activator="{ on, attrs }">
                    <v-btn
                      icon
                      outlined
                      color="primary"
                      class="amtli-help-button"
                      aria-label="Hilfe zur Ämtli-Konfiguration"
                      v-bind="attrs"
                      v-on="on">
                      <v-icon>mdi-help-circle-outline</v-icon>
                    </v-btn>
                  </template>
                  <v-card :dark="isDarkMode" class="pa-4 amtli-help-card">
                    <div class="font-weight-bold mb-3">So funktioniert es</div>
                    <div class="amtli-help-row">
                      <v-icon small color="success">mdi-check-circle-outline</v-icon>
                      <span>Änderungen werden direkt gespeichert.</span>
                    </div>
                    <div class="amtli-help-row">
                      <v-icon small color="primary">mdi-view-sidebar-outline</v-icon>
                      <span>Konfigurierte Ämtli stehen sofort in der Sidebar bereit.</span>
                    </div>
                    <div class="amtli-help-row">
                      <v-icon small color="indigo">mdi-auto-fix</v-icon>
                      <span>Die Automatik ist optional und erstellt zuerst kontrollierbare Vorschläge.</span>
                    </div>
                  </v-card>
                </v-menu>
                <v-btn
                  depressed
                  color="primary"
                  class="text-none font-weight-bold"
                  :disabled="amtliTemplateReadinessSummary.ready === 0"
                  @click="openAutoAssignDialog">
                  <v-icon left>mdi-auto-fix</v-icon>Automatik starten<v-icon right>mdi-arrow-right</v-icon>
                </v-btn>
              </template>
            </work-calendar-amtli-journey-header>

            <v-card-text class="pa-0 flex-grow-1 amtli-journey-body" :style="{ background: isDarkMode ? '#111111' : '#f8fafc', overflow: 'hidden' }">
              <v-row no-gutters class="fill-height amtli-config-layout">
                <!-- RIGHT COLUMN: CONFIGURABLE TEMPLATE LIST -->
                <v-col cols="12" class="pa-5 custom-scrollbar amtli-config-main" :style="{ overflowY: 'auto', height: '100%' }">
                  <work-calendar-cleaning-merge
                    v-if="amtliConfigDialog && hasCleaningTaskMergeRight"
                    :key="studioId"
                    :studio-id="String(studioId)"
                    :is-dark-mode="isDarkMode"
                    @merged="handleCleaningTasksMerged" />
                  <div class="d-flex align-center justify-space-between mb-4">
                    <div>
                      <h2 style="font-size: 1.4rem; font-weight: 800; letter-spacing: -0.5px" :style="{ color: isDarkMode ? 'white' : '#1e293b' }">
                        Konfigurierte Ämtli-Vorlagen
                      </h2>
                    </div>

                    <v-btn color="primary" class="font-weight-bold text-none rounded-lg pa-4" depressed @click="openAmtliPoolDialog">
                      <v-icon left>mdi-plus-box-multiple-outline</v-icon> Aus Ämtli-Pool hinzufügen
                    </v-btn>
                  </div>

                  <div class="amtli-config-filterbar mb-4">
                    <v-text-field
                      v-model="amtliConfigSearch"
                      outlined
                      dense
                      hide-details
                      clearable
                      prepend-inner-icon="mdi-magnify"
                      label="Ämtli suchen"
                      class="amtli-config-search" />
                    <v-chip-group v-model="amtliConfigFrequencyFilter" mandatory active-class="success white--text" class="amtli-config-filters">
                      <v-chip small value="all">Alle</v-chip>
                      <v-chip small value="daily">Täglich</v-chip>
                      <v-chip small value="weekly">Wöchentlich</v-chip>
                      <v-chip small value="monthly">Monatlich</v-chip>
                      <v-chip small value="once"><v-icon left small>mdi-hand-pointing-right</v-icon> Nur manuell</v-chip>
                      <v-chip small value="needs_attention"><v-icon left small>mdi-alert-outline</v-icon> Zu prüfen</v-chip>
                    </v-chip-group>
                  </div>

                  <v-alert
                    v-if="amtliTemplateReadinessSummary.total > 0 && amtliTemplateReadinessSummary.ready === 0"
                    type="info"
                    text
                    dense
                    class="mb-4 text-caption">
                    Die manuelle Nutzung ist bereits möglich. Für einen Automatiklauf öffnest du bei mindestens einer Vorlage
                    <strong>„Anpassen“</strong> und ergänzt unter <strong>„Automatik-Regeln (optional)“</strong> die Wiederholung und Planung.
                  </v-alert>

                  <work-calendar-task-variant-groups
                    :groups="filteredAmtliVariantGroups"
                    :reference-date="currentDate"
                    :is-dark-mode="isDarkMode"
                    :busy="taskDashboardLoading"
                    @configure="openTemplateConfigDrawer"
                    @add="openNewTaskVariant"
                    @duplicate="duplicateTaskVariant"
                    @toggle="toggleTaskVariant" />
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-dialog>

        <!-- CENTERED MODAL POPUP DIALOG FOR ÄMTLI CONFIGURATION (REDESIGNED SPACIOUS 2-COLUMN MODAL) -->
        <v-dialog v-if="hasTaskManagementRight" v-model="configDrawer" fullscreen persistent :dark="isDarkMode" transition="dialog-bottom-transition">
          <v-card
            class="overflow-hidden pa-0 amtli-template-editor-card"
            :style="{ background: isDarkMode ? '#1e1e1e' : '#ffffff', display: 'flex', flexDirection: 'column' }">
            <div v-if="editingConfigTemplate" class="amtli-template-editor-shell" style="display: flex; flex-direction: column">
              <!-- Dialog Header -->
              <div
                class="pa-5 d-flex align-center justify-space-between"
                :style="{
                  background: isDarkMode
                    ? 'linear-gradient(135deg, rgba(33, 150, 243, 0.15), rgba(0, 0, 0, 0))'
                    : 'linear-gradient(135deg, rgba(33, 150, 243, 0.08), rgba(255, 255, 255, 0))',
                  borderBottom: isDarkMode ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid rgba(0, 0, 0, 0.08)',
                }">
                <div class="d-flex align-center" style="gap: 14px">
                  <div
                    class="d-flex align-center justify-center rounded-xl elevation-2"
                    :style="{ background: editingConfigTemplate.color || '#2196F3', width: '46px', height: '46px' }">
                    <v-icon color="white">{{ editingConfigTemplate.icon || "mdi-calendar" }}</v-icon>
                  </div>
                  <div>
                    <div style="font-weight: 800; font-size: 1.25rem; line-height: 1.2" :style="{ color: isDarkMode ? '#ffffff' : '#1e293b' }">
                      {{ editingConfigTemplate.title }}
                    </div>
                    <div class="d-flex align-center mt-1" style="gap: 8px">
                      <v-chip x-small outlined color="primary" class="font-weight-bold" style="height: 20px">
                        Frequenz: {{ getAmtliFrequencyLabel(editingConfigTemplate) }}
                      </v-chip>
                      <span style="font-size: 0.75rem; color: grey">Dauer: {{ editingConfigTemplate.duration || 30 }} Min</span>
                    </div>
                    <div class="mt-2 d-flex align-center flex-wrap" style="gap: 8px">
                      <v-chip x-small outlined :color="getAmtliTemplateReadiness(editingConfigTemplate).color" class="font-weight-bold">
                        <v-icon left x-small>{{ getAmtliTemplateReadiness(editingConfigTemplate).icon }}</v-icon>
                        {{ getAmtliTemplateReadiness(editingConfigTemplate).label }}
                      </v-chip>
                      <span class="grey--text" style="font-size: 0.72rem">{{ getAmtliTemplatePlainSummary(editingConfigTemplate) }}</span>
                    </div>
                  </div>
                </div>
                <v-btn icon :dark="isDarkMode" @click="cancelTemplateConfig">
                  <v-icon>mdi-close</v-icon>
                </v-btn>
              </div>

              <!-- Dialog Body (Spacious 2-Column Grid) -->
              <v-card-text class="pa-6 custom-scrollbar" style="flex: 1; overflow-y: auto">
                <work-calendar-task-variant-content :template-data="editingConfigTemplate" :is-dark-mode="isDarkMode" />
                <div class="amtli-editor-guide mb-5">
                  <div>
                    <v-icon color="success" class="mr-2">mdi-hand-back-right-outline</v-icon>
                    <span
                      ><strong>Sidebar-Grundlagen:</strong> Dauer und Wiederholung bestimmen die manuelle Nutzung und den passenden
                      Sidebar-Bereich.</span
                    >
                  </div>
                  <div>
                    <v-icon color="primary" class="mr-2">mdi-auto-fix</v-icon>
                    <span
                      ><strong>Für den Assistenten:</strong> Zeiten, Team und Wochenend-Regeln verfeinern zusätzlich die automatische Planung.</span
                    >
                  </div>
                </div>
                <div
                  class="amtli-editor-manual-card pa-4 rounded-xl mb-4"
                  :style="{
                    background: isDarkMode ? 'rgba(19,164,90,.08)' : 'rgba(19,164,90,.045)',
                    border: isDarkMode ? '1px solid rgba(19,164,90,.22)' : '1px solid rgba(19,164,90,.18)',
                  }">
                  <div class="d-flex align-center justify-space-between flex-wrap mb-1" style="gap: 8px">
                    <div class="text-subtitle-2 font-weight-bold d-flex align-center" :style="{ color: isDarkMode ? '#ffffff' : '#2c3e50' }">
                      <v-icon small color="success" class="mr-2">mdi-hand-back-right-outline</v-icon>
                      Grunddaten für die manuelle Sidebar
                    </div>
                    <v-chip x-small color="success" outlined class="font-weight-bold">Danach bereits nutzbar</v-chip>
                  </div>
                  <div style="font-size: 0.75rem; color: grey" class="mb-3 pl-6">Diese Dauer wird beim manuellen Einfügen übernommen.</div>
                  <div class="d-flex align-center" style="gap: 12px">
                    <v-slider
                      v-model.number="editingConfigTemplate.duration"
                      :min="5"
                      :max="240"
                      :step="5"
                      hide-details
                      thumb-label
                      class="flex-grow-1"
                      :dark="isDarkMode"></v-slider>
                    <v-text-field
                      v-model.number="editingConfigTemplate.duration"
                      type="number"
                      outlined
                      dense
                      hide-details
                      style="width: 110px; flex-shrink: 0"
                      suffix="Min"
                      :dark="isDarkMode"></v-text-field>
                  </div>
                </div>

                <work-calendar-task-frequency-settings
                  :template-data="editingConfigTemplate"
                  :frequency-editing-disabled="frequencyEditingDisabled"
                  :locked-message="frequencyEditingLockedMessage"
                  :is-dark-mode="isDarkMode"
                  class="mb-4"
                  @normalize="normalizeTaskFrequencyConfig(editingConfigTemplate)"
                  @toggle-weekday="toggleWeeklyDayInConfig(editingConfigTemplate, $event)" />

                <v-btn
                  block
                  outlined
                  color="primary"
                  class="amtli-automation-toggle text-none font-weight-bold mb-4"
                  :aria-expanded="String(amtliAutomationSettingsOpen)"
                  @click="amtliAutomationSettingsOpen = !amtliAutomationSettingsOpen">
                  <v-icon left>mdi-auto-fix</v-icon>
                  Automatik-Regeln (optional)
                  <v-spacer></v-spacer>
                  <span class="text-caption mr-2">{{ getAmtliTemplateReadiness(editingConfigTemplate).label }}</span>
                  <v-icon>{{ amtliAutomationSettingsOpen ? "mdi-chevron-up" : "mdi-chevron-down" }}</v-icon>
                </v-btn>

                <v-expand-transition>
                  <div v-show="amtliAutomationSettingsOpen">
                    <v-alert text dense type="info" class="mb-4 text-caption">
                      Nur ausfüllen, wenn dieses Ämtli vom Assistenten vorgeschlagen werden soll. Manuelle Nutzung bleibt unabhängig davon möglich.
                    </v-alert>
                    <v-row>
                      <!-- LEFT COLUMN: ZUWEISUNG & TEAM -->
                      <v-col cols="12" lg="6" class="d-flex flex-column" style="gap: 16px">
                        <div
                          class="pa-4 rounded-xl"
                          :style="{
                            background: isDarkMode ? 'rgba(255,255,255,0.02)' : '#f8fafc',
                            border: isDarkMode ? '1px solid rgba(255,255,255,0.05)' : '1px solid rgba(0,0,0,0.05)',
                          }">
                          <div
                            class="text-subtitle-2 mb-1 font-weight-bold d-flex align-center"
                            :style="{ color: isDarkMode ? '#ffffff' : '#2c3e50' }">
                            <v-icon small color="primary" class="mr-2">mdi-door-open</v-icon>
                            Randzeiten
                          </div>
                          <v-switch
                            v-model="editingConfigTemplate.allowDuringOpenClose"
                            label="Während Eröffnung / Schliessung zuteilen"
                            hide-details
                            dense
                            color="primary"
                            :dark="isDarkMode"
                            class="mt-2"></v-switch>
                          <div style="font-size: 0.73rem; color: grey; line-height: 1.3" class="mt-1">
                            Erlaubt die ersten bzw. letzten 30 Minuten einer Schicht für dieses Ämtli.
                          </div>
                        </div>

                        <!-- 1. Fest zugewiesener Mitarbeiter -->
                        <div
                          class="pa-4 rounded-xl"
                          :style="{
                            background: isDarkMode ? 'rgba(255,255,255,0.02)' : '#f8fafc',
                            border: isDarkMode ? '1px solid rgba(255,255,255,0.05)' : '1px solid rgba(0,0,0,0.05)',
                          }">
                          <div
                            class="text-subtitle-2 mb-1 font-weight-bold d-flex align-center"
                            :style="{ color: isDarkMode ? '#ffffff' : '#2c3e50' }">
                            <v-icon small color="primary" class="mr-2">mdi-account-star-outline</v-icon>
                            Fest zugewiesen an (Einzelperson)
                            <v-chip x-small color="blue" outlined class="ml-2 font-weight-bold" style="height: 18px">Für Auto-Zuweisung</v-chip>
                          </div>
                          <div style="font-size: 0.75rem; color: grey" class="mb-3 pl-6">
                            Hier eine Person auswählen, wenn diese Aufgabe fest einer bestimmten Person zugeteilt werden soll.
                          </div>
                          <v-select
                            v-model="editingConfigTemplate.fixedEmployeeId"
                            :items="employeeOptions"
                            clearable
                            dense
                            outlined
                            rounded="lg"
                            :dark="isDarkMode"
                            placeholder="Keine feste Person (Pool/Zufall)"
                            hide-details></v-select>
                        </div>

                        <!-- 2. Mitarbeiter-Pool (Alternativen) -->
                        <div
                          class="pa-4 rounded-xl"
                          :style="{
                            background: isDarkMode ? 'rgba(255,255,255,0.02)' : '#f8fafc',
                            border: isDarkMode ? '1px solid rgba(255,255,255,0.05)' : '1px solid rgba(0,0,0,0.05)',
                          }">
                          <div
                            class="text-subtitle-2 mb-1 font-weight-bold d-flex align-center"
                            :style="{ color: isDarkMode ? '#ffffff' : '#2c3e50' }">
                            <v-icon small color="primary" class="mr-2">mdi-account-group-outline</v-icon>
                            Mitarbeiter-Pool (Alternativen)
                            <v-chip x-small color="blue" outlined class="ml-2 font-weight-bold" style="height: 18px">Für Auto-Zuweisung</v-chip>
                          </div>
                          <div style="font-size: 0.75rem; color: grey" class="mb-3 pl-6">
                            Mitarbeiter auswählen, die für diese Aufgabe bevorzugt eingeteilt werden.
                          </div>
                          <v-select
                            v-model="editingConfigTemplate.assignedEmployees"
                            :items="employeeOptions"
                            multiple
                            chips
                            deletable-chips
                            dense
                            outlined
                            rounded="lg"
                            :dark="isDarkMode"
                            placeholder="Alle Mitarbeiter zugelassen"
                            hide-details></v-select>
                        </div>

                        <!-- 3. Verkettete Ämtli -->
                        <div
                          class="pa-4 rounded-xl"
                          :style="{
                            background: isDarkMode ? 'rgba(255,255,255,0.02)' : '#f8fafc',
                            border: isDarkMode ? '1px solid rgba(255,255,255,0.05)' : '1px solid rgba(0,0,0,0.05)',
                          }">
                          <div
                            class="text-subtitle-2 mb-1 font-weight-bold d-flex align-center"
                            :style="{ color: isDarkMode ? '#ffffff' : '#2c3e50' }">
                            <v-icon small color="teal" class="mr-2">mdi-link-variant</v-icon>
                            Verkettete Ämtli (Gleicher Mitarbeiter)
                            <v-chip x-small color="blue" outlined class="ml-2 font-weight-bold" style="height: 18px">Für Auto-Zuweisung</v-chip>
                          </div>
                          <div style="font-size: 0.75rem; color: grey" class="mb-3 pl-6">
                            Wähle weitere Ämtlis, die am selben Tag direkt hintereinander geplant werden.
                          </div>
                          <v-select
                            v-model="editingConfigTemplate.linkedTemplateIds"
                            :items="
                              autoAssignConfig.templates
                                .filter((t) => (t._id || t.id) !== (editingConfigTemplate._id || editingConfigTemplate.id))
                                .map((t) => ({ text: t.title, value: t._id || t.id }))
                            "
                            multiple
                            chips
                            deletable-chips
                            dense
                            outlined
                            rounded="lg"
                            :dark="isDarkMode"
                            placeholder="Keine Verkettung"
                            hide-details></v-select>
                        </div>
                      </v-col>

                      <!-- RIGHT COLUMN: ZEITEN & TAGE -->
                      <v-col cols="12" lg="6" class="d-flex flex-column" style="gap: 16px">
                        <!-- 4. Uhrzeiten -->
                        <div
                          class="pa-4 rounded-xl"
                          :style="{
                            background: isDarkMode ? 'rgba(255,255,255,0.02)' : '#f8fafc',
                            border: isDarkMode ? '1px solid rgba(255,255,255,0.05)' : '1px solid rgba(0,0,0,0.05)',
                          }">
                          <div
                            class="text-subtitle-2 font-weight-bold d-flex align-center mb-1"
                            :style="{ color: isDarkMode ? '#ffffff' : '#2c3e50' }">
                            <v-icon small color="amber darken-2" class="mr-2">mdi-clock-outline</v-icon>
                            Wann soll das Ämtli stattfinden?
                            <v-chip x-small color="teal" outlined class="ml-2 font-weight-bold" style="height: 18px">Planungsregel</v-chip>
                          </div>
                          <div style="font-size: 0.75rem; color: grey" class="mb-3 pl-6">
                            Eine klare Regel ersetzt mehrere Schalter. Termine und andere Ämtli werden dabei immer berücksichtigt.
                          </div>

                          <div class="pl-6">
                            <v-select
                              v-model="editingConfigTemplate.timePlanningMode"
                              :items="taskTimeModeOptions"
                              item-text="text"
                              item-value="value"
                              dense
                              outlined
                              rounded="lg"
                              :dark="isDarkMode"
                              hide-details
                              @change="setTaskTimeMode(editingConfigTemplate, $event)"></v-select>

                            <div
                              class="mt-2 pa-3 rounded-lg d-flex align-start"
                              :style="{
                                background: isDarkMode ? 'rgba(255,255,255,0.025)' : '#ffffff',
                                border: isDarkMode ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(15,23,42,0.06)',
                              }">
                              <v-icon
                                small
                                :color="editingConfigTemplate.timePlanningMode === 'required_window' ? 'orange' : 'primary'"
                                class="mr-2 mt-1">
                                {{
                                  editingConfigTemplate.timePlanningMode === "required_window"
                                    ? "mdi-alert-circle-outline"
                                    : "mdi-information-outline"
                                }}
                              </v-icon>
                              <span style="font-size: 0.75rem; color: grey; line-height: 1.45">{{
                                getTaskTimeModeHelp(editingConfigTemplate.timePlanningMode)
                              }}</span>
                            </div>
                          </div>

                          <div
                            v-if="
                              editingConfigTemplate.timePlanningMode === 'preferred_window' ||
                              editingConfigTemplate.timePlanningMode === 'required_window'
                            "
                            class="pl-6 mt-3">
                            <div class="d-flex" style="gap: 10px">
                              <v-text-field
                                v-model="editingConfigTemplate.timePreference.windowStart"
                                type="time"
                                label="Von"
                                :dark="isDarkMode"
                                dense
                                outlined
                                rounded="lg"
                                hide-details></v-text-field>
                              <v-text-field
                                v-model="editingConfigTemplate.timePreference.windowEnd"
                                type="time"
                                label="Bis"
                                :dark="isDarkMode"
                                dense
                                outlined
                                rounded="lg"
                                hide-details></v-text-field>
                            </div>
                            <div class="text-caption mt-2" style="color: grey">
                              Das Zeitfenster gilt für das gesamte Ämtli inklusive seiner Dauer.
                            </div>
                          </div>

                          <div v-if="editingConfigTemplate.timePlanningMode === 'fixed_times'" class="pl-6 mt-3">
                            <div v-for="(time, tIdx) in editingConfigTemplate.defaultTimes" :key="tIdx" class="d-flex align-center mb-2">
                              <v-text-field
                                v-model="editingConfigTemplate.defaultTimes[tIdx]"
                                type="time"
                                :dark="isDarkMode"
                                dense
                                outlined
                                rounded="lg"
                                hide-details></v-text-field>
                              <v-btn icon @click="editingConfigTemplate.defaultTimes.splice(tIdx, 1)" color="red lighten-1" class="ml-2">
                                <v-icon>mdi-close</v-icon>
                              </v-btn>
                            </div>
                            <v-btn
                              outlined
                              small
                              color="primary"
                              class="mt-2 font-weight-bold"
                              block
                              style="border-style: dashed; border-width: 1px; text-transform: none; border-radius: 8px"
                              @click="editingConfigTemplate.defaultTimes.push('12:00')">
                              <v-icon left>mdi-clock-plus-outline</v-icon> Zeit hinzufügen
                            </v-btn>
                          </div>
                        </div>

                        <!-- 6. Wochenend-Einstellung -->
                        <div
                          class="pa-4 rounded-xl"
                          v-if="
                            editingConfigTemplate.frequency === 'daily' ||
                            editingConfigTemplate.frequency === 'weekly' ||
                            editingConfigTemplate.frequency === 'monthly'
                          "
                          :style="{
                            background: isDarkMode ? 'rgba(255,255,255,0.02)' : '#f8fafc',
                            border: isDarkMode ? '1px solid rgba(255,255,255,0.05)' : '1px solid rgba(0,0,0,0.05)',
                          }">
                          <div
                            class="text-subtitle-2 mb-1 font-weight-bold d-flex align-center"
                            :style="{ color: isDarkMode ? '#ffffff' : '#2c3e50' }">
                            <v-icon small color="purple" class="mr-2">mdi-weather-sunset-up</v-icon>
                            Wochenend-Einstellung
                            <v-chip x-small color="blue" outlined class="ml-2 font-weight-bold" style="height: 18px">Für Auto-Zuweisung</v-chip>
                          </div>
                          <div style="font-size: 0.75rem; color: grey" class="mb-3 pl-6">Handhabung an Samstagen und Sonntagen.</div>
                          <v-radio-group
                            v-if="editingConfigTemplate.weekendConfig"
                            v-model="editingConfigTemplate.weekendConfig.mode"
                            hide-details
                            class="mt-1 pl-6"
                            :dark="isDarkMode"
                            @change="normalizeTaskFrequencyConfig(editingConfigTemplate)">
                            <v-radio label="Auch am Wochenende einplanen" value="always" color="primary"></v-radio>
                            <v-radio label="Am Wochenende deaktivieren (nur Mo–Fr)" value="skip_weekend" color="primary"></v-radio>
                            <v-radio label="Andere Dauer am Wochenende" value="custom_duration" color="primary"></v-radio>
                          </v-radio-group>
                          <v-alert
                            v-if="
                              editingConfigTemplate.weekendConfig &&
                              editingConfigTemplate.weekendConfig.mode === 'skip_weekend' &&
                              editingConfigTemplate.frequency === 'monthly' &&
                              hasSelectedWeekendTaskDay(editingConfigTemplate)
                            "
                            type="info"
                            outlined
                            dense
                            class="text-caption ml-6 mt-2 mb-0">
                            Fällt ein gewählter Monatstag auf Samstag oder Sonntag, wird einmalig der nächste passende Werktag im selben Monat
                            verwendet.
                          </v-alert>
                          <div
                            v-if="editingConfigTemplate.weekendConfig && editingConfigTemplate.weekendConfig.mode === 'custom_duration'"
                            class="mt-3 pl-6">
                            <v-text-field
                              v-model.number="editingConfigTemplate.weekendConfig.weekendDuration"
                              label="Dauer am Wochenende (Minuten)"
                              type="number"
                              dense
                              outlined
                              rounded="lg"
                              :dark="isDarkMode"
                              hide-details></v-text-field>
                          </div>

                          <!-- Weekend Clock Times Override -->
                          <div
                            class="mt-4 pl-6"
                            v-if="editingConfigTemplate.weekendConfig && editingConfigTemplate.weekendConfig.mode !== 'skip_weekend'">
                            <v-checkbox
                              v-model="editingConfigTemplate.weekendConfig.hasWeekendTimes"
                              label="Abweichende Uhrzeit am Wochenende?"
                              dense
                              :dark="isDarkMode"
                              hide-details
                              class="mt-0"
                              color="primary"
                              style="font-size: 0.8rem"
                              @change="
                                editingConfigTemplate.weekendConfig.hasWeekendTimes &&
                                (!editingConfigTemplate.weekendConfig.weekendTimes || editingConfigTemplate.weekendConfig.weekendTimes.length === 0)
                                  ? $set(editingConfigTemplate.weekendConfig, 'weekendTimes', ['10:00'])
                                  : null
                              "></v-checkbox>

                            <div v-if="editingConfigTemplate.weekendConfig.hasWeekendTimes" class="mt-2">
                              <div
                                v-for="(time, tIdx) in editingConfigTemplate.weekendConfig.weekendTimes"
                                :key="'we-time-' + tIdx"
                                class="d-flex align-center mb-2">
                                <v-text-field
                                  v-model="editingConfigTemplate.weekendConfig.weekendTimes[tIdx]"
                                  type="time"
                                  :dark="isDarkMode"
                                  dense
                                  outlined
                                  rounded="lg"
                                  hide-details></v-text-field>
                                <v-btn
                                  icon
                                  @click="editingConfigTemplate.weekendConfig.weekendTimes.splice(tIdx, 1)"
                                  color="red lighten-1"
                                  class="ml-2">
                                  <v-icon>mdi-close</v-icon>
                                </v-btn>
                              </div>
                              <v-btn
                                outlined
                                small
                                color="primary"
                                class="mt-2 font-weight-bold"
                                block
                                style="border-style: dashed; border-width: 1px; text-transform: none; border-radius: 8px"
                                @click="editingConfigTemplate.weekendConfig.weekendTimes.push('10:00')">
                                <v-icon left>mdi-clock-plus-outline</v-icon> Wochenend-Zeit hinzufügen
                              </v-btn>
                            </div>
                          </div>
                        </div>
                      </v-col>
                    </v-row>
                  </div>
                </v-expand-transition>
              </v-card-text>

              <!-- Dialog Footer -->
              <v-divider :style="{ borderColor: isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)' }"></v-divider>
              <div class="pa-4 d-flex justify-end" :style="{ background: isDarkMode ? '#1e1e1e' : '#fcfcfc' }">
                <v-btn text class="mr-2 font-weight-bold text-none" @click="cancelTemplateConfig">Abbrechen</v-btn>
                <v-btn
                  depressed
                  color="primary"
                  class="px-8 font-weight-bold rounded-lg pa-4 elevation-1"
                  @click="saveTemplateConfig"
                  style="text-transform: none">
                  <v-icon left>mdi-check</v-icon> Speichern & Übernehmen
                </v-btn>
              </div>
            </div>
          </v-card>
        </v-dialog>

        <!-- Ämtli-Pool Dialog -->
        <v-dialog v-if="hasTaskManagementRight" v-model="amtliPoolDialog" max-width="680px" rounded="xl" persistent :dark="isDarkMode">
          <v-card class="rounded-xl pa-2" :style="{ background: isDarkMode ? '#1e1e1e' : '#ffffff' }">
            <v-card-title class="d-flex align-center justify-space-between pa-4 pb-2">
              <div class="d-flex align-center" style="gap: 12px">
                <div class="pa-2 rounded-lg" style="background: rgba(33, 150, 243, 0.15)">
                  <v-icon color="primary">mdi-package-variant-closed</v-icon>
                </div>
                <div>
                  <div style="font-weight: 700; font-size: 1.2rem">Ämtli-Pool</div>
                  <div style="font-size: 0.8rem; color: grey">Wähle ein Ämtli aus dem Vorlagen-Pool für dein Studio aus.</div>
                </div>
              </div>
              <v-btn icon @click="closeAmtliPoolDialog">
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </v-card-title>

            <v-card-text class="pa-4 pt-2 custom-scrollbar" style="max-height: 460px; overflow-y: auto">
              <div v-if="masterAmtliPool.length === 0" class="text-center py-8 text-muted">Keine Ämtli-Vorlagen im Pool vorhanden.</div>
              <div v-else style="display: grid; grid-template-columns: repeat(auto-fill, minmax(280px, 1fr)); gap: 12px">
                <v-card
                  v-for="tpl in masterAmtliPool"
                  :key="tpl._id || tpl.id"
                  outlined
                  class="pa-3 rounded-xl d-flex align-center justify-space-between"
                  :class="{ 'cursor-pointer hover-card': !isAmtliAdded(tpl) }"
                  :style="{
                    background: isAmtliAdded(tpl)
                      ? isDarkMode
                        ? 'rgba(255,255,255,0.01)'
                        : '#f1f5f9'
                      : isDarkMode
                      ? 'rgba(255,255,255,0.03)'
                      : '#ffffff',
                    borderColor: isAmtliAdded(tpl)
                      ? isDarkMode
                        ? 'rgba(76, 175, 80, 0.3)'
                        : 'rgba(76, 175, 80, 0.4)'
                      : isDarkMode
                      ? 'rgba(255,255,255,0.08)'
                      : 'rgba(0,0,0,0.08)',
                    opacity: isAmtliAdded(tpl) ? 0.65 : 1,
                    cursor: isAmtliAdded(tpl) ? 'default' : 'pointer',
                  }"
                  @click="!isAmtliAdded(tpl) && selectAmtliFromPool(tpl)">
                  <div class="d-flex align-center" style="gap: 12px">
                    <div
                      class="d-flex align-center justify-center rounded-lg"
                      :style="{ background: isAmtliAdded(tpl) ? '#4CAF50' : tpl.color || '#2196F3', width: '40px', height: '40px', flexShrink: 0 }">
                      <v-icon color="white">{{ isAmtliAdded(tpl) ? "mdi-check" : tpl.icon || "mdi-checkbox-marked" }}</v-icon>
                    </div>
                    <div>
                      <div class="d-flex align-center" style="gap: 6px">
                        <span style="font-weight: 700; font-size: 0.95rem" :style="{ color: isDarkMode ? 'white' : '#1e293b' }">{{ tpl.title }}</span>
                        <v-chip
                          v-if="isAmtliAdded(tpl)"
                          x-small
                          color="success"
                          outlined
                          class="font-weight-bold"
                          style="height: 18px; font-size: 0.65rem">
                          Hinzugefügt
                        </v-chip>
                      </div>
                      <div style="font-size: 0.75rem; color: grey">
                        <template v-if="tpl.isVariantGroup">Eigene Variante erstellen</template>
                        <template v-else>{{ tpl.duration || 30 }} Min · {{ getAmtliFrequencyLabel(tpl) }}</template>
                      </div>
                    </div>
                  </div>
                  <v-btn icon :color="isAmtliAdded(tpl) ? 'success' : 'primary'" :disabled="isAmtliAdded(tpl)">
                    <v-icon>{{ isAmtliAdded(tpl) ? "mdi-check-circle" : "mdi-plus-circle-outline" }}</v-icon>
                  </v-btn>
                </v-card>
              </div>
            </v-card-text>
          </v-card>
        </v-dialog>

        <!-- Ämtli-Dashboard Dialog -->
        <v-dialog v-if="hasTaskManagementRight" v-model="taskDashboardDialog" fullscreen persistent transition="dialog-bottom-transition" :dark="isDarkMode">
          <v-card class="amtli-review-card" :style="{ background: isDarkMode ? '#121212' : '#f8fafc' }">
            <work-calendar-amtli-journey-header
              v-if="amtliJourneyActive"
              :step="3"
              title="Zuweisungen übernehmen"
              subtitle="Mitarbeiter anpassen und vorbereitete Ämtli einzeln, pro Tag oder gemeinsam speichern"
              :is-dark-mode="isDarkMode"
              @back="handleAmtliDashboardBack"
              @close="closeAmtliDashboard">
              <template #actions>
                <v-btn
                  v-if="publishableDraftTasks.length"
                  depressed
                  color="success"
                  class="text-none font-weight-bold"
                  @click="publishAllDraftsInDashboard">
                  <v-icon left>mdi-content-save-check-outline</v-icon> {{ publishableDraftTasks.length }} speichern
                </v-btn>
              </template>
            </work-calendar-amtli-journey-header>
            <v-toolbar
              v-else
              flat
              :dark="isDarkMode"
              :color="isDarkMode ? '#1e1e1e' : 'white'"
              style="border-bottom: 1px solid rgba(128, 128, 128, 0.15)">
              <v-btn icon @click="closeAmtliDashboard"><v-icon>mdi-close</v-icon></v-btn>
              <v-toolbar-title class="font-weight-bold" style="font-size: 1.25rem">
                <v-icon left color="primary" class="mr-2">mdi-view-dashboard-outline</v-icon> Ämtli-Manager
              </v-toolbar-title>
              <v-spacer></v-spacer>
              <v-btn
                v-if="publishableDraftTasks.length"
                depressed
                color="success"
                class="text-none font-weight-bold"
                @click="publishAllDraftsInDashboard">
                <v-icon left>mdi-content-save-check-outline</v-icon>{{ publishableDraftTasks.length }} speichern
              </v-btn>
            </v-toolbar>

            <div class="amtli-review-rangebar">
              <div class="amtli-review-presets">
                <v-btn text small color="primary" class="font-weight-bold text-none" @click="setDashboardRange('thisWeek')">Diese Woche</v-btn>
                <v-btn text small color="primary" class="font-weight-bold text-none" @click="setDashboardRange('thisMonth')">Dieser Monat</v-btn>
                <v-btn text small color="primary" class="font-weight-bold text-none" @click="setDashboardRange('nextWeek')">Nächste Woche</v-btn>
                <v-btn text small color="primary" class="font-weight-bold text-none" @click="setDashboardRange('nextMonth')">Nächster Monat</v-btn>
              </div>
              <div class="amtli-review-dates">
                <v-text-field
                  v-model="taskDashboardConfig.startDate"
                  label="Von"
                  type="date"
                  outlined
                  dense
                  hide-details
                  :dark="isDarkMode"
                  @change="fetchDashboardData" />
                <v-text-field
                  v-model="taskDashboardConfig.endDate"
                  label="Bis"
                  type="date"
                  outlined
                  dense
                  hide-details
                  :dark="isDarkMode"
                  @change="fetchDashboardData" />
              </div>
            </div>

            <v-card-text class="pa-6 amtli-review-scroll">
              <!-- Loading indicator -->
              <div v-if="taskDashboardLoading" class="d-flex flex-column align-center justify-center py-12" style="height: 50vh">
                <v-progress-circular indeterminate color="primary" size="64" width="6" class="mb-4"></v-progress-circular>
                <div class="text-subtitle-1 grey--text">Dashboard-Daten werden geladen...</div>
              </div>

              <div v-else>
                <!-- Metrics cards -->
                <v-row class="mb-6">
                  <v-col cols="12" sm="6" md="4">
                    <v-card flat outlined class="pa-4 rounded-xl d-flex align-center" :style="{ background: isDarkMode ? '#1e1e1e' : 'white' }">
                      <div class="rounded-lg pa-3 mr-4" style="background: rgba(76, 175, 80, 0.15)">
                        <v-icon color="success">mdi-file-check-outline</v-icon>
                      </div>
                      <div>
                        <div class="text-h5 font-weight-bold">{{ dashboardSummaryMetrics.drafts }}</div>
                        <div class="text-caption grey--text">Planungsvorschläge</div>
                      </div>
                    </v-card>
                  </v-col>

                  <v-col cols="12" sm="6" md="4">
                    <v-card flat outlined class="pa-4 rounded-xl d-flex align-center" :style="{ background: isDarkMode ? '#1e1e1e' : 'white' }">
                      <div class="rounded-lg pa-3 mr-4" style="background: rgba(33, 150, 243, 0.15)">
                        <v-icon color="info">mdi-checkbox-marked-circle-outline</v-icon>
                      </div>
                      <div>
                        <div class="text-h5 font-weight-bold">{{ dashboardSummaryMetrics.published }}</div>
                        <div class="text-caption grey--text">Veröffentlichte Ämtli</div>
                      </div>
                    </v-card>
                  </v-col>

                  <v-col cols="12" sm="6" md="4">
                    <v-card flat outlined class="pa-4 rounded-xl d-flex align-center" :style="{ background: isDarkMode ? '#1e1e1e' : 'white' }">
                      <div class="rounded-lg pa-3 mr-4" style="background: rgba(255, 193, 7, 0.15)">
                        <v-icon color="warning">mdi-chart-line</v-icon>
                      </div>
                      <div style="flex-grow: 1">
                        <div class="text-h5 font-weight-bold d-flex align-center justify-space-between">
                          <span>{{ dashboardSummaryMetrics.completionRate }}%</span>
                          <span class="text-body-2 font-weight-regular grey--text"
                            >{{ dashboardSummaryMetrics.completed }}/{{ dashboardSummaryMetrics.published }}</span
                          >
                        </div>
                        <div class="text-caption grey--text">Erledigungsquote</div>
                      </div>
                    </v-card>
                  </v-col>
                </v-row>

                <div
                  class="amtli-review-callout mb-5"
                  :class="dashboardNeedsAssignmentCount ? 'amtli-review-callout--warning' : 'amtli-review-callout--ready'">
                  <div class="d-flex align-center" style="gap: 12px">
                    <div class="amtli-review-callout__icon">
                      <v-icon :color="dashboardNeedsAssignmentCount ? 'warning' : 'success'">
                        {{ dashboardNeedsAssignmentCount ? "mdi-account-alert-outline" : "mdi-shield-check-outline" }}
                      </v-icon>
                    </div>
                    <div>
                      <div class="font-weight-black">
                        {{ dashboardReadyDraftCount }} Vorschläge sind speicherbereit
                        <span v-if="dashboardNeedsAssignmentCount"> · {{ dashboardNeedsAssignmentCount }} brauchen noch eine Zuweisung</span>
                      </div>
                      <div class="grey--text" style="font-size: 0.76rem">
                        Unzugewiesene Vorschläge bleiben ausschliesslich hier im Manager und erscheinen nicht im Kalender.
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Tabs navigation -->
                <v-tabs v-model="taskDashboardTab" background-color="transparent" color="primary" class="mb-4">
                  <v-tab class="font-weight-bold text-none">
                    <v-icon left>mdi-file-check-outline</v-icon> Planungsvorschläge
                    <v-chip x-small class="ml-2 font-weight-bold" :color="dashboardSummaryMetrics.drafts > 0 ? 'primary' : 'grey'" dark>
                      {{ dashboardSummaryMetrics.drafts }}
                    </v-chip>
                  </v-tab>
                  <v-tab class="font-weight-bold text-none"> <v-icon left>mdi-chart-bar</v-icon> Auslastung & Statistik </v-tab>
                  <v-tab class="font-weight-bold text-none">
                    <v-icon left>mdi-history</v-icon> Überfällige Ämtli
                    <v-chip x-small class="ml-2 font-weight-bold" :color="overdueTasks.length > 0 ? 'warning' : 'grey'" dark>
                      {{ overdueTasks.length }}
                    </v-chip>
                  </v-tab>
                </v-tabs>

                <v-tabs-items v-model="taskDashboardTab" style="background: transparent">
                  <!-- TAB 1: DRAFTS -->
                  <v-tab-item>
                    <v-card flat class="rounded-xl border-1 pa-5" :style="{ background: isDarkMode ? '#1e1e1e' : 'white' }">
                      <div class="d-flex align-center justify-space-between mb-6">
                        <div>
                          <h3 class="text-h6 font-weight-bold mb-1">Planungsvorschläge prüfen</h3>
                          <p class="text-caption grey--text mb-0">
                            Diese Einträge sind noch nicht im Kalender sichtbar. Bereits zugewiesene Vorschläge speicherst du gesammelt oder pro Tag.
                            Bei einem Vorschlag ohne Mitarbeiter wird deine manuelle Auswahl direkt gespeichert und veröffentlicht.
                          </p>
                        </div>
                      </div>

                      <div v-if="draftTasksGrouped.length === 0" class="d-flex flex-column align-center justify-center py-12">
                        <v-icon size="64" color="grey lighten-1" class="mb-4">mdi-file-document-outline</v-icon>
                        <div class="text-subtitle-1 font-weight-bold mb-1">Keine Planungsvorschläge vorhanden</div>
                        <div class="text-caption grey--text">Alle Ämtli im gewählten Zeitraum sind bereits verbindlich eingeplant.</div>
                      </div>

                      <div v-else>
                        <div
                          v-for="group in draftTasksGrouped"
                          :key="group.date"
                          class="mb-8 overflow-hidden rounded-xl border-1"
                          :style="{
                            background: isDarkMode ? '#1e1e1e' : '#ffffff',
                            borderColor: isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
                          }">
                          <!-- Redesigned Luxury Day Header -->
                          <div
                            class="pa-4 d-flex align-center justify-space-between flex-wrap"
                            style="gap: 12px"
                            :style="{
                              background: isDarkMode ? 'linear-gradient(90deg, #2a2a2a, #1e1e1e)' : 'linear-gradient(90deg, #f8fafc, #ffffff)',
                              borderBottom: isDarkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(0,0,0,0.06)',
                            }">
                            <div class="d-flex align-center flex-wrap" style="gap: 16px">
                              <!-- Calendar Icon Badge -->
                              <div
                                class="d-flex align-center justify-center rounded-lg"
                                style="width: 40px; height: 40px; background: rgba(33, 150, 243, 0.12); border: 1px solid rgba(33, 150, 243, 0.2)">
                                <v-icon color="primary">mdi-calendar-today</v-icon>
                              </div>

                              <!-- Date -->
                              <div>
                                <span class="font-weight-bold text-subtitle-1" :style="{ color: isDarkMode ? 'white' : '#1e293b' }">{{
                                  group.formattedDate
                                }}</span>
                                <div class="d-flex align-center mt-1" style="gap: 10px">
                                  <v-chip x-small class="font-weight-bold" color="primary" outlined>{{ group.totalCount }} Ämtli</v-chip>
                                  <v-chip x-small class="font-weight-bold" color="grey" outlined>{{ group.totalDuration }} Min. Gesamtzeit</v-chip>
                                  <v-chip x-small class="font-weight-bold" color="success" outlined>
                                    <v-icon x-small left>mdi-calendar-clock</v-icon>
                                    {{ group.freeHours }} Std. frei für Termine
                                  </v-chip>
                                </div>

                                <!-- Heatmap Timeline -->
                                <div class="d-flex align-center mt-2 flex-wrap" style="gap: 4px">
                                  <span style="font-size: 0.7rem; font-weight: 700; color: grey" class="mr-1">Verlauf:</span>
                                  <v-tooltip bottom v-for="slot in calculateHourlyCapacity(group.date)" :key="slot.hour">
                                    <template v-slot:activator="{ on, attrs }">
                                      <div
                                        v-bind="attrs"
                                        v-on="on"
                                        class="d-flex align-center justify-center rounded"
                                        :style="{
                                          background: slot.color,
                                          width: '26px',
                                          height: '22px',
                                          fontSize: '0.68rem',
                                          fontWeight: '800',
                                          color: slot.capacity > 0 ? 'white' : 'rgba(255,255,255,0.4)',
                                          border: '1px solid rgba(0,0,0,0.05)',
                                          cursor: 'default',
                                        }">
                                        {{ slot.hour }}
                                      </div>
                                    </template>
                                    <div style="font-size: 0.78rem; padding: 4px 8px; line-height: 1.4">
                                      <div class="font-weight-bold mb-1">{{ slot.timeText }}</div>
                                      <div>Gesamtkapazität: {{ slot.capacity }} Trainer-Minuten</div>
                                      <div>Frei für Termine: {{ slot.freeMinutes }} Minuten ({{ slot.percentFree }}% frei)</div>
                                      <div class="mt-1 font-weight-bold" :style="{ color: slot.color === '#747d8c' ? 'grey' : slot.color }">
                                        {{ slot.label }}
                                      </div>
                                    </div>
                                  </v-tooltip>
                                </div>
                              </div>

                              <!-- Custom Visual capacity / Space Meter -->
                              <div class="d-flex flex-column" style="width: 220px; max-width: 100%">
                                <div class="d-flex align-center justify-space-between mb-1" style="font-size: 0.78rem">
                                  <span class="grey--text">Zuteilungs-Quote:</span>
                                  <span class="font-weight-bold" :color="group.percent === 100 ? 'success' : 'warning'"
                                    >{{ group.assignedCount }} von {{ group.totalCount }} ({{ group.percent }}%)</span
                                  >
                                </div>
                                <v-progress-linear
                                  :value="group.percent"
                                  height="6"
                                  rounded
                                  :color="group.percent === 100 ? 'success' : 'primary'"
                                  background-color="rgba(128,128,128,0.15)"></v-progress-linear>
                              </div>
                            </div>

                            <!-- Action -->
                            <v-btn
                              depressed
                              color="success"
                              class="rounded-lg font-weight-bold text-none px-4"
                              :disabled="group.assignedCount === 0"
                              @click="publishDayDrafts(group.date)">
                              <v-icon left small>mdi-content-save-check-outline</v-icon> Zugewiesene speichern
                            </v-btn>
                          </div>

                          <div class="pa-3">
                            <v-simple-table :dark="isDarkMode" class="elevation-0 transparent amtli-proposal-table">
                              <template v-slot:default>
                                <thead>
                                  <tr>
                                    <th class="text-left font-weight-bold grey--text" style="width: 100px">Uhrzeit</th>
                                    <th class="text-left font-weight-bold grey--text">Ämtli</th>
                                    <th class="text-left font-weight-bold grey--text" style="width: 320px">Mitarbeiter-Zuweisung</th>
                                    <th class="text-right font-weight-bold grey--text" style="width: 110px">Aktionen</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  <tr v-for="task in group.tasks" :key="task._id" class="wc-dashboard-row transition-all">
                                    <td>
                                      <div class="d-flex align-center">
                                        <v-text-field
                                          v-if="task.isEditingTime"
                                          v-model="task.time"
                                          type="time"
                                          dense
                                          outlined
                                          hide-details
                                          style="width: 100px"
                                          :dark="isDarkMode"
                                          @blur="saveTaskTimeInDashboard(task)"
                                          @keyup.enter="saveTaskTimeInDashboard(task)"
                                          autofocus></v-text-field>
                                        <template v-else>
                                          <v-chip
                                            small
                                            outlined
                                            class="font-weight-bold text-body-2 cursor-pointer"
                                            style="border-width: 1.5px"
                                            @click="$set(task, 'isEditingTime', true)"
                                            title="Klicken zum Bearbeiten">
                                            {{ task.time }}
                                          </v-chip>
                                          <v-btn
                                            icon
                                            x-small
                                            class="ml-1"
                                            color="primary lighten-1"
                                            @click="$set(task, 'isEditingTime', true)"
                                            title="Uhrzeit ändern">
                                            <v-icon x-small>mdi-pencil</v-icon>
                                          </v-btn>
                                        </template>
                                      </div>
                                    </td>
                                    <td>
                                      <div class="d-flex align-center py-2">
                                        <v-avatar size="36" :color="task.color || 'primary'" class="white--text mr-3 elevation-1">
                                          <v-icon size="20" color="white">{{ task.icon || "mdi-checkbox-marked" }}</v-icon>
                                        </v-avatar>
                                        <div>
                                          <div class="font-weight-bold text-body-1" :style="{ color: isDarkMode ? 'white' : '#1e293b' }">
                                            {{ task.title }}
                                          </div>
                                          <div class="text-caption grey--text mt-0.5">{{ task.description || "Keine Beschreibung hinterlegt" }}</div>
                                        </div>
                                      </div>
                                    </td>
                                    <td>
                                      <div class="py-2">
                                        <v-select
                                          v-model="task.assignedEmployee"
                                          :items="getEligibleEmployeesForTask(task)"
                                          dense
                                          outlined
                                          hide-details
                                          class="rounded-lg"
                                          placeholder="Mitarbeiter wählen & direkt speichern"
                                          :dark="isDarkMode"
                                          @change="updateTaskAssigneeInDashboard(task)"></v-select>

                                        <!-- Meta info for assigned employee -->
                                        <div
                                          v-if="task.assignedEmployee"
                                          class="text-caption mt-1 px-1 d-flex align-center"
                                          style="font-size: 0.78rem; opacity: 0.85">
                                          <v-icon x-small class="mr-1" color="primary">mdi-calendar-check</v-icon>
                                          <strong>{{ getEmployeeStatsText(task.assignedEmployee) }}</strong>
                                        </div>
                                        <!-- Reason why it couldn't be assigned -->
                                        <div
                                          v-else-if="task.unassignedReason"
                                          class="text-caption mt-1 px-1 text-none d-flex align-center"
                                          style="font-size: 0.78rem; color: #ff9800; font-weight: 500">
                                          <v-icon x-small class="mr-1" color="warning">mdi-alert-circle-outline</v-icon>
                                          <span>Grund: {{ task.unassignedReason }}</span>
                                        </div>
                                        <div v-if="!task.assignedEmployee" class="text-caption mt-1 px-1 grey--text" style="font-size: 0.72rem">
                                          Die Auswahl veröffentlicht dieses Ämtli sofort im Kalender.
                                        </div>
                                      </div>
                                    </td>
                                    <td class="text-right">
                                      <v-btn v-if="task.assignedEmployee" icon color="success" class="mr-1" @click="publishSingleTask(task._id)">
                                        <v-icon>mdi-check-circle-outline</v-icon>
                                      </v-btn>
                                      <v-btn icon color="error" @click="deleteDashboardTask(task._id)">
                                        <v-icon>mdi-trash-can-outline</v-icon>
                                      </v-btn>
                                    </td>
                                  </tr>
                                </tbody>
                              </template>
                            </v-simple-table>
                          </div>
                        </div>
                      </div>
                    </v-card>
                  </v-tab-item>
                  <!-- Nicht zugewiesene Auto-Ergebnisse werden bereits im
                       ersten Tab als Planungsvorschläge verwaltet. Eine zweite
                       operative Liste "Offene Ämtli" würde denselben Entwurf
                       irreführend doppelt anzeigen. -->
                  <v-tab-item v-if="false">
                    <v-card flat class="rounded-xl border-1 pa-5" :style="{ background: isDarkMode ? '#1e1e1e' : 'white' }">
                      <div class="mb-6">
                        <h3 class="text-h6 font-weight-bold mb-1">Nicht zugewiesene Ämtli</h3>
                        <p class="text-caption grey--text mb-0">
                          Dies sind Ämtli, für die das System oder der Planer keinen Mitarbeiter zuteilen konnte. Zuweisung erfolgt inline.
                        </p>

                        <!-- Beautiful Filter Selection Chips -->
                        <div class="mt-4 d-flex align-center flex-wrap" style="gap: 8px">
                          <v-chip
                            filter
                            :input-value="unassignedFilter === 'all'"
                            class="font-weight-bold"
                            color="primary"
                            :outlined="unassignedFilter !== 'all'"
                            @click="unassignedFilter = 'all'">
                            Alle ({{ unassignedCountAll }})
                          </v-chip>
                          <v-chip
                            filter
                            :input-value="unassignedFilter === 'missing_time'"
                            class="font-weight-bold"
                            color="warning"
                            :outlined="unassignedFilter !== 'missing_time'"
                            @click="unassignedFilter = 'missing_time'">
                            ⚠️ Fehlende Arbeitszeit / Überlappung ({{ unassignedCountMissingTime }})
                          </v-chip>
                          <v-chip
                            filter
                            :input-value="unassignedFilter === 'missing_shift'"
                            class="font-weight-bold"
                            color="error"
                            :outlined="unassignedFilter !== 'missing_shift'"
                            @click="unassignedFilter = 'missing_shift'">
                            🚫 Kein Mitarbeiter im Dienst ({{ unassignedCountMissingShift }})
                          </v-chip>
                        </div>
                      </div>

                      <div v-if="unassignedTasks.length === 0" class="d-flex flex-column align-center justify-center py-12">
                        <v-icon size="64" :color="unassignedCountAll === 0 ? 'success' : 'grey lighten-1'" class="mb-4">
                          {{ unassignedCountAll === 0 ? "mdi-check-circle-outline" : "mdi-filter-off-outline" }}
                        </v-icon>
                        <div class="text-subtitle-1 font-weight-bold mb-1">
                          {{ unassignedCountAll === 0 ? "Alle Ämtli zugewiesen!" : "Keine passenden Ämtli" }}
                        </div>
                        <div class="text-caption grey--text">
                          {{
                            unassignedCountAll === 0
                              ? "Es gibt im ausgewählten Zeitraum keine nicht zugewiesenen Ämtli."
                              : "Für den ausgewählten Filter gibt es keine Planungsvorschläge."
                          }}
                        </div>
                      </div>

                      <v-simple-table v-else :dark="isDarkMode" class="elevation-0 transparent">
                        <template v-slot:default>
                          <thead>
                            <tr>
                              <th class="text-left font-weight-bold">Datum & Zeit</th>
                              <th class="text-left font-weight-bold">Ämtli</th>
                              <th class="text-left font-weight-bold">Status</th>
                              <th class="text-left font-weight-bold">Zuweisung</th>
                              <th class="text-right font-weight-bold">Aktionen</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr v-for="task in unassignedTasks" :key="task._id">
                              <td style="width: 200px">
                                <div class="font-weight-bold">{{ $moment(task.date).format("DD.MM.YYYY") }}</div>
                                <v-chip x-small outlined class="font-weight-bold mt-1">{{ task.time }}</v-chip>
                              </td>
                              <td>
                                <div class="d-flex align-center">
                                  <v-avatar size="24" :color="task.color || 'primary'" class="white--text mr-2">
                                    <v-icon small color="white">{{ task.icon || "mdi-checkbox-marked" }}</v-icon>
                                  </v-avatar>
                                  <div>
                                    <div class="font-weight-bold">{{ task.title }}</div>
                                    <div class="text-caption grey--text text-truncate" style="max-width: 300px">{{ task.description }}</div>
                                  </div>
                                </div>
                              </td>
                              <td style="width: 120px">
                                <v-chip x-small :color="task.status === 'draft' ? 'warning' : 'info'" label class="font-weight-bold">
                                  {{ task.status === "draft" ? "Entwurf" : "Veröffentlicht" }}
                                </v-chip>
                              </td>
                              <td style="width: 300px">
                                <v-select
                                  v-model="task.assignedEmployee"
                                  :items="getEligibleEmployeesForTask(task)"
                                  dense
                                  outlined
                                  hide-details
                                  class="rounded-lg"
                                  placeholder="Mitarbeiter zuteilen..."
                                  :dark="isDarkMode"
                                  @change="updateTaskAssigneeInDashboard(task)"></v-select>
                                <!-- Meta info for assigned employee -->
                                <div
                                  v-if="task.assignedEmployee"
                                  class="text-caption mt-1 px-1 d-flex align-center justify-space-between"
                                  style="font-size: 0.78rem; opacity: 0.85">
                                  <span class="d-flex align-center">
                                    <v-icon x-small class="mr-1" color="primary">mdi-calendar-check</v-icon>
                                    <strong>{{ getEmployeeStatsText(task.assignedEmployee) }}</strong>
                                  </span>
                                </div>
                                <!-- Reason why it couldn't be assigned -->
                                <div
                                  v-else-if="task.unassignedReason"
                                  class="text-caption mt-1 px-1 text-none d-flex align-center"
                                  style="font-size: 0.78rem; color: #ff9800; font-weight: 500">
                                  <v-icon x-small class="mr-1" color="warning">mdi-alert-circle-outline</v-icon>
                                  <span>Grund: {{ task.unassignedReason }}</span>
                                </div>
                              </td>
                              <td class="text-right">
                                <v-btn
                                  icon
                                  color="warning"
                                  class="mr-1"
                                  title="Alle offenen dieses Ämtlis löschen"
                                  @click="deleteTasksByTemplate(task)">
                                  <v-icon>mdi-trash-can-sweep-outline</v-icon>
                                </v-btn>
                                <v-btn icon color="error" @click="deleteDashboardTask(task._id)">
                                  <v-icon>mdi-trash-can-outline</v-icon>
                                </v-btn>
                              </td>
                            </tr>
                          </tbody>
                        </template>
                      </v-simple-table>
                    </v-card>
                  </v-tab-item>

                  <!-- TAB 3: STATISTICS & WORKLOAD -->
                  <v-tab-item>
                    <v-card flat class="rounded-xl border-1 pa-5" :style="{ background: isDarkMode ? '#1e1e1e' : 'white' }">
                      <div class="mb-6">
                        <h3 class="text-h6 font-weight-bold mb-1">Mitarbeiter-Auslastung (Ämtli-Statistik)</h3>
                        <p class="text-caption grey--text mb-0">
                          Auswertung der zugeteilten Dienste im eingestellten Zeitraum, um eine faire Verteilung zu gewährleisten.
                        </p>
                      </div>

                      <v-row>
                        <v-col cols="12" md="6" v-for="emp in employeeStats" :key="emp.id" v-if="emp.totalCount > 0">
                          <v-card flat outlined class="pa-4 rounded-xl" :style="{ background: isDarkMode ? 'rgba(255,255,255,0.02)' : '#f8fafc' }">
                            <div class="d-flex align-center justify-space-between mb-3">
                              <span class="font-weight-bold">{{ emp.name }}</span>
                              <v-chip small class="font-weight-bold" color="primary" outlined>{{ emp.totalCount }} Ämtli</v-chip>
                            </div>

                            <div style="font-size: 0.8rem" class="grey--text mb-1 d-flex justify-space-between">
                              <span>Erledigt: {{ emp.completedCount }} / {{ emp.publishedCount }} freigegeben</span>
                              <span>{{ emp.draftCount }} Entwürfe</span>
                            </div>

                            <v-progress-linear
                              :value="emp.publishedCount > 0 ? (emp.completedCount / emp.publishedCount) * 100 : 0"
                              height="10"
                              rounded
                              color="success"
                              background-color="rgba(128,128,128,0.15)"></v-progress-linear>
                          </v-card>
                        </v-col>
                      </v-row>
                    </v-card>
                  </v-tab-item>

                  <!-- TAB 4: OVERDUE -->
                  <v-tab-item>
                    <v-card flat class="rounded-xl border-1 pa-5" :style="{ background: isDarkMode ? '#1e1e1e' : 'white' }">
                      <div class="mb-6">
                        <h3 class="text-h6 font-weight-bold mb-1">Überfällige Ämtli</h3>
                        <p class="text-caption grey--text mb-0">
                          Dies sind bereits veröffentlichte Aufgaben aus der Vergangenheit, die noch nicht als erledigt markiert wurden.
                        </p>
                      </div>

                      <div v-if="overdueTasks.length === 0" class="d-flex flex-column align-center justify-center py-12">
                        <v-icon size="64" color="success" class="mb-4">mdi-check-all</v-icon>
                        <div class="text-subtitle-1 font-weight-bold mb-1">Alles sauber!</div>
                        <div class="text-caption grey--text">Es gibt keine überfälligen Ämtli aus der Vergangenheit.</div>
                      </div>

                      <v-simple-table v-else :dark="isDarkMode" class="elevation-0 transparent">
                        <template v-slot:default>
                          <thead>
                            <tr>
                              <th class="text-left font-weight-bold">Datum & Zeit</th>
                              <th class="text-left font-weight-bold">Ämtli</th>
                              <th class="text-left font-weight-bold">Zugewiesen an</th>
                              <th class="text-right font-weight-bold">Aktionen</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr v-for="task in overdueTasks" :key="task._id">
                              <td style="width: 200px">
                                <div class="red--text font-weight-bold">{{ $moment(task.date).format("DD.MM.YYYY") }}</div>
                                <v-chip x-small outlined class="font-weight-bold mt-1">{{ task.time }}</v-chip>
                              </td>
                              <td>
                                <div class="d-flex align-center">
                                  <v-avatar size="24" :color="task.color || 'primary'" class="white--text mr-2">
                                    <v-icon small color="white">{{ task.icon || "mdi-checkbox-marked" }}</v-icon>
                                  </v-avatar>
                                  <div>
                                    <div class="font-weight-bold">{{ task.title }}</div>
                                    <div class="text-caption grey--text text-truncate" style="max-width: 300px">{{ task.description }}</div>
                                  </div>
                                </div>
                              </td>
                              <td>
                                <div class="d-flex align-center" v-if="task.assignedEmployee">
                                  <v-avatar size="20" class="mr-2" v-if="task.assignedEmployee.picture">
                                    <v-img :src="task.assignedEmployee.picture"></v-img>
                                  </v-avatar>
                                  <span class="font-weight-bold">{{
                                    task.assignedEmployee.fullName || task.assignedEmployee.vorname + " " + task.assignedEmployee.nachname
                                  }}</span>
                                </div>
                                <span v-else class="grey--text">Nicht zugewiesen</span>
                              </td>
                              <td class="text-right">
                                <v-btn
                                  depressed
                                  small
                                  color="success"
                                  class="mr-2 text-none font-weight-bold rounded-lg"
                                  @click="markDashboardTaskAsDone(task)">
                                  <v-icon left small>mdi-check</v-icon> Erledigt
                                </v-btn>
                                <v-btn
                                  depressed
                                  small
                                  color="primary"
                                  class="mr-2 text-none font-weight-bold rounded-lg"
                                  @click="rescheduleTaskToToday(task)">
                                  <v-icon left small>mdi-calendar-arrow-right</v-icon> Auf heute legen
                                </v-btn>
                                <v-btn icon color="error" @click="deleteDashboardTask(task._id)">
                                  <v-icon>mdi-trash-can-outline</v-icon>
                                </v-btn>
                              </td>
                            </tr>
                          </tbody>
                        </template>
                      </v-simple-table>
                    </v-card>
                  </v-tab-item>
                </v-tabs-items>
              </div>
            </v-card-text>
          </v-card>
        </v-dialog>

        <v-dialog v-model="createTemplateDialog" max-width="580" persistent>
          <v-card dark class="rounded-xl" style="background: rgba(22, 22, 22, 0.98) !important; border: 1px solid rgba(255, 255, 255, 0.06)">
            <v-card-title
              class="white--text pa-6 pb-4"
              style="
                background: linear-gradient(135deg, rgba(76, 175, 80, 0.1), rgba(0, 0, 0, 0));
                border-bottom: 1px solid rgba(255, 255, 255, 0.05);
              ">
              <div class="d-flex align-center w-100">
                <div
                  class="d-flex align-center justify-center mr-4"
                  style="
                    width: 44px;
                    height: 44px;
                    background: rgba(76, 175, 80, 0.15);
                    border-radius: 12px;
                    border: 1px solid rgba(76, 175, 80, 0.2);
                  ">
                  <v-icon color="primary">mdi-plus</v-icon>
                </div>
                <div>
                  <div style="font-size: 1.25rem; font-weight: 700; letter-spacing: 0.5px">
                    {{ hasAppointmentManagementRight ? (editingTemplateId ? "Vorlage bearbeiten" : "Neue Vorlage") : "Ämtli konfigurieren" }}
                  </div>
                  <div style="font-size: 0.75rem; color: rgba(255, 255, 255, 0.5); font-weight: 400; letter-spacing: 0.3px; margin-top: 2px">
                    {{
                      hasAppointmentManagementRight
                        ? editingTemplateId
                          ? "Bearbeite die bestehende Vorlage"
                          : "Erstelle Termine oder wiederkehrende Ämtli"
                        : "Passe Dauer, Ziel und Zeiten für dein Studio an"
                    }}
                  </div>
                </div>
              </div>
            </v-card-title>
            <v-card-text class="px-5 pt-2">
              <v-row dense>
                <v-col cols="12" class="mb-2">
                  <v-text-field
                    v-model="newTemplate.title"
                    label="Titel"
                    outlined
                    dark
                    autofocus
                    hide-details
                    :disabled="!hasAppointmentManagementRight"></v-text-field>
                </v-col>
                <v-col cols="6">
                  <v-select
                    v-model="newTemplate.category"
                    :items="[
                      { text: 'Ämtli', value: 'ämtli' },
                      { text: 'Aufgabe', value: 'aufgabe' },
                      { text: 'Termin', value: 'termin' },
                      { text: 'Info', value: 'info' },
                    ]"
                    label="Kategorie"
                    outlined
                    dark
                    hide-details
                    :disabled="!hasAppointmentManagementRight"></v-select>
                </v-col>
                <v-col cols="6">
                  <v-text-field v-model="newTemplate.duration" label="Dauer (Min)" type="number" outlined dark hide-details></v-text-field>
                </v-col>
                <v-col cols="12" v-if="newTemplate.category === 'ämtli'" class="mt-2">
                  <v-select
                    v-model="newTemplate.frequency"
                    :items="[
                      { text: 'Täglich / Manuell', value: 'daily' },
                      { text: 'Wöchentlich', value: 'weekly' },
                      { text: 'Monatlich', value: 'monthly' },
                    ]"
                    label="Wiederholung / Frequenz"
                    outlined
                    dark
                    hide-details
                    :disabled="!hasAppointmentManagementRight"></v-select>
                </v-col>
                <v-col
                  cols="12"
                  v-if="
                    newTemplate.category === 'ämtli' &&
                    newTemplate.frequency !== 'daily' &&
                    (!hasSelectedTaskDays(newTemplate) || newTemplate.frequencyConfig.daySelectionMode !== 'all')
                  "
                  class="mt-2">
                  <v-text-field
                    v-model.number="newTemplate.targetCount"
                    :label="newTemplate.frequency === 'weekly' ? 'Wie oft pro Woche?' : 'Wie oft pro Monat?'"
                    type="number"
                    min="1"
                    outlined
                    dark
                    hide-details></v-text-field>
                </v-col>

                <!-- Wochentage Auswahl für wöchentliche Ämtli -->
                <v-col cols="12" v-if="newTemplate.category === 'ämtli' && newTemplate.frequency === 'weekly'" class="mt-2">
                  <div
                    class="text-caption mb-2"
                    style="color: rgba(255, 255, 255, 0.3); font-size: 0.65rem; text-transform: uppercase; letter-spacing: 0.5px">
                    Erlaubte Wochentage (optional)
                  </div>
                  <div class="d-flex flex-wrap gap-1">
                    <v-btn
                      v-for="day in [
                        { text: 'Mo', value: 1 },
                        { text: 'Di', value: 2 },
                        { text: 'Mi', value: 3 },
                        { text: 'Do', value: 4 },
                        { text: 'Fr', value: 5 },
                        { text: 'Sa', value: 6 },
                        { text: 'So', value: 7 },
                      ]"
                      :key="day.value"
                      x-small
                      outlined
                      :color="
                        newTemplate.frequencyConfig &&
                        newTemplate.frequencyConfig.daysOfWeek &&
                        newTemplate.frequencyConfig.daysOfWeek.includes(day.value)
                          ? 'primary'
                          : 'grey'
                      "
                      :input-value="
                        newTemplate.frequencyConfig &&
                        newTemplate.frequencyConfig.daysOfWeek &&
                        newTemplate.frequencyConfig.daysOfWeek.includes(day.value)
                      "
                      @click="toggleWeeklyDay(day.value)"
                      class="mr-1 mb-1"
                      style="min-width: 38px">
                      {{ day.text }}
                    </v-btn>
                  </div>
                  <div class="caption mt-2" style="color: rgba(255, 255, 255, 0.45); line-height: 1.4">
                    <template v-if="newTemplate.frequencyConfig.daySelectionMode === 'all'">
                      Das Ämtli wird an jedem ausgewählten Wochentag eingeplant.
                    </template>
                    <template v-else
                      >Leer lassen = beliebiger passender Arbeitstag. Mit Auswahl wird die gewünschte Anzahl nur auf diese Tage verteilt.</template
                    >
                  </div>
                </v-col>

                <!-- Monatstage Auswahl für monatliche Ämtli -->
                <v-col cols="12" v-if="newTemplate.category === 'ämtli' && newTemplate.frequency === 'monthly'" class="mt-2">
                  <v-select
                    v-model="newTemplate.frequencyConfig.daysOfMonth"
                    :items="Array.from({ length: 31 }, (_, i) => i + 1)"
                    label="Erlaubte Monatstage (optional, 1-31)"
                    multiple
                    chips
                    deletable-chips
                    outlined
                    dark
                    hide-details></v-select>
                  <div class="caption mt-2" style="color: rgba(255, 255, 255, 0.45); line-height: 1.4">
                    <template v-if="newTemplate.frequencyConfig.daySelectionMode === 'all'">
                      Das Ämtli wird an jedem ausgewählten Kalendertag eingeplant.
                    </template>
                    <template v-else>
                      Leer lassen = beliebiger passender Arbeitstag. Mit Auswahl wird die gewünschte Anzahl nur auf diese Kalendertage verteilt.
                    </template>
                  </div>
                </v-col>

                <v-col
                  cols="12"
                  v-if="newTemplate.category === 'ämtli' && newTemplate.frequency !== 'daily' && hasSelectedTaskDays(newTemplate)"
                  class="mt-2">
                  <v-select
                    v-model="newTemplate.frequencyConfig.daySelectionMode"
                    :items="[
                      { text: 'Gewünschte Anzahl flexibel auf diese Tage verteilen', value: 'one' },
                      { text: 'An jedem ausgewählten Tag einplanen', value: 'all' },
                    ]"
                    label="Wie sollen die ausgewählten Tage verwendet werden?"
                    outlined
                    dark
                    hide-details></v-select>
                </v-col>

                <!-- Mitarbeiter-Pool Auswahl -->
                <v-col cols="12" v-if="newTemplate.category === 'ämtli'" class="mt-2">
                  <v-select
                    v-model="newTemplate.assignedEmployees"
                    :items="employeeOptions"
                    label="Mitarbeiter-Pool für dieses Ämtli"
                    multiple
                    chips
                    deletable-chips
                    outlined
                    dark
                    hide-details></v-select>
                </v-col>

                <!-- Verknüpftes Ämtli Auswahl -->
                <v-col cols="12" v-if="newTemplate.category === 'ämtli'" class="mt-2">
                  <v-select
                    v-model="newTemplate.linkedTemplateId"
                    :items="
                      templates
                        .filter((t) => (t.category === 'ämtli' || (!t.category && t.type === 'task')) && (t._id || t.id) !== editingTemplateId)
                        .map((t) => ({ text: t.title, value: t._id || t.id }))
                    "
                    label="Verknüpftes Ämtli (sollte vom gleichen Mitarbeiter ausgeführt werden)"
                    clearable
                    outlined
                    dark
                    hide-details></v-select>
                </v-col>
                <v-col cols="12" class="mt-4">
                  <div
                    class="text-caption mb-2"
                    style="color: rgba(255, 255, 255, 0.3); font-size: 0.6rem; text-transform: uppercase; letter-spacing: 0.5px">
                    Farbe
                  </div>
                  <div class="d-flex flex-wrap" style="gap: 6px">
                    <div
                      v-for="c in colorOptions"
                      :key="c"
                      class="wc-color-dot"
                      :class="{ active: newTemplate.color === c }"
                      :style="{ background: c, width: '24px', height: '24px', cursor: hasAppointmentManagementRight ? 'pointer' : 'not-allowed' }"
                      @click="hasAppointmentManagementRight ? (newTemplate.color = c) : null"></div>
                  </div>
                </v-col>
                <v-col cols="12" class="mt-4">
                  <div
                    class="text-caption mb-2"
                    style="color: rgba(255, 255, 255, 0.3); font-size: 0.65rem; text-transform: uppercase; letter-spacing: 0.5px">
                    Icon
                  </div>
                  <div class="d-flex flex-wrap custom-scrollbar" style="max-height: 160px; overflow-y: auto; gap: 6px">
                    <div
                      v-for="ic in iconOptions"
                      :key="ic.icon"
                      class="wc-icon-pick"
                      :class="{ active: newTemplate.icon === ic.icon }"
                      :style="{ cursor: hasAppointmentManagementRight ? 'pointer' : 'not-allowed' }"
                      @click="hasAppointmentManagementRight ? (newTemplate.icon = ic.icon) : null">
                      <v-icon :color="newTemplate.icon === ic.icon ? 'white' : 'rgba(255,255,255,0.3)'">{{ ic.icon }}</v-icon>
                    </div>
                  </div>
                </v-col>
                <v-col cols="12" v-if="newTemplate.category === 'termin' || newTemplate.category === 'info'">
                  <v-switch
                    v-model="newTemplate.requiresCustomer"
                    label="Erfordert Kunde?"
                    dense
                    inset
                    dark
                    hide-details
                    class="mt-3"
                    color="blue accent-2"
                    :disabled="!hasAppointmentManagementRight"></v-switch>
                </v-col>
                <v-col v-if="false" cols="12">
                  <v-select
                    v-model="newTemplate.tacServiceType"
                    :items="[
                      { text: 'Keine TAC-Kartensuche', value: null },
                      { text: 'Personal Training – Package buchen', value: 'personal_training' },
                      { text: 'Massage – Package buchen', value: 'massage' },
                    ]"
                    label="TAC-Package im Termin verwenden"
                    outlined
                    dark
                    hide-details
                    @change="newTemplate.tacServiceType ? (newTemplate.requiresCustomer = true) : null"></v-select>
                </v-col>
                <template v-if="false">
                  <v-col cols="12">
                    <v-alert dense text type="info" class="mb-0 caption">
                      Verfügbare Kunden-Packages und TAC-Zeitfenster werden geprüft. Beim Speichern wird eine Package-Leistung gebucht.
                    </v-alert>
                  </v-col>
                  <v-col cols="12">
                    <v-text-field
                      v-model="newTemplate.tacActivityTemplateId"
                      label="TAC Activity-Template-ID (optional)"
                      hint="Leer lassen, um die ID aus config.js für diese Leistungsart zu verwenden."
                      persistent-hint
                      outlined
                      dark></v-text-field>
                  </v-col>
                </template>
                <v-col cols="12">
                  <v-switch
                    v-model="newTemplate.requiresNote"
                    label="Notiz ist Pflichtfeld?"
                    dense
                    inset
                    dark
                    hide-details
                    class="mt-1"
                    color="orange accent-2"
                    :disabled="!hasAppointmentManagementRight"></v-switch>
                  <div v-if="newTemplate.requiresNote" class="caption mt-1" style="color: rgba(255, 255, 255, 0.3); font-size: 0.6rem">
                    Beim Erstellen muss eine Notiz eingegeben werden
                  </div>
                </v-col>
                <v-col cols="12" v-if="newTemplate.category === 'ämtli' || newTemplate.category === 'aufgabe'" class="mt-3">
                  <div
                    class="text-caption mb-2"
                    style="color: rgba(255, 255, 255, 0.45); font-size: 0.65rem; text-transform: uppercase; letter-spacing: 0.5px">
                    Zeitregel
                  </div>
                  <v-select
                    v-model="newTemplate.timePlanningMode"
                    :items="taskTimeModeOptions"
                    item-text="text"
                    item-value="value"
                    dark
                    dense
                    outlined
                    hide-details
                    class="mb-2"
                    @change="setTaskTimeMode(newTemplate, $event)"></v-select>
                  <div class="caption mb-4" style="color: rgba(255, 255, 255, 0.45); line-height: 1.45">
                    {{ getTaskTimeModeHelp(newTemplate.timePlanningMode) }}
                  </div>

                  <div
                    v-if="newTemplate.timePlanningMode === 'preferred_window' || newTemplate.timePlanningMode === 'required_window'"
                    class="d-flex mb-4"
                    style="gap: 10px">
                    <v-text-field
                      v-model="newTemplate.timePreference.windowStart"
                      type="time"
                      label="Von"
                      dark
                      dense
                      outlined
                      hide-details></v-text-field>
                    <v-text-field
                      v-model="newTemplate.timePreference.windowEnd"
                      type="time"
                      label="Bis"
                      dark
                      dense
                      outlined
                      hide-details></v-text-field>
                  </div>

                  <div v-if="newTemplate.timePlanningMode === 'fixed_times'" class="mb-4">
                    <div
                      class="text-caption mb-2"
                      style="color: rgba(255, 255, 255, 0.3); font-size: 0.65rem; text-transform: uppercase; letter-spacing: 0.5px">
                      Uhrzeiten festlegen
                    </div>
                    <div class="d-flex flex-wrap gap-2 mb-3">
                      <div v-for="(time, idx) in newTemplate.defaultTimes" :key="'t' + idx" class="d-flex align-center">
                        <v-text-field
                          v-model="newTemplate.defaultTimes[idx]"
                          type="time"
                          dark
                          hide-details
                          outlined
                          class="wc-time-input"
                          style="max-width: 100px; font-size: 0.9rem"></v-text-field>
                        <v-btn icon @click="newTemplate.defaultTimes.splice(idx, 1)" color="red lighten-1" class="ml-2"
                          ><v-icon small>mdi-close</v-icon></v-btn
                        >
                      </div>
                      <v-btn
                        outlined
                        color="primary"
                        style="
                          height: 56px;
                          border-radius: 8px;
                          border-style: dashed;
                          border-width: 1px;
                          text-transform: none;
                          letter-spacing: 0;
                          font-size: 0.85rem;
                        "
                        @click="newTemplate.defaultTimes.push('12:00')">
                        <v-icon left small>mdi-clock-plus-outline</v-icon> Zeit hinzufügen
                      </v-btn>
                    </div>
                  </div>
                  <div
                    class="text-caption mb-2 mt-4"
                    style="color: rgba(255, 255, 255, 0.3); font-size: 0.65rem; text-transform: uppercase; letter-spacing: 0.5px">
                    Checkliste für die Aufgabe
                  </div>
                  <div v-for="(item, idx) in newTemplate.checklist" :key="idx" class="d-flex align-center mb-1">
                    <v-text-field
                      v-model="item.text"
                      placeholder="Aufgabe..."
                      dense
                      dark
                      hide-details
                      outlined
                      class="mr-2"
                      style="font-size: 0.8rem; height: 36px"></v-text-field>
                    <v-btn icon x-small @click="newTemplate.checklist.splice(idx, 1)" color="red lighten-1"><v-icon x-small>mdi-close</v-icon></v-btn>
                  </div>
                  <v-btn
                    outlined
                    color="primary"
                    class="mt-2"
                    style="
                      height: 36px;
                      border-radius: 8px;
                      border-style: dashed;
                      border-width: 1px;
                      text-transform: none;
                      letter-spacing: 0;
                      font-size: 0.8rem;
                      width: 100%;
                    "
                    @click="newTemplate.checklist.push({ text: '', isDone: false })">
                    <v-icon left small>mdi-plus</v-icon> Punkt hinzufügen
                  </v-btn>
                  <v-textarea
                    v-model="newTemplate.instructions"
                    label="Anleitung / Briefing"
                    outlined
                    dense
                    dark
                    hide-details
                    rows="3"
                    class="mt-4"
                    style="font-size: 0.8rem"
                    :disabled="!hasAppointmentManagementRight"></v-textarea>
                </v-col>
              </v-row>
            </v-card-text>
            <v-card-actions class="px-5 pb-4">
              <v-spacer></v-spacer>
              <v-btn text small @click="createTemplateDialog = false" style="color: rgba(255, 255, 255, 0.4); text-transform: none">Abbrechen</v-btn>
              <v-btn
                small
                depressed
                @click="saveTemplate"
                :disabled="!newTemplate.title"
                style="
                  background: linear-gradient(135deg, #4caf50, #2e7d32) !important;
                  color: white !important;
                  border-radius: 8px;
                  text-transform: none;
                  font-weight: 600;
                ">
                <v-icon left x-small>mdi-check</v-icon> {{ editingTemplateId ? "Speichern" : "Erstellen" }}
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

        <v-dialog v-model="noteDialog" max-width="440">
          <v-card dark class="rounded-xl" style="background: rgba(22, 22, 22, 0.98) !important; border: 1px solid rgba(255, 255, 255, 0.06)">
            <v-card-title style="font-size: 1rem">
              <v-icon left small color="blue lighten-2">mdi-message-text</v-icon>
              Notizen · {{ selectedCol?.info.firstName }}
            </v-card-title>
            <v-card-text class="pt-2">
              <div v-if="selectedCol?.notes.length > 0" class="mb-3">
                <div
                  v-for="note in selectedCol.notes"
                  :key="note._id"
                  class="d-flex align-start pa-3 mb-2 rounded-lg"
                  style="background: rgba(255, 255, 255, 0.03); border: 1px solid rgba(255, 255, 255, 0.04)">
                  <v-icon x-small color="blue lighten-2" class="mr-2 mt-1">mdi-message</v-icon>
                  <div class="flex-grow-1">
                    <div class="white--text text-body-2" style="white-space: pre-wrap; font-size: 0.85rem">{{ note.content }}</div>
                    <div class="text-caption mt-1" style="color: rgba(255, 255, 255, 0.3); font-size: 0.65rem">
                      {{ note.author ? note.author.vorname : "Team" }} · {{ $moment(note.createdAt).format("HH:mm") }}
                    </div>
                  </div>
                  <v-btn icon x-small @click="deleteNote(note._id)" style="color: rgba(255, 255, 255, 0.2)"><v-icon x-small>mdi-close</v-icon></v-btn>
                </div>
              </div>
              <div v-else class="text-center py-4" style="color: rgba(255, 255, 255, 0.2); font-size: 0.8rem">Keine Notizen</div>
              <v-textarea
                v-model="newNoteContent"
                outlined
                dense
                label="Nachricht..."
                rows="2"
                hide-details
                dark
                class="mt-2"
                style="font-size: 0.85rem"></v-textarea>
            </v-card-text>
            <v-card-actions class="px-5 pb-4">
              <v-spacer></v-spacer>
              <v-btn text small @click="noteDialog = false" style="color: rgba(255, 255, 255, 0.4); text-transform: none">Schliessen</v-btn>
              <v-btn
                small
                depressed
                @click="createNote"
                :disabled="!newNoteContent"
                style="
                  background: linear-gradient(135deg, var(--v-primary-base), color-mix(in srgb, var(--v-primary-base) 80%, black)) !important;
                  color: white !important;
                  border-radius: 8px;
                  text-transform: none;
                  font-weight: 600;
                ">
                <v-icon left x-small>mdi-send</v-icon> Senden
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </template>

      <!-- Employee Switcher Dialog (Mobile) -->
      <v-dialog v-model="employeeSwitcherDialog" max-width="340" :fullscreen="$vuetify.breakpoint.xsOnly">
        <v-card dark class="rounded-xl" style="background: rgba(18, 18, 18, 0.98) !important; border: 1px solid rgba(255, 255, 255, 0.07)">
          <v-card-title class="d-flex align-center pa-4 pb-2">
            <span style="font-size: 0.95rem; font-weight: 700; color: white">Mitarbeiter wechseln</span>
            <v-spacer></v-spacer>
            <v-btn icon small @click="employeeSwitcherDialog = false" style="color: rgba(255, 255, 255, 0.4)">
              <v-icon small>mdi-close</v-icon>
            </v-btn>
          </v-card-title>
          <v-card-text class="pa-3">
            <div
              v-for="col in viewColumns"
              :key="col.id"
              class="d-flex align-center pa-3 mb-2 rounded-lg cursor-pointer"
              :style="
                col.id === (mobileViewColumns[0] && mobileViewColumns[0].id)
                  ? 'background: rgba(255,255,255,0.08); border: 1px solid rgba(255,255,255,0.1);'
                  : 'background: rgba(255,255,255,0.02); border: 1px solid rgba(255,255,255,0.04);'
              "
              @click="switchEmployee(col)">
              <v-badge
                bottom
                overlap
                :color="col.liveStatus ? col.liveStatus.color : 'transparent'"
                dot
                offset-x="8"
                offset-y="8"
                :value="!!col.liveStatus">
                <v-avatar size="48" class="mr-3" :style="col.isHelper ? 'background: rgba(255,255,255,0.1)' : ''">
                  <img v-if="col.picture" :src="'/img/profilbilder/' + col.picture" style="object-fit: cover" />
                  <v-icon v-else-if="col.isHelper" color="white">{{ col.info.icon }}</v-icon>
                  <img v-else src="/img/default-avatar.png" style="object-fit: cover" />
                </v-avatar>
              </v-badge>
              <div style="flex: 1; overflow: hidden">
                <div class="white--text font-weight-bold" style="font-size: 0.9rem">{{ col.firstName }}</div>
                <div class="d-flex align-center mt-1" v-if="col.shiftRange">
                  <v-icon x-small style="color: rgba(255, 255, 255, 0.3); font-size: 10px" class="mr-1">mdi-clock-outline</v-icon>
                  <span style="color: rgba(255, 255, 255, 0.4); font-size: 0.75rem">{{ col.shiftRange }}</span>
                </div>
              </div>
              <v-icon v-if="col.id === (mobileViewColumns[0] && mobileViewColumns[0].id)" small color="primary">mdi-check-circle</v-icon>
            </div>
          </v-card-text>
        </v-card>
      </v-dialog>

      <!-- Sick Details Dialog (Partial Sickness) -->
      <v-dialog v-model="sickDetailsDialog" max-width="450" :dark="isDarkMode">
        <v-card dark class="rounded-xl pa-4" style="background: rgba(18, 18, 18, 0.98) !important; border: 1px solid rgba(255, 255, 255, 0.07)">
          <v-card-title class="d-flex align-center pa-2 pb-4">
            <v-icon color="red lighten-1" class="mr-3">mdi-emoticon-sick-outline</v-icon>
            <span style="font-size: 1.1rem; font-weight: 700; color: white"
              >Krankheit für {{ sickDialogCol ? sickDialogCol.firstName : "" }} erfassen</span
            >
          </v-card-title>

          <v-card-text class="pa-2">
            <div class="mb-4 text-body-2 grey--text">
              Erfasse die Details für die Schicht am <strong>{{ sickDialogShift ? $moment(sickDialogShift.date).format("DD.MM.YYYY") : "" }}</strong
              >.
              <div v-if="sickDialogShiftTimes" class="mt-1 white--text">
                Schichtzeit: <strong>{{ sickDialogShiftTimes }}</strong>
              </div>
            </div>

            <!-- Toggle complete/partial -->
            <v-btn-toggle v-model="sickDialogPartialType" mandatory dense class="glass-toggle rounded-lg w-100 mb-4" color="primary">
              <v-btn value="complete" class="flex-grow-1">100% Krank</v-btn>
              <v-btn value="partial" class="flex-grow-1">Teilweise Krank</v-btn>
            </v-btn-toggle>

            <v-expand-transition>
              <div v-if="sickDialogPartialType === 'partial'">
                <!-- Sickness Percentage -->
                <v-select
                  label="Einschränkung in %"
                  v-model="sickDialogPercentage"
                  :items="getSickDialogPercentageOptions()"
                  suffix="%"
                  outlined
                  dense
                  class="rounded-lg mb-2"
                  @change="onSickDialogPercentageChange"></v-select>

                <v-select
                  label="Dienstplan-Auswirkung"
                  v-model="sickDialogCalculationMode"
                  :items="[
                    { value: 'time_window', text: 'Zeitfenster markieren (Schicht im Kalender aufteilen)' },
                    { value: 'fixed', text: 'Schicht belassen (nur Krankstunden erfassen)' },
                  ]"
                  outlined
                  dense
                  hide-details
                  class="rounded-lg mb-2"></v-select>

                <div
                  class="mb-3 pa-3 rounded-lg text-caption"
                  style="line-height: 1.4; background: rgba(33, 150, 243, 0.08); border: 1px solid rgba(33, 150, 243, 0.2)">
                  <template v-if="sickDialogCalculationMode === 'time_window'">
                    <v-icon x-small color="blue lighten-2" class="mr-1">mdi-calendar-clock</v-icon>
                    Das gewählte Zeitfenster wird im Schichtkalender exakt als krank markiert. Es wird nicht automatisch am Schichtanfang oder
                    Schichtende gekürzt.
                  </template>
                  <template v-else>
                    <v-icon x-small color="blue lighten-2" class="mr-1">mdi-calendar-check</v-icon>
                    Die Krankstunden werden erfasst; die geplante Schicht bleibt im Schichtkalender unverändert.
                  </template>
                </div>

                <v-row dense class="mb-2">
                  <!-- Start 1 -->
                  <v-col cols="6">
                    <v-menu
                      v-model="range1StartMenu"
                      :close-on-content-click="false"
                      :nudge-right="40"
                      transition="scale-transition"
                      offset-y
                      max-width="290px"
                      min-width="290px">
                      <template v-slot:activator="{ on, attrs }">
                        <v-text-field
                          label="Start 1"
                          v-model="sickDialogRange1Start"
                          readonly
                          outlined
                          dense
                          hide-details
                          prepend-inner-icon="mdi-clock-outline"
                          v-bind="attrs"
                          v-on="on"></v-text-field>
                      </template>
                      <v-time-picker
                        v-if="range1StartMenu"
                        v-model="sickDialogRange1Start"
                        full-width
                        format="24hr"
                        @click:minute="
                          range1StartMenu = false
                          recalculateSickHoursFromRanges()
                        "></v-time-picker>
                    </v-menu>
                  </v-col>

                  <!-- Ende 1 -->
                  <v-col cols="6">
                    <v-menu
                      v-model="range1EndMenu"
                      :close-on-content-click="false"
                      :nudge-right="40"
                      transition="scale-transition"
                      offset-y
                      max-width="290px"
                      min-width="290px">
                      <template v-slot:activator="{ on, attrs }">
                        <v-text-field
                          label="Ende 1"
                          v-model="sickDialogRange1End"
                          readonly
                          outlined
                          dense
                          hide-details
                          prepend-inner-icon="mdi-clock-outline"
                          v-bind="attrs"
                          v-on="on"></v-text-field>
                      </template>
                      <v-time-picker
                        v-if="range1EndMenu"
                        v-model="sickDialogRange1End"
                        full-width
                        format="24hr"
                        @click:minute="
                          range1EndMenu = false
                          recalculateSickHoursFromRanges()
                        "></v-time-picker>
                    </v-menu>
                  </v-col>
                </v-row>

                <!-- Toggle for Zeitfenster 2 -->
                <div class="my-2">
                  <v-btn small text color="primary" class="px-0 font-weight-bold" @click="toggleRange2">
                    <v-icon left small>{{ sickDialogShowRange2 ? "mdi-minus-circle-outline" : "mdi-plus-circle-outline" }}</v-icon>
                    {{ sickDialogShowRange2 ? "Zweites Zeitfenster entfernen" : "Zweites Zeitfenster hinzufügen" }}
                  </v-btn>
                </div>

                <!-- Zeitfenster 2 inputs -->
                <v-expand-transition>
                  <div v-if="sickDialogShowRange2" class="mb-3">
                    <div class="text-caption grey--text mb-1">Zeitfenster 2:</div>
                    <v-row dense>
                      <!-- Start 2 -->
                      <v-col cols="6">
                        <v-menu
                          v-model="range2StartMenu"
                          :close-on-content-click="false"
                          :nudge-right="40"
                          transition="scale-transition"
                          offset-y
                          max-width="290px"
                          min-width="290px">
                          <template v-slot:activator="{ on, attrs }">
                            <v-text-field
                              label="Start 2"
                              v-model="sickDialogRange2Start"
                              readonly
                              outlined
                              dense
                              hide-details
                              prepend-inner-icon="mdi-clock-outline"
                              v-bind="attrs"
                              v-on="on"></v-text-field>
                          </template>
                          <v-time-picker
                            v-if="range2StartMenu"
                            v-model="sickDialogRange2Start"
                            full-width
                            format="24hr"
                            @click:minute="
                              range2StartMenu = false
                              recalculateSickHoursFromRanges()
                            "></v-time-picker>
                        </v-menu>
                      </v-col>

                      <!-- Ende 2 -->
                      <v-col cols="6">
                        <v-menu
                          v-model="range2EndMenu"
                          :close-on-content-click="false"
                          :nudge-right="40"
                          transition="scale-transition"
                          offset-y
                          max-width="290px"
                          min-width="290px">
                          <template v-slot:activator="{ on, attrs }">
                            <v-text-field
                              label="Ende 2"
                              v-model="sickDialogRange2End"
                              readonly
                              outlined
                              dense
                              hide-details
                              prepend-inner-icon="mdi-clock-outline"
                              v-bind="attrs"
                              v-on="on"></v-text-field>
                          </template>
                          <v-time-picker
                            v-if="range2EndMenu"
                            v-model="sickDialogRange2End"
                            full-width
                            format="24hr"
                            @click:minute="
                              range2EndMenu = false
                              recalculateSickHoursFromRanges()
                            "></v-time-picker>
                        </v-menu>
                      </v-col>
                    </v-row>
                  </div>
                </v-expand-transition>

                <!-- Calculated Sickness & Work Hours Breakdown -->
                <div
                  class="mt-3 pa-3 rounded-lg d-flex align-center justify-space-between"
                  style="background: rgba(255, 255, 255, 0.03); border: 1px solid rgba(255, 255, 255, 0.05)">
                  <div>
                    <span class="grey--text text-caption">Krank-Stunden:</span>
                    <div class="white--text font-weight-bold">{{ sickDialogHours }} h</div>
                  </div>
                  <div>
                    <span class="grey--text text-caption">Arbeits-Stunden:</span>
                    <div class="white--text font-weight-bold">
                      {{ Math.max(0, parseFloat((sickDialogShiftTotalHours - sickDialogHours).toFixed(2))) }} h
                    </div>
                  </div>
                </div>
              </div>
            </v-expand-transition>
          </v-card-text>

          <v-card-actions class="pa-2 pt-4">
            <v-spacer></v-spacer>
            <v-btn text @click="sickDetailsDialog = false" class="grey--text">Abbrechen</v-btn>
            <v-btn color="error" class="px-6 rounded-lg font-weight-bold" @click="saveSickDetails" :loading="sickDialogSaving"> Speichern </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Trainerpoint Live wird nur bei geöffnetem Dialog gemountet. -->
      <v-dialog v-model="customerSearchDialog" fullscreen persistent transition="dialog-bottom-transition">
        <v-card style="height: 100%; overflow-y: auto; background: #f3f8f5">
          <work-calendar-checkin-pool
            v-if="customerSearchDialog"
            closable
            :appointment-templates="allAppointmentTemplates"
            @close="customerSearchDialog = false"
            @entry-selected="onCheckinPoolSelect"
            @create-appointment="openTrainerpointAppointment"
            @open-appointment="openTrainerpointExistingAppointment"
            @create-interaction="openTrainerpointInteraction" />
        </v-card>
      </v-dialog>
      <customer-interaction
        v-if="trainerpointInteractionMounted"
        ref="trainerpointInteraction"
        @interactionCreated="onTrainerpointInteractionCreated" />
      <!-- Edit Shift Dialog -->
      <v-dialog v-model="editShiftDialog" max-width="520">
        <v-card dark class="rounded-xl" style="background: rgba(22, 22, 22, 0.98) !important; border: 1px solid rgba(255, 255, 255, 0.06)">
          <div class="pa-5">
            <div class="d-flex align-center mb-4" style="gap: 12px">
              <div
                style="
                  width: 40px;
                  height: 40px;
                  border-radius: 10px;
                  background: rgba(255, 152, 0, 0.15);
                  display: flex;
                  align-items: center;
                  justify-content: center;
                ">
                <v-icon color="orange">mdi-clock-edit-outline</v-icon>
              </div>
              <div>
                <div style="font-size: 1rem; font-weight: 700; color: white">Schichtzeiten anpassen</div>
                <div style="font-size: 0.7rem; color: rgba(255, 255, 255, 0.4)">{{ selectedCol ? selectedCol.firstName : "" }}</div>
              </div>
            </div>
            <div class="mb-4">
              <div class="d-flex justify-space-between align-center mb-2">
                <span class="text-subtitle-2 white--text">Zeiten & Pausen</span>
                <v-btn x-small color="blue" text @click="addEditShiftTimeBlock"> <v-icon left x-small>mdi-plus</v-icon> Zeit </v-btn>
              </div>

              <div v-for="(time, i) in editShiftData.times" :key="i" class="d-flex align-center mb-2">
                <v-select
                  v-model="time.type"
                  :items="['Arbeitszeit', 'Pause']"
                  dense
                  outlined
                  dark
                  hide-details
                  style="max-width: 120px"
                  class="mr-2"></v-select>
                <v-text-field v-model="time.start" type="time" dense outlined dark hide-details class="mr-1 centered-input"></v-text-field>
                <span class="grey--text mx-1">-</span>
                <v-text-field v-model="time.end" type="time" dense outlined dark hide-details class="mr-2 centered-input"></v-text-field>
                <v-btn
                  icon
                  color="red lighten-2"
                  small
                  @click="removeEditShiftTimeBlock(i)"
                  :disabled="editShiftData.times && editShiftData.times.length === 1">
                  <v-icon>mdi-close</v-icon>
                </v-btn>
              </div>
            </div>

            <!-- Umschalter für Centeröffnung / Centerschliessung -->
            <div class="d-flex mb-3" style="gap: 16px">
              <v-switch v-model="editShiftData.isOpeningShift" label="Centeröffnung" dense dark color="orange" hide-details class="mt-0"></v-switch>
              <v-switch
                v-model="editShiftData.isClosingShift"
                label="Centerschliessung"
                dense
                dark
                color="orange"
                hide-details
                class="mt-0"></v-switch>
            </div>

            <div style="font-size: 0.75rem; color: rgba(255, 255, 255, 0.5)" class="mb-2">Begründung für die Änderung (Pflicht)</div>
            <v-select
              :items="shiftReasons"
              v-model="editShiftData.selectedReason"
              label="Grund auswählen"
              outlined
              dense
              dark
              hide-details
              class="mb-3"
              style="font-size: 0.85rem"></v-select>

            <v-textarea
              v-if="editShiftData.selectedReason === 'Sonstiges (bitte angeben)'"
              v-model="editShiftData.customReason"
              placeholder="Bitte gib eine Begründung an..."
              outlined
              dense
              rows="3"
              dark
              hide-details
              style="font-size: 0.85rem"
              class="mt-2"></v-textarea>
            <div class="d-flex justify-end mt-4" style="gap: 8px">
              <v-btn text small @click="editShiftDialog = false" style="color: rgba(255, 255, 255, 0.4); text-transform: none">Abbrechen</v-btn>
              <v-btn small depressed color="primary" style="text-transform: none; font-weight: 600; border-radius: 8px" @click="saveEditShift"
                >Speichern</v-btn
              >
            </div>
          </div>
        </v-card>
      </v-dialog>

      <!-- Transfer Shift Dialog -->
      <v-dialog v-model="transferShiftDialog" max-width="440">
        <v-card dark class="rounded-xl" style="background: rgba(22, 22, 22, 0.98) !important; border: 1px solid rgba(255, 255, 255, 0.06)">
          <div class="pa-5">
            <div class="d-flex align-center mb-4" style="gap: 12px">
              <div
                style="
                  width: 40px;
                  height: 40px;
                  border-radius: 10px;
                  background: rgba(156, 39, 176, 0.15);
                  display: flex;
                  align-items: center;
                  justify-content: center;
                ">
                <v-icon color="purple lighten-1">{{ transferOrSwapMode === "swap" ? "mdi-swap-horizontal" : "mdi-account-switch" }}</v-icon>
              </div>
              <div>
                <div style="font-size: 1rem; font-weight: 700; color: white">
                  {{ transferOrSwapMode === "swap" ? "Schicht tauschen" : "Schicht übertragen" }}
                </div>
                <div style="font-size: 0.7rem; color: rgba(255, 255, 255, 0.4)">Von: {{ selectedCol ? selectedCol.firstName : "" }}</div>
              </div>
            </div>
            <div style="font-size: 0.75rem; color: rgba(255, 255, 255, 0.5)" class="mb-3">
              {{
                transferOrSwapMode === "swap"
                  ? "Tausch-Mitarbeiter wählen (Verfügbare oben sortiert)"
                  : "Ziel-Mitarbeiter wählen (Verfügbare oben sortiert)"
              }}
            </div>
            <div
              class="wc-transfer-list custom-scrollbar mb-2"
              style="max-height: 250px; overflow-y: auto; display: flex; flex-direction: column; gap: 8px; padding-right: 4px">
              <div
                v-for="emp in transferableEmployees"
                :key="emp.id"
                class="wc-transfer-card"
                :class="{ active: transferShiftData.targetId === emp.id }"
                @click="transferShiftData.targetId = emp.id">
                <div class="d-flex align-center w-100" style="gap: 10px">
                  <v-avatar size="32" class="wc-transfer-avatar">
                    <img v-if="emp.picture" :src="'/img/profilbilder/' + emp.picture" style="object-fit: cover" />
                    <v-icon v-else small color="white">mdi-account</v-icon>
                  </v-avatar>
                  <div class="flex-grow-1" style="min-width: 0">
                    <div class="wc-transfer-name text-truncate">{{ emp.name }}</div>
                    <div class="wc-transfer-status text-truncate" :style="getStatusStyle(emp.statusType)">
                      {{ emp.statusText }}
                    </div>
                  </div>
                  <div class="wc-transfer-checkbox">
                    <v-icon small :color="transferShiftData.targetId === emp.id ? 'primary' : 'rgba(255, 255, 255, 0.15)'">
                      {{ transferShiftData.targetId === emp.id ? "mdi-checkbox-marked-circle" : "mdi-minus-circle-outline" }}
                    </v-icon>
                  </div>
                </div>
              </div>
            </div>

            <!-- Reason for Transfer (only when in transfer mode) -->
            <div v-if="transferOrSwapMode === 'transfer'" class="mt-4 mb-2">
              <div style="font-size: 0.75rem; color: rgba(255, 255, 255, 0.5)" class="mb-2">Grund für Übertragung</div>
              <v-radio-group v-model="transferShiftData.reason" row dark class="mt-0" hide-details>
                <v-radio label="Normaler Tausch" value="normal" color="primary"></v-radio>
                <v-radio label="Mitarbeiter ist krank" value="sick" color="red"></v-radio>
              </v-radio-group>
            </div>

            <div class="d-flex justify-end mt-4" style="gap: 8px">
              <v-btn text small @click="transferShiftDialog = false" style="color: rgba(255, 255, 255, 0.4); text-transform: none">Abbrechen</v-btn>
              <v-btn small depressed color="primary" style="text-transform: none; font-weight: 600; border-radius: 8px" @click="saveTransferShift">
                {{ transferOrSwapMode === "swap" ? "Tauschen" : "Übertragen" }}
              </v-btn>
            </div>
          </div>
        </v-card>
      </v-dialog>

      <v-dialog v-model="appointmentRestrictionsDialog" max-width="590" persistent content-class="wc-restrictions-dialog">
        <v-card
          :dark="isDarkMode"
          :light="!isDarkMode"
          class="wc-restrictions-card"
          :class="isDarkMode ? 'wc-restrictions-card--dark' : 'wc-restrictions-card--light'">
          <v-progress-linear v-if="appointmentRestrictionsLoading" absolute top indeterminate color="primary"></v-progress-linear>

          <div class="wc-restrictions-header">
            <div class="wc-restrictions-header-icon">
              <v-icon color="white">mdi-shield-account-outline</v-icon>
            </div>
            <div class="wc-restrictions-heading">
              <div class="wc-restrictions-eyebrow">Vorlagenregeln</div>
              <div class="wc-restrictions-title">Wer darf Vorlagen übernehmen?</div>
              <div class="wc-restrictions-subtitle">Lege die Ausnahmen für Termine, Ämtli, Aufgaben und Info-Vorlagen fest.</div>
            </div>
            <v-btn icon small class="wc-restrictions-close" :disabled="appointmentRestrictionsSaving" @click="appointmentRestrictionsDialog = false">
              <v-icon small>mdi-close</v-icon>
            </v-btn>
          </div>

          <div class="wc-restrictions-body">
            <section class="wc-restrictions-section">
              <div class="wc-restrictions-section-label"><span>1</span> Vorlage auswählen</div>
              <v-select
                v-model="selectedAppointmentRestrictionKey"
                :items="appointmentRestrictionTemplateOptions"
                placeholder="Vorlage wählen"
                prepend-inner-icon="mdi-view-grid-plus-outline"
                outlined
                dense
                hide-details
                :loading="appointmentRestrictionsLoading"
                :disabled="appointmentRestrictionsLoading || appointmentRestrictionsSaving"
                @change="loadAppointmentRestrictionDraft"></v-select>
            </section>

            <section class="wc-restrictions-section">
              <div class="wc-restrictions-section-label"><span>2</span> Zugriff einschränken</div>
              <div class="wc-restrictions-rules">
                <div class="wc-restrictions-rule">
                  <div class="wc-restrictions-rule-icon"><v-icon small>mdi-school-outline</v-icon></div>
                  <div class="wc-restrictions-rule-copy">
                    <strong>Alle Lernenden ausschliessen</strong>
                    <small>Diese Vorlage kann keinem Lernenden neu zugewiesen werden.</small>
                  </div>
                  <v-switch
                    v-model="appointmentRestrictionDraft.excludeApprentices"
                    color="primary"
                    inset
                    hide-details
                    class="ma-0 wc-restrictions-switch"
                    :disabled="appointmentRestrictionsLoading || appointmentRestrictionsSaving"
                    aria-label="Alle Lernenden ausschliessen"></v-switch>
                </div>

                <div class="wc-restrictions-rule wc-restrictions-rule--employees">
                  <div class="wc-restrictions-rule-heading">
                    <div class="wc-restrictions-rule-icon"><v-icon small>mdi-account-minus-outline</v-icon></div>
                    <div class="wc-restrictions-rule-copy">
                      <strong>Einzelne Mitarbeitende</strong>
                      <small>Zusätzliche Personen unabhängig von ihrer Rolle ausschliessen.</small>
                    </div>
                    <span v-if="appointmentRestrictionDraft.excludedEmployees.length" class="wc-restrictions-count">
                      {{ appointmentRestrictionDraft.excludedEmployees.length }} ausgewählt
                    </span>
                  </div>
                  <v-autocomplete
                    v-model="appointmentRestrictionDraft.excludedEmployees"
                    :items="employeeOptions"
                    placeholder="Mitarbeitende suchen und auswählen"
                    prepend-inner-icon="mdi-magnify"
                    outlined
                    dense
                    hide-details
                    multiple
                    chips
                    small-chips
                    deletable-chips
                    clearable
                    :disabled="appointmentRestrictionsLoading || appointmentRestrictionsSaving"
                    :menu-props="{ maxHeight: 280 }"></v-autocomplete>
                </div>
              </div>
            </section>

            <div class="wc-restrictions-note">
              <v-icon small>mdi-information-outline</v-icon>
              <span
                >Gilt für neue Termine, Aufgaben und Ämtli sowie für Kopien, Verschiebungen, Auto-Zuteilung und Freigabe. Bestehende Einträge bleiben
                unverändert.</span
              >
            </div>
          </div>

          <div class="wc-restrictions-footer">
            <span class="wc-restrictions-footer-hint">Änderungen gelten erst nach dem Speichern.</span>
            <div class="d-flex align-center" style="gap: 8px">
              <v-btn text small class="text-none" :disabled="appointmentRestrictionsSaving" @click="appointmentRestrictionsDialog = false"
                >Abbrechen</v-btn
              >
              <v-btn
                color="primary"
                depressed
                small
                class="text-none font-weight-bold px-5"
                :loading="appointmentRestrictionsSaving"
                :disabled="appointmentRestrictionsLoading || !selectedAppointmentRestrictionKey"
                @click="saveAppointmentRestriction">
                Regeln speichern
              </v-btn>
            </div>
          </div>
        </v-card>
      </v-dialog>

      <!-- Towel Admin Dialog -->
      <v-dialog v-model="showTowelAdminDialog" fullscreen persistent transition="dialog-bottom-transition">
        <towel-admin v-if="showTowelAdminDialog" @close="showTowelAdminDialog = false"></towel-admin>
      </v-dialog>

      <!-- License Plates Dialog -->
      <license-plate-list v-model="showLicensePlates" @select-user="customerSelected" />
    </div>
  </div>
</template>

<script>
import api from "@/api"
import userService from "@/services/userService"
import { onlineMeetingTemplate, availableMeetingTemplates } from "@/services/onlineMeeting"
import socketService from "@/socketService"
import moment from "moment"

// Import Sub-Components
import WorkCalendarColumn from "./WorkCalendarColumn"
import WorkCalendarSidebar from "./WorkCalendarSidebar"
import WorkCalendarDropDialog from "./WorkCalendarDropDialog"
import WorkCalendarEditDialog from "./WorkCalendarEditDialog"
import WorkCalendarGuestbookDialog from "./WorkCalendarGuestbookDialog"
import WorkCalendarAppointmentSearch from "./WorkCalendarAppointmentSearch"
import WorkCalendarAmtliJourneyHeader from "./WorkCalendarAmtliJourneyHeader.vue"
import WorkCalendarTaskAssignmentInfo from "./WorkCalendarTaskAssignmentInfo.vue"
import WorkCalendarTaskFrequencySettings from "./WorkCalendarTaskFrequencySettings.vue"
import WorkCalendarTaskVariantGroups from "./WorkCalendarTaskVariantGroups.vue"
import WorkCalendarTaskVariantContent from "./WorkCalendarTaskVariantContent.vue"
import WorkCalendarCleaningMerge from "./WorkCalendarCleaningMerge.vue"
import { buildTaskVariantGroups, taskMatchesTemplate, taskVariantLabels } from "./taskVariantPresentation"
import { createCalendarReferenceCache, createCalendarRefreshQueue } from "./calendarRequestCoordinator"
import WorkCalendarWeek from "./WorkCalendarWeek"
import TowelAdmin from "@/views/employee/components/TowelAdmin.vue"
import LicensePlateList from "@/views/employee/components/licensePlateList.vue"

import { mapActions, mapState, mapGetters } from "vuex"

// Async Component: Der zusätzliche Trainerpoint-Code wird im normalen
// Kalenderbetrieb nicht einmal in den Browser geladen.
const WorkCalendarCheckinPool = () => import(/* webpackChunkName: "trainerpoint-live" */ "./WorkCalendarCheckinPool.vue")
const CustomerInteraction = () => import(/* webpackChunkName: "customer-interaction" */ "@/components/employee/customerInteraction.vue")
// Gemeinsamer Schalter für Sidebar und mobiles Bottom-Sheet.
const GUESTBOOK_ENTRY_NAV_VISIBLE = true

export default {
  name: "WorkCalendar",
  components: {
    WorkCalendarColumn,
    WorkCalendarSidebar,
    WorkCalendarDropDialog,
    WorkCalendarEditDialog,
    WorkCalendarGuestbookDialog,
    WorkCalendarAppointmentSearch,
    WorkCalendarAmtliJourneyHeader,
    WorkCalendarTaskAssignmentInfo,
    WorkCalendarTaskFrequencySettings,
    WorkCalendarTaskVariantGroups,
    WorkCalendarTaskVariantContent,
    WorkCalendarCleaningMerge,
    WorkCalendarWeek,
    WorkCalendarCheckinPool,
    CustomerInteraction,
    TowelAdmin,
    LicensePlateList,
  },
  beforeCreate() {
    // Bis zur Überarbeitung des Darkmodes wird auch eine früher gespeicherte
    // Benutzereinstellung bewusst auf das helle Design zurückgesetzt.
    if (typeof localStorage !== "undefined") localStorage.setItem("wc_isDarkMode", "false")
  },
  data() {
    return {
      contextMenu: { show: false, x: 0, y: 0, block: null, col: null },
      emptyContextMenu: { show: false, x: 0, y: 0, col: null, time: null },
      appointmentClipboard: null,
      appointmentClipboardBusy: false,
      appointmentSearchDialog: false,
      appointmentSearchOpening: false,
      appointmentOpenSequence: 0,
      fetchDataRequestId: 0,
      appointmentHistoryDialog: false,
      appointmentHistoryLoading: false,
      appointmentHistoryEntries: [],
      trainerControlMenuOpen: false,
      appointmentTemplateRestrictions: [],
      appointmentRestrictionsDialog: false,
      appointmentRestrictionsLoading: false,
      appointmentRestrictionsSaving: false,
      selectedAppointmentRestrictionKey: null,
      appointmentRestrictionDraft: {
        excludeApprentices: false,
        excludedEmployees: [],
      },
      copyDialog: false,
      copySourceBlock: null,
      copyTargetEmployees: [],
      socketHandlers: {},
      calendarSocketReady: false,
      isDarkMode: false,
      isPerformanceMode: localStorage.getItem("wc_isPerformanceMode") === "true",
      showLastNameInitial: localStorage.getItem("wc_showLastNameInitial") === "true",
      viewMode: "day", // 'day' or 'week'

      currentDate: null, // Will be set in created()
      datePickerMenu: false,
      loading: false,
      customerLoading: false, // NEW: Loading state for customer details
      customerDetailsError: false,
      customerDetailsRequestId: 0,
      collisionDialog: false,
      collisionsList: [],
      collisionPendingData: null,
      columns: [],
      helperAppointments: [],
      helperTasks: [],
      guestbookEntries: [],
      sickEmployeesToday: [],
      viewColumns: [],
      hiddenSickTrainerIds: [],
      templates: [],
      completions: [], // NEW: Task completions
      liveAttendanceMap: {},
      editingUsers: {}, // { itemId: { userId, userName, userPicture } }
      studioId: userService.getUser().studio._id,
      userAllowedStudios: null,

      headerOffset: 80,
      timerInterval: null,
      currentTimeTop: 0,
      sidebarOpen: localStorage.getItem("wc_sidebarOpen") !== "false",
      hoverState: null,
      activeMobileColIndex: 0, // Mobile column tracking
      allEmployeesForHover: [],
      weeklyShiftsForHover: [],

      amtliConfigDialog: false,
      autoAssignTasksDialog: false,
      autoAssignLoading: false,
      assignStep: 1,
      amtliJourneyActive: false,
      amtliConfigSearch: "",
      amtliConfigFrequencyFilter: "all",
      overwriteAcknowledged: false,
      pendingAmtliPoolTemplateId: null,
      amtliTemplateReturnTarget: "config",
      amtliAutomationSettingsOpen: false,
      autoAssignStats: null,
      autoAssignResults: [],
      selectedPreviewDate: null,
      showAdvancedAssignSettings: false,
      autoAssignViewMode: "list",
      autoAssignConfig: {
        startDate: "",
        endDate: "",
        overwriteExisting: false,
        excludedEmployeeIds: [],
        preventOverlap: true,
        linkedTasksSameEmployee: true,
        preventConsecutiveDays: false,
        distributionMode: "prefer_pool",
        templates: [],
      },
      autoAssignLogs: [],
      deleteSeriesDialog: false,
      deleteSeriesScope: "single",
      deleteSeriesReason: "",
      deleteSeriesBlock: null,
      deleteSeriesCol: null,
      manageSeriesDialog: false,
      loadingActiveSeries: false,
      activeSeries: [],
      expandedSeriesPanel: null,
      configDrawer: false,
      editingConfigTemplate: null,
      amtliPoolDialog: false,
      selectedTemplateIds: [],

      sortEmployeesDialog: false,
      sortedEmployeeList: [],
      draggedSortIdx: null,
      savingSortOrder: false,

      // Partial Sickness Dialog
      sickDetailsDialog: false,
      sickDialogCol: null,
      sickDialogShift: null,
      sickDialogPartialType: "complete",
      sickDialogPercentage: 100,
      sickDialogCalculationMode: "time_window",
      sickDialogHours: 0,
      sickDialogTimeRange: "",
      sickDialogShiftTimes: "",
      sickDialogShiftTotalHours: 0,
      sickDialogSaving: false,
      sickDialogRange1Start: "",
      sickDialogRange1End: "",
      sickDialogShowRange2: false,
      sickDialogRange2Start: "",
      sickDialogRange2End: "",
      range1StartMenu: false,
      range1EndMenu: false,
      range2StartMenu: false,
      range2EndMenu: false,

      // Employee Switcher
      employeeSwitcherDialog: false,

      // Customer Search
      customerSearchDialog: false,
      trainerpointInteractionMounted: false,
      customerAppointmentLauncherDialog: false,
      customerAppointmentLauncherCustomer: null,
      customerAppointmentLauncherLoading: false,
      customerAppointmentLauncherHandledId: null,
      showTowelAdminDialog: false,
      showLicensePlates: false,

      // Display Settings
      monochromeShifts: localStorage.getItem("wc_monochromeShifts") === "true",
      isCompactMode: localStorage.getItem("wc_isCompactMode") === "true",
      isThickerLines: localStorage.getItem("wc_isThickerLines") === "true",
      currentTimeLineWidth: (() => {
        const value = Number(localStorage.getItem("wc_currentTimeLineWidth"))
        return [1, 2, 3, 4].includes(value) ? value : 2
      })(),
      showPastTimeOverlay: localStorage.getItem("wc_showPastTimeOverlay") === "true",
      pastTimeOverlayOpacity: (() => {
        const value = Number(localStorage.getItem("wc_pastTimeOverlayOpacity"))
        return Number.isFinite(value) && value >= 4 && value <= 40 ? value : 10
      })(),
      colorNoShift: localStorage.getItem("wc_colorNoShift") === "true",
      noShiftBgColor: localStorage.getItem("wc_noShiftBgColor") || "#e2e8f0",
      monochromeShiftColor: localStorage.getItem("wc_monochromeShiftColor") || "",
      monochromeShiftOpacity: (() => {
        const value = Number(localStorage.getItem("wc_monochromeShiftOpacity"))
        return Number.isFinite(value) && value >= 10 && value <= 100 ? value : 100
      })(),
      focusedEmployeeId: localStorage.getItem("wc_focused_employee_id") || null,
      showHelperColumnsDuringFocus: localStorage.getItem("wc_show_helper_columns_during_focus") === "true",
      calendarStart: localStorage.getItem("wc_calendarStart") || "06:00",
      calendarEnd: localStorage.getItem("wc_calendarEnd") || "23:00",
      calendarTimeStep: Number(localStorage.getItem("wc_calendarTimeStep")) || 60,
      customHourHeight: (() => {
        const val = localStorage.getItem("wc_customHourHeight")
        if (val === "fit") return "fit"
        if (val === "null" || !val) return null
        const num = Number(val)
        return isNaN(num) || !num ? null : num
      })(),
      calendarAutoAdjust: localStorage.getItem("wc_calendarAutoAdjust") === "true",
      calendarBufferTime: localStorage.getItem("wc_calendarBufferTime") !== null ? parseInt(localStorage.getItem("wc_calendarBufferTime")) : 30,
      scrollerHeight: 600,

      // Mobile Template Sheet
      mobileTemplateSheet: false,
      mobileActiveTab: 1,
      mobileEditMode: false,
      mobileSearchQuery: "",
      mobileClickContext: null, // Speichert Klick-Kontext (Zeit + Spalte) für Tap-to-Add auf Mobile

      // Ämtli Smart-Assignment
      taskAssignDialog: false,
      taskAssignData: {},
      taskAssignTime: "",
      taskAssignDate: "",
      taskAssignDuration: 30,
      taskAvailability: [],
      taskAvailabilityRequestId: 0,
      taskAvailabilityLoading: false,
      taskAvailabilityNotice: "",
      taskAvailabilityKey: "",
      taskAssignSaving: false,

      // Ämtli Dashboard
      taskDashboardDialog: false,
      taskDashboardTab: 0,
      taskDashboardLoading: false,
      taskDashboardTasks: [],
      taskDashboardShifts: [],
      taskDashboardAppointments: [],
      unassignedFilter: "all",
      taskDashboardConfig: {
        startDate: "",
        endDate: "",
      },

      // Dialogs
      dropDialog: false,
      dropDialogSaving: false,
      pendingDrop: null,
      // TAC-Packagepruefung und -abbuchung bleiben vorerst vollstaendig ausserhalb des Kalenderablaufs.
      tacAppointmentBillingActive: false,
      editDialog: false,
      guestbookDialog: false,
      activeGuestbookEntry: null,
      isResizing: false,
      editData: {},
      customerDetails: null,
      createTemplateDialog: false,
      editingTemplateId: null,
      taskTimeModeOptions: [
        { text: "Flexibel – nächste freie Zeit", value: "flexible" },
        { text: "Bevorzugtes Zeitfenster – wenn möglich", value: "preferred_window" },
        { text: "Pflicht-Zeitfenster – nur dann einplanen", value: "required_window" },
        { text: "Feste Startzeit(en)", value: "fixed_times" },
      ],
      newTemplate: {
        title: "",
        type: "appointment",
        duration: 60,
        color: "#2196F3",
        icon: "mdi-calendar",
        requiresCustomer: false,
        tacServiceType: null,
        tacActivityTemplateId: "",
        tacAccessPassTemplateIds: [],
        checklist: [],
        instructions: "",
        defaultTimes: [],
        hasFixedTimes: false,
        timePlanningMode: "flexible",
        timePreference: { mode: "flexible", windowStart: "09:00", windowEnd: "12:00" },
        assignedEmployees: [],
        frequencyConfig: { daysOfWeek: [], daysOfMonth: [], daySelectionMode: "one" },
        linkedTemplateId: null,
      },
      noteDialog: false,
      selectedCol: null,
      newNoteContent: "",

      // Shift Actions
      editShiftDialog: false,
      editShiftData: { start: "", end: "", reason: "", shiftId: null, times: [], selectedReason: "", customReason: "" },
      shiftReasons: [
        "Krankheitsvertretung",
        "Mitarbeiterwunsch",
        "Kurzfristiger Tausch",
        "Verspätung / Früher gegangen",
        "Falsch eingetragen",
        "Sonstiges (bitte angeben)",
      ],
      transferShiftDialog: false,
      transferOrSwapMode: "transfer",
      transferShiftData: { targetId: null, reason: "normal" },
      transferableEmployees: [],

      // Config Options
      iconOptions: [
        { name: "Training", icon: "mdi-dumbbell" },
        { name: "Plan", icon: "mdi-clipboard-list-outline" },
        { name: "Probetraining", icon: "mdi-account-star" },
        { name: "Check-Up", icon: "mdi-heart-pulse" },
        { name: "Ernährung", icon: "mdi-food-apple" },
        { name: "Kurs", icon: "mdi-yoga" },
        { name: "Verkauf", icon: "mdi-cash-register" },
        { name: "Meeting", icon: "mdi-account-group" },
        { name: "Büro", icon: "mdi-laptop" },
        { name: "Telefon", icon: "mdi-phone" },
        { name: "Reinigung", icon: "mdi-broom" },
        { name: "Wartung", icon: "mdi-tools" },
        { name: "Theke", icon: "mdi-coffee" },
        { name: "Rundgang", icon: "mdi-eye-check" },
        { name: "Müll", icon: "mdi-delete" },
        { name: "Inventar", icon: "mdi-package-variant" },
        { name: "Schliessen", icon: "mdi-door-closed-lock" },
        { name: "Öffnen", icon: "mdi-door-open" },
        { name: "Pause", icon: "mdi-pause-circle" },
        { name: "Event", icon: "mdi-party-popper" },
        { name: "Spray", icon: "mdi-spray-bottle" },
        { name: "Pflanzen", icon: "mdi-leaf" },
        { name: "Dokument", icon: "mdi-file-document-edit" },
        { name: "Check", icon: "mdi-check-all" },
        { name: "Uhrzeit", icon: "mdi-clock-outline" },
        { name: "Nachricht", icon: "mdi-message-text-outline" },
        { name: "Email", icon: "mdi-email-outline" },
        { name: "Wäsche", icon: "mdi-washing-machine" },
        { name: "Shirt", icon: "mdi-tshirt-crew" },
        { name: "Handtuch", icon: "mdi-hanger" },
        { name: "Korb", icon: "mdi-basket" },
        { name: "Kasse", icon: "mdi-account-cash" },
      ],
      colorOptions: [
        "#2196F3",
        "#1976D2",
        "#0D47A1",
        "#4CAF50",
        "#2E7D32",
        "#00963f",
        "#FF9800",
        "#F57C00",
        "#E65100",
        "#F44336",
        "#C62828",
        "#9C27B0",
        "#7B1FA2",
        "#00BCD4",
        "#0097A7",
        "#607D8B",
        "#455A64",
        "#E91E63",
        "#AD1457",
        "#FFD700",
        "#FF6F00",
        "#3F51B5",
        "#283593",
        "#009688",
        "#00695C",
      ],
    }
  },
  watch: {
    mobileActiveTab(val) {
      this.mobileEditMode = false
    },
    selectedStudio: {
      immediate: true,
      handler(newStudio) {
        if (newStudio && (newStudio._id || newStudio.id)) {
          const previousStudioId = this.studioId
          const studioChanged = String(previousStudioId || "") !== String(newStudio._id || newStudio.id)
          if (studioChanged) {
            this.teardownCalendarSocket()
            this.invalidateCalendarReferences()
            this.calendarSocketReady = false
          }
          this.studioId = newStudio._id || newStudio.id
          if (newStudio.openingClosingOffset !== undefined && newStudio.openingClosingOffset !== null) {
            localStorage.setItem(`wc_studio_offset_${this.studioId}`, String(newStudio.openingClosingOffset))
          }
          if (this.hasStudioAccess) {
            this.fetchData()
          }
        }
      },
    },
    getSelectedEmployee: {
      immediate: true,
      handler(newEmp) {
        if (newEmp && newEmp.user) {
          this.syncMobileColumnWithEmployee()
        }
      },
    },
    viewColumns: {
      immediate: true,
      handler() {
        this.syncMobileColumnWithEmployee()
      },
    },
    currentDate() {
      this.hiddenSickTrainerIds = []
      this.$nextTick(() => this.updateTimeLine())
    },
    editDialog(val) {
      if (!val) {
        this.customerDetailsRequestId += 1
        this.customerLoading = false
      }
      // Wenn Dialog schliesst → Editing-Presence beenden
      if (!val && this.editData && this.editData.id) {
        this.emitEditingStop(this.editData.id)
      }
    },
    guestbookDialog(val) {
      if (!val) this.activeGuestbookEntry = null
    },
    isDarkMode(val) {
      localStorage.setItem("wc_isDarkMode", val)
    },
    isPerformanceMode(val) {
      localStorage.setItem("wc_isPerformanceMode", val)
    },
    monochromeShifts(val) {
      localStorage.setItem("wc_monochromeShifts", val)
    },
    colorNoShift(val) {
      localStorage.setItem("wc_colorNoShift", val)
    },
    noShiftBgColor(val) {
      localStorage.setItem("wc_noShiftBgColor", val)
    },
    monochromeShiftColor(val) {
      localStorage.setItem("wc_monochromeShiftColor", val)
    },
    monochromeShiftOpacity(val) {
      localStorage.setItem("wc_monochromeShiftOpacity", String(val))
    },
    isCompactMode(val) {
      localStorage.setItem("wc_isCompactMode", val)
    },
    isThickerLines(val) {
      localStorage.setItem("wc_isThickerLines", val)
    },
    currentTimeLineWidth(val) {
      localStorage.setItem("wc_currentTimeLineWidth", String(val))
    },
    showPastTimeOverlay(val) {
      localStorage.setItem("wc_showPastTimeOverlay", val ? "true" : "false")
    },
    pastTimeOverlayOpacity(val) {
      localStorage.setItem("wc_pastTimeOverlayOpacity", String(val))
    },
    showLastNameInitial(val) {
      localStorage.setItem("wc_showLastNameInitial", val ? "true" : "false")
    },
    focusedEmployeeId(val) {
      localStorage.setItem("wc_focused_employee_id", val || "")
      this.processViewData()
    },
    showHelperColumnsDuringFocus(val) {
      localStorage.setItem("wc_show_helper_columns_during_focus", val ? "true" : "false")
      this.processViewData()
    },
    calendarStart(val) {
      localStorage.setItem("wc_calendarStart", val)
      this.processViewData()
    },
    calendarEnd(val) {
      localStorage.setItem("wc_calendarEnd", val)
      this.processViewData()
    },
    calendarTimeStep(val) {
      localStorage.setItem("wc_calendarTimeStep", String(val))
      this.processViewData()
    },
    customHourHeight(val) {
      localStorage.setItem("wc_customHourHeight", String(val))
      if (val === "fit") {
        this.updateScrollerHeight()
      }
      this.processViewData()
    },
    calendarAutoAdjust(val) {
      localStorage.setItem("wc_calendarAutoAdjust", String(val))
      this.processViewData()
    },
    calendarBufferTime(val) {
      localStorage.setItem("wc_calendarBufferTime", String(val))
      this.processViewData()
    },
    hourHeight(val, oldVal) {
      if (val !== oldVal) this.scheduleCalendarLayoutRebuild()
    },
  },
  computed: {
    appointmentSearchDayItems() {
      const results = []
      const seen = new Set()
      const addEntry = (entry, employeeName = "", employeeId = null, isTask = false) => {
        if (!entry) return
        if (entry.date && entry.date !== this.currentDate) return
        const id = String(entry._id || entry.id || "")
        const resultKey = `${isTask ? "task" : "appointment"}:${id}`
        if (!id || seen.has(resultKey)) return
        seen.add(resultKey)
        const start = isTask ? entry.time || entry.start || "12:00" : entry.start
        const duration = Number(entry.duration) > 0 ? Number(entry.duration) : 30
        const end = isTask ? entry.end || moment(start, "HH:mm").add(duration, "minutes").format("HH:mm") : entry.end
        results.push({
          ...entry,
          _id: entry._id || entry.id,
          date: entry.date || this.currentDate,
          start,
          end,
          isTask,
          kind: isTask ? "task" : "appointment",
          type: isTask ? "task" : entry.type || "appointment",
          employeeName,
          employeeId,
        })
      }

      ;(this.columns || []).forEach((column) => {
        const info = column.info || {}
        const employeeName = info.fullName || [info.firstName, info.lastName].filter(Boolean).join(" ") || ""
        const employeeId = info._id || null
        ;(column.appointments || []).forEach((appointment) => addEntry(appointment, employeeName, employeeId, false))
        ;(column.tasks || []).forEach((task) => addEntry(task, employeeName, employeeId, true))
      })

      const helperNames = {
        ma_info: "MA Info",
        no_show: "Nicht gekommen",
        unassigned: "Nicht zugewiesen",
      }
      ;(this.helperAppointments || []).forEach((appointment) => {
        addEntry(appointment, helperNames[appointment.helperColumn] || "Hilfsspalte", null, false)
      })
      ;(this.helperTasks || []).forEach((task) => {
        addEntry(task, helperNames[task.helperColumn] || "Hilfsspalte", null, true)
      })

      return results
    },
    frequencyEditingDisabled() {
      const tpl = this.editingConfigTemplate
      if (!tpl) return false
      const isOpeningOrClosing = tpl.title === "Centeröffnung" || tpl.title === "Centerschliessung"
      if (!isOpeningOrClosing) return false

      const user = userService.getUser()
      const isSteckborn = user && user.studio && (user.studio.code === "steckborn" || String(user.studio.name).toLowerCase() === "steckborn")
      if (isSteckborn) return true

      return !userService.hasRight("sm_create")
    },
    frequencyEditingLockedMessage() {
      const user = userService.getUser()
      const studio = user && user.studio
      const isSteckborn = studio && (studio.code === "steckborn" || String(studio.name || "").toLocaleLowerCase("de-CH") === "steckborn")
      return isSteckborn
        ? "Die Frequenz für Centeröffnungen/-schliessungen ist für den Account Steckborn gesperrt."
        : "Frequenzänderungen für Centeröffnungen/-schliessungen erfordern das Recht sm_create."
    },
    allAppointmentTemplates() {
      const standardAppts = [
        // trainingTemplates
        {
          id: "w8",
          title: "Beratungstermin",
          icon: "mdi-account-tie",
          color: "#d4b72e",
          duration: 60,
          type: "appointment",
          specialType: "consultation",
          requiresCustomer: true,
        },
        {
          id: "w1",
          title: "Probetraining",
          icon: "mdi-account-star",
          color: "#d4b72e",
          duration: 60,
          type: "appointment",
          specialType: "trial",
          requiresCustomer: true,
        },
        {
          id: "w11d",
          title: "Abschlusstermin 11 Tage",
          icon: "mdi-calendar-check",
          color: "#009688",
          duration: 60,
          type: "appointment",
          specialType: "abschlusstermin_11d",
          requiresCustomer: true,
        },
        {
          id: "w9",
          title: "Trainingsplan",
          icon: "mdi-clipboard-list",
          color: "#397f9e",
          duration: 60,
          type: "appointment",
          specialType: "trainingplan",
          requiresCustomer: true,
        },
        {
          id: "w4",
          title: "Kontrolltermin",
          icon: "mdi-calendar-check",
          color: "#c66d78",
          duration: 30,
          type: "appointment",
          specialType: "check",
          requiresCustomer: true,
        },
        {
          id: "w2",
          title: "Einführungstraining",
          icon: "mdi-dumbbell",
          color: "#3d8c70",
          duration: 60,
          type: "appointment",
          specialType: "intro",
          requiresCustomer: true,
        },
        {
          id: "w2b",
          title: "Ergänzungstraining",
          icon: "mdi-dumbbell",
          color: "#d46f3a",
          duration: 30,
          type: "appointment",
          specialType: "followup",
          requiresCustomer: true,
        },
        {
          id: "w2d",
          title: "Personal Training",
          icon: "mdi-run",
          color: "#168b8d",
          duration: 90,
          type: "appointment",
          specialType: "workout",
          tacServiceType: "personal_training",
          requiresCustomer: true,
        },
        {
          id: "w2e",
          title: "Massage",
          icon: "mdi-spa",
          color: "#8E24AA",
          duration: 45,
          type: "appointment",
          specialType: "massage",
          tacServiceType: "massage",
          requiresCustomer: true,
        },
        {
          id: "w5",
          title: "Milon Einführung",
          icon: "mdi-sync",
          color: "#a65086",
          duration: 60,
          type: "appointment",
          specialType: "milon",
          requiresCustomer: true,
        },
        {
          id: "w10",
          title: "Mescan",
          icon: "mdi-clipboard-pulse",
          color: "#00ACC1",
          duration: 30,
          type: "appointment",
          specialType: "anamnese",
          requiresCustomer: true,
        },
        {
          id: "w11",
          title: "PelviX",
          icon: "mdi-clipboard-pulse",
          color: "#00ACC1",
          duration: 30,
          type: "appointment",
          specialType: "anamnese",
          requiresCustomer: true,
        },
        {
          id: "w6",
          title: "Ernährungsberatung",
          icon: "mdi-food-apple",
          color: "#356fa3",
          duration: 60,
          type: "appointment",
          specialType: "nutrition",
          requiresCustomer: true,
        },
        // contractTemplates
        {
          id: "a1",
          title: "Vertrag erstellen",
          icon: "mdi-file-document-edit",
          color: "#37474F",
          duration: 30,
          type: "appointment",
          specialType: "new_contract",
          requiresCustomer: false,
        },
        {
          id: "a2",
          title: "Upgrade / Anpassung",
          icon: "mdi-star-circle",
          color: "#455A64",
          duration: 30,
          type: "appointment",
          specialType: "upgrade",
          requiresCustomer: true,
        },
        {
          id: "w7",
          title: "Anamnese / Checkup",
          icon: "mdi-clipboard-pulse",
          color: "#00ACC1",
          duration: 30,
          type: "appointment",
          specialType: "anamnese",
          requiresCustomer: true,
        },
        // schulungTemplates
        {
          id: "s1",
          title: "Schulung",
          icon: "mdi-school",
          color: "#E65100",
          duration: 60,
          type: "appointment",
          requiresCustomer: false,
        },
        {
          id: "s2",
          title: "Teamsitzung",
          icon: "mdi-account-group",
          color: "#FF9800",
          duration: 60,
          type: "appointment",
          requiresCustomer: false,
        },
        // standardAppointmentTemplates
        {
          id: "ap1",
          title: "Termin",
          icon: "mdi-calendar",
          color: "#00963f",
          duration: 60,
          type: "appointment",
          requiresCustomer: true,
        },
      ]
      const customAppts = (this.templates || [])
        .filter((t) => t.category === "termin" || (!t.category && t.type === "appointment"))
        .map((t) => ({
          id: String(t._id || t.id),
          title: t.title,
          icon: t.icon || "mdi-calendar",
          color: t.color || "#00963f",
          duration: t.duration || 60,
          type: "appointment",
          specialType: t.specialType || null,
          requiresCustomer: t.requiresCustomer || false,
          tacServiceType: t.tacServiceType,
          tacActivityTemplateId: t.tacActivityTemplateId || null,
          tacAccessPassTemplateIds: Array.isArray(t.tacAccessPassTemplateIds) ? [...t.tacAccessPassTemplateIds] : [],
        }))
      return availableMeetingTemplates([...standardAppts, { ...onlineMeetingTemplate }, ...customAppts]).map((template) => this.withAppointmentRestriction(template))
    },
    appointmentRestrictionTemplates() {
      const templatesByKey = new Map()
      const addTemplate = (template, category) => {
        const key = this.appointmentTemplateKey(template)
        if (!key || templatesByKey.has(key)) return
        templatesByKey.set(key, {
          ...template,
          restrictionCategory: category,
          appointmentRestriction: this.getAppointmentRestriction(template),
        })
      }

      ;(this.allAppointmentTemplates || []).forEach((template) => addTemplate(template, "termin"))
      ;(this.templates || []).forEach((template) => {
        const category = this.templateRestrictionCategory(template)
        if (!category || category === "termin") return
        // Ämtli werden studioübergreifend geteilt. In dieser Liste sollen nur
        // jene erscheinen, die im aktuellen Studio tatsächlich im Pool aktiv sind.
        if (category === "ämtli" && template.enabledInStudio !== true) return
        addTemplate(template, category)
      })

      const categoryOrder = { ämtli: 0, aufgabe: 1, termin: 2, info: 3 }
      return [...templatesByKey.values()].sort((left, right) => {
        const categoryDiff = categoryOrder[left.restrictionCategory] - categoryOrder[right.restrictionCategory]
        return categoryDiff || String(left.title || "").localeCompare(String(right.title || ""), "de-CH")
      })
    },
    appointmentRestrictionTemplateOptions() {
      const categoryLabels = {
        ämtli: "Ämtli aus dem Pool",
        aufgabe: "Aufgaben",
        termin: "Termine",
        info: "Info-Vorlagen",
      }
      const options = []
      Object.keys(categoryLabels).forEach((category) => {
        const categoryTemplates = (this.appointmentRestrictionTemplates || []).filter((template) => template.restrictionCategory === category)
        if (categoryTemplates.length === 0) return
        if (options.length > 0) options.push({ divider: true })
        options.push({ header: categoryLabels[category] })
        categoryTemplates.forEach((template) => {
          options.push({
            value: this.appointmentTemplateKey(template),
            text: template.title || categoryLabels[category],
          })
        })
      })
      return options
    },
    customerAppointmentLauncherTemplates() {
      return (this.allAppointmentTemplates || []).filter((template) => template && template.requiresCustomer === true && template.type !== "task")
    },
    ...mapState("studioStore", ["selectedStudio"]),
    ...mapGetters("studioStore", ["getSelectedEmployee"]),
    selectedGuestbookTrainerId() {
      const selectedUser = this.getSelectedEmployee?.user
      return String(selectedUser?._id || selectedUser?.id || selectedUser || "")
    },
    selectedGuestbookTrainerName() {
      const selectedEmployee = this.getSelectedEmployee || {}
      return selectedEmployee.fullName || `${selectedEmployee.vorname || ""} ${selectedEmployee.nachname || ""}`.trim() || ""
    },
    hasStudioAccess() {
      if (userService.hasRight("stat_admin")) {
        return true
      }
      if (!this.userAllowedStudios) {
        return true
      }
      const targetId = String(this.selectedStudio?._id || this.selectedStudio?.id || this.studioId || "")
      if (!targetId) return true
      return this.userAllowedStudios.some((s) => String(s._id || s.id || s) === targetId)
    },
    employeeOptions() {
      return (this.allEmployeesForHover || [])
        .filter((emp) => {
          const studioInfo = emp.studio?.find((s) => String(s._id || s.id) === String(this.studioId))
          if (!studioInfo) return false
          return studioInfo.hide !== true || studioInfo.showInCalendarAnyway === true
        })
        .map((emp) => {
          const userId = emp.user?._id || emp.user || emp._id
          return {
            value: String(userId),
            text: emp.fullName || (emp.user ? `${emp.user.vorname} ${emp.user.nachname}` : "") || "Unbekannter Mitarbeiter",
          }
        })
        .filter((emp) => emp.value)
        .sort((a, b) => a.text.localeCompare(b.text))
    },
    filteredAmtliVariantGroups() {
      const query = String(this.amtliConfigSearch || "").trim().toLocaleLowerCase("de-CH")
      const filter = this.amtliConfigFrequencyFilter
      return buildTaskVariantGroups(this.templates).map((group) => ({
        ...group,
        variants: group.variants.map((variant) => ({
          ...variant,
          summary: this.getAmtliTemplatePlainSummary(variant),
          readiness: this.getAmtliTemplateReadiness(variant),
        })),
      })).filter((group) => group.variants.some((variant) => {
        const matchesFilter = filter === "all" || (filter === "needs_attention"
          ? variant.enabledInStudio && !variant.readiness.ready && !variant.readiness.manualOnly
          : variant.frequency === filter)
        const text = [group.title, variant.variantName, variant.description, variant.summary].filter(Boolean).join(" ").toLocaleLowerCase("de-CH")
        return matchesFilter && (!query || text.includes(query))
      }))
    },
    amtliTemplateReadinessSummary() {
      const templates = this.autoAssignConfig.templates || []
      const ready = templates.filter((template) => this.getAmtliTemplateReadiness(template).ready).length
      const manualOnly = templates.filter((template) => this.getAmtliTemplateReadiness(template).manualOnly).length
      return {
        total: templates.length,
        ready,
        manualOnly,
        needsAttention: Math.max(0, templates.length - ready - manualOnly),
      }
    },
    selectedAutoAssignTemplates() {
      const selectedIds = new Set((this.selectedTemplateIds || []).map(String))
      return (this.autoAssignConfig.templates || []).filter((template) => selectedIds.has(String(template._id || template.id)))
    },
    autoAssignRunIssues() {
      const issues = []
      const start = this.$moment(this.autoAssignConfig.startDate, "YYYY-MM-DD", true)
      const end = this.$moment(this.autoAssignConfig.endDate, "YYYY-MM-DD", true)
      if (!start.isValid() || !end.isValid()) issues.push("Bitte einen gültigen Planungszeitraum wählen.")
      else if (start.isAfter(end)) issues.push("Das Startdatum muss vor dem Enddatum liegen.")
      else if (end.diff(start, "days") > 62) issues.push("Ein Planungslauf darf höchstens 63 Tage umfassen.")

      if (!this.selectedAutoAssignTemplates.length) issues.push("Bitte mindestens ein Ämtli für diesen Lauf auswählen.")
      else if (this.selectedAutoAssignTemplates.length > 100) issues.push("Pro Planungslauf können höchstens 100 Ämtli ausgewählt werden.")
      this.selectedAutoAssignTemplates
        .filter((template) => !this.getAmtliTemplateReadiness(template).ready)
        .forEach((template) => issues.push(`${template.title}: ${this.getAmtliTemplateReadiness(template).message}`))
      if (this.autoAssignConfig.overwriteExisting && !this.overwriteAcknowledged) {
        issues.push("Bitte die Auswirkungen der Neuberechnung bestätigen.")
      }
      return issues
    },
    canExecuteAutoAssign() {
      return !this.autoAssignLoading && this.autoAssignRunIssues.length === 0
    },
    autoAssignResultSummary() {
      const stats = this.autoAssignStats || {}
      const assigned = Number(stats.assignedFromPool || 0) + Number(stats.assignedFallback || 0)
      const unassigned = Number(stats.unassigned || 0)
      return {
        total: Number(stats.totalCreated || assigned + unassigned || 0),
        assigned,
        unassigned,
      }
    },
    dashboardReadyDraftCount() {
      return (this.draftTasks || []).filter((task) => task.status === "draft" && Boolean(task.assignedEmployee)).length
    },
    dashboardNeedsAssignmentCount() {
      return (this.draftTasks || []).filter((task) => !task.assignedEmployee).length
    },
    selectedPreviewDateObj() {
      if (!this.selectedPreviewDate || !this.autoAssignResultsGrouped) return null
      return this.autoAssignResultsGrouped.find((g) => g.date === this.selectedPreviewDate)
    },
    autoAssignResultsGrouped() {
      if (!this.autoAssignResults || this.autoAssignResults.length === 0) return []
      const map = {}
      this.autoAssignResults.forEach((a) => {
        if (!map[a.date]) map[a.date] = []
        map[a.date].push(a)
      })
      return Object.keys(map)
        .sort()
        .map((date) => {
          return {
            date: date,
            formattedDate: this.$moment(date).format("dddd, DD. MMMM YYYY"),
            items: map[date],
            employeeGroups: this.groupTasksByEmployee(map[date]),
          }
        })
    },
    autoAssignCalendarDays() {
      if (!this.autoAssignResults || this.autoAssignResults.length === 0) return []
      const start = this.$moment(this.autoAssignConfig.startDate).startOf("isoWeek")
      const end = this.$moment(this.autoAssignConfig.endDate).endOf("isoWeek")
      const days = []
      let curr = start.clone()
      while (curr.isSameOrBefore(end)) {
        days.push(curr.clone())
        curr.add(1, "days")
      }
      const tasksMap = {}
      this.autoAssignResults.forEach((task) => {
        if (!tasksMap[task.date]) tasksMap[task.date] = []
        tasksMap[task.date].push(task)
      })
      return days.map((d) => {
        const dateStr = d.format("YYYY-MM-DD")
        const isCurrentMonth = d.isBetween(
          this.$moment(this.autoAssignConfig.startDate).subtract(1, "day"),
          this.$moment(this.autoAssignConfig.endDate).add(1, "day"),
        )
        return {
          date: dateStr,
          dayNum: d.date(),
          isCurrentMonth,
          isToday: d.isSame(this.$moment(), "day"),
          tasks: tasksMap[dateStr] || [],
        }
      })
    },
    isSelfSelectedEmployee() {
      return this.getSelectedEmployee && this.getSelectedEmployee.user === userService.getUserId()
    },
    canSwitchEmployee() {
      return this.getSelectedEmployee && !this.isSelfSelectedEmployee
    },
    employeePicture() {
      if (this.getSelectedEmployee && this.getSelectedEmployee.picture) {
        return "/img/profilbilder/" + this.getSelectedEmployee.picture
      }
      if (userService.getUserPicture()) {
        return "/img/profilbilder/" + userService.getUserPicture()
      }
      return "/img/icons/user_placeholder.png"
    },
    getSelectedEmployeeLastName() {
      if (!this.getSelectedEmployee) return ""
      return (
        this.getSelectedEmployee.nachname ||
        this.getSelectedEmployee.lastName ||
        (this.getSelectedEmployee.fullName ? this.getSelectedEmployee.fullName.split(" ").slice(1).join(" ") : "")
      )
    },
    showMobileEmployeeWarning() {
      if (!this.$vuetify.breakpoint.smAndDown) return false
      if (!this.canSwitchEmployee) return false
      if (!this.getSelectedEmployee) return false
      const col = this.mobileViewColumns[0]
      if (!col || col.id === "ma_info" || col.id === "no_show" || col.id === "unassigned") return false
      return String(col.id) !== String(this.getSelectedEmployee.user)
    },
    currentColumnEmployeeName() {
      const col = this.mobileViewColumns[0]
      return col ? col.firstName : ""
    },
    hourHeight() {
      // Auf Handys kompakter (70px), auf Desktop normal (100px)
      return this.$vuetify.breakpoint.smAndDown ? 70 : 100
    },
    employeeList() {
      return this.viewColumns.map((col) => {
        const info = col.info || {}
        const profile = (this.allEmployeesForHover || []).find(
          (employee) => String(employee?.user?._id || employee?.user || employee?._id || "") === String(col.id || info._id || ""),
        )
        let name = col.firstName || ""
        if (info.firstName) {
          name = `${info.firstName} ${info.lastName || ""}`.trim()
        }
        return {
          id: col.id,
          name: name,
          picture: col.picture || info.picture || null,
          appointments: col.appointments || [],
          shifts: col.shifts || [],
          tasks: col.tasks || [],
          absenceReason: col.absenceReason || null,
          dbId: info._id || null,
          isApprentice: profile?.isApprentice === true,
        }
      })
    },
    allStudioEmployees() {
      const helpers = [
        { id: "ma_info", name: "MA Info", dbId: null, isApprentice: false },
        { id: "no_show", name: "Nicht gekommen", dbId: null, isApprentice: false },
      ]

      const realEmployees = (this.allEmployeesForHover || [])
        .filter((emp) => {
          const studioIdStr = String(this.studioId)
          const mainStudio = emp.studio?.find((s) => String(s._id || s) === studioIdStr)
          return mainStudio && mainStudio.percentage > 0
        })
        .map((emp) => {
          const userId = emp.user?._id || emp.user || null
          return {
            id: userId ? String(userId) : null,
            name: emp.fullName || emp.name || "Mitarbeiter",
            picture: emp.picture || null,
            dbId: userId ? String(userId) : null,
            isApprentice: emp.isApprentice === true,
          }
        })
        .filter((e) => e.id)

      return [...realEmployees, ...helpers]
    },
    focusEmployeeItems() {
      const workingColumns = (this.columns || []).filter((col) => !col.isHelper && col.shifts && col.shifts.length > 0)

      const list = workingColumns.map((col) => {
        const empUser = col.info || col
        return {
          text: (empUser.firstName || "") + " " + (empUser.lastName || ""),
          value: String(empUser._id || empUser.id),
        }
      })
      list.sort((a, b) => a.text.localeCompare(b.text))
      return [{ text: "Alle Mitarbeiter", value: null }, ...list]
    },
    focusedEmployee() {
      if (!this.focusedEmployeeId) return null
      const emp = (this.allEmployeesForHover || []).find((e) => String(e.user?._id || e.user) === String(this.focusedEmployeeId))
      return emp ? emp.user || emp : null
    },
    themeClasses() {
      return this.isDarkMode ? "theme--dark" : "theme--light"
    },
    startHour() {
      return Math.floor(this.getCalendarStartMinutes / 60)
    },
    endHour() {
      return Math.ceil(this.getCalendarEndMinutes / 60) - 1
    },
    getCalendarStartMinutes() {
      const timeStr = this.calendarAutoAdjust && this.autoAdjustedTimes ? this.autoAdjustedTimes.start : this.calendarStart || "06:00"
      const [h, m] = timeStr.split(":").map(Number)
      return h * 60 + m
    },
    getCalendarEndMinutes() {
      const timeStr = this.calendarAutoAdjust && this.autoAdjustedTimes ? this.autoAdjustedTimes.end : this.calendarEnd || "23:00"
      const [h, m] = timeStr.split(":").map(Number)
      return h * 60 + m
    },
    autoAdjustedTimes() {
      if (!this.calendarAutoAdjust) return null

      let earliestMin = 1440
      let latestMin = 0

      const allShifts = []
      this.columns.forEach((col) => {
        if (col.shifts) {
          col.shifts.forEach((s) => {
            const times = s.times || (s.shiftTemplate ? s.shiftTemplate.times : [])
            if (times) allShifts.push(...times)
          })
        }
      })

      if (allShifts.length === 0) {
        return { start: "08:00", end: "20:00" }
      }

      allShifts.forEach((t) => {
        if (t.start && t.end) {
          const [sh, sm] = t.start.split(":").map(Number)
          const [eh, em] = t.end.split(":").map(Number)
          const start = sh * 60 + sm
          const end = eh * 60 + em

          if (start < earliestMin) earliestMin = start
          if (end > latestMin) latestMin = end
        }
      })

      const buffer = this.calendarBufferTime !== undefined ? this.calendarBufferTime : 30
      const paddedStart = Math.max(0, Math.floor((earliestMin - buffer) / 30) * 30)
      const paddedEnd = Math.min(1440, Math.ceil((latestMin + buffer) / 30) * 30)

      const startH = Math.floor(paddedStart / 60)
      const startM = paddedStart % 60
      const endH = Math.floor(paddedEnd / 60)
      const endM = paddedEnd % 60

      return {
        start: `${String(startH).padStart(2, "0")}:${String(startM).padStart(2, "0")}`,
        end: `${String(endH).padStart(2, "0")}:${String(endM).padStart(2, "0")}`,
      }
    },
    timeSlots() {
      const slots = []
      const startMin = this.getCalendarStartMinutes
      const endMin = this.getCalendarEndMinutes
      const step = this.calendarTimeStep || 60

      let current = startMin

      // Calculate first full hour alignment mark
      const firstHourMin = Math.ceil(startMin / 60) * 60

      if (firstHourMin > startMin && firstHourMin < endMin) {
        const diff = firstHourMin - startMin
        const hours = Math.floor(startMin / 60)
        const mins = startMin % 60
        const timeStr = `${String(hours).padStart(2, "0")}:${String(mins).padStart(2, "0")}`

        slots.push({
          minutes: startMin,
          time: timeStr,
          label: timeStr,
          showLabel: false, // Hide label so the first interval text starts at the full hour
          height: (diff / 60) * this.hourHeight,
          top: 0,
        })
        current = firstHourMin
      }

      while (current < endMin) {
        const hours = Math.floor(current / 60)
        const mins = current % 60
        const timeStr = `${String(hours).padStart(2, "0")}:${String(mins).padStart(2, "0")}`

        const showLabel = step >= 30 || mins === 0

        const nextVal = Math.min(endMin, current + step)
        const slotDuration = nextVal - current

        slots.push({
          minutes: current,
          time: timeStr,
          label: timeStr,
          showLabel,
          height: (slotDuration / 60) * this.hourHeight,
          top: ((current - startMin) / 60) * this.hourHeight,
        })
        current = nextVal
      }
      return slots
    },
    hoursCount() {
      const startMin = this.getCalendarStartMinutes
      const endMin = this.getCalendarEndMinutes
      return (endMin - startMin) / 60
    },
    hourHeight() {
      if (this.customHourHeight === "fit") {
        const count = this.hoursCount || 12
        const availableHeight = this.scrollerHeight - this.headerOffset
        const calculated = availableHeight / count
        return Math.max(40, Math.floor(calculated))
      }
      if (this.customHourHeight) {
        return this.customHourHeight
      }
      return this.$vuetify.breakpoint.smAndDown ? 70 : 100
    },
    hours() {
      const h = []
      for (let i = this.startHour; i <= this.endHour; i++) h.push(i)
      return h
    },
    isToday() {
      return moment(this.currentDate).isSame(moment(), "day")
    },
    hasAppointmentManagementRight() {
      return userService.hasRight("sm_appointment_management")
    },
    hasTaskManagementRight() {
      return userService.hasRight("c_create", "sm_appointment_management", "sm_create")
    },
    hasCleaningTaskMergeRight() {
      return userService.hasRight("g_studioadmin", "sm_create", "sm_appointment_management")
    },
    hasGuestbookEntryRight() {
      return GUESTBOOK_ENTRY_NAV_VISIBLE && userService.hasRight("ab_create", "c_create")
    },
    hasEmployeeManagerRight() {
      return userService.hasRight("g_employeemanager")
    },
    mobileViewColumns() {
      const cols = (this.viewColumns || []).filter((c) => c !== null && c !== undefined)
      if (this.$vuetify && this.$vuetify.breakpoint && this.$vuetify.breakpoint.smAndDown) {
        if (cols.length === 0) return []
        const maxIndex = Math.max(0, cols.length - 1)
        const safeIndex = Math.min(this.activeMobileColIndex, maxIndex)
        return [cols[safeIndex]].filter((c) => c !== null && c !== undefined)
      }
      return cols
    },
    // Mobile template lists (same data as sidebar, but used in the bottom sheet)
    mobileTrainingTemplates() {
      return this.mobileFilterBySearch([
        {
          id: "w8",
          title: "Beratungstermin",
          icon: "mdi-account-tie",
          color: "#d4b72e",
          duration: 60,
          type: "appointment",
          specialType: "consultation",
          requiresCustomer: true,
        },
        {
          id: "w1",
          title: "Probetraining",
          icon: "mdi-account-star",
          color: "#d4b72e",
          duration: 60,
          type: "appointment",
          specialType: "trial",
          requiresCustomer: true,
        },
        {
          id: "w11d",
          title: "Abschlusstermin 11 Tage",
          icon: "mdi-calendar-check",
          color: "#009688",
          duration: 60,
          type: "appointment",
          specialType: "abschlusstermin_11d",
          requiresCustomer: true,
        },
        {
          id: "w9",
          title: "Trainingsplan",
          icon: "mdi-clipboard-list",
          color: "#397f9e",
          duration: 60,
          type: "appointment",
          specialType: "trainingplan",
          requiresCustomer: true,
        },
        {
          id: "w4",
          title: "Kontrolltermin",
          icon: "mdi-calendar-check",
          color: "#c66d78",
          duration: 30,
          type: "appointment",
          specialType: "check",
          requiresCustomer: true,
        },
        {
          id: "w2",
          title: "Einführungstraining",
          icon: "mdi-dumbbell",
          color: "#3d8c70",
          duration: 60,
          type: "appointment",
          specialType: "intro",
          requiresCustomer: true,
        },
        {
          id: "w2b",
          title: "Ergänzungstraining",
          icon: "mdi-dumbbell",
          color: "#d46f3a",
          duration: 30,
          type: "appointment",
          specialType: "followup",
          requiresCustomer: true,
        },
        {
          id: "w2d",
          title: "Personal Training",
          icon: "mdi-run",
          color: "#168b8d",
          duration: 90,
          type: "appointment",
          specialType: "workout",
          tacServiceType: "personal_training",
          requiresCustomer: true,
        },
        {
          id: "w2e",
          title: "Massage",
          icon: "mdi-spa",
          color: "#8E24AA",
          duration: 45,
          type: "appointment",
          specialType: "massage",
          tacServiceType: "massage",
          requiresCustomer: true,
        },
        {
          id: "w5",
          title: "Milon Einführung",
          icon: "mdi-sync",
          color: "#a65086",
          duration: 60,
          type: "appointment",
          specialType: "milon",
          requiresCustomer: true,
        },
        {
          id: "w10",
          title: "Mescan",
          icon: "mdi-clipboard-pulse",
          color: "#00ACC1",
          duration: 30,
          type: "appointment",
          specialType: "anamnese",
          requiresCustomer: true,
        },
        {
          id: "w11",
          title: "PelviX",
          icon: "mdi-clipboard-pulse",
          color: "#00ACC1",
          duration: 30,
          type: "appointment",
          specialType: "anamnese",
          requiresCustomer: true,
        },
        {
          id: "w6",
          title: "Ernährungsberatung",
          icon: "mdi-food-apple",
          color: "#356fa3",
          duration: 60,
          type: "appointment",
          specialType: "nutrition",
          requiresCustomer: true,
        },
      ])
    },
    mobileContractTemplates() {
      return this.mobileFilterBySearch([
        {
          id: "a1",
          title: "Vertrag erstellen",
          icon: "mdi-file-document-edit",
          color: "#37474F",
          duration: 30,
          type: "appointment",
          specialType: "new_contract",
          requiresCustomer: false,
        },
        {
          id: "a2",
          title: "Upgrade / Anpassung",
          icon: "mdi-star-circle",
          color: "#455A64",
          duration: 30,
          type: "appointment",
          specialType: "upgrade",
          requiresCustomer: true,
        },
        {
          id: "w7",
          title: "Anamnese / Checkup",
          icon: "mdi-clipboard-pulse",
          color: "#00ACC1",
          duration: 30,
          type: "appointment",
          specialType: "anamnese",
          requiresCustomer: true,
        },
      ])
    },
    mobileSchulungTemplates() {
      return this.mobileFilterBySearch([
        {
          id: "s1",
          title: "Schulung",
          icon: "mdi-school",
          color: "#E65100",
          duration: 60,
          type: "appointment",
          requiresCustomer: false,
        },
        {
          id: "s2",
          title: "Teamsitzung",
          icon: "mdi-account-group",
          color: "#FF9800",
          duration: 60,
          type: "appointment",
          requiresCustomer: false,
        },
      ])
    },
    mobileStandardTemplates() {
      return this.mobileFilterBySearch([
        { ...onlineMeetingTemplate },
        { id: "ap1", title: "Termin", icon: "mdi-calendar", color: "#00963f", duration: 60, type: "appointment", requiresCustomer: true },
      ])
    },
    mobileCustomAppointmentTemplates() {
      return this.mobileFilterBySearch(this.templates.filter((t) => t.category === "termin" || (!t.category && t.type === "appointment")))
    },
    mobileCustomTaskTemplates() {
      // Ämtli (nur konfigurierte)
      return this.templates.filter((t) => (t.category === "ämtli" || (!t.category && t.type === "task")) && t.enabledInStudio)
    },
    mobileWeeklyTaskTemplates() {
      return this.mobileFilterBySearch(this.mobileCustomTaskTemplates.filter((t) => t.frequency === "weekly"))
    },
    mobileManualTaskTemplates() {
      return this.mobileFilterBySearch(this.mobileCustomTaskTemplates.filter((t) => t.frequency === "once"))
    },
    mobileManualTasks() {
      return this.mobileManualTaskTemplates.map((tpl) => {
        const templateId = String(tpl._id || tpl.id || "")
        let isAssigned = false

        for (const col of this.viewColumns) {
          if (isAssigned || !Array.isArray(col.items)) continue
          isAssigned = col.items.some((item) => {
            if (!item.isTask) return false
            return taskMatchesTemplate(item, tpl)
          })
        }

        return {
          ...tpl,
          id: `${templateId}-manual`,
          templateId,
          isAssigned,
        }
      })
    },
    mobileMonthlyTaskTemplates() {
      return this.mobileFilterBySearch(this.mobileCustomTaskTemplates.filter((t) => t.frequency === "monthly"))
    },
    mobileTaskTemplates() {
      // Aufgabe
      return this.mobileFilterBySearch(this.templates.filter((t) => t.category === "aufgabe"))
    },
    mobileUnsupervisedTemplates() {
      // Unbetreut
      return this.mobileFilterBySearch(this.templates.filter((t) => t.category === "info"))
    },
    // Ämtli die für heute noch offen sind (basierend auf defaultTimes oder 1x täglich)
    dailyTasks() {
      const tasks = []
      this.mobileCustomTaskTemplates.forEach((tpl) => {
        if (tpl.frequency && tpl.frequency !== "daily") return

        // Finde die konkreten Instanzen dieser Vorlage. Die templateId ist die
        // verlässliche Zuordnung; der Titel bleibt nur für alte Legacy-Einträge
        // ohne templateId als Fallback bestehen.
        const templateId = String(tpl._id || tpl.id || "")
        let assignedItems = []
        for (const col of this.viewColumns) {
          if (col.items) {
            col.items.forEach((item) => {
              const raw = item.rawData || {}
              const matchesTemplate = taskMatchesTemplate(item, tpl)
              if (item.isTask && matchesTemplate) {
                assignedItems.push({
                  employeeName: col.info ? col.info.fullName || col.info.firstName : null,
                  time: item.start,
                  isDone: Boolean(raw.isDone || item.isDone || item.status === "completed" || item.status === "done"),
                })
              }
            })
          }
        }
        assignedItems.sort((a, b) => String(a.time || "").localeCompare(String(b.time || "")))

        if (tpl.defaultTimes && tpl.defaultTimes.length > 0) {
          const usedAssignedItems = new Set()
          tpl.defaultTimes.forEach((time, index) => {
            // Konfigurierte Uhrzeiten bleiben für ihre eigene Instanz reserviert.
            // Nur abweichende Zeiten dürfen als ältere/verschobene Einträge zählen.
            let assignedIndex = assignedItems.findIndex((item, itemIndex) => !usedAssignedItems.has(itemIndex) && item.time === time)
            if (assignedIndex === -1) {
              assignedIndex = assignedItems.findIndex(
                (item, itemIndex) => !usedAssignedItems.has(itemIndex) && !tpl.defaultTimes.includes(item.time),
              )
            }
            if (assignedIndex !== -1) usedAssignedItems.add(assignedIndex)
            const assignedItem = assignedIndex !== -1 ? assignedItems[assignedIndex] : null
            const isAssigned = Boolean(assignedItem)
            const isDone = Boolean(assignedItem && assignedItem.isDone)

            tasks.push({
              ...tpl,
              id: templateId + "-" + time, // Eindeutige ID für die Instanz
              templateId,
              time: time,
              assignedTime: assignedItem ? assignedItem.time : "",
              isAssigned: isAssigned,
              isDone,
              assignedTo: assignedItem ? assignedItem.employeeName : null,
            })
          })
        } else {
          // Keine festen Uhrzeiten -> 1x täglich manuell
          const assignedItem = assignedItems[0] || null
          const isAssigned = Boolean(assignedItem)
          const isDone = Boolean(assignedItem && assignedItem.isDone)

          tasks.push({
            ...tpl,
            id: templateId + "-daily",
            templateId,
            time: isAssigned ? "1/1 eingeplant" : "0/1 eingeplant",
            assignedTime: assignedItem ? assignedItem.time : "",
            isAssigned: isAssigned,
            isDone,
            assignedTo: assignedItem ? assignedItem.employeeName : null,
            isManualDaily: true,
          })
        }
      })
      // Sortiere nach Status (vergeben nach unten) und dann nach Zeit
      tasks.sort((a, b) => {
        if (a.isAssigned !== b.isAssigned) {
          return a.isAssigned ? 1 : -1
        }
        return a.time.localeCompare(b.time)
      })
      return tasks
    },
    // Performance: Kalender-Grid verstecken wenn Fullscreen-Dialog auf Mobile offen ist
    mobileDialogActive() {
      if (!this.$vuetify || !this.$vuetify.breakpoint || !this.$vuetify.breakpoint.mdAndDown) return false
      return this.editDialog || this.dropDialog || this.mobileTemplateSheet || this.taskAssignDialog
    },
    openDailyTasksCount() {
      if (!this.dailyTasks) return 0
      return this.dailyTasks.filter((t) => !t.isAssigned).length
    },
    draftTasks() {
      // Neben echten Entwürfen auch alte, versehentlich bereits veröffentlichte
      // Auto-Ergebnisse ohne Mitarbeiter auffangen. Sie bleiben hier
      // bearbeitbar, sind aber in keinem normalen Kalender-Read sichtbar.
      return (this.taskDashboardTasks || []).filter((t) => this.isAmtliCategory(t) && this.isTaskPlanningProposal(t))
    },
    publishableDraftTasks() {
      return this.draftTasks.filter((t) => t.status === "draft" && Boolean(t.assignedEmployee))
    },
    draftTasksGrouped() {
      if (!this.draftTasks || this.draftTasks.length === 0) return []
      const map = {}
      this.draftTasks.forEach((t) => {
        if (!map[t.date]) map[t.date] = []
        map[t.date].push(t)
      })
      return Object.keys(map)
        .sort()
        .map((date) => {
          const dayTasks = map[date]
          const totalCount = dayTasks.length
          const assignedCount = dayTasks.filter((t) => t.assignedEmployee).length
          const unassignedCount = totalCount - assignedCount
          const totalDuration = dayTasks.reduce((sum, t) => sum + (t.duration || 30), 0)
          const percent = totalCount > 0 ? Math.round((assignedCount / totalCount) * 100) : 0

          // Calculate free appointment capacity
          const dayShifts = (this.taskDashboardShifts || []).filter((s) => s.date === date)
          let totalShiftMinutes = 0
          dayShifts.forEach((s) => {
            this.getShiftWorkMinuteIntervals(s).forEach((interval) => {
              totalShiftMinutes += interval.endMin - interval.startMin
            })
          })
          const openingClosingMinutes = this.getOpeningClosingMinutes(dayShifts)

          // Subtract duration of all assigned tasks on that day
          const dayAllTasks = (this.taskDashboardTasks || []).filter((t) => t.date === date && this.isAmtliCategory(t))
          const totalAssignedTaskMinutes = dayAllTasks.reduce((sum, t) => sum + (t.assignedEmployee ? t.duration || 30 : 0), 0)

          // Subtract duration of all active appointments on that day
          const dayAppts = (this.taskDashboardAppointments || []).filter((a) => a.date === date)
          let totalApptMinutes = 0
          dayAppts.forEach((a) => {
            if (a.start && a.end) {
              const [sh, sm] = a.start.split(":").map(Number)
              const [eh, em] = a.end.split(":").map(Number)
              const startMins = sh * 60 + sm
              let endMins = eh * 60 + em
              if (endMins < startMins) endMins += 1440
              totalApptMinutes += endMins - startMins
            }
          })

          const freeMinutes = Math.max(0, totalShiftMinutes - openingClosingMinutes - totalAssignedTaskMinutes - totalApptMinutes)
          const freeHours = (freeMinutes / 60).toFixed(1)

          return {
            date: date,
            formattedDate: this.$moment(date).format("dddd, DD. MMMM YYYY"),
            tasks: dayTasks.sort((a, b) => a.time.localeCompare(b.time)),
            totalCount,
            assignedCount,
            unassignedCount,
            totalDuration,
            percent,
            freeHours,
          }
        })
    },
    unassignedTasks() {
      const baseList = (this.taskDashboardTasks || []).filter((t) => !t.assignedEmployee && this.isAmtliCategory(t))
      if (this.unassignedFilter === "missing_time") {
        return baseList.filter((t) => {
          const reason = (t.unassignedReason || "").toLowerCase()
          return reason.includes("besetzt") || reason.includes("überlappung") || reason.includes("freie schichtzeit")
        })
      } else if (this.unassignedFilter === "missing_shift") {
        return baseList.filter((t) => {
          const reason = (t.unassignedReason || "").toLowerCase()
          return reason.includes("schicht")
        })
      }
      return baseList
    },
    unassignedCountAll() {
      return (this.taskDashboardTasks || []).filter((t) => !t.assignedEmployee && this.isAmtliCategory(t)).length
    },
    unassignedCountMissingTime() {
      return (this.taskDashboardTasks || []).filter((t) => {
        const isUnassigned = !t.assignedEmployee && this.isAmtliCategory(t)
        const reason = (t.unassignedReason || "").toLowerCase()
        const isMissingTime = reason.includes("besetzt") || reason.includes("überlappung") || reason.includes("freie schichtzeit")
        return isUnassigned && isMissingTime
      }).length
    },
    unassignedCountMissingShift() {
      return (this.taskDashboardTasks || []).filter((t) => {
        const isUnassigned = !t.assignedEmployee && this.isAmtliCategory(t)
        const reason = (t.unassignedReason || "").toLowerCase()
        const isMissingShift = reason.includes("schicht")
        return isUnassigned && isMissingShift
      }).length
    },
    overdueTasks() {
      const todayStr = this.$moment().format("YYYY-MM-DD")
      return (this.taskDashboardTasks || []).filter(
        (t) => t.status !== "draft" && Boolean(t.assignedEmployee) && !t.isDone && t.date < todayStr && this.isAmtliCategory(t),
      )
    },
    employeeStats() {
      const statsMap = {}
      this.employeeOptions.forEach((opt) => {
        statsMap[opt.value] = {
          id: opt.value,
          name: opt.text,
          totalCount: 0,
          completedCount: 0,
          draftCount: 0,
          publishedCount: 0,
        }
      })
      ;(this.taskDashboardTasks || [])
        .filter((t) => this.isAmtliCategory(t))
        .forEach((t) => {
          const empId = t.assignedEmployee?._id || t.assignedEmployee
          if (empId && statsMap[empId]) {
            statsMap[empId].totalCount++
            if (t.status === "draft") {
              statsMap[empId].draftCount++
            } else {
              statsMap[empId].publishedCount++
              if (t.isDone) {
                statsMap[empId].completedCount++
              }
            }
          }
        })
      return Object.values(statsMap).sort((a, b) => b.totalCount - a.totalCount)
    },
    dashboardSummaryMetrics() {
      const tasks = (this.taskDashboardTasks || []).filter((t) => this.isAmtliCategory(t))
      const total = tasks.length
      const drafts = tasks.filter((t) => this.isTaskPlanningProposal(t)).length
      const publishedTasks = tasks.filter((t) => t.status !== "draft" && Boolean(t.assignedEmployee))
      const published = publishedTasks.length
      const completed = publishedTasks.filter((t) => t.isDone).length
      const unassigned = tasks.filter((t) => !t.assignedEmployee).length
      const completionRate = published > 0 ? Math.round((completed / published) * 100) : 0
      return {
        total,
        drafts,
        published,
        completed,
        unassigned,
        completionRate,
      }
    },
    masterAmtliPool() {
      const list = this.templates || []
      return list.filter((t) => !t.parentTemplateId && !t.isGroupedVariant && (t.category === "ämtli" || (!t.category && t.type === "task")))
    },
    employeeDashboardStats() {
      const stats = {}
      this.employeeOptions.forEach((opt) => {
        stats[opt.value] = {
          id: opt.value,
          name: opt.text,
          amtliCount: 0,
          amtliDuration: 0,
          shiftDuration: 0,
          workloadPercent: 0,
          teamSharePercent: 0,
        }
      })

      const shifts = this.taskDashboardShifts || []
      shifts.forEach((s) => {
        const empId = s.assignedEmployee?._id || s.assignedEmployee
        if (empId && stats[empId]) {
          const times = s.times || (s.shiftTemplate ? s.shiftTemplate.times : [])
          if (Array.isArray(times)) {
            times.forEach((t) => {
              if (t.start && t.end) {
                const [sh, sm] = t.start.split(":").map(Number)
                const [eh, em] = t.end.split(":").map(Number)
                const startMins = sh * 60 + sm
                const endMins = eh * 60 + em
                if (endMins > startMins) {
                  stats[empId].shiftDuration += endMins - startMins
                }
              }
            })
          }
        }
      })

      const tasks = (this.taskDashboardTasks || []).filter((t) => this.isAmtliCategory(t))
      let totalAssignedAmtliCount = 0

      tasks.forEach((t) => {
        const empId = t.assignedEmployee?._id || t.assignedEmployee
        if (empId && stats[empId]) {
          stats[empId].amtliCount++
          stats[empId].amtliDuration += t.duration || 30
          totalAssignedAmtliCount++
        }
      })

      Object.keys(stats).forEach((empId) => {
        const emp = stats[empId]
        if (emp.shiftDuration > 0) {
          emp.workloadPercent = Math.round((emp.amtliDuration / emp.shiftDuration) * 100)
        } else {
          emp.workloadPercent = 0
        }
        if (totalAssignedAmtliCount > 0) {
          emp.teamSharePercent = Math.round((emp.amtliCount / totalAssignedAmtliCount) * 100)
        } else {
          emp.teamSharePercent = 0
        }
      })

      return stats
    },
  },
  async mounted() {
    // Read date from query param (e.g. from EmployeeCard "Zum Kalender" shortcut)
    if (this.$route && this.$route.query && this.$route.query.date) {
      this.currentDate = this.$route.query.date
    } else {
      this.currentDate = moment().format("YYYY-MM-DD")
    }
    this.$nextTick(() => {
      this.updateScrollerHeight()
    })
    this.updateTimeLine()
    window.addEventListener("resize", this.updateScrollerHeight)
    window.addEventListener("dragend", this.onDragEndGlobal)
    document.addEventListener("visibilitychange", this.onCalendarVisibilityChange)
    this.startClock()

    // Studio permissions and calendar payloads are independent from the
    // current-time marker and must not delay its first render.
    await this.loadAllowedStudios()
    this.fetchData()
    this.fetchLiveStatus()
  },
  beforeDestroy() {
    this._calendarDisposed = true
    this.fetchDataRequestId += 1
    this._calendarRefreshQueue?.dispose()
    this._calendarReferenceCache?.invalidate()
    if (this.timerInterval) clearInterval(this.timerInterval)
    if (this._smartScrollTimer) clearTimeout(this._smartScrollTimer)
    if (this._calendarLayoutRebuildTimer) clearTimeout(this._calendarLayoutRebuildTimer)
    this.teardownCalendarSocket()
    window.removeEventListener("dragend", this.onDragEndGlobal)
    window.removeEventListener("resize", this.updateScrollerHeight)
    document.removeEventListener("visibilitychange", this.onCalendarVisibilityChange)
  },
  methods: {
    taskVariantLabels,
    invalidateCalendarReferences() {
      this._calendarReferenceCache?.invalidate()
    },
    async updateCalendarShift(...args) {
      try {
        return await api.shiftManagement.updateShift(...args)
      } finally {
        // Auch ohne Socket-Verbindung nach eigenen Änderungen neu laden.
        this.invalidateCalendarReferences()
      }
    },
    async createCalendarSickFromTransfer(...args) {
      try {
        return await api.shiftManagement.createSickFromTransfer(...args)
      } finally {
        this.invalidateCalendarReferences()
      }
    },
    onCalendarVisibilityChange() {
      if (document.visibilityState === "visible") {
        this.invalidateCalendarReferences()
        this.fetchData(true)
      }
    },
    employeeShiftChangeMetadata(reason = "") {
      if (userService.hasRight("stat_viewstudio")) {
        return {
          changeSource: "management",
          ...(reason ? { changeReason: reason } : {}),
        }
      }

      const selectedEmployee = this.getSelectedEmployee || {}
      const selectedEmployeeUser =
        selectedEmployee.user && typeof selectedEmployee.user === "object" ? selectedEmployee.user._id : selectedEmployee.user
      const actingEmployeeId = selectedEmployeeUser || selectedEmployee._id || selectedEmployee.id || null
      return {
        changeSource: "employee_self",
        ...(actingEmployeeId ? { actingEmployeeId: String(actingEmployeeId) } : {}),
        ...(reason ? { changeReason: reason } : {}),
      }
    },
    templateRestrictionCategory(template) {
      const category = String(template?.category || "")
        .trim()
        .toLocaleLowerCase("de-CH")
      if (["ämtli", "aufgabe", "termin", "info"].includes(category)) return category
      if (template?.type === "task") return "ämtli"
      if (template?.type === "appointment") return "termin"
      return null
    },
    appointmentTemplateKey(template) {
      const rawValue = template?._id || template?.id || template?.templateId?._id || template?.templateId
      return String(rawValue || "").trim()
    },
    getAppointmentRestriction(templateOrKey) {
      const embedded = typeof templateOrKey === "object" ? templateOrKey?.appointmentRestriction : null
      const key = typeof templateOrKey === "string" ? templateOrKey : this.appointmentTemplateKey(templateOrKey)
      const source =
        embedded || (this.appointmentTemplateRestrictions || []).find((restriction) => String(restriction.templateKey || "") === String(key || ""))
      return {
        templateKey: key,
        templateTitle: source?.templateTitle || (typeof templateOrKey === "object" ? templateOrKey?.title : "") || "",
        excludeApprentices: source?.excludeApprentices === true,
        excludedEmployees: (source?.excludedEmployees || []).map((employee) => String(employee?._id || employee || "")).filter(Boolean),
      }
    },
    withAppointmentRestriction(template) {
      if (!template) return template
      return {
        ...template,
        appointmentRestriction: this.getAppointmentRestriction(template),
      }
    },
    appointmentEmployeeRestrictionMessage(template, employeeOrColumn) {
      if (!template || template.type === "pause" || template.isPause || template.specialType === "pause") return ""
      const employeeId = String(
        employeeOrColumn?.info?._id ||
          employeeOrColumn?.user?._id ||
          employeeOrColumn?.user ||
          employeeOrColumn?.id ||
          employeeOrColumn?._id ||
          employeeOrColumn ||
          "",
      )
      if (!employeeId || ["ma_info", "no_show", "unassigned", "none"].includes(employeeId)) return ""

      const restriction = this.getAppointmentRestriction(template)
      const title = template.title || restriction.templateTitle || "Diese Vorlage"
      if (restriction.excludedEmployees.includes(employeeId)) {
        return `Die Vorlage „${title}“ ist für diesen Mitarbeiter gesperrt.`
      }

      if (restriction.excludeApprentices) {
        const employee = (this.allEmployeesForHover || []).find(
          (candidate) => String(candidate?.user?._id || candidate?.user || candidate?._id || "") === employeeId,
        )
        if (employee?.isApprentice === true || employeeOrColumn?.isApprentice === true || employeeOrColumn?.info?.isApprentice === true) {
          return `Die Vorlage „${title}“ ist für Lernende gesperrt.`
        }
      }
      return ""
    },
    findEligibleAppointmentColumn(template, preferredColumn = null) {
      const candidates = [preferredColumn, ...(this.viewColumns || [])].filter(Boolean)
      const seen = new Set()
      return candidates.find((column) => {
        const id = String(column?.info?._id || column?.id || "")
        // MA-Info is a neutral staging column, not an employee assignment.
        // It must remain available even when the work plan has no shifts yet.
        if (!id || seen.has(id) || ["no_show", "unassigned"].includes(id)) return false
        seen.add(id)
        return !this.appointmentEmployeeRestrictionMessage(template, column)
      })
    },
    async openAppointmentRestrictions() {
      if (!this.hasEmployeeManagerRight) {
        this.$toast.error("Dir fehlt das Recht, Vorlagen-Zugriffe zu verwalten.")
        return
      }
      this.appointmentRestrictionsDialog = true
      this.appointmentRestrictionsLoading = true
      try {
        const restrictions = await api.shiftManagement.fetchAppointmentTemplateRestrictions()
        this.appointmentTemplateRestrictions = Array.isArray(restrictions) ? restrictions : []
        if (
          !this.selectedAppointmentRestrictionKey ||
          !this.appointmentRestrictionTemplateOptions.some((item) => item.value === this.selectedAppointmentRestrictionKey)
        ) {
          this.selectedAppointmentRestrictionKey = this.appointmentRestrictionTemplateOptions.find((item) => item.value)?.value || null
        }
        this.loadAppointmentRestrictionDraft()
      } catch (error) {
        this.$toast.error(error?.response?.data?.message || "Vorlagen-Zugriffe konnten nicht geladen werden.")
      } finally {
        this.appointmentRestrictionsLoading = false
      }
    },
    openAppointmentRestrictionsFromOptions() {
      this.trainerControlMenuOpen = false
      this.$nextTick(() => this.openAppointmentRestrictions())
    },
    loadAppointmentRestrictionDraft() {
      const restriction = this.getAppointmentRestriction(this.selectedAppointmentRestrictionKey)
      this.appointmentRestrictionDraft = {
        excludeApprentices: restriction.excludeApprentices,
        excludedEmployees: [...restriction.excludedEmployees],
      }
    },
    async saveAppointmentRestriction() {
      if (!this.hasEmployeeManagerRight || !this.selectedAppointmentRestrictionKey || this.appointmentRestrictionsSaving) return
      const template = (this.appointmentRestrictionTemplates || []).find(
        (candidate) => this.appointmentTemplateKey(candidate) === String(this.selectedAppointmentRestrictionKey),
      )
      this.appointmentRestrictionsSaving = true
      try {
        const saved = await api.shiftManagement.updateAppointmentTemplateRestriction(this.selectedAppointmentRestrictionKey, {
          templateTitle: template?.title || "",
          excludeApprentices: this.appointmentRestrictionDraft.excludeApprentices === true,
          excludedEmployees: [...(this.appointmentRestrictionDraft.excludedEmployees || [])],
        })
        const nextRestrictions = [...(this.appointmentTemplateRestrictions || [])]
        const existingIndex = nextRestrictions.findIndex(
          (restriction) => String(restriction.templateKey || "") === String(this.selectedAppointmentRestrictionKey),
        )
        if (existingIndex === -1) nextRestrictions.push(saved)
        else nextRestrictions.splice(existingIndex, 1, saved)
        this.appointmentTemplateRestrictions = nextRestrictions
        this.loadAppointmentRestrictionDraft()
        this.$toast.success("Vorlagen-Zugriff gespeichert.")
      } catch (error) {
        this.$toast.error(error?.response?.data?.message || "Vorlagen-Zugriff konnte nicht gespeichert werden.")
      } finally {
        this.appointmentRestrictionsSaving = false
      }
    },
    changeStudio() {
      window.app.$root.$children[0].showStudioSelection = true
    },
    isAmtliCategory(task) {
      if (!task) return false
      if (task.templateId) {
        const found = (this.templates || []).find((t) => String(t._id || t.id) === String(task.templateId))
        if (found) {
          return found.category === "ämtli" || found.category === "aufgabe" || (!found.category && found.type === "task")
        }
      }
      return task.category === "ämtli" || task.category === "aufgabe" || (!task.category && task.type === "task")
    },
    isTaskPlanningProposal(task) {
      if (!task) return false
      if (task.status === "draft") return true
      const helperColumn = String(task.helperColumn || "none")
      return !task.assignedEmployee && helperColumn !== "ma_info" && helperColumn !== "no_show"
    },
    getEmployeeStatsText(empId) {
      if (!empId) return ""
      const cleanEmpId = typeof empId === "object" ? empId._id || empId : empId
      const stats = this.employeeDashboardStats[cleanEmpId]
      if (!stats) return ""
      return `${stats.amtliCount} Ämtli (${stats.workloadPercent}% d. Arbeitszeit | ${stats.teamSharePercent}% Team-Anteil)`
    },
    updateScrollerHeight() {
      if (this.$refs.scroller) {
        this.scrollerHeight = this.$refs.scroller.clientHeight
        this.updateTimeLine()
      }
    },
    scheduleCalendarLayoutRebuild() {
      if (this._calendarLayoutRebuildTimer) clearTimeout(this._calendarLayoutRebuildTimer)
      this._calendarLayoutRebuildTimer = setTimeout(() => {
        this._calendarLayoutRebuildTimer = null
        this.processViewData()
        this.updateTimeLine()
        this.syncTimeAxisToScroller()
      }, 60)
    },
    ...mapActions("studioStore", ["selectEmployee"]),

    groupTasksByEmployee(tasks) {
      if (!tasks || tasks.length === 0) return []
      const groups = {}
      const unassigned = []

      tasks.forEach((t) => {
        if (t.type === "unassigned" || !t.employeeName || t.employeeName.startsWith("Nicht zugewiesen")) {
          unassigned.push(t)
        } else {
          const name = t.employeeName
          if (!groups[name]) groups[name] = []
          groups[name].push(t)
        }
      })

      const result = Object.keys(groups)
        .sort()
        .map((name) => {
          return {
            employeeName: name,
            tasks: groups[name].sort((a, b) => (a.time || "").localeCompare(b.time || "")),
          }
        })

      if (unassigned.length > 0) {
        result.push({
          employeeName: "Nicht zugewiesen / Warnungen",
          isUnassigned: true,
          tasks: unassigned.sort((a, b) => (a.time || "").localeCompare(b.time || "")),
        })
      }

      return result
    },

    onEmployeeChipClick() {
      if (this.canSwitchEmployee) {
        this.$router.push("/select-employee")
      }
    },
    syncMobileColumnWithEmployee() {
      if (this.$vuetify.breakpoint.smAndDown && this.getSelectedEmployee && this.viewColumns && this.viewColumns.length > 0) {
        const activeUserId = String(this.getSelectedEmployee.user)
        const colIdx = this.viewColumns.findIndex((col) => String(col.id) === activeUserId)
        if (colIdx !== -1) {
          this.activeMobileColIndex = colIdx
        }
      }
    },
    syncEmployeeToActiveColumn() {
      const col = this.mobileViewColumns[0]
      if (!col) return
      const emp = this.allEmployeesForHover.find((e) => String(e.user?._id || e.user) === String(col.id))
      if (emp) {
        const targetEmp = emp.user && typeof emp.user === "object" ? emp.user : emp
        const names = (targetEmp.fullName || "").split(" ")
        const formattedEmp = {
          user: emp.user?._id || emp.user || targetEmp._id || targetEmp.id || targetEmp,
          fullName:
            targetEmp.fullName || `${targetEmp.vorname || targetEmp.firstName || ""} ${targetEmp.nachname || targetEmp.lastName || ""}`.trim(),
          vorname: targetEmp.vorname || targetEmp.firstName || names[0] || "",
          nachname: targetEmp.nachname || targetEmp.lastName || names.slice(1).join(" ") || "",
          picture: targetEmp.picture,
        }
        this.selectEmployee(formattedEmp)
        this.$toast.success(`Aktiver Trainer geändert zu ${formattedEmp.fullName}`)
      } else {
        const emp2 = this.employeeList.find((e) => String(e.id) === String(col.id))
        if (emp2) {
          const names = (emp2.name || "").split(" ")
          const formattedEmp = {
            user: emp2.id,
            fullName: emp2.name,
            vorname: names[0] || "",
            nachname: names.slice(1).join(" ") || "",
            picture: emp2.picture,
          }
          this.selectEmployee(formattedEmp)
          this.$toast.success(`Aktiver Trainer geändert zu ${formattedEmp.fullName}`)
        }
      }
    },
    triggerSearch() {
      this.customerSearchDialog = true
    },
    triggerLeads() {
      this.$router.push("/dashboard/leads/all")
    },
    trigger11Days() {
      this.$router.push({ name: "MembercardView", query: { date: this.currentDate } })
    },
    triggerTowel() {
      this.showTowelAdminDialog = true
    },
    triggerLicensePlate() {
      this.showLicensePlates = true
    },
    hideSickColumn(trainerId) {
      const idStr = String(trainerId)
      if (!this.hiddenSickTrainerIds.includes(idStr)) {
        this.hiddenSickTrainerIds.push(idStr)
        this.$toast.success("Spalte wurde vorübergehend ausgeblendet.")
        this.processViewData()
      }
    },
    showSickColumn(trainerId) {
      const idStr = String(trainerId)
      this.hiddenSickTrainerIds = this.hiddenSickTrainerIds.filter((id) => id !== idStr)
      this.$toast.success("Spalte wird wieder angezeigt.")
      this.processViewData()
    },
    openSortDialog() {
      const list = [...this.allEmployeesForHover]
      list.sort((a, b) => {
        const studioA = a.studio && a.studio[0]
        const studioB = b.studio && b.studio[0]
        const sortA = studioA ? studioA.workCalendarSort ?? 9999 : 9999
        const sortB = studioB ? studioB.workCalendarSort ?? 9999 : 9999
        if (sortA !== sortB) return sortA - sortB
        return (a.fullName || "").localeCompare(b.fullName || "")
      })
      this.sortedEmployeeList = list
      this.sortEmployeesDialog = true
    },
    async saveSortOrder() {
      this.savingSortOrder = true
      try {
        const employeeIds = this.sortedEmployeeList.map((e) => e._id)
        await api.shiftManagement.saveEmployeeSortOrder(employeeIds)
        this.invalidateCalendarReferences()
        this.$toast.success("Mitarbeiter-Reihenfolge erfolgreich gespeichert")
        this.sortEmployeesDialog = false
        this.fetchData(true)
      } catch (err) {
        console.error(err)
        this.$toast.error("Fehler beim Speichern der Reihenfolge")
      } finally {
        this.savingSortOrder = false
      }
    },
    async changeStudioOffset(val) {
      const offset = parseInt(val, 10)
      const studioId = this.selectedStudio?._id || this.selectedStudio?.id
      if (!studioId) return
      try {
        localStorage.setItem(`wc_studio_offset_${studioId}`, String(offset))
        await api.shiftManagement.updateStudioOffset(studioId, offset)
        this.invalidateCalendarReferences()
        if (this.selectedStudio) {
          this.$store.commit("studioStore/SET_SELECTED_STUDIO", {
            ...this.selectedStudio,
            openingClosingOffset: offset,
          })
        }
        this.$toast.success("Studio-Einstellung erfolgreich gespeichert")
        this.fetchData(true)
      } catch (err) {
        console.error(err)
        this.$toast.error("Fehler beim Speichern der Studio-Einstellung")
      }
    },
    onSortDragStart(event, index) {
      event.dataTransfer.effectAllowed = "move"
      event.dataTransfer.setData("text/plain", index)
      this.draggedSortIdx = index
    },
    onSortDragOver(event, index) {
      event.preventDefault()
    },
    onSortDrop(event, index) {
      event.preventDefault()
      const fromIdx = this.draggedSortIdx
      const toIdx = index
      if (fromIdx !== null && fromIdx !== toIdx) {
        const list = [...this.sortedEmployeeList]
        const movedItem = list.splice(fromIdx, 1)[0]
        list.splice(toIdx, 0, movedItem)
        this.sortedEmployeeList = list
      }
    },
    onSortDragEnd() {
      this.draggedSortIdx = null
    },
    sortItemStyle(index) {
      const isDragging = this.draggedSortIdx === index
      const borderTheme = this.isDarkMode ? "rgba(255, 255, 255, 0.08)" : "rgba(0, 0, 0, 0.08)"
      const bgTheme = this.isDarkMode
        ? isDragging
          ? "rgba(76, 175, 80, 0.15)"
          : "rgba(255, 255, 255, 0.03)"
        : isDragging
        ? "rgba(76, 175, 80, 0.1)"
        : "rgba(0, 0, 0, 0.02)"

      return {
        background: bgTheme,
        border: `1px ${isDragging ? "dashed" : "solid"} ${isDragging ? "var(--v-primary-base, #4CAF50)" : borderTheme}`,
        opacity: isDragging ? 0.6 : 1,
        transition: "all 0.2s ease",
      }
    },
    getEmpInitials(emp) {
      const name = emp.fullName || emp.vorname + " " + emp.nachname || ""
      const parts = name.split(" ").filter((p) => p.trim())
      if (parts.length >= 2) {
        return (parts[0][0] + parts[1][0]).toUpperCase()
      }
      return name.slice(0, 2).toUpperCase()
    },

    mobileFilterBySearch(items) {
      items = availableMeetingTemplates(items)
      if (!this.mobileSearchQuery || !this.mobileSearchQuery.trim()) return items
      const q = this.mobileSearchQuery.trim().toLowerCase()
      return items.filter((t) => (t.title || "").toLowerCase().includes(q))
    },

    // ---------- WebSocket (Calendar Silent Refresh + Editing Presence) ----------
    setupCalendarSocket() {
      try {
        if (this._calendarDisposed) return
        this.teardownCalendarSocket()
        const sid = this.studioId
        const socket = socketService.socket

        if (!sid) {
          console.warn("[Calendar WS] Kein studioId gefunden.")
          return
        }
        if (!socket) {
          console.warn("[Calendar WS] Socket noch nicht initialisiert, retry in 2s...")
          this._calendarSocketRetry = setTimeout(() => this.setupCalendarSocket(), 2000)
          return
        }

        if (this.socketHandlers.onCalendarConnect) socket.off("connect", this.socketHandlers.onCalendarConnect)
        this.socketHandlers.onCalendarConnect = () => {
          const activeStudioId = this.studioId
          if (activeStudioId) socket.emit("join_calendar_room", { studioId: activeStudioId })
          this.invalidateCalendarReferences()
          this.fetchData(true)
        }
        socket.on("connect", this.socketHandlers.onCalendarConnect)
        // Beim ersten Beitritt wurden die Daten gerade geladen; bei einer
        // echten Wiederverbindung müssen verpasste Änderungen nachgeholt werden.
        if (socket.connected) socket.emit("join_calendar_room", { studioId: sid })

        // Silent Refresh
        this.socketHandlers.onCalendarUpdate = (event) => {
          if (event?.studioId && String(event.studioId) !== String(this.studioId)) return
          if (event?.refreshScope !== "day") this.invalidateCalendarReferences()
          this.fetchData(true)
        }
        socket.off("calendar_update", this.socketHandlers.onCalendarUpdate)
        socket.on("calendar_update", this.socketHandlers.onCalendarUpdate)

        // Editing Presence: Jemand beginnt zu bearbeiten
        this.socketHandlers.onEditingStart = (data) => {
          if (data && data.itemId) {
            this.$set(this.editingUsers, data.itemId, {
              userId: data.userId,
              userName: data.userName,
              userPicture: data.userPicture,
            })
          }
        }
        socket.off("calendar_editing_start", this.socketHandlers.onEditingStart)
        socket.on("calendar_editing_start", this.socketHandlers.onEditingStart)

        // Editing Presence: Jemand hört auf zu bearbeiten
        this.socketHandlers.onEditingStop = (data) => {
          if (data && data.itemId) {
            this.$delete(this.editingUsers, data.itemId)
          }
        }
        socket.off("calendar_editing_stop", this.socketHandlers.onEditingStop)
        socket.on("calendar_editing_stop", this.socketHandlers.onEditingStop)
      } catch (err) {
        console.error("[Calendar WS] FEHLER bei Setup:", err)
      }
    },
    teardownCalendarSocket() {
      clearTimeout(this._calendarSocketRetry)
      const sid = this.studioId
      const socket = socketService.socket
      if (socket) {
        if (sid) socket.emit("leave_calendar_room", { studioId: sid })
        if (this.socketHandlers.onCalendarConnect) socket.off("connect", this.socketHandlers.onCalendarConnect)
        if (this.socketHandlers.onCalendarUpdate) socket.off("calendar_update", this.socketHandlers.onCalendarUpdate)
        if (this.socketHandlers.onEditingStart) socket.off("calendar_editing_start", this.socketHandlers.onEditingStart)
        if (this.socketHandlers.onEditingStop) socket.off("calendar_editing_stop", this.socketHandlers.onEditingStop)
      }
    },
    // Emit Editing-State bei Dialog-Öffnung/Schliessung
    emitEditingStart(itemId) {
      const socket = socketService.socket
      if (socket && socket.connected && this.studioId && itemId) {
        socket.emit("calendar_editing_start", { studioId: this.studioId, itemId })
      }
    },
    emitEditingStop(itemId) {
      const socket = socketService.socket
      if (socket && socket.connected && this.studioId && itemId) {
        socket.emit("calendar_editing_stop", { studioId: this.studioId, itemId })
      }
    },

    scrollToSmart() {
      // 1. Position berechnen
      let targetScrollTop = 0

      if (this.isToday) {
        const nowMinutes = moment().hours() * 60 + moment().minutes()
        const startMinutes = this.getCalendarStartMinutes
        const offsetMinutes = Math.max(0, nowMinutes - startMinutes - 60)
        targetScrollTop = (offsetMinutes / 60) * this.hourHeight
      } else if (this.$vuetify.breakpoint.smAndDown && this.mobileViewColumns.length > 0) {
        const col = this.mobileViewColumns[0]
        let firstStart = "23:59"

        if (col.shifts) {
          col.shifts.forEach((s) =>
            s.times?.forEach((t) => {
              if (t.start < firstStart) firstStart = t.start
            }),
          )
        }
        if (col.appointments && firstStart === "23:59") {
          col.appointments.forEach((a) => {
            if (a.start < firstStart) firstStart = a.start
          })
        }

        if (firstStart !== "23:59") {
          const [h, m] = firstStart.split(":").map(Number)
          const targetMinutes = h * 60 + m
          const startMinutes = this.getCalendarStartMinutes
          const offsetMinutes = Math.max(0, targetMinutes - startMinutes - 30)
          targetScrollTop = (offsetMinutes / 60) * this.hourHeight
        }
      }

      // 2. Nur noch 50ms warten (reicht aus, damit Vue die DOM-Höhe berechnet hat)
      if (this._smartScrollTimer) clearTimeout(this._smartScrollTimer)
      this._smartScrollTimer = setTimeout(() => {
        this._smartScrollTimer = null
        const scroller = this.$refs.scroller || document.querySelector(".wc-scroller")

        if (!scroller) return

        // Der Kalender ist die einzige Scroll-Quelle. Die Zeitachse folgt dem
        // tatsächlichen, vom Browser gegebenenfalls begrenzten Scrollwert.
        const maxScrollTop = Math.max(0, scroller.scrollHeight - scroller.clientHeight)
        const clampedTarget = Math.max(0, Math.min(targetScrollTop, maxScrollTop))
        this.syncTimeAxisToScroller(scroller)
        scroller.scrollTo({
          top: clampedTarget,
          behavior: "smooth",
        })
      }, 50)
    },
    handleTemplateClick(template) {
      // Wenn wir auf Mobile/iPad sind, Sidebar schliessen
      if (this.$vuetify.breakpoint.mdAndDown) {
        this.sidebarOpen = false
      }

      // Wenn es einen gespeicherten Klick-Kontext gibt (Tap auf Zeitslot), diesen verwenden
      let defaultTime
      let defaultCol
      if (this.mobileClickContext) {
        defaultTime = this.mobileClickContext.time
        defaultCol = this.mobileClickContext.col
        this.mobileClickContext = null // Kontext verbraucht
      } else {
        // Standard-Werte: aktuelle Zeit, auf 15 Min gerundet
        const now = moment().minutes(Math.round(moment().minutes() / 15) * 15)
        defaultTime = now.format("HH:mm")
        // Falls wir in der mobilen Ansicht nur eine Spalte sehen, nehmen wir diese
        defaultCol = this.mobileViewColumns.length > 0 ? this.mobileViewColumns[0] : this.viewColumns[0]
      }

      const normalizedTemplate = this.withAppointmentRestriction({ ...template })
      if (!normalizedTemplate.type) {
        if (normalizedTemplate.category === "ämtli" || normalizedTemplate.category === "aufgabe" || normalizedTemplate.category === "info") {
          normalizedTemplate.type = "task"
        } else {
          normalizedTemplate.type = "appointment"
        }
      }

      if (normalizedTemplate.type === "appointment" || normalizedTemplate.type === "task") {
        const eligibleColumn = this.findEligibleAppointmentColumn(normalizedTemplate, defaultCol)
        if (eligibleColumn) {
          defaultCol = eligibleColumn
        } else if (normalizedTemplate.type === "appointment") {
          this.$toast.error("Für diese Vorlage ist aktuell kein berechtigter Mitarbeiter im Kalender verfügbar.")
          return
        }
      }

      this.pendingDrop = {
        template: normalizedTemplate,
        col: defaultCol,
        time: defaultTime,
        description: normalizedTemplate.type === "task" ? normalizedTemplate.description || "" : "",
      }
      this.dropDialog = true
    },
    toggleSidebar() {
      if (this.$vuetify.breakpoint.mdAndDown) {
        // Mobile/Tablet: Bottom-Sheet öffnen
        this.mobileClickContext = null // Kein Zeitkontext wenn über Button geöffnet
        this.mobileTemplateSheet = !this.mobileTemplateSheet
      } else {
        // Desktop: Sidebar togglen
        this.sidebarOpen = !this.sidebarOpen
      }
    },
    openGuestbookEntry() {
      if (!this.hasGuestbookEntryRight) return
      this.mobileTemplateSheet = false
      this.$router.push({ name: "guestbookEntry" })
    },
    handleMobileTemplateClick(template) {
      this.mobileTemplateSheet = false
      if (template.category === "ämtli") {
        this.openTaskAssignment(template)
      } else {
        this.handleTemplateClick(template)
      }
    },
    // ===== ÄMTLI SMART ASSIGNMENT =====
    openTaskAssignment(template) {
      if (template.isAssigned || this.taskAssignSaving) return

      this.mobileTemplateSheet = false
      const templateId = template.templateId?._id || template.templateId || template._id || template.id
      this.taskAssignData = this.withAppointmentRestriction({ ...template, _id: templateId })
      this.taskAssignDuration = Number(template.duration) || 30
      this.taskAssignDate = this.currentDate
      this.taskAssignTime = this.taskAssignmentFixedTimes()[0] || moment().minutes(Math.round(moment().minutes() / 15) * 15).format("HH:mm")
      this.taskAssignDialog = true
      return this.computeTaskAvailability({ suggestTime: true })
    },
    taskAssignmentFixedTimes() {
      const template = this.taskAssignData || {}
      const isClockTime = (time) => typeof time === "string" && /^(?:[01]\d|2[0-3]):[0-5]\d$/.test(time)
      // Eine angeklickte tägliche Instanz behält ihre Uhrzeit; Fortschrittslabels sind keine Uhrzeiten.
      if (isClockTime(template.time) && !template.isManualDaily) return [template.time]
      const weekend = moment(this.taskAssignDate, "YYYY-MM-DD", true).isoWeekday() >= 6
      const times = weekend && template.weekendConfig?.hasWeekendTimes ? template.weekendConfig.weekendTimes : template.defaultTimes
      return [...new Set((Array.isArray(times) ? times : []).filter(isClockTime))].sort()
    },
    taskAssignmentWindow() {
      if (this.taskAssignmentFixedTimes().length) return null
      const preference = this.normalizeTaskTimePreference(this.taskAssignData)
      if (preference.mode === "flexible") return null
      const startMin = this.timeToMinutes(preference.windowStart)
      const endMin = this.timeToMinutes(preference.windowEnd)
      const valid = moment(preference.windowStart, "HH:mm", true).isValid() && moment(preference.windowEnd, "HH:mm", true).isValid() && endMin > startMin
      return { ...preference, startMin, endMin, valid }
    },
    taskAssignmentSelectionKey() {
      return JSON.stringify([this.studioId, this.taskAssignData?.templateId || this.taskAssignData?._id || this.taskAssignData?.id,
        this.taskAssignDate, this.taskAssignTime, Number(this.taskAssignDuration)])
    },
    taskAssignmentIntervals(col) {
      if (col.isSick && !this.isWorkingDespiteAbsence(col)) return { working: [], free: [] }
      const shifts = col.shifts || []
      const exclusions = []
      const intervalForTimes = (start, end) => {
        if (typeof start !== "string" || typeof end !== "string" || !moment(start, "HH:mm", true).isValid() || !moment(end, "HH:mm", true).isValid()) return null
        const startMin = this.timeToMinutes(start)
        let endMin = this.timeToMinutes(end)
        if (endMin < startMin) endMin += 1440
        return { startMin, endMin }
      }
      shifts.forEach((shift) => {
        this.getShiftTimes(shift).forEach((entry) => {
          if (String(entry.type || "").toLowerCase() !== "pause") return
          const interval = intervalForTimes(entry.start, entry.end)
          if (interval) exclusions.push(interval)
        })
      })
      // Die Kalenderdarstellung kennt volle und zeitlich begrenzte Krankmeldungen.
      exclusions.push(...this.calculateBackgrounds(col).filter((block) => block.isSick)
        .map((block) => intervalForTimes(block.start, block.end)).filter(Boolean))
      const working = shifts.flatMap((shift) => {
        let intervals = this.getAssignableTimes(shift, this.taskAssignData.allowDuringOpenClose === true)
        // Nächtliche Krank-Schichten haben keine darstellbaren Tagesblöcke.
        if (shift.isSick && !this.getShiftWorkSegments(shift).length) intervals = []
        return this.subtractCalendarMinuteIntervals(intervals.map((entry) => ({ startMin: entry.startMin, endMin: Math.min(1440, entry.endMin) })), exclusions)
      })
      const busy = []
      const items = [...(col.items || []), ...(col.tasks || []).map((entry) => ({ ...entry, isTask: true })), ...(col.appointments || [])]
      items.forEach((item) => {
        const entry = item.rawData || item
        if (item.type === "system" || entry.deleted === true || entry.suppressed === true ||
          ["cancelled", "noshow", "draft"].includes(entry.status) || (entry.helperColumn && entry.helperColumn !== "none")) return
        const start = entry.time || entry.start || item.start
        if (!moment(start, "HH:mm", true).isValid()) return
        const duration = Number(entry.duration || item.duration)
        const startMin = this.timeToMinutes(start)
        const explicitInterval = intervalForTimes(start, entry.end || item.end)
        const endMin = item.isTask && duration > 0 ? startMin + duration : explicitInterval?.endMin || startMin + (duration > 0 ? duration : 30)
        const dayOffset = entry.date ? moment(entry.date, "YYYY-MM-DD", true).diff(moment(this.taskAssignDate, "YYYY-MM-DD", true), "days") * 1440 : 0
        busy.push({ startMin: startMin + dayOffset, endMin: endMin + dayOffset })
      })
      // Schichten einzeln erhalten: eine Aufgabe muss in einen vollständigen Arbeitsblock passen.
      return { working, free: working.flatMap((interval) => this.subtractCalendarMinuteIntervals([interval], busy)) }
    },
    async computeTaskAvailability({ suggestTime = false, reload = false } = {}) {
      const requestId = ++this.taskAvailabilityRequestId
      const selectionKey = this.taskAssignmentSelectionKey()
      this.taskAvailability = []
      this.taskAvailabilityKey = ""
      this.taskAvailabilityNotice = ""
      this.taskAvailabilityLoading = true
      const isCurrent = () => requestId === this.taskAvailabilityRequestId && selectionKey === this.taskAssignmentSelectionKey() && this.taskAssignDialog
      const duration = Number(this.taskAssignDuration)
      try {
        if (!moment(this.taskAssignDate, "YYYY-MM-DD", true).isValid() || !Number.isInteger(duration) || duration <= 0 || duration > 1440) {
          this.taskAvailabilityNotice = "Bitte ein gültiges Datum und eine Dauer zwischen 1 und 1440 Minuten wählen."
          return
        }
        const window = this.taskAssignmentWindow()
        if (window && !window.valid) {
          this.taskAvailabilityNotice = "Das konfigurierte Zeitfenster ist ungültig. Bitte die Ämtli-Einstellungen prüfen."
          return
        }
        let columns = this.columns?.length ? this.columns : this.viewColumns || []
        if (reload || this.taskAssignDate !== this.currentDate) {
          const response = await api.shiftManagement.fetchDailyView(this.taskAssignDate)
          if (!Array.isArray(response?.data?.columns)) throw new Error("Ungültige Tagesansicht")
          columns = response.data.columns
        }
        if (!isCurrent()) return
        const candidates = columns.filter((col) => {
          const id = String(col.info?._id || col.id || "")
          return id && !["ma_info", "no_show", "unassigned"].includes(id) && !col.isHelper && !col.info?.isHelper
        }).map((col) => ({ col, ...this.taskAssignmentIntervals(col), restriction: this.appointmentEmployeeRestrictionMessage(this.taskAssignData, col) }))
        const clockTime = (minute) => `${String(Math.floor(minute / 60)).padStart(2, "0")}:${String(minute % 60).padStart(2, "0")}`
        const fits = (intervals, start) => intervals.some((entry) => start >= entry.startMin && start + duration <= entry.endMin)
        const earliest = (intervals, from = 0, until = 1440) => {
          const starts = intervals.map((entry) => Math.max(entry.startMin, from)).filter((start, index) => start + duration <= Math.min(intervals[index].endMin, until))
          return starts.length ? Math.min(...starts) : null
        }
        const available = candidates.filter((candidate) => !candidate.restriction).flatMap((candidate) => candidate.free)
        if (suggestTime) {
          const fixedTimes = this.taskAssignmentFixedTimes()
          let selected = null
          if (fixedTimes.length) {
            this.taskAssignTime = fixedTimes.find((time) => fits(available, this.timeToMinutes(time))) || fixedTimes[0]
          } else if (window) {
            selected = earliest(available, window.startMin, window.endMin)
            if (selected === null && window.mode === "preferred_window") selected = earliest(available)
            this.taskAssignTime = clockTime(selected === null ? window.startMin : selected)
          } else {
            const current = moment(this.taskAssignTime, "HH:mm", true).isValid() ? this.timeToMinutes(this.taskAssignTime) : 0
            selected = earliest(available, current)
            if (selected === null) selected = earliest(available)
            if (selected !== null) this.taskAssignTime = clockTime(selected)
          }
        }
        if (!moment(this.taskAssignTime, "HH:mm", true).isValid() || this.timeToMinutes(this.taskAssignTime) + duration > 1440) {
          this.taskAvailabilityNotice = "Bitte eine gültige Uhrzeit wählen. Die gesamte Dauer muss in diesen Tag passen."
          return
        }
        const start = this.timeToMinutes(this.taskAssignTime)
        const inWindow = !window || (start >= window.startMin && start + duration <= window.endMin)
        this.taskAvailability = candidates.map(({ col, working, free, restriction }) => {
          const result = {
            id: col.info?._id || col.id,
            firstName: col.info?.firstName || col.firstName,
            picture: col.info?.picture || col.picture,
            isApprentice: col.isApprentice === true || col.info?.isApprentice === true,
            shiftRange: col.shiftRange || (working.length ? `${clockTime(Math.min(...working.map((entry) => entry.startMin)))} - ${clockTime(Math.max(...working.map((entry) => entry.endMin)))}` : "Keine Schicht"),
            status: "noshift", nextFree: null,
          }
          if (restriction) return { ...result, status: "restricted", restrictionMessage: restriction }
          if (window?.mode === "required_window" && !inWindow) return { ...result, status: "outside_window" }
          if (fits(free, start)) result.status = "free"
          else if (fits(working, start)) result.status = "busy"
          const next = earliest(free, Math.max(start, window?.mode === "required_window" ? window.startMin : 0), window?.mode === "required_window" ? window.endMin : 1440)
          if (next !== null && result.status !== "free") result.nextFree = clockTime(next)
          return result
        })
        const hasFree = this.taskAvailability.some((employee) => employee.status === "free")
        if (window?.mode === "required_window" && !hasFree) {
          this.taskAvailabilityNotice = `Im Pflichtfenster ${window.windowStart}–${window.windowEnd} ist zur gewählten Zeit niemand für die gesamte Dauer verfügbar.`
        } else if (window?.mode === "preferred_window" && !inWindow) {
          this.taskAvailabilityNotice = `Bevorzugtes Zeitfenster: ${window.windowStart}–${window.windowEnd}. ${suggestTime ? "Dort ist niemand für die gesamte Dauer frei. Der Vorschlag liegt ausserhalb." : "Die gewählte Uhrzeit liegt ausserhalb."}`
        } else if (!hasFree && candidates.length) {
          this.taskAvailabilityNotice = "Zur gewählten Zeit ist niemand für die gesamte Dauer verfügbar."
        }
        const order = { free: 0, busy: 1, restricted: 2, outside_window: 3, noshift: 4 }
        this.taskAvailability.sort((a, b) => order[a.status] - order[b.status])
        this.taskAvailabilityKey = this.taskAssignmentSelectionKey()
      } catch (error) {
        if (isCurrent()) {
          console.error("Fehler beim Laden der Ämtli-Verfügbarkeit", error)
          this.taskAvailability = []
          this.taskAvailabilityNotice = "Die Verfügbarkeit konnte nicht geladen werden. Bitte Datum oder Uhrzeit erneut wählen."
        }
      } finally {
        if (requestId === this.taskAvailabilityRequestId) this.taskAvailabilityLoading = false
      }
    },
    adjustTaskTime(delta) {
      const current = moment(this.taskAssignTime, "HH:mm")
      current.add(delta, "minutes")
      const currentMin = current.hours() * 60 + current.minutes()
      const startMin = this.getCalendarStartMinutes
      const endMin = this.getCalendarEndMinutes
      if (currentMin < startMin) {
        current.hours(Math.floor(startMin / 60)).minutes(startMin % 60)
      }
      if (currentMin > endMin) {
        current.hours(Math.floor(endMin / 60)).minutes(endMin % 60)
      }
      this.taskAssignTime = current.format("HH:mm")
      this.computeTaskAvailability()
    },
    adjustTaskDuration(delta) {
      let current = this.taskAssignDuration + delta
      if (current < 5) current = 5
      if (current > 480) current = 480
      this.taskAssignDuration = current
      return this.computeTaskAvailability({ suggestTime: true })
    },
    async assignTask(emp) {
      if (!this.taskAssignDialog || this.taskAssignSaving || this.taskAvailabilityLoading) return
      if (!this.taskAvailabilityKey || this.taskAvailabilityKey !== this.taskAssignmentSelectionKey()) {
        this.$toast.warning("Bitte zuerst die Verfügbarkeit für die gewählte Zeit prüfen.")
        return
      }
      const availableEmployee = this.taskAvailability.find((candidate) => String(candidate.id) === String(emp.id) && candidate.status === "free")
      if (!availableEmployee) return
      const template = this.taskAssignData
      const time = this.taskAssignTime

      const restrictionMessage = this.appointmentEmployeeRestrictionMessage(template, availableEmployee)
      if (restrictionMessage) {
        this.$toast.warning(restrictionMessage)
        return
      }

      this.taskAssignSaving = true
      try {
        await api.shiftManagement.createTask({
          date: this.taskAssignDate,
          time,
          duration: this.taskAssignDuration,
          title: template.title,
          description: template.description || "",
          assignedEmployee: emp.id,
          icon: template.icon,
          color: template.color,
          checklist: template.checklist ? JSON.parse(JSON.stringify(template.checklist)) : [],
          instructions: template.instructions || "",
          templateId: template.templateId || template._id || template.id,
        })
        this.$toast.success(`${template.title} → ${emp.firstName} um ${time}`)
        this.taskAssignDialog = false
        this.fetchData(true)
      } catch (e) {
        this.$toast.error(e?.response?.data?.message || "Fehler beim Erstellen")
        await this.computeTaskAvailability({ reload: true })
      } finally {
        this.taskAssignSaving = false
      }
    },
    openDayFromWeek(date) {
      this.currentDate = date
      this.viewMode = "day"
      this.fetchData()
    },

    async loadAllowedStudios() {
      try {
        const studios = await api.fetchAllowedAdminStudios()
        if (Array.isArray(studios)) {
          this.userAllowedStudios = studios
        }
      } catch (err) {
        console.error("[WorkCalendar] Fehler beim Laden der berechtigten Studios:", err)
      }
    },
    fetchData(silent = false) {
      if (this._calendarDisposed) return Promise.resolve(false)
      if (!this._calendarReferenceCache) this._calendarReferenceCache = createCalendarReferenceCache()
      if (!this._calendarRefreshQueue) this._calendarRefreshQueue = createCalendarRefreshQueue((context) => this.loadCalendarData(context))
      const requestId = ++this.fetchDataRequestId
      const requestedDate = this.currentDate
      const requestedStudioId = this.studioId
      if (!silent) this.loading = true
      return this._calendarRefreshQueue.request({
        key: JSON.stringify([requestedStudioId, requestedDate]),
        requestId, requestedDate, requestedStudioId, silent,
      })
    },
    async loadCalendarData({ requestId, requestedDate, requestedStudioId, silent }) {
      if (this._calendarDisposed || requestId !== this.fetchDataRequestId) return false
      if (!this.hasStudioAccess || !requestedDate || this.currentDate !== requestedDate || String(this.studioId || "") !== String(requestedStudioId || "")) {
        if (requestId === this.fetchDataRequestId) this.loading = false
        return false
      }
      try {
        // Fetch all employees and weekly shifts for hover information
        const startOfWeek = this.$moment(requestedDate).clone().startOf("isoWeek").format("YYYY-MM-DD")
        const endOfWeek = this.$moment(requestedDate).clone().endOf("isoWeek").format("YYYY-MM-DD")
        const [res, empRes, weekPlan] = await Promise.all([
          api.shiftManagement.fetchDailyView(requestedDate),
          this._calendarReferenceCache.get(`employees:${requestedStudioId}`, () =>
            api.shiftManagement.fetchShiftManagementEmployee(requestedStudioId)).catch(() => []),
          this._calendarReferenceCache.get(`shifts:${requestedStudioId}:${startOfWeek}`, () =>
            api.shiftManagement.fetchPlan(requestedStudioId, startOfWeek, endOfWeek)).catch(() => ({ data: { shifts: [] } })),
        ])

        if (
          requestId !== this.fetchDataRequestId ||
          this.currentDate !== requestedDate ||
          String(this.studioId || "") !== String(requestedStudioId || "")
        ) {
          return false
        }

        this.columns = res.data.columns || []
        this.templates = res.data.templates || []
        this.helperAppointments = res.data.helperAppointments || []
        this.helperTasks = res.data.helperTasks || []
        this.guestbookEntries = res.data.guestbookEntries || []
        if (this.guestbookDialog && this.activeGuestbookEntry) {
          const refreshedEntry = this.guestbookEntries.find((entry) => String(entry._id) === String(this.activeGuestbookEntry._id))
          if (refreshedEntry) this.activeGuestbookEntry = refreshedEntry
          else this.guestbookDialog = false
        }
        this.appointmentTemplateRestrictions = res.data.appointmentTemplateRestrictions || []
        // Die Monatsliste enthält aus Kompatibilitätsgründen sowohl echte
        // Erledigungen als auch bloss eingeplante Instanzen. Für den geladenen
        // Tag können wir den echten Task-Status aus den vorhandenen Spalten
        // ergänzen, damit `eingeplant` niemals als `erledigt` erscheint.
        const visibleTaskState = new Map()
        this.columns.forEach((column) => {
          ;(column.tasks || []).forEach((task) => {
            const taskId = task && (task._id || task.id)
            if (taskId) visibleTaskState.set(String(taskId), task)
          })
        })
        this.helperTasks.forEach((task) => {
          const taskId = task && (task._id || task.id)
          if (taskId) visibleTaskState.set(String(taskId), task)
        })
        this.completions = (res.data.completions || []).map((entry) => {
          const task = entry && visibleTaskState.get(String(entry._id || entry.id || ""))
          return task ? { ...entry, isDone: Boolean(task.isDone), status: task.isDone ? "completed" : task.status } : entry
        })
        this.sickEmployeesToday = res.data.sickEmployeesToday || []
        this.allEmployeesForHover = empRes || []

        const responseData = weekPlan && weekPlan.data ? weekPlan.data : weekPlan
        this.weeklyShiftsForHover = responseData && responseData.shifts ? responseData.shifts : Array.isArray(responseData) ? responseData : []

        await this.fetchLiveStatus(requestedStudioId)
        if (
          requestId !== this.fetchDataRequestId ||
          this.currentDate !== requestedDate ||
          String(this.studioId || "") !== String(requestedStudioId || "")
        ) {
          return false
        }

        // Vollständig abwesende Mitarbeiter ohne Tagesinhalt automatisch
        // ausblenden. Eine echte Schicht trotz Ferien/Freiwunsch bleibt sichtbar.
        if (!silent && this.columns && this.columns.length > 0) {
          const autoHiddenIds = this.columns
            .filter((col) => {
              if (!col.isSick) return false
              const hasItems = (col.appointments && col.appointments.length > 0) || (col.tasks && col.tasks.length > 0)
              return !hasItems && !this.isWorkingDespiteAbsence(col)
            })
            .map((col) => String(col.info?._id || col.id))

          autoHiddenIds.forEach((id) => {
            if (!this.hiddenSickTrainerIds.includes(id)) {
              this.hiddenSickTrainerIds.push(id)
            }
          })
        }

        this.updateScrollerHeight()
        this.processViewData()
        this.consumeCustomerAppointmentRequest()

        // Deep-linking: check if appointmentId is in query parameters
        if (this.$route && this.$route.query && this.$route.query.appointmentId) {
          const appointmentId = this.$route.query.appointmentId
          const openGuestbookAnamnese = this.$route.query.openGuestbookAnamnese === "1"
          const resolved = this.resolveAppointmentForOpen(appointmentId)
          if (resolved) {
            await this.handleBlockClick(resolved.block, resolved.col)
            if (openGuestbookAnamnese) {
              await this.$nextTick()
              await this.$refs.workCalendarEditDialog?.openGuestbookAnamnese()
            }
          }

          // Clear query params to avoid re-opening the dialog on user actions
          const query = { ...this.$route.query }
          delete query.appointmentId
          delete query.openGuestbookAnamnese
          this.$router.replace({ query }).catch(() => {})
        }

        // WebSocket erst nach erstem erfolgreichen Laden starten
        if (!this.calendarSocketReady) {
          this.calendarSocketReady = true
          this.$nextTick(() => this.setupCalendarSocket())
        }
        return true
      } catch (e) {
        if (requestId === this.fetchDataRequestId) console.error(e)
        return false
      } finally {
        if (requestId === this.fetchDataRequestId) {
          this.loading = false
          this.$nextTick(() => {
            this.updateTimeLine()
            if (!silent) {
              this.scrollToSmart()
            }
          })
        }
      }
    },
    isWorkingDuringHoliday(col) {
      if (!col || col.absenceReason !== "holiday") return false
      if (col.worksDuringHoliday === true) return true

      // Kompatibilität mit einer noch nicht aktualisierten Daily-View-Antwort:
      // Eine aktive, nicht als krank markierte Arbeitszeit während Ferien ist
      // ebenfalls ein bewusst geplanter Ferieneinsatz.
      return (col.shifts || []).some((shift) => shift && shift.isSick !== true && this.getShiftWorkSegments(shift).length > 0)
    },
    isWorkingDespiteAbsence(col) {
      if (!col || !["holiday", "freewish"].includes(col.absenceReason)) return false
      if (col.worksDuringAbsence === true) return true
      if (col.absenceReason === "holiday" && this.isWorkingDuringHoliday(col)) return true
      if (col.absenceReason === "freewish" && col.worksDuringFreeWish === true) return true

      // Kompatibilität mit einer noch nicht aktualisierten Daily-View-Antwort:
      // Eine tatsächlich vorhandene, nicht kranke Schicht darf weder durch
      // Ferien noch durch einen (z. B. halbtägigen) Freiwunsch verschwinden.
      return (col.shifts || []).some((shift) => shift && shift.isSick !== true && this.getShiftWorkSegments(shift).length > 0)
    },
    processViewData() {
      const employeeColumns = this.columns
        .filter((col) => {
          const colId = col.info?._id || col.id
          if (this.hiddenSickTrainerIds && this.hiddenSickTrainerIds.includes(String(colId))) {
            return false
          }
          if (this.focusedEmployeeId && String(col.info?._id || col.id) === String(this.focusedEmployeeId)) {
            return true
          }
          if (col.isSick && !this.isWorkingDespiteAbsence(col)) {
            return true // Falls krank und nicht in hiddenSickTrainerIds, immer anzeigen
          }
          return col.shifts && col.shifts.length > 0 // FILTER: Nur mit Schicht
        })
        .map((col) => {
          const worksDuringHoliday = this.isWorkingDuringHoliday(col)
          const worksDuringAbsence = this.isWorkingDespiteAbsence(col)
          const worksDuringFreeWish = col.absenceReason === "freewish" && worksDuringAbsence
          const items = this.calculateItems(col)
          this.applyOverlapLayout(items) // NEU: Overlap-Algorithmus anwenden

          const mappedCol = {
            id: col.info?._id || col.id,
            firstName: col.info?.firstName || col.firstName,
            picture: col.info?.picture || col.picture,
            info: col.info || col,
            shifts: col.shifts || [],
            tasks: col.tasks || [],
            appointments: col.appointments || [],
            isSick: worksDuringAbsence ? false : col.isSick || false,
            absenceReason: col.absenceReason || null,
            worksDuringHoliday,
            worksDuringFreeWish,
            worksDuringAbsence,

            shiftRange: this.getShiftRange(col),
            liveStatus: this.getLiveStatus(col),
            notes: col.notes || [],
            backgrounds: this.calculateBackgrounds(col),
            items: items, // Überschneidungen sind jetzt berechnet
          }

          mappedCol.hoverDetails = this.calculateHoverDetails(mappedCol)
          return mappedCol
        })

      const visibleEmployeeIds = new Set(employeeColumns.map((col) => String(col.id)))
      const hiddenSickEmployeeIds = new Set((this.hiddenSickTrainerIds || []).map(String))
      const appointmentsWithoutVisibleEmployeeColumn = (this.columns || [])
        .filter((col) => {
          const colId = String(col.info?._id || col.id)
          return !visibleEmployeeIds.has(colId) && !hiddenSickEmployeeIds.has(colId)
        })
        .flatMap((col) =>
          (col.appointments || []).map((appointment) => ({
            ...appointment,
            helperColumn: "ma_info",
            orphanedEmployeeAssignment: true,
            orphanedEmployeeReason: "employee_without_visible_shift",
            orphanedEmployeeName: col.info?.fullName || col.fullName || col.info?.firstName || col.firstName || "",
          })),
        )

      // Virtuelle Spalten für "MA Info" und "Nicht gekommen" hinzufügen.
      // Hat ein Termin noch eine Mitarbeiter-Zuordnung, aber keine sichtbare
      // Arbeitsspalte mehr (z. B. nach dem Leeren der letzten Schicht), bleibt
      // die Datenbank-Zuordnung unangetastet und der Termin erscheint hier.
      const maInfoItems = [
        ...(this.guestbookEntries || []).map((entry) => this.mapGuestbookToItem(entry)),
        ...this.helperAppointments.filter((a) => a.helperColumn === "ma_info").map((a) => this.mapAppointmentToItem(a, "ma_info")),
        ...appointmentsWithoutVisibleEmployeeColumn.map((a) => this.mapAppointmentToItem(a, "ma_info")),
        ...(this.helperTasks || []).filter((t) => t.helperColumn === "ma_info").map((t) => this.mapTaskToItem(t, "ma_info")),
      ]
      this.applyOverlapLayout(maInfoItems) // NEU: Overlap für Hilfsspalte

      const noShowItems = [
        ...this.helperAppointments.filter((a) => a.helperColumn === "no_show").map((a) => this.mapAppointmentToItem(a, "no_show")),
        ...(this.helperTasks || []).filter((t) => t.helperColumn === "no_show").map((t) => this.mapTaskToItem(t, "no_show")),
      ]
      this.applyOverlapLayout(noShowItems) // NEU: Overlap für Hilfsspalte

      const unassignedItems = [
        ...this.helperAppointments.filter((a) => a.helperColumn === "unassigned").map((a) => this.mapAppointmentToItem(a, "unassigned")),
        ...(this.helperTasks || []).filter((t) => t.helperColumn === "unassigned").map((t) => this.mapTaskToItem(t, "unassigned")),
      ]
      this.applyOverlapLayout(unassignedItems)

      const helperColumns = [
        {
          id: "ma_info",
          firstName: "MA Info",
          picture: null,
          info: { firstName: "MA Info", isHelper: true, icon: "mdi-information-outline" },
          shifts: [],
          tasks: [],
          appointments: [],
          shiftRange: "", // Kein Objekt, sonst [object Object] Fehler
          liveStatus: null,
          notes: [],
          backgrounds: [{ start: "00:00", end: "23:59", type: "system" }],
          items: maInfoItems,
          isHelper: true,
        },
        {
          id: "no_show",
          firstName: "Nicht gekommen",
          picture: null,
          info: { firstName: "Nicht gekommen", isHelper: true, icon: "mdi-account-cancel-outline" },
          shifts: [],
          tasks: [],
          appointments: [],
          shiftRange: "",
          liveStatus: null,
          notes: [],
          backgrounds: [{ start: "00:00", end: "23:59", type: "system" }],
          items: noShowItems,
          isHelper: true,
        },
      ]

      // Removed unassigned column from calendar view since drafts and unassigned tasks are managed in the dashboard.

      if (this.focusedEmployeeId) {
        const selectedCol = employeeColumns.filter((c) => String(c.id) === String(this.focusedEmployeeId))
        if (this.showHelperColumnsDuringFocus) {
          this.viewColumns = [...selectedCol, ...helperColumns]
        } else {
          this.viewColumns = selectedCol
        }
      } else {
        this.viewColumns = [...employeeColumns, ...helperColumns]
      }
    },
    calculateNextShiftsForTrainer(trainerId) {
      const empId = String(trainerId)
      const currentDateStr = this.$moment(this.currentDate).format("YYYY-MM-DD")

      // Filter shifts that are assigned to this employee and are on or after today (currentDate)
      const futureShifts = (this.weeklyShiftsForHover || [])
        .filter((s) => s.assignedEmployee && String(s.assignedEmployee._id || s.assignedEmployee) === empId && s.date >= currentDateStr)
        .sort((a, b) => a.date.localeCompare(b.date))

      const nextThree = futureShifts.slice(0, 3)
      if (nextThree.length === 0) {
        return [{ date: "Keine weiteren Schichten geplant", time: "" }]
      }

      return nextThree.map((s) => {
        const times = s.times && s.times.length > 0 ? s.times : s.shiftTemplate ? s.shiftTemplate.times : []
        let minStart = null
        let maxEnd = null
        times.forEach((t) => {
          if (t.type === "Arbeitszeit" && t.start && t.end) {
            if (!minStart || t.start < minStart) minStart = t.start
            if (!maxEnd || t.end > maxEnd) maxEnd = t.end
          }
        })

        const mom = this.$moment(s.date)
        const dayOfWeek = mom.format("dd") // e.g. "Mo"
        const isToday = s.date === currentDateStr
        return {
          date: `${dayOfWeek}, ${mom.format("DD.MM.")}${isToday ? " (Heute)" : ""}:`,
          time: minStart && maxEnd ? `${minStart} - ${maxEnd}` : "keine Arbeitszeit",
        }
      })
    },
    calculateHoverDetails(col) {
      if (col.isHelper) return null

      const trainerId = col.id
      const emp = (this.allEmployeesForHover || []).find((e) => String(e.user?._id || e.user) === String(trainerId))

      // 1. Next 3 scheduled shifts
      const nextShifts = this.calculateNextShiftsForTrainer(trainerId)

      // 2. Remaining tasks for today
      const remainingTasks = (col.items || []).filter((item) => item.isTask && item.status !== "completed").length
      const taskText = remainingTasks > 0 ? `Verbleibende Aufgaben: ${remainingTasks}` : "Alle Aufgaben erledigt"
      const openTasks = (col.items || []).filter((item) => item.isTask && item.status !== "completed").map((item) => item.title)

      // 3. Upcoming holiday/vacation dates
      const currentDateStr = this.$moment(this.currentDate).format("YYYY-MM-DD")
      const upcomingHolidays = []
      let isCurrentlyOnHoliday = false
      let activeHolidayRange = ""

      if (emp) {
        ;(emp.holidayEntries || []).forEach((h) => {
          if (!h.status || h.status === "accepted") {
            const dates = (h.dates || []).map((d) => d.date).sort()
            if (dates.length > 0) {
              const start = dates[0]
              const end = dates[dates.length - 1]
              if (currentDateStr >= start && currentDateStr <= end) {
                isCurrentlyOnHoliday = true
                const startFormatted = this.$moment(start).format("DD.MM.")
                const endFormatted = this.$moment(end).format("DD.MM.")
                activeHolidayRange = `${startFormatted} - ${endFormatted}`
              }
              if (dates.some((d) => d >= currentDateStr)) {
                upcomingHolidays.push({
                  start,
                  end,
                })
              }
            }
          }
        })
      }
      upcomingHolidays.sort((a, b) => a.start.localeCompare(b.start))
      let holidayText = "Nächster Urlaub: Keiner geplant"
      if (isCurrentlyOnHoliday) {
        holidayText = `Aktuell im Urlaub: ${activeHolidayRange}`
      } else if (upcomingHolidays.length > 0) {
        const nextHoliday = upcomingHolidays[0]
        const startFormatted = this.$moment(nextHoliday.start).format("DD.MM.")
        const endFormatted = this.$moment(nextHoliday.end).format("DD.MM.")
        holidayText = `Nächster Urlaub: ${startFormatted} - ${endFormatted}`
      }

      return {
        nextShifts: nextShifts,
        remainingTasks: remainingTasks,
        taskText: taskText,
        openTasks: openTasks,
        holidayText: holidayText,
        isCurrentlyOnHoliday: isCurrentlyOnHoliday,
      }
    },

    // NEU: Berechnet überschneidende Termine und ordnet sie nebeneinander an (wie Google Calendar)
    applyOverlapLayout(items) {
      if (!items || items.length === 0) return

      // 1. Sortieren nach Startzeit, dann nach Endzeit (die längsten zuerst)
      items.sort((a, b) => {
        if (a.top !== b.top) return a.top - b.top
        const aEnd = a.top + a.height
        const bEnd = b.top + b.height
        return bEnd - aEnd
      })

      let columns = []
      let lastEventEnding = null

      const packEvents = (cols) => {
        const numColumns = cols.length
        cols.forEach((col, colIdx) => {
          col.forEach((item) => {
            item.style = item.style || {}
            // 6px Padding links und rechts aus der .wc-block Klasse berücksichtigen
            item.style.left = `calc(6px + calc(100% - 12px) / ${numColumns} * ${colIdx})`
            item.style.width = `calc(calc(100% - 12px) / ${numColumns})`
            item.style.right = "auto" // Verhindert, dass das Standard-right: 6px die Width überschreibt
          })
        })
      }

      items.forEach((item) => {
        // Wenn das neue Element nach dem Ende der aktuellen Gruppe startet, packen wir die Gruppe
        if (lastEventEnding !== null && item.top >= lastEventEnding) {
          packEvents(columns)
          columns = []
          lastEventEnding = null
        }

        // Versuche das Item in die erste Spalte zu setzen, in der es nicht kollidiert
        let placed = false
        for (let i = 0; i < columns.length; i++) {
          let col = columns[i]
          let lastEvent = col[col.length - 1]
          if (lastEvent.top + lastEvent.height <= item.top) {
            col.push(item)
            placed = true
            break
          }
        }

        // Wenn es in keine bestehende Spalte passt, mach eine neue auf
        if (!placed) {
          columns.push([item])
        }

        let itemEnd = item.top + item.height
        if (lastEventEnding === null || itemEnd > lastEventEnding) {
          lastEventEnding = itemEnd
        }
      })

      if (columns.length > 0) {
        packEvents(columns)
      }
    },

    mapGuestbookToItem(entry) {
      const calendarState = entry.walkInCalendar || {}
      const fallbackTime = calendarState.submittedAt ? this.$moment(calendarState.submittedAt).format("HH:mm") : "12:00"
      const start = /^([01]\d|2[0-3]):[0-5]\d$/.test(calendarState.time || "") ? calendarState.time : fallbackTime
      const startParts = start.split(":").map(Number)
      const startMinutes = startParts[0] * 60 + startParts[1]
      const endMinutes = Math.min(startMinutes + 30, 23 * 60 + 59)
      const end = `${String(Math.floor(endMinutes / 60)).padStart(2, "0")}:${String(endMinutes % 60).padStart(2, "0")}`
      const top = this.getTopFromTime(start)
      const height = Math.max(this.getTopFromTime(end) - top, 28)
      const color = "#00897b"
      const name = `${entry.name || ""} ${entry.surname || ""}`.trim() || "Unbekannter Gast"
      const contact = [entry.email, entry.phone].filter(Boolean).join(" · ")
      const handoff = calendarState.handedOverByName ? `Übergeben von ${calendarState.handedOverByName}` : ""

      return {
        _id: entry._id,
        id: entry._id,
        type: "guestbook",
        isGuestbook: true,
        isTask: false,
        isAppt: false,
        title: "Neuer Gästebucheintrag",
        subtitle: [name, handoff].filter(Boolean).join(" · "),
        description: [contact, handoff].filter(Boolean).join("\n"),
        start,
        end,
        top,
        height,
        class: "wc-block-guestbook",
        style: {
          background: "linear-gradient(135deg, rgba(0, 137, 123, 0.88) 0%, rgba(38, 166, 154, 0.72) 100%)",
          border: "1px solid rgba(0, 105, 92, 0.82)",
          boxShadow: "0 7px 18px rgba(0, 105, 92, 0.24)",
        },
        borderColor: color,
        icon: "mdi-book-open-page-variant-outline",
        iconColor: "white",
        status: "open",
        rawData: entry,
      }
    },
    mapAppointmentToItem(appt, colId) {
      // Fehlende Start-/Endzeit sicherstellen
      const start = appt.start || "12:00"
      const endStr = appt.end || moment(start, "HH:mm").add(30, "minutes").format("HH:mm")

      // Top und Height berechnen (löste das Stacking-Problem)
      const top = this.getTopFromTime(start)
      const height = this.getTopFromTime(endStr) - top
      const color = appt.color || "#607d8b"

      return {
        _id: appt._id,
        id: appt._id,
        seriesId: appt.seriesId,
        type: "appointment",
        isTask: false,
        title: appt.title || "Termin",
        subtitle: appt.customer
          ? `${appt.customer.vorname} ${appt.customer.nachname}${appt.customer2 ? ` + ${appt.customer2.vorname}` : ""}`
          : appt.noAccountData && appt.noAccountData.firstName
          ? `${appt.noAccountData.firstName} ${appt.noAccountData.lastName} (Gast)`
          : appt.description,
        description: appt.description || "",
        start: start,
        end: endStr,
        top: top,
        height: height,
        style: {
          background: `linear-gradient(135deg, ${this.hexToRgba(color, 0.55)} 0%, ${this.hexToRgba(color, 0.35)} 100%)`,
          border: `1px solid ${this.hexToRgba(color, 0.65)}`,
          boxShadow: `0 4px 15px ${this.hexToRgba(color, 0.2)}`,
        },
        borderColor: color, // Für den linken Rand
        icon: appt.icon || "mdi-calendar",
        iconColor: "white",
        isAppt: true, // Damit der Termin in den Stats oben zählt
        isDone: appt.isDone || false,
        rawData: appt, // Wichtig für handleBlockClick
      }
    },
    mapTaskToItem(t, colId) {
      const color = t.color || "#9C27B0" // Default Lila
      const duration = t.duration || 30
      const endStr = moment(t.time, "HH:mm").add(duration, "minutes").format("HH:mm")
      const top = this.getTopFromTime(t.time)
      const height = (duration / 60) * this.hourHeight
      const isDraft = t.status === "draft"

      return {
        _id: t._id,
        id: t._id,
        seriesId: t.seriesId,
        rawData: t,
        title: isDraft ? `${t.title} (Entwurf)` : t.title,
        subtitle: "",
        description: t.description || "",
        start: t.time,
        end: endStr,
        top: top,
        height: height,
        style: {
          background: isDraft
            ? `repeating-linear-gradient(45deg, ${this.hexToRgba(color, 0.1)} 0px, ${this.hexToRgba(color, 0.1)} 8px, ${this.hexToRgba(
                color,
                0.2,
              )} 8px, ${this.hexToRgba(color, 0.2)} 16px)`
            : `linear-gradient(135deg, ${this.hexToRgba(color, 0.35)} 0%, ${this.hexToRgba(color, 0.15)} 100%)`,
          border: isDraft ? `2px dashed ${this.hexToRgba(color, 0.65)}` : `1px solid ${this.hexToRgba(color, 0.5)}`,
          boxShadow: isDraft ? "none" : `0 4px 15px ${this.hexToRgba(color, 0.15)}`,
          opacity: isDraft ? 0.8 : 1,
        },
        borderColor: color,
        icon: t.icon || "mdi-checkbox-marked",
        iconColor: "white",
        isTask: true,
        status: t.isDone ? "completed" : isDraft ? "draft" : "planned",
        rawData: t,
      }
    },
    // --- CONTEXT MENU & COPY ACTIONS ---
    showContextMenu({ event, block, col }) {
      if (block.type === "system" || block.type === "guestbook") return
      event.preventDefault()
      this.emptyContextMenu.show = false
      this.contextMenu = {
        show: true,
        x: event.clientX,
        y: event.clientY,
        block: block,
        col: col,
      }
    },
    showEmptyContextMenu(event, col) {
      if (!event || !col) return
      this.contextMenu.show = false
      const targetRect = event.currentTarget?.getBoundingClientRect()
      if (!targetRect) return

      const targetClientY = Number.isFinite(event.calendarTargetClientY) ? event.calendarTargetClientY : event.clientY
      const relativeY = Math.max(0, Math.min(targetRect.height, targetClientY - targetRect.top))
      const clickedMinutes = this.getCalendarStartMinutes + (relativeY / this.hourHeight) * 60
      const snappedMinutes = Math.min(this.getCalendarEndMinutes - 15, Math.max(this.getCalendarStartMinutes, Math.round(clickedMinutes / 15) * 15))
      const hour = Math.floor(snappedMinutes / 60)
      const minute = snappedMinutes % 60
      this.emptyContextMenu = {
        show: true,
        x: event.clientX,
        y: event.clientY,
        col,
        time: `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`,
      }
    },
    isCalendarAppointmentBlock(block) {
      return !!block && block.type === "appointment" && !block.isTask && !block.isPause
    },
    isCalendarTaskBlock(block) {
      return !!block && (block.isTask || block.type === "task") && !block.isPause
    },
    isCalendarClipboardBlock(block) {
      return this.isCalendarAppointmentBlock(block) || this.isCalendarTaskBlock(block)
    },
    calendarClipboardTypeLabel(block) {
      if (this.isCalendarTaskBlock(block)) return "Ämtli / Aufgabe"
      if (this.isCalendarAppointmentBlock(block)) return "Termin"
      if (block?.isPause || block?.type === "pause") return "Pause"
      return "Kalendereintrag"
    },
    setAppointmentClipboard(block, col, mode) {
      if (!this.isCalendarClipboardBlock(block) || !block._id) return
      const raw = block.rawData || {}
      const startMinutes = moment(block.start, "HH:mm")
      const rawDuration = Number(raw.duration || 0)
      const endMinutes = block.end ? moment(block.end, "HH:mm") : startMinutes.clone().add(rawDuration || 30, "minutes")
      const duration = Math.max(15, rawDuration || endMinutes.diff(startMinutes, "minutes") || 30)
      const entryType = this.isCalendarTaskBlock(block) ? "task" : "appointment"
      if (entryType === "task" && !raw.updatedAt) {
        this.$toast.error("Das Ämtli muss für diese Aktion einmal aktualisiert werden.")
        this.fetchData(true)
        return
      }
      this.appointmentClipboard = {
        mode,
        entryType,
        sourceEntryId: block._id,
        sourceAppointmentId: entryType === "appointment" ? block._id : null,
        sourceTaskId: entryType === "task" ? block._id : null,
        sourceUpdatedAt: raw.updatedAt || null,
        sourceDate: raw.date || this.currentDate,
        sourceColId: col?.id || null,
        title: block.title || raw.title || (entryType === "task" ? "Ämtli" : "Termin"),
        duration,
      }
      const verb = mode === "move" ? "ausgeschnitten" : "kopiert"
      this.$toast.success(`"${this.appointmentClipboard.title}" ${verb}. Jetzt einen freien Zielslot antippen.`)
    },
    clearAppointmentClipboard() {
      this.appointmentClipboard = null
      this.emptyContextMenu.show = false
    },
    handleAppointmentClipboardCopy() {
      const { block, col } = this.contextMenu
      this.contextMenu.show = false
      this.setAppointmentClipboard(block, col, "copy")
    },
    handleContextMenuMove() {
      this.contextMenu.show = false
      const { block, col } = this.contextMenu
      if (!this.isCalendarClipboardBlock(block) || !col) return
      this.setAppointmentClipboard(block, col, "move")
    },
    async handleContextMenuFinderMove() {
      this.contextMenu.show = false
      const { block, col } = this.contextMenu
      if (!this.isCalendarAppointmentBlock(block) || !col) return
      await this.handleBlockClick(block, col, { openFinderDirectly: true })
    },
    handleContextMenuCopy() {
      this.contextMenu.show = false
      const { block, col } = this.contextMenu
      if (!block || !col) return

      this.copySourceBlock = block
      const isTask = block.isTask
      const isPause = block.isPause || block.type === "pause"

      let duration = block.height ? Math.round((block.height / this.hourHeight) * 60) : 30
      if (block.rawData && block.rawData.duration) {
        duration = block.rawData.duration
      }

      const endStr = moment(block.start, "HH:mm").add(duration, "minutes").format("HH:mm")

      this.copyTargetEmployees = this.viewColumns
        .filter((c) => !c.isHelper && c.id !== "ma_info" && c.id !== "no_show")
        .map((c) => {
          const firstName = c.firstName || c.info?.firstName || ""
          const lastName = c.lastName || c.info?.lastName || ""
          const name = `${firstName} ${lastName}`.trim()

          const freeResult = this.isTimeSlotFree(c, block.start, endStr, null, isTask, isPause)
          let statusText = "Frei"
          let isAvailable = true
          if (freeResult !== true) {
            statusText = freeResult
            isAvailable = false
          }

          return {
            ...c,
            name: name,
            statusText: statusText,
            isAvailable: isAvailable,
          }
        })
      this.copyDialog = true
    },
    async pasteAppointmentClipboard() {
      const clipboard = this.appointmentClipboard
      const target = this.emptyContextMenu
      if (!clipboard || !target.col || !target.time || this.appointmentClipboardBusy) return

      const startMinutes = moment(target.time, "HH:mm")
      const endMinutes = startMinutes.clone().add(clipboard.duration, "minutes")
      if (!endMinutes.isSame(startMinutes, "day") || endMinutes.format("HH:mm") <= target.time) {
        this.$toast.error("Der Eintrag würde über das Tagesende hinausreichen.")
        return
      }
      const end = endMinutes.format("HH:mm")
      const isTask = clipboard.entryType === "task"
      const excludeId = clipboard.mode === "move" ? clipboard.sourceEntryId : null
      const validation = this.isTimeSlotFree(target.col, target.time, end, excludeId, isTask, false)
      if (validation !== true) {
        this.$toast.error(`Einfügen nicht möglich: ${validation}`)
        return
      }

      const isHelper = target.col.isHelper || target.col.id === "ma_info" || target.col.id === "no_show"
      const targetDate = target.col.date || target.col.currentDate || this.currentDate
      if (!isTask && clipboard.mode !== "move" && !this.confirmPastAppointmentCreation([{ date: targetDate, start: target.time }])) return
      this.appointmentClipboardBusy = true
      try {
        await api.shiftManagement.applyAppointmentClipboard({
          entryType: clipboard.entryType || "appointment",
          action: clipboard.mode === "move" ? "move" : "copy",
          sourceEntryId: clipboard.sourceEntryId,
          sourceAppointmentId: clipboard.sourceAppointmentId,
          sourceTaskId: clipboard.sourceTaskId,
          sourceUpdatedAt: clipboard.sourceUpdatedAt,
          target: {
            date: targetDate,
            start: target.time,
            end,
            time: target.time,
            duration: clipboard.duration,
            assignedEmployee: isHelper ? null : target.col.info?._id || target.col.id,
            helperColumn: isHelper ? target.col.id : "none",
          },
        })

        this.emptyContextMenu.show = false
        if (clipboard.mode === "move") this.appointmentClipboard = null
        const typeLabel = isTask ? "Ämtli" : "Termin"
        this.$toast.success(clipboard.mode === "move" ? `${typeLabel} erfolgreich verschoben.` : `${typeLabel} erfolgreich kopiert.`)
        await this.fetchData(true)
        if (!isTask && this.appointmentHistoryDialog) await this.loadAppointmentHistory()
      } catch (err) {
        const message = err?.response?.data?.message || err?.message || "Eintrag konnte nicht eingefügt werden."
        this.$toast.error(message)
      } finally {
        this.appointmentClipboardBusy = false
      }
    },
    async openAppointmentHistory() {
      this.appointmentHistoryDialog = true
      await this.loadAppointmentHistory()
    },
    async loadAppointmentHistory() {
      if (!this.currentDate || this.appointmentHistoryLoading) return
      this.appointmentHistoryLoading = true
      try {
        const result = await api.shiftManagement.fetchAppointmentChangeHistory(this.currentDate.slice(0, 7), 80)
        this.appointmentHistoryEntries = result?.entries || []
      } catch (err) {
        this.$toast.error(err?.response?.data?.message || "Änderungsverlauf konnte nicht geladen werden.")
      } finally {
        this.appointmentHistoryLoading = false
      }
    },
    formatAppointmentHistoryTime(value) {
      return value ? moment(value).format("DD.MM.YYYY, HH:mm") : "–"
    },
    appointmentHistoryIcon(action) {
      if (action === "copied") return "mdi-content-copy"
      if (action === "moved") return "mdi-arrow-right-bold"
      if (action && action.includes("deleted")) return "mdi-delete-outline"
      if (action && action.includes("created")) return "mdi-plus"
      return "mdi-pencil-outline"
    },
    appointmentHistoryColor(action) {
      if (action === "copied") return "blue"
      if (action === "moved") return "orange"
      if (action && action.includes("deleted")) return "red"
      if (action && action.includes("created")) return "green"
      return "grey"
    },
    async handleContextMenuDelete() {
      this.contextMenu.show = false
      const { block, col } = this.contextMenu
      if (!block) return

      const isTask = block.isTask
      const isPause = block.isPause || block.type === "pause"

      if (isPause) {
        if (!confirm("Pause wirklich löschen?")) return
        try {
          const deleteData = {
            isPause: true,
            parentShiftId: block.parentShiftId,
            colId: col.id,
            timeIndex: block.timeIndex,
          }
          await this.deleteItem(deleteData)
        } catch (e) {
          this.$toast.error("Fehler beim Löschen")
        }
        return
      }

      // If it is part of a series, open the series delete dialog!
      if (block.seriesId) {
        this.deleteSeriesBlock = block
        this.deleteSeriesCol = col
        this.deleteSeriesScope = "single"
        this.deleteSeriesReason = ""
        this.deleteSeriesDialog = true
        return
      }

      if (isTask) {
        if (
          !confirm(
            "Ämtli wirklich löschen? Automatisch erzeugte Ämtli werden als bewusst ausgelassen gespeichert und bei einer normalen Neuplanung nicht auf einen anderen Tag verschoben.",
          )
        )
          return
        try {
          await api.shiftManagement.deleteTask(block._id, "single")
          this.$toast.success("Ämtli gelöscht")
          this.fetchData(true)
        } catch (e) {
          this.$toast.error("Fehler beim Löschen")
        }
      } else {
        if (!confirm("Termin wirklich löschen?")) return
        const reason = prompt("Löschgrund angeben (optional):")
        if (reason === null) return

        try {
          await api.shiftManagement.deleteAppointment(block._id, reason || "", "single")
          this.$toast.success("Termin gelöscht")
          this.fetchData(true)
        } catch (e) {
          this.$toast.error("Fehler beim Löschen")
        }
      }
    },
    async confirmDeleteSeries() {
      const block = this.deleteSeriesBlock
      const scope = this.deleteSeriesScope
      const reason = this.deleteSeriesReason.trim()

      this.deleteSeriesDialog = false
      this.loading = true

      try {
        const isTask = block.isTask || block.type === "task"
        if (isTask) {
          await api.shiftManagement.deleteTask(block._id, scope)
          this.$toast.success("Ämtli gelöscht")
        } else {
          await api.shiftManagement.deleteAppointment(block._id, reason, scope)
          this.$toast.success("Termin gelöscht")
        }
        this.fetchData(true)
      } catch (e) {
        this.$toast.error("Fehler beim Löschen")
      } finally {
        this.loading = false
        this.deleteSeriesBlock = null
        this.deleteSeriesCol = null
      }
    },
    closeDeleteSeriesDialog() {
      this.deleteSeriesDialog = false
      this.deleteSeriesBlock = null
      this.deleteSeriesCol = null
    },
    async openSeriesManager() {
      this.manageSeriesDialog = true
      this.expandedSeriesPanel = null
      await this.refreshSeriesList()
    },
    async refreshSeriesList() {
      this.loadingActiveSeries = true
      try {
        const data = await api.shiftManagement.fetchActiveSeries()
        this.activeSeries = data || []
      } catch (e) {
        console.error("Fehler beim Laden der Serien:", e)
        this.$toast.error("Serientermine konnten nicht geladen werden")
      } finally {
        this.loadingActiveSeries = false
      }
    },
    formatSeriesOccDate(dateStr) {
      if (!dateStr) return ""
      return this.$moment(dateStr).locale("de").format("dddd, DD.MM.YYYY")
    },
    async editOccurrence(occ, seriesType) {
      this.manageSeriesDialog = false
      this.currentDate = occ.date

      this.loading = true
      try {
        await this.fetchData(true)

        let foundBlock = null
        let foundCol = null
        for (const col of this.viewColumns) {
          const block = col.items ? col.items.find((b) => String(b._id) === String(occ._id)) : null
          if (block) {
            foundBlock = block
            foundCol = col
            break
          }
        }

        if (foundBlock && foundCol) {
          this.handleBlockClick(foundBlock, foundCol)
        } else {
          this.$toast.error("Termin konnte im Kalender nicht geladen werden.")
        }
      } catch (err) {
        console.error("Fehler beim Navigieren zum Termin:", err)
        this.$toast.error("Fehler beim Navigieren")
      } finally {
        this.loading = false
      }
    },
    async deleteSingleOcc(series, occ) {
      const typeLabel = series.type === "task" ? "Ämtli" : "Termin"
      if (!confirm(`Dieses ${typeLabel} am ${this.formatSeriesOccDate(occ.date)} wirklich löschen?`)) return

      this.loading = true
      try {
        if (series.type === "task") {
          await api.shiftManagement.deleteTask(occ._id, "single")
        } else {
          await api.shiftManagement.deleteAppointment(occ._id, "Gelöscht über Serien-Manager", "single")
        }
        this.$toast.success(`${typeLabel} erfolgreich gelöscht!`)
        await this.refreshSeriesList()
        this.fetchData(true)
      } catch (e) {
        console.error("Fehler beim Löschen:", e)
        this.$toast.error("Fehler beim Löschen")
      } finally {
        this.loading = false
      }
    },
    async deleteWholeSeries(series) {
      const typeLabel = series.type === "task" ? "Ämtli" : "Termin"
      if (!confirm(`Möchten Sie die gesamte Serie "${series.title}" mit allen ${series.occurrences.length} Vorkommen wirklich löschen?`)) return

      if (!series.occurrences || !series.occurrences.length) {
        this.$toast.error("Keine Termine in dieser Serie gefunden.")
        return
      }

      this.loading = true
      try {
        const refId = series.occurrences[0]._id
        if (series.type === "task") {
          await api.shiftManagement.deleteTask(refId, "all")
        } else {
          await api.shiftManagement.deleteAppointment(refId, "Gelöscht über Serien-Manager", "all")
        }
        this.$toast.success("Gesamte Serie erfolgreich gelöscht!")
        await this.refreshSeriesList()
        this.fetchData(true)
      } catch (e) {
        console.error("Fehler beim Löschen der Serie:", e)
        this.$toast.error("Fehler beim Löschen")
      } finally {
        this.loading = false
      }
    },
    extendSeries(series) {
      this.manageSeriesDialog = false

      const nextDate = moment(series.lastOccurrenceDate).add(1, "day").format("YYYY-MM-DD")

      const template = {
        id: series.templateId,
        _id: series.templateId,
        title: series.title,
        duration: series.duration,
        color: series.color,
        icon: series.icon || "mdi-calendar",
        type: series.type,
        specialType: series.specialType,
        checklist: series.checklist || [],
        instructions: series.instructions || "",
        requiresCustomer: !!series.customer || !!series.customerName,
      }

      let colId = series.assignedEmployeeId
      if (series.helperColumn && series.helperColumn !== "none") {
        colId = series.helperColumn
      }

      const col = {
        id: colId,
        name: series.assignedEmployee,
        firstName: series.assignedEmployee,
      }

      this.pendingDrop = {
        template,
        col,
        time: series.lastOccurrenceTime || "08:00",
        date: nextDate,
        description: series.description || "",
        isExtend: true,
        extendSeriesId: series.seriesId,
        customer: series.customer || null,
        customer2: series.customer2 || null,
        noAccountData: series.noAccountData || null,
        noAccountData2: series.noAccountData2 || null,
        guestData: series.guestData || null,
      }

      this.dropDialog = true
    },
    async selectEmployeeForCopy(targetCol) {
      const block = this.copySourceBlock
      if (!block || !targetCol) return

      const isTask = block.isTask
      const isPause = block.isPause || block.type === "pause"

      if (!isTask && !isPause && !this.confirmPastAppointmentCreation([{ date: this.currentDate, start: block.start }])) return
      this.copyDialog = false

      let duration = block.height ? Math.round((block.height / this.hourHeight) * 60) : 30
      if (block.rawData && block.rawData.duration) {
        duration = block.rawData.duration
      }

      const endStr = moment(block.start, "HH:mm").add(duration, "minutes").format("HH:mm")
      const valid = this.isTimeSlotFree(targetCol, block.start, endStr, null, isTask, isPause)
      if (valid !== true) {
        if (this.isHardCalendarCollision(valid, isTask)) {
          this.$toast.warning(valid)
          return
        }
        if (!confirm(`Der Ziel-Mitarbeiter hat um diese Zeit eine Kollision: ${valid}. Trotzdem kopieren?`)) {
          return
        }
      }

      try {
        const isHelper = targetCol.isHelper || targetCol.id === "ma_info" || targetCol.id === "no_show"

        if (isTask) {
          const payload = {
            date: this.currentDate,
            time: block.start,
            title: block.title,
            description: block.rawData?.description || "",
            assignedEmployee: isHelper ? null : targetCol.info?._id || targetCol.id,
            helperColumn: isHelper ? targetCol.id : "none",
            icon: block.icon,
            color: block.borderColor,
            duration: duration,
            checklist: block.rawData?.checklist ? JSON.parse(JSON.stringify(block.rawData.checklist)) : [],
            instructions: block.rawData?.instructions || "",
            templateId: (block.rawData?.templateId && (block.rawData.templateId._id || block.rawData.templateId)) || null,
          }
          await api.shiftManagement.createTask(payload)
        } else if (isPause) {
          if (isHelper) throw new Error("Pausen können nur für echte Mitarbeiter erstellt werden.")
          if (!targetCol.shifts || !targetCol.shifts.length) throw new Error("Mitarbeiter hat an diesem Tag keine Schicht.")

          let targetShift = null
          const pauseStart = moment(block.start, "HH:mm")
          const pauseEnd = moment(endStr, "HH:mm")
          for (const shift of targetCol.shifts) {
            const shiftSegments = this.getShiftCoverageSegments(shift)
            if (
              shiftSegments.some((segment) => {
                const shiftStart = moment(segment.start, "HH:mm")
                const shiftEnd = moment(segment.end, "HH:mm")
                return pauseStart.isSameOrAfter(shiftStart) && pauseEnd.isSameOrBefore(shiftEnd)
              })
            ) {
              targetShift = shift
              break
            }
          }
          if (!targetShift) throw new Error("Die Pause muss vollständig innerhalb der Arbeitszeit der Schicht liegen.")

          const newTimes = this.rebuildShiftTimesWithPause(targetShift, block.start, endStr)
          if (!newTimes) return
          await this.updateCalendarShift(targetShift._id, { times: newTimes, ...this.employeeShiftChangeMetadata() })
        } else {
          const appointmentData = {
            date: this.currentDate,
            start: block.start,
            end: endStr,
            title: block.title,
            description: block.rawData?.description || "",
            assignedEmployee: isHelper ? null : targetCol.info?._id || targetCol.id,
            helperColumn: isHelper ? targetCol.id : "none",
            customer: block.rawData?.customer?._id || block.rawData?.customer || null,
            customer2: block.rawData?.customer2?._id || block.rawData?.customer2 || null,
            icon: block.icon,
            color: block.borderColor,
            specialType: block.rawData?.specialType || null,
            templateId: (block.rawData?.templateId && (block.rawData.templateId._id || block.rawData.templateId)) || null,
          }
          if (block.rawData?.noAccountData && block.rawData.noAccountData.firstName) {
            appointmentData.noAccountData = block.rawData.noAccountData
          }
          if (block.rawData?.noAccountData2 && block.rawData.noAccountData2.firstName) {
            appointmentData.noAccountData2 = block.rawData.noAccountData2
          }
          if (block.rawData?.rawData?.guestData) {
            appointmentData.rawData = { guestData: block.rawData.rawData.guestData }
          }

          await api.shiftManagement.createAppointment(appointmentData)
        }

        this.$toast.success("Eintrag erfolgreich kopiert!")
        this.fetchData(true)
      } catch (err) {
        console.error("Fehler beim Kopieren:", err)
        const msg = err?.response?.data?.message || err?.message || "Fehler beim Kopieren"
        this.$toast.error(msg)
      }
    },
    getInitials(name) {
      if (!name) return ""
      const parts = name.trim().split(/\s+/)
      return parts
        .map((p) => p.charAt(0).toUpperCase())
        .join("")
        .substring(0, 2)
    },
    resolveAppointmentForOpen(appointmentId, isTask = false) {
      const targetId = String(appointmentId || "")
      if (!targetId) return null

      for (const column of this.viewColumns || []) {
        const block = (column.items || []).find(
          (item) => (isTask ? item.isTask : item.isAppt && !item.isTask) && String(item._id || item.id) === targetId,
        )
        if (block) return { block, col: column }
      }

      for (const rawColumn of this.columns || []) {
        const entry = (isTask ? rawColumn.tasks || [] : rawColumn.appointments || []).find((item) => String(item._id || item.id) === targetId)
        if (!entry) continue
        const columnId = rawColumn.info?._id || rawColumn.id
        const visibleColumn = (this.viewColumns || []).find((column) => String(column.id) === String(columnId))
        const fallbackColumn = {
          id: columnId,
          info: rawColumn.info || {},
          isHelper: false,
          items: [],
        }
        return {
          block: isTask ? this.mapTaskToItem(entry, columnId) : this.mapAppointmentToItem(entry, columnId),
          col: visibleColumn || fallbackColumn,
        }
      }

      const helperEntry = (isTask ? this.helperTasks || [] : this.helperAppointments || []).find((item) => String(item._id || item.id) === targetId)
      if (!helperEntry) return null
      const helperColumnId =
        helperEntry.helperColumn === "ma_info" || helperEntry.helperColumn === "no_show" ? helperEntry.helperColumn : "unassigned"
      const helperColumn = (this.viewColumns || []).find((column) => String(column.id) === helperColumnId) || {
        id: helperColumnId,
        isHelper: true,
        info: {
          _id: null,
          fullName: helperColumnId === "ma_info" ? "MA Info" : helperColumnId === "no_show" ? "Nicht gekommen" : "Nicht zugewiesen",
        },
        items: [],
      }
      return {
        block: isTask ? this.mapTaskToItem(helperEntry, helperColumnId) : this.mapAppointmentToItem(helperEntry, helperColumnId),
        col: helperColumn,
      }
    },
    async openAppointmentSearchResult(result) {
      if (!result?.id || !result?.date || this.appointmentSearchOpening) return
      const openingSequence = ++this.appointmentOpenSequence
      const targetId = String(result.id)
      const targetDate = result.date
      const isTask = Boolean(result.isTask || result.kind === "task")
      this.appointmentSearchOpening = true
      try {
        let resolved = this.currentDate === targetDate && result.scope !== "all" ? this.resolveAppointmentForOpen(targetId, isTask) : null

        if (!resolved) {
          if (this.currentDate !== targetDate) this.currentDate = targetDate
          let loaded = await this.fetchData(true)
          if (!loaded && openingSequence === this.appointmentOpenSequence && this.currentDate === targetDate) {
            loaded = await this.fetchData(true)
          }
          if (openingSequence !== this.appointmentOpenSequence || this.currentDate !== targetDate) return
          resolved = this.resolveAppointmentForOpen(targetId, isTask)
        }

        if (!resolved) {
          this.$toast.error("Der Eintrag ist nicht mehr vorhanden oder wurde inzwischen verschoben.")
          return
        }

        this.appointmentSearchOpening = false
        this.appointmentSearchDialog = false
        await this.$nextTick()
        if (openingSequence !== this.appointmentOpenSequence) return
        await this.handleBlockClick(resolved.block, resolved.col)
      } catch (error) {
        console.error("[WorkCalendar] Suchtreffer konnte nicht geöffnet werden:", error)
        this.$toast.error("Der Eintrag konnte nicht geöffnet werden.")
      } finally {
        if (openingSequence === this.appointmentOpenSequence) this.appointmentSearchOpening = false
      }
    },
    // --- ITEM ACTIONS ---
    async handleBlockClick(block, col, options = {}) {
      if (this.isResizing) return
      if (block.type === "system") return

      if (block.type === "guestbook") {
        this.activeGuestbookEntry = block.rawData || null
        this.guestbookDialog = Boolean(this.activeGuestbookEntry)
        return
      }

      const raw = block.rawData || {}
      this.editData = {
        id: block._id,
        seriesId: block.seriesId || raw.seriesId || null,
        isTask: block.isTask,
        isPause: block.type === "pause",
        title: block.title,
        description: raw.description || "",
        color: raw.color || block.borderColor,
        icon: raw.icon || block.icon || "mdi-calendar",
        templateId: raw.templateId || null,
        start: block.start,
        end: block.end,
        date: raw.date || this.currentDate,
        colId: col.id,
        sourceDate: raw.date || this.currentDate,
        sourceStart: block.start,
        sourceEnd: block.end,
        sourceColId: col.id,
        sourceUpdatedAt: raw.updatedAt || null,
        specialType: raw.specialType || null,
        bookingSource: raw.bookingSource || null,
        tacBilling: raw.tacBilling || null,
        customer: raw.customer || null,
        customer2: raw.customer2 || null,
        guestbookLead: raw.guestbookLead || null,
        guestbookPrefill: raw.guestbookPrefill || null,
        guestData: raw.guestData || null,
        noAccountData: raw.noAccountData || null,
        noAccountData2: raw.noAccountData2 || null,
        checklist: raw.checklist || [],
        instructions: raw.instructions || "",
        isDone: raw.isDone || false,
        status: raw.status || "planned",
        customerRatings: raw.customerRatings || [],
        completedActions: raw.completedActions || [],
        orphanedEmployeeAssignment: raw.orphanedEmployeeAssignment === true,
        orphanedEmployeeReason: raw.orphanedEmployeeReason || "",
        orphanedEmployeeName: raw.orphanedEmployeeName || "",
        parentShiftId: block.parentShiftId,
        timeIndex: block.timeIndex,
        duration: block.end ? moment(block.end, "HH:mm").diff(moment(block.start, "HH:mm"), "minutes") : 30,
        openFinderDirectly: options.openFinderDirectly === true && this.isCalendarAppointmentBlock(block),
      }

      this.customerDetails = null
      this.customerDetailsError = false
      this.customerDetailsRequestId += 1
      this.customerLoading = false

      // 🔴 Editing Presence: Allen mitteilen, dass wir diesen Eintrag bearbeiten
      if (block._id) this.emitEditingStart(block._id)

      this.editDialog = true
      const customerId = this.editData.customer?._id || this.editData.customer?.id
      if (customerId) await this.loadCustomerDetailsById(customerId)
    },
    async handleGuestbookResolved(result = {}) {
      this.guestbookDialog = false
      this.activeGuestbookEntry = null
      const appointment = result && result.appointment
      if (!appointment || !appointment._id) {
        await this.fetchData(true)
        return
      }

      const targetDate = appointment.date || this.currentDate
      this.currentDate = targetDate
      const query = {
        ...this.$route.query,
        date: targetDate,
        appointmentId: appointment._id,
      }
      if (result.withoutAccount) delete query.openGuestbookAnamnese
      else query.openGuestbookAnamnese = "1"
      await this.$router.replace({ query }).catch(() => {})
      await this.fetchData(true)
    },
    openCustomerProfile(customer) {
      if (!customer?._id) return
      this.$router.push({ name: "userDashboardCustomerSelected", params: { id: customer._id } })
    },
    async loadCustomerDetailsById(customerId) {
      const requestId = ++this.customerDetailsRequestId
      this.customerDetails = null
      this.customerDetailsError = false
      if (!customerId) {
        this.customerLoading = false
        return
      }
      this.customerLoading = true
      try {
        let poolData = null
        try {
          const lookupRes = await api.lookupCheckinPool(customerId)
          if (lookupRes && lookupRes.inPool) poolData = lookupRes
        } catch (e) {}
        if (requestId !== this.customerDetailsRequestId) return
        if (!poolData) {
          poolData = await api.fetchCustomerInfosAndUpdatePool({ roodyUserId: customerId })
        }
        if (requestId !== this.customerDetailsRequestId) return
        if (!poolData) throw new Error("Missing customer details")
        this.customerDetails = poolData
      } catch (e) {
        if (requestId === this.customerDetailsRequestId) {
          this.customerDetailsError = true
          console.error("Customer info fetch failed", e)
        }
      } finally {
        if (requestId === this.customerDetailsRequestId) this.customerLoading = false
      }
    },
    onCustomerAssigned() {
      // Kalender-Daten neu laden, damit der neue Kunde in den Blocks angezeigt wird
      this.editDialog = false
      this.fetchData(true)
    },
    // --- (Rest of the methods remain unchanged: isTimeSlotFree, handleDragOver, handleDrop, confirmDrop, saveEdit, deleteItem, triggerSpecialAction, etc.) ---
    isHardCalendarCollision(message, isTask = false) {
      return Boolean(isTask || String(message || "").includes("Ämtli"))
    },
    isTimeSlotFree(col, startStr, endStr, excludeId = null, isTask = false, isPause = false) {
      if (!col) return "Spalte nicht gefunden."
      const newStart = moment(startStr, "HH:mm")
      const newEnd = moment(endStr, "HH:mm")
      if (newStart.isSameOrAfter(newEnd)) return "Start muss vor Ende liegen."

      // Hilfsspalten erlauben IMMER Termine
      if (col.isHelper || col.id === "ma_info" || col.id === "no_show") return true

      if (isPause) {
        // Eine vorhandene Pause gehört zur Arbeitsabdeckung, eine echte
        // Split-Lücke dagegen nicht. So lässt sich eine Pause bearbeiten,
        // ohne dass 08–13 / 17–21 fälschlich als durchgehend gilt.
        if (!col.shifts || !col.shifts.length) return "Mitarbeiter hat keine Schicht."
        let covered = false
        for (const shift of col.shifts) {
          const shiftSegments = this.getShiftCoverageSegments(shift)
          if (
            shiftSegments.some((segment) => {
              const shiftStart = moment(segment.start, "HH:mm")
              const shiftEnd = moment(segment.end, "HH:mm")
              return newStart.isSameOrAfter(shiftStart) && newEnd.isSameOrBefore(shiftEnd)
            })
          ) {
            covered = true
            break
          }
        }
        if (!covered) return "Ausserhalb der Arbeitszeit."

        // Eine Pause kollidiert nicht mit Terminen und blockiert sich nicht selbst
        return true
      }

      if (!col.backgrounds || !col.backgrounds.length) return "Mitarbeiter hat keine Schicht."
      let covered = false
      for (const bg of col.backgrounds) {
        const bgStart = moment(bg.start, "HH:mm")
        const bgEnd = moment(bg.end, "HH:mm")
        if (newStart.isSameOrAfter(bgStart) && newEnd.isSameOrBefore(bgEnd)) {
          covered = true
          break
        }
      }
      if (!covered) {
        // Prüfe, ob es eine Kollision mit einer Pause gibt, für eine spezifischere Fehlermeldung
        if (col.shifts && col.shifts.length) {
          for (const shift of col.shifts) {
            const times = shift.times || []
            for (const t of times) {
              if (t.type === "Pause") {
                const pStart = moment(t.start, "HH:mm")
                const pEnd = moment(t.end, "HH:mm")
                if (newStart.isBefore(pEnd) && newEnd.isAfter(pStart)) {
                  return "Kollision mit Pause"
                }
              }
            }
          }
        }
        return "Ausserhalb der Arbeitszeit."
      }

      for (const item of col.items) {
        if (item._id === excludeId) continue
        if (item.type === "system") continue

        if (item.isTask || item.type === "task") {
          // A planned Ämtli remains visible but does not reserve the time for
          // appointments. Planning another Ämtli still checks its conflicts.
          if (!isTask) continue
          const itemStart = moment(item.start || item.time, "HH:mm")
          const itemEndStr =
            item.end ||
            moment(item.start || item.time, "HH:mm")
              .add(item.duration || 30, "minutes")
              .format("HH:mm")
          const itemEnd = moment(itemEndStr, "HH:mm")
          if (newStart.isBefore(itemEnd) && newEnd.isAfter(itemStart)) return `Kollision mit Ämtli "${item.title || "Aufgabe"}"`
          continue
        }

        // Pausen-Kollision prüfen
        if (item.type === "pause" || item.isPause) {
          const itemStart = moment(item.start, "HH:mm")
          let itemEndStr = item.end || moment(item.start, "HH:mm").add(30, "minutes").format("HH:mm")
          const itemEnd = moment(itemEndStr, "HH:mm")
          if (newStart.isBefore(itemEnd) && newEnd.isAfter(itemStart)) {
            return "Kollision mit Pause"
          }
          continue
        }

        const itemStart = moment(item.start, "HH:mm")
        let itemEndStr = item.end || moment(item.start, "HH:mm").add(30, "minutes").format("HH:mm")
        const itemEnd = moment(itemEndStr, "HH:mm")
        if (newStart.isBefore(itemEnd) && newEnd.isAfter(itemStart)) return `Kollision mit "${item.title}"`
      }
      return true
    },
    handleDragStart(tpl) {
      this._draggedDuration = tpl.duration || 60
      this._draggedTemplate = this.withAppointmentRestriction(tpl)
    },
    onBlockDragStart(moveData) {
      // Wird von Column emittiert wenn ein bestehender Block gezogen wird
      this._draggedDuration = moveData.duration || 60
      this._movingBlock = moveData
      this._draggedTemplate = moveData
    },
    handleDragOver({ event, colId, relativeY }) {
      if (event && event.dataTransfer) {
        const types = Array.from(event.dataTransfer.types || []).map((t) => t.toLowerCase())
        const isBlock = types.includes("blockmove")
        event.dataTransfer.dropEffect = isBlock ? (event.ctrlKey ? "copy" : "move") : "copy"
      }
      // Performance-Optimierung: Drag-Event drosseln (Throttle) um extreme Re-Renders zu verhindern
      const now = Date.now()
      if (this._lastDragOverTime && now - this._lastDragOverTime < 40) return
      this._lastDragOverTime = now

      const moveRaw = this._movingBlock
      const dragOffsetY = moveRaw && moveRaw.dragOffsetY ? moveRaw.dragOffsetY : 0
      const adjustedRelativeY = Math.max(0, relativeY - dragOffsetY)

      const hoursClicked = Math.max(0, adjustedRelativeY / this.hourHeight)
      const totalMin = hoursClicked * 60
      const clickedMinutes = this.getCalendarStartMinutes + totalMin
      let hour = Math.floor(clickedMinutes / 60)
      let min = Math.round((clickedMinutes % 60) / 15) * 15
      if (min === 60) {
        min = 0
        hour++
      }
      const timeStr = `${String(hour).padStart(2, "0")}:${String(min).padStart(2, "0")}`

      if (this.hoverState && this.hoverState.colId === colId && this.hoverState.time === timeStr) {
        return
      }

      const topPx = (Math.max(0, hour * 60 + min - this.getCalendarStartMinutes) / 60) * this.hourHeight
      const dragDuration = this._draggedDuration || 60
      const ghostHeight = (dragDuration / 60) * this.hourHeight

      // Update hoverState immediately for responsive snapping
      this.hoverState = { colId: colId, time: timeStr, top: topPx, height: ghostHeight, error: null }

      if (this._collisionTimer) {
        clearTimeout(this._collisionTimer)
      }

      const isCopy = event && event.ctrlKey

      // Debounce collision validation to prevent lag during fast sweeps
      this._collisionTimer = setTimeout(() => {
        const col = this.viewColumns.find((c) => String(c.id) === String(colId))
        const endTime = moment(timeStr, "HH:mm").add(dragDuration, "minutes").format("HH:mm")
        const excludeId = this._movingBlock && !isCopy ? this._movingBlock._id : null
        const isTask = this._movingBlock
          ? this._movingBlock.isTask
          : this._draggedTemplate
          ? this._draggedTemplate.type === "task" || this._draggedTemplate.isTask
          : false
        const isPause = this._movingBlock
          ? this._movingBlock.isPause
          : this._draggedTemplate
          ? this._draggedTemplate.type === "pause" || this._draggedTemplate.isPause || this._draggedTemplate.specialType === "pause"
          : false

        const validation = this.isTimeSlotFree(col, timeStr, endTime, excludeId, isTask, isPause)
        const error = validation === true ? null : validation
        if (this.hoverState && this.hoverState.colId === colId && this.hoverState.time === timeStr) {
          this.hoverState.error = error
        }
      }, 75)
    },
    onDragEndGlobal() {
      if (this._collisionTimer) {
        clearTimeout(this._collisionTimer)
        this._collisionTimer = null
      }
      this._lastDragOverTime = 0
      this.hoverState = null
      // Defer clearing drag objects to make sure drop handlers can read them as fallbacks
      setTimeout(() => {
        this._movingBlock = null
        this._draggedTemplate = null
      }, 50)
    },
    openEmployeeSwitcher(col) {
      if (this.$vuetify.breakpoint.smAndDown) {
        this.employeeSwitcherDialog = true
      }
    },
    switchEmployee(col) {
      const idx = this.viewColumns.findIndex((c) => c.id === col.id)
      if (idx !== -1) this.activeMobileColIndex = idx
      this.employeeSwitcherDialog = false
      this.scrollToSmart()
    },
    prevMobileCol() {
      if (this.activeMobileColIndex > 0) this.activeMobileColIndex--
    },
    nextMobileCol() {
      if (this.activeMobileColIndex < this.viewColumns.length - 1) this.activeMobileColIndex++
    },
    handlePointerBlockDrop(payload) {
      const targetId = payload?.targetColId
      const targetCol =
        (this.mobileViewColumns || []).find((col) => String(col.id) === String(targetId)) ||
        (this.viewColumns || []).find((col) => String(col.id) === String(targetId))

      if (!payload || !payload.moveData || !targetCol || !payload.targetColumnEl || !payload.laneBody) {
        return
      }

      // `handleDrop` bleibt die einzige Stelle, die Rasterung, Kollisionen und
      // den anschliessenden Speicherweg bestimmt. Der Pointer-Fallback liefert
      // ihm lediglich ein kleines Event-Objekt ohne dataTransfer.
      const original = payload.pointerEvent
      const syntheticEvent = {
        type: "pointer-block-drop",
        pointerType: original?.pointerType || "mouse",
        pointerId: original?.pointerId ?? null,
        isPrimary: original?.isPrimary ?? true,
        button: original?.button ?? 0,
        buttons: original?.buttons ?? 0,
        clientX: payload.clientX,
        clientY: payload.clientY,
        wcRelativeY: Number.isFinite(payload.relativeY) ? payload.relativeY : null,
        ctrlKey: !!payload.ctrlKey,
        currentTarget: payload.targetColumnEl,
        target: payload.laneBody,
        dataTransfer: null,
        cancelable: false,
        defaultPrevented: true,
        preventDefault() {},
        stopPropagation() {},
      }

      // Der lokale Zustand enthält absichtlich weiterhin das vollständige
      // rawData (u. a. für Kopien); handleDrop nutzt ihn als dataTransfer-
      // Fallback und ruft danach unverändert executeBlockMove auf.
      this._movingBlock = payload.moveData
      this._draggedTemplate = payload.moveData
      this.handleDrop({ event: syntheticEvent, col: targetCol })
    },
    handleDrop({ event, col }) {
      try {
        if (this._collisionTimer) {
          clearTimeout(this._collisionTimer)
          this._collisionTimer = null
        }
        this.hoverState = null

        const colEl = event.currentTarget || (event.target ? event.target.closest(".employee-column") : null)
        const laneBody = colEl ? colEl.querySelector(".lane-body") : null
        if (!laneBody) {
          return
        }

        // --- BLOCK MOVE: Bestehenden Block verschieben ---
        const moveRaw = event.dataTransfer ? event.dataTransfer.getData("blockMove") : null
        let moveData = null
        if (moveRaw) {
          try {
            moveData = JSON.parse(moveRaw)
          } catch (e) {
            console.error("Error parsing blockMove data:", e)
          }
        }
        // dataTransfer enthält absichtlich nur ein kleines DTO. Das vollständige
        // Objekt (z. B. für Kopien) liegt während dieses In-App-Drags lokal vor.
        if (moveData && this._movingBlock && String(moveData._id) === String(this._movingBlock._id)) {
          moveData = { ...this._movingBlock, ...moveData, rawData: this._movingBlock.rawData || null }
        }
        if (!moveData && this._movingBlock) {
          moveData = this._movingBlock
        }

        if (moveData) {
          this._movingBlock = null
          const rect = laneBody.getBoundingClientRect()
          const relativeY = Number.isFinite(event.wcRelativeY) ? event.wcRelativeY : event.clientY - rect.top
          const dragOffsetY = moveData.dragOffsetY || 0
          const adjustedRelativeY = Math.max(0, relativeY - dragOffsetY)

          const clickedMinutes = this.getCalendarStartMinutes + Math.max(0, adjustedRelativeY / this.hourHeight) * 60
          let hour = Math.floor(clickedMinutes / 60)
          let min = Math.round((clickedMinutes % 60) / 15) * 15
          if (min === 60) {
            min = 0
            hour++
          }
          const timeStr = moment().hour(hour).minute(min).format("HH:mm")
          this.executeBlockMove(moveData, col, timeStr, event.ctrlKey)
          return
        }

        // --- TEMPLATE DROP: Neue Vorlage erstellen ---
        const tplData = event.dataTransfer ? event.dataTransfer.getData("templateObj") : null
        let template = null
        if (tplData) {
          try {
            template = JSON.parse(tplData)
          } catch (e) {
            console.error("Error parsing templateObj data:", e)
          }
        }
        if (!template && this._draggedTemplate) {
          template = this._draggedTemplate
        }

        if (!template) {
          return
        }

        template = this.withAppointmentRestriction(template)

        if (!template.type) {
          if (template.category === "ämtli" || template.category === "aufgabe" || template.category === "info") {
            template.type = "task"
          } else {
            template.type = "appointment"
          }
        }

        const rect = laneBody.getBoundingClientRect()
        const relativeY = Number.isFinite(event.wcRelativeY) ? event.wcRelativeY : event.clientY - rect.top
        const clickedMinutes = this.getCalendarStartMinutes + Math.max(0, relativeY / this.hourHeight) * 60
        let hour = Math.floor(clickedMinutes / 60)
        let min = Math.round((clickedMinutes % 60) / 15) * 15
        if (min === 60) {
          min = 0
          hour++
        }
        const timeStr = moment().hour(hour).minute(min).format("HH:mm")
        const duration = template.duration || 60
        const endM = moment(timeStr, "HH:mm").add(duration, "minutes")
        const isPause = template.type === "pause" || template.isPause || template.specialType === "pause"

        const restrictionMessage = this.appointmentEmployeeRestrictionMessage(template, col)
        if (restrictionMessage) {
          this.$toast.warning(restrictionMessage)
          return
        }

        // Check if drop dialog is required
        const needsDialog = template.requiresCustomer || template.specialType === "new_contract" || template.id === "ap1" || template.requiresNote

        if (needsDialog) {
          const valid = this.isTimeSlotFree(col, timeStr, endM.format("HH:mm"), null, template.type === "task", isPause)
          if (valid !== true) {
            if (this.isHardCalendarCollision(valid, template.type === "task")) {
              this.$toast.warning(valid)
              return
            }
            if (!confirm(`Dieser Eintrag kollidiert (${valid}). Trotzdem einfügen?`)) return
          }
          this.pendingDrop = { template: { ...template }, col, time: timeStr, description: template.type === "task" ? template.description || "" : "" }
          this.dropDialog = true
        } else {
          // Direct creation without dialog
          const payload = {
            template: { ...template },
            col,
            colId: col.id,
            time: timeStr,
            description: template.type === "task" ? template.description || "" : "",
            duration: duration,
            date: this.currentDate,
          }
          this.confirmDrop(payload)
        }
      } catch (err) {
        console.error("Error in handleDrop:", err)
      }
    },
    async consumeCustomerAppointmentRequest() {
      const customerId = this.$route?.query?.createAppointmentFor
      if (!customerId || this.customerAppointmentLauncherLoading || String(this.customerAppointmentLauncherHandledId || "") === String(customerId)) {
        return
      }

      this.customerAppointmentLauncherHandledId = String(customerId)
      const query = { ...this.$route.query }
      delete query.createAppointmentFor
      this.$router.replace({ query }).catch(() => {})

      if (!this.hasAppointmentManagementRight) {
        this.$toast.error("Dir fehlt das Recht, Termine zu verwalten.")
        return
      }

      this.customerAppointmentLauncherLoading = true
      try {
        const customer = await api.getCustomerDashboard(customerId)
        if (!customer || !customer._id) throw new Error("customer_not_found")
        this.customerAppointmentLauncherCustomer = customer
        this.customerAppointmentLauncherDialog = true
      } catch (error) {
        this.customerAppointmentLauncherCustomer = null
        this.$toast.error("Der Kunde konnte für den Termin nicht geladen werden.")
      } finally {
        this.customerAppointmentLauncherLoading = false
      }
    },
    closeCustomerAppointmentLauncher() {
      this.customerAppointmentLauncherDialog = false
      this.customerAppointmentLauncherCustomer = null
    },
    startCustomerAppointment(template) {
      const customer = this.customerAppointmentLauncherCustomer
      if (!customer || !template) return

      const normalizedTemplate = this.withAppointmentRestriction({ ...template, type: "appointment", requiresCustomer: true })
      const col = this.findEligibleAppointmentColumn(normalizedTemplate, this.trainerpointTargetColumn())
      if (!col) {
        this.$toast.error("Für diese Terminart ist aktuell kein berechtigter Mitarbeiter verfügbar.")
        return
      }

      const selectedDate = this.currentDate || moment().format("YYYY-MM-DD")
      const start = moment()
      const remainder = start.minute() % 15
      if (remainder) start.add(15 - remainder, "minutes")
      start.seconds(0).milliseconds(0)

      const firstShiftStart = (col.shifts || [])
        .flatMap((shift) => this.getShiftWorkSegments(shift))
        .map((segment) => segment.start)
        .filter(Boolean)
        .sort()[0]
      const defaultTime = moment(selectedDate).isSame(moment(), "day") ? start.format("HH:mm") : String(firstShiftStart || "09:00")

      this.pendingDrop = {
        template: normalizedTemplate,
        col,
        time: defaultTime,
        date: selectedDate,
        customer,
        description: "",
      }
      this.customerAppointmentLauncherDialog = false
      this.customerAppointmentLauncherCustomer = null
      this.dropDialog = true
    },
    trainerpointCustomerFromEntry(entry) {
      if (!entry || !entry.user || !entry.user._id) return null
      return {
        ...entry.user,
        _id: entry.user._id,
        vorname: entry.firstName || entry.user.vorname || "",
        nachname: entry.lastName || entry.user.nachname || "",
        email: entry.email || entry.user.email || "",
      }
    },
    trainerpointComparableId(value) {
      if (value === null || value === undefined) return ""
      if (typeof value === "object") {
        return this.trainerpointComparableId(value._id || value.id || value.user)
      }
      return String(value)
    },
    trainerpointTargetColumn() {
      const realColumns = (this.viewColumns || []).filter(
        (col) => col && !col.isHelper && !["ma_info", "no_show", "unassigned"].includes(String(col.id)),
      )

      const selected = this.getSelectedEmployee || {}
      const selectedIds = [selected.user, selected._id, selected.id, selected.info?._id, selected.info?.user, userService.getUserId()]
        .map((value) => this.trainerpointComparableId(value))
        .filter(Boolean)

      const matchingColumn = realColumns.find((col) => {
        const columnIds = [col.id, col._id, col.user, col.info?._id, col.info?.user]
          .map((value) => this.trainerpointComparableId(value))
          .filter(Boolean)
        return columnIds.some((columnId) => selectedIds.includes(columnId))
      })

      if (matchingColumn) return matchingColumn

      const preferredId = selectedIds[0]
      if (preferredId) {
        return {
          id: preferredId,
          firstName: selected.vorname || selected.firstName || selected.fullName || "Mitarbeiter",
          info: { _id: preferredId },
          shifts: [],
        }
      }

      return realColumns[0] || null
    },
    openTrainerpointAppointment({ entry, template } = {}) {
      const customer = this.trainerpointCustomerFromEntry(entry)
      if (!customer || !template) {
        this.$toast.error("Kunde oder Terminart konnte nicht übernommen werden.")
        return
      }

      const col = this.trainerpointTargetColumn()
      if (!col) {
        this.$toast.error("Heute ist kein Mitarbeiter mit einer Schicht verfügbar.")
        return
      }

      const roundedStart = moment()
      const remainder = roundedStart.minute() % 15
      if (remainder) roundedStart.add(15 - remainder, "minutes")
      roundedStart.seconds(0).milliseconds(0)

      this.pendingDrop = {
        template: {
          ...template,
          type: "appointment",
          requiresCustomer: true,
        },
        col,
        time: roundedStart.format("HH:mm"),
        date: roundedStart.format("YYYY-MM-DD"),
        customer,
        description: "",
      }
      this.customerSearchDialog = false
      this.dropDialog = true
    },
    async openTrainerpointExistingAppointment({ appointment } = {}) {
      if (!appointment || !appointment._id || !appointment.date) {
        this.$toast.error("Der Termin konnte nicht geöffnet werden.")
        return
      }
      this.customerSearchDialog = false
      await this.$nextTick()
      await this.openAppointmentSearchResult({
        id: appointment._id,
        date: appointment.date,
        kind: "appointment",
        isTask: false,
        scope: "all",
      })
    },
    openTrainerpointInteraction(entry) {
      const customer = this.trainerpointCustomerFromEntry(entry)
      if (!customer) {
        this.$toast.error("Für diesen Check-in ist kein roody-Konto verknüpft.")
        return
      }
      this.trainerpointInteractionMounted = true
      this.$nextTick(() => {
        const interactionDialog = this.$refs.trainerpointInteraction
        if (interactionDialog && typeof interactionDialog.setVisible === "function") {
          interactionDialog.setVisible(customer._id)
        }
      })
    },
    onTrainerpointInteractionCreated() {
      this.$toast.success("Interaktion wurde erfasst.")
    },
    onCheckinPoolSelect(entry, options = {}) {
      if (!entry || !entry.user || !entry.user._id) return
      this.customerSearchDialog = false
      const query = {}
      if (options.section) query.section = options.section
      if (options.action) query.action = options.action
      this.$router.push({
        path: `/dashboard/user/search/${entry.user._id}`,
        query,
      })
    },
    async executeBlockMove(moveData, targetCol, newTime, isCopy = false) {
      const duration = moveData.duration || 60
      const endStr = moment(newTime, "HH:mm").add(duration, "minutes").format("HH:mm")

      if (isCopy) {
        if (moveData.type === "system") {
          return this.$toast.error("Centeröffnung/-schliessung kann nicht kopiert werden.")
        }
        if (moveData.isPause) {
          return this.$toast.error("Pausen können nicht kopiert werden.")
        }
        if (!moveData.isTask && !this.confirmPastAppointmentCreation([{ date: this.currentDate, start: newTime }])) return
      }

      // Kollisionsprüfung (bei Kopie eigene ID NICHT ausschliessen)
      const valid = this.isTimeSlotFree(targetCol, newTime, endStr, isCopy ? null : moveData._id, moveData.isTask, moveData.isPause)
      if (valid !== true) {
        const actionName = isCopy ? "kopieren" : "verschieben"
        if (this.isHardCalendarCollision(valid, moveData.isTask)) {
          this.$toast.warning(valid)
          return
        }
        if (!confirm(`Dieser Eintrag kollidiert (${valid}). Trotzdem ${actionName}?`)) {
          return
        }
      }

      try {
        // Optimistisches Frontend-Update (Direkt im UI anzeigen)
        const sourceCol = this.viewColumns.find((c) => c.id === moveData.sourceColId)
        if (sourceCol && targetCol.items) {
          const itemIdx = sourceCol.items.findIndex((i) => {
            if (moveData.isPause) {
              return i.type === "pause" && String(i.parentShiftId) === String(moveData.parentShiftId) && i.timeIndex === moveData.timeIndex
            }
            if (moveData.type === "system") {
              return i.type === "system" && String(i.parentShiftId) === String(moveData.parentShiftId) && i.title === moveData.title
            }
            return i._id && String(i._id) === String(moveData._id)
          })
          if (itemIdx > -1) {
            if (isCopy) {
              const originalItem = sourceCol.items[itemIdx]
              const newItem = JSON.parse(JSON.stringify(originalItem))
              newItem._id = "copy_" + Date.now() + "_" + Math.random().toString(36).substr(2, 9)
              newItem.id = newItem._id
              newItem.start = newTime
              newItem.end = endStr
              const h = parseInt(newTime.split(":")[0], 10)
              const m = parseInt(newTime.split(":")[1], 10)
              newItem.top = (Math.max(0, (h - this.startHour) * 60 + m) / 60) * this.hourHeight
              if (newItem.rawData) {
                newItem.rawData.duration = duration
                newItem.rawData.start = newTime
                newItem.rawData.end = endStr
              }
              targetCol.items.push(newItem)
            } else {
              const item = sourceCol.items.splice(itemIdx, 1)[0]
              item.start = newTime
              item.end = endStr
              const h = parseInt(newTime.split(":")[0], 10)
              const m = parseInt(newTime.split(":")[1], 10)
              item.top = (Math.max(0, (h - this.startHour) * 60 + m) / 60) * this.hourHeight
              if (item.rawData) item.rawData.duration = duration
              targetCol.items.push(item)
            }
          }
        }

        const isHelper = targetCol.isHelper || targetCol.id === "ma_info" || targetCol.id === "no_show"

        if (moveData.type === "system" && moveData.parentShiftId) {
          if (moveData.sourceColId !== targetCol.id) {
            this.fetchData(true)
            return this.$toast.error("Centeröffnung/-schliessung kann den Mitarbeiter nicht wechseln.")
          }
          const shift = targetCol.shifts ? targetCol.shifts.find((s) => String(s._id) === String(moveData.parentShiftId)) : null
          if (shift) {
            let newTimes = JSON.parse(JSON.stringify(shift.times || []))
            newTimes.sort((a, b) => a.start.localeCompare(b.start))
            if (moveData.title === "Centeröffnung") {
              newTimes[0].start = newTime
            } else if (moveData.title === "Centerschliessung") {
              newTimes[newTimes.length - 1].end = endStr
            }
            await this.updateCalendarShift(moveData.parentShiftId, { times: newTimes, ...this.employeeShiftChangeMetadata() })
            this.fetchData(true)
            return
          }
        } else if (moveData.isPause && moveData.parentShiftId) {
          const shift = targetCol.shifts ? targetCol.shifts.find((s) => String(s._id) === String(moveData.parentShiftId)) : null
          if (!shift) throw new Error("Pausen koennen nicht zwischen Mitarbeitern verschoben werden")
          const newTimes = this.rebuildShiftTimesWithPause(shift, newTime, endStr, moveData.timeIndex)
          if (!newTimes) {
            this.fetchData(true)
            return
          }
          await this.updateCalendarShift(moveData.parentShiftId, { times: newTimes, ...this.employeeShiftChangeMetadata() })
        } else if (isCopy) {
          // --- KOPIE SPEICHERN ---
          if (moveData.isTask) {
            const orig = moveData.rawData || {}
            await api.shiftManagement.createTask({
              date: this.currentDate,
              time: newTime,
              title: orig.title || moveData.title || "Ämtli",
              description: orig.description || "",
              assignedEmployee: isHelper ? null : targetCol.info._id,
              helperColumn: isHelper ? targetCol.id : "none",
              icon: orig.icon || "mdi-checkbox-marked",
              color: orig.color || "#9C27B0",
              duration: duration || orig.duration || 30,
              checklist: orig.checklist ? JSON.parse(JSON.stringify(orig.checklist)) : [],
              instructions: orig.instructions || "",
              // Eine manuelle Kopie ist ein zusätzlicher Einzelauftrag. Sie
              // darf nicht als weitere Wochen-/Monatsinstanz der Vorlage
              // zählen oder beim späteren Löschen deren Frequenz unterdrücken.
              templateId: null,
            })
          } else {
            const orig = moveData.rawData || {}
            const appointmentData = {
              date: this.currentDate,
              start: newTime,
              end: endStr,
              title: orig.title || moveData.title || "Termin",
              description: orig.description || "",
              assignedEmployee: isHelper ? null : targetCol.info._id,
              helperColumn: isHelper ? targetCol.id : "none",
              customer: orig.customer ? orig.customer._id || orig.customer : null,
              customer2: orig.customer2 ? orig.customer2._id || orig.customer2 : null,
              icon: orig.icon || "mdi-calendar",
              color: orig.color || "#4CAF50",
              specialType: orig.specialType || null,
              templateId: (orig.templateId && (orig.templateId._id || orig.templateId)) || null,
            }
            if (orig.noAccountData && orig.noAccountData.firstName) {
              appointmentData.noAccountData = orig.noAccountData
            }
            if (orig.noAccountData2 && orig.noAccountData2.firstName) {
              appointmentData.noAccountData2 = orig.noAccountData2
            }
            if (orig.guestData) {
              appointmentData.rawData = { guestData: orig.guestData }
            } else if (orig.rawData && orig.rawData.guestData) {
              appointmentData.rawData = { guestData: orig.rawData.guestData }
            }
            await api.shiftManagement.createAppointment(appointmentData)
          }
        } else {
          // --- NORMALER VERSCHIEBUNGS-UPDATE ---
          if (moveData.isTask) {
            await api.shiftManagement.updateTask(moveData._id, {
              time: newTime,
              assignedEmployee: isHelper ? null : targetCol.info._id,
              helperColumn: isHelper ? targetCol.id : "none",
            })
          } else {
            const payload = {
              start: newTime,
              end: endStr,
              assignedEmployee: isHelper ? null : targetCol.info._id,
              helperColumn: isHelper ? targetCol.id : "none",
            }
            // No-Show Logik
            if (
              targetCol.id === "no_show" &&
              moveData.sourceColId !== "no_show" &&
              moveData.sourceColId !== "ma_info" &&
              moveData.sourceColId !== "unassigned"
            ) {
              payload.originalEmployee = moveData.sourceColId
              payload.status = "noshow"
              payload.isDone = false
            } else if (moveData.sourceColId === "no_show" && !isHelper) {
              // Aus "Nicht gekommen" zurück zu echtem Mitarbeiter verschoben
              payload.originalEmployee = null
              payload.status = "planned"
              payload.isDone = false
            }
            await api.shiftManagement.updateAppointment(moveData._id, payload)
          }
        }

        const isSameCol = moveData.sourceColId === targetCol.id
        let action = ""
        if (isCopy) {
          action = isSameCol ? "kopiert" : `kopiert nach ${targetCol.firstName}`
        } else {
          action = isSameCol ? "verschoben" : `→ ${targetCol.firstName}`
        }
        this.$toast.success(`${moveData.title} ${action} (${newTime})`)
        this.fetchData(true)
      } catch (e) {
        console.error(e)
        const actionStr = isCopy ? "Kopieren" : "Verschieben"
        const msg = e?.response?.data?.message || e.message || `Fehler beim ${actionStr}`
        this.$toast.error(msg)
        await this.fetchData(true)
      }
    },
    async handleBlockResize({ block, col, newDuration, newStartStr, newEndStr }) {
      this.isResizing = true
      try {
        const startStr = newStartStr || block.start
        const payload = {
          start: startStr,
          end: newEndStr,
          duration: newDuration,
        }

        const isPause = block.type === "pause"

        // Kollisionsprüfung (eigene ID ausschliessen)
        const valid = this.isTimeSlotFree(col, startStr, newEndStr, block._id, block.isTask, isPause)
        if (valid !== true) {
          if (this.isHardCalendarCollision(valid, block.isTask)) {
            this.$toast.warning(valid)
            return
          }
          if (!confirm(`Die Änderung kollidiert (${valid}). Trotzdem anwenden?`)) return
        }

        // Optimistisches Frontend-Update (Direkt im UI anzeigen)
        block.start = startStr
        block.end = newEndStr
        block.height = (newDuration / 60) * this.hourHeight
        block.top = this.getTopFromTime(startStr)
        if (block.rawData) {
          block.rawData.start = startStr
          block.rawData.end = newEndStr
          block.rawData.duration = newDuration
        }

        const parentShiftId = block.parentShiftId || block.rawData?.parentShiftId
        if (isPause && parentShiftId) {
          const shift = col.shifts ? col.shifts.find((s) => String(s._id) === String(parentShiftId)) : null
          if (shift) {
            const newTimes = this.rebuildShiftTimesWithPause(shift, startStr, newEndStr, block.timeIndex)
            if (!newTimes) {
              this.fetchData(true)
              return
            }
            await this.updateCalendarShift(parentShiftId, { times: newTimes, ...this.employeeShiftChangeMetadata() })
          }
        } else if (block.isTask) {
          const taskPayload = { duration: newDuration }
          if (newStartStr) {
            taskPayload.time = newStartStr
          }
          await api.shiftManagement.updateTask(block._id, taskPayload)
        } else {
          await api.shiftManagement.updateAppointment(block._id, payload)
        }
        this.$toast.success(`Dauer aktualisiert: ${newDuration} min`)
        this.fetchData(true)
      } catch (e) {
        console.error(e)
        const msg = e?.response?.data?.message || e.message || "Fehler beim Ändern der Dauer"
        this.$toast.error(msg)
        await this.fetchData(true)
      } finally {
        setTimeout(() => {
          this.isResizing = false
        }, 300)
      }
    },
    applyShiftResizeToTimes(times, originalStart, originalEnd, newStart, newEnd) {
      if (!Array.isArray(times) || !times.length) return null

      const adjustedTimes = JSON.parse(JSON.stringify(times))
      const isWorkEntry = (entry) => entry && String(entry.type || "").toLowerCase() !== "pause"
      const isPauseEntry = (entry) => entry && String(entry.type || "").toLowerCase() === "pause"
      const changedStart = newStart && originalStart && newStart !== originalStart
      const changedEnd = newEnd && originalEnd && newEnd !== originalEnd

      if (changedStart) {
        // A legacy split shift can be stored as one outer work interval plus an
        // overlapping pause. Its visible first segment therefore ends before
        // the stored work entry does. The matching boundary is the start
        // itself, not the visible segment's derived end.
        const startEntries = adjustedTimes.filter((entry) => isWorkEntry(entry) && entry.start === originalStart)
        if (startEntries.length === 1) {
          startEntries[0].start = newStart
        } else {
          // With the legacy representation `08:00-21:00 + pause
          // 13:00-17:00`, the second visible segment begins at the end of the
          // pause. Resizing that inner boundary must therefore move the pause,
          // not the outer work interval.
          const adjoiningPauses = adjustedTimes.filter((entry) => isPauseEntry(entry) && entry.end === originalStart)
          if (adjoiningPauses.length !== 1) return null
          adjoiningPauses[0].end = newStart
        }
      }

      if (changedEnd) {
        // Same compatibility rule for the final visible segment of an outer
        // work interval containing a pause.
        const endEntries = adjustedTimes.filter((entry) => isWorkEntry(entry) && entry.end === originalEnd)
        if (endEntries.length === 1) {
          endEntries[0].end = newEnd
        } else {
          // The first visible legacy segment ends where the overlapping pause
          // begins. Keep the outer work interval and move that pause boundary.
          const adjoiningPauses = adjustedTimes.filter((entry) => isPauseEntry(entry) && entry.start === originalEnd)
          if (adjoiningPauses.length !== 1) return null
          adjoiningPauses[0].start = newEnd
        }
      }

      const hasInvalidEntry = adjustedTimes.some((entry) => {
        const start = moment(entry.start, "HH:mm", true)
        const end = moment(entry.end, "HH:mm", true)
        return !start.isValid() || !end.isValid() || !start.isBefore(end)
      })
      if (hasInvalidEntry) return null

      return adjustedTimes.sort((left, right) => {
        const byStart = String(left.start || "").localeCompare(String(right.start || ""))
        if (byStart !== 0) return byStart
        return String(left.end || "").localeCompare(String(right.end || ""))
      })
    },
    handleShiftResize({ col, shiftId, originalStart, originalEnd, newStart, newEnd, times }) {
      this.selectedCol = col
      const shift = (col.shifts || []).find((s) => String(s._id || s.id) === String(shiftId))
      const adjustedTimes = this.applyShiftResizeToTimes(
        Array.isArray(times) && times.length ? times : shift && shift.times,
        originalStart,
        originalEnd,
        newStart,
        newEnd,
      )
      if (!adjustedTimes) {
        this.$toast.error("Die gezogene Schichtzeit konnte nicht eindeutig übernommen werden.")
        return
      }
      this.editShiftData = {
        shiftId: shiftId,
        start: newStart,
        end: newEnd,
        times: adjustedTimes,
        reason: "",
        selectedReason: "",
        customReason: "",
        isOpeningShift: shift
          ? shift.isOpeningShift !== undefined && shift.isOpeningShift !== null
            ? shift.isOpeningShift
            : shift.shiftTemplate
            ? shift.shiftTemplate.isOpeningShift
            : false
          : false,
        isClosingShift: shift
          ? shift.isClosingShift !== undefined && shift.isClosingShift !== null
            ? shift.isClosingShift
            : shift.shiftTemplate
            ? shift.shiftTemplate.isClosingShift
            : false
          : false,
      }
      this.editShiftDialog = true
    },
    async handleQuickCheck(block) {
      try {
        if (block.isTask) {
          const sourceChecklist = Array.isArray(block.rawData && block.rawData.checklist) ? block.rawData.checklist : []
          const updatedChecklist = sourceChecklist.map((checklistItem) => ({ ...checklistItem, isDone: true }))
          await api.shiftManagement.updateTask(block._id, { isDone: true, checklist: updatedChecklist })
          this.$toast.success("Aufgabe als erledigt markiert")
        } else if (block.isAppt) {
          await api.shiftManagement.updateAppointment(block._id, { status: "checkedin" })
          this.$toast.success("Termin als 'Erschienen' markiert")
        }

        // Optimistic UI Update directly on the block
        const targetCol = this.viewColumns.find((c) => String(c.id) === String(block.colId))
        const item =
          (targetCol && Array.isArray(targetCol.items)
            ? targetCol.items.find((candidate) => String(candidate._id || candidate.id) === String(block._id || block.id))
            : null) || block
        item.status = block.isTask ? "completed" : "checkedin"
        if (item.rawData) {
          item.rawData.status = item.status
          if (block.isTask) {
            item.rawData.isDone = true
            const itemChecklist = Array.isArray(item.rawData.checklist) ? item.rawData.checklist : []
            item.rawData.checklist = itemChecklist.map((ch) => ({ ...ch, isDone: true }))
          }
        }

        this.fetchData(true)
      } catch (e) {
        console.error(e)
        this.$toast.error("Fehler beim Aktualisieren")
      }
    },
    confirmPastAppointmentCreation(appointments) {
      const now = moment()
      const pastStarts = (Array.isArray(appointments) ? appointments : [])
        .map((appointment) => moment(`${appointment.date} ${appointment.start}`, "YYYY-MM-DD HH:mm", true))
        .filter((start) => start.isValid() && start.isBefore(now, "minute"))
      if (!pastStarts.length) return true

      const firstStart = moment.min(pastStarts).format("DD.MM.YYYY, HH:mm")
      const message = pastStarts.length === 1
        ? `Der Termin am ${firstStart} liegt in der Vergangenheit.`
        : `${pastStarts.length} Termine liegen in der Vergangenheit (ab ${firstStart}).`
      return confirm(`${message}\nTrotzdem erstellen?`)
    },
    async confirmDrop(data) {
      if (this.dropDialogSaving) return
      if (data.isBatch || data.items) {
        if (data.type !== "task" && !this.confirmPastAppointmentCreation(data.items)) return
        this.dropDialogSaving = true
        try {
          await api.shiftManagement.batchCreateSeries({
            items: data.items,
            type: data.type || "appointment",
            extendSeriesId: data.extendSeriesId || null,
          })
          this.$toast.success("Serie erfolgreich erstellt!")
          this.dropDialog = false
          this.fetchData(true)
        } catch (e) {
          const msg = e?.response?.data?.message || "Fehler beim Erstellen der Serie"
          this.$toast.error(msg)
        } finally {
          this.dropDialogSaving = false
        }
        return
      }

      // colId auslesen (kommt aus unserem neuen Select)
      const { template, time, duration, description, customer, colId } = data
      const targetDate = data.date || this.currentDate
      const targetColId = colId || data.col?.id
      const isRealAppointmentTemplate = !(
        template.type === "task" ||
        template.type === "pause" ||
        template.isPause ||
        template.specialType === "pause"
      )

      if (isRealAppointmentTemplate && (!targetColId || ["none", "unassigned"].includes(String(targetColId)))) {
        this.$toast.error("Bitte zuerst einen Mitarbeiter für den Termin auswählen.")
        return
      }

      // Richtige Spalte anvisieren anhand des Dropdowns im Dialog
      const col =
        this.viewColumns.find((c) => String(c.id) === String(targetColId)) ||
        (targetColId
          ? {
              id: targetColId,
              info: { _id: targetColId },
              isHelper: targetColId === "ma_info" || targetColId === "no_show" || targetColId === "unassigned",
              shifts: [],
            }
          : data.col)
      if (!col) {
        this.$toast.error("Der gewählte Mitarbeiter wurde nicht gefunden.")
        return
      }

      const restrictionMessage = this.appointmentEmployeeRestrictionMessage(template, col)
      if (restrictionMessage) {
        this.$toast.warning(restrictionMessage)
        return
      }

      const endM = moment(`${targetDate} ${time}`, "YYYY-MM-DD HH:mm").add(duration, "minutes")
      const finderSelection = data.finderSelection || null
      const finderSelectionMatches = Boolean(
        finderSelection &&
          String(finderSelection.date) === String(targetDate) &&
          String(finderSelection.start) === String(time) &&
          String(finderSelection.colId) === String(targetColId),
      )

      if (targetDate === this.currentDate && !data.recurrence) {
        const isPause = template.type === "pause" || template.isPause || template.specialType === "pause"
        const valid = this.isTimeSlotFree(col, time, endM.format("HH:mm"), null, template.type === "task", isPause)
        if (valid !== true) {
          if (this.isHardCalendarCollision(valid, template.type === "task")) {
            this.$toast.warning(valid)
            return
          }
          if (!confirm(`Dieser Eintrag kollidiert (${valid}). Trotzdem einfügen?`)) return
        }
      }
      if (isRealAppointmentTemplate && !this.confirmPastAppointmentCreation([{ date: targetDate, start: time }])) return
      this.dropDialogSaving = true
      try {
        const isHelper = col.isHelper || col.id === "ma_info" || col.id === "no_show"
        let tacCreateWarning = null
        if (template.type === "task") {
          await api.shiftManagement.createTask({
            date: targetDate,
            time,
            title: template.title,
            description,
            assignedEmployee: isHelper ? null : col.info._id,
            helperColumn: isHelper ? col.id : "none",
            icon: template.icon,
            color: template.color,
            duration: duration || template.duration || 30,
            checklist: template.checklist ? JSON.parse(JSON.stringify(template.checklist)) : [],
            instructions: template.instructions || "",
            templateId: template._id || template.id,
            recurrence: data.recurrence,
          })
        } else if (template.type === "pause" || template.isPause || template.specialType === "pause") {
          if (isHelper) throw new Error("Pausen können nur für echte Mitarbeiter erstellt werden.")
          if (!col.shifts || !col.shifts.length) throw new Error("Mitarbeiter hat an diesem Tag keine Schicht.")

          let targetShift = null
          const pauseStart = moment(time, "HH:mm")
          const pauseEnd = moment(endM.format("HH:mm"), "HH:mm")
          for (const shift of col.shifts) {
            const shiftSegments = this.getShiftCoverageSegments(shift)
            if (
              shiftSegments.some((segment) => {
                const shiftStart = moment(segment.start, "HH:mm")
                const shiftEnd = moment(segment.end, "HH:mm")
                return pauseStart.isSameOrAfter(shiftStart) && pauseEnd.isSameOrBefore(shiftEnd)
              })
            ) {
              targetShift = shift
              break
            }
          }
          if (!targetShift) throw new Error("Die Pause muss vollständig innerhalb der Arbeitszeit der Schicht liegen.")

          const newTimes = this.rebuildShiftTimesWithPause(targetShift, time, endM.format("HH:mm"))
          if (!newTimes) return
          await this.updateCalendarShift(targetShift._id, { times: newTimes, ...this.employeeShiftChangeMetadata() })
        } else {
          const appointmentData = {
            date: targetDate,
            start: time,
            end: endM.format("HH:mm"),
            title: template.title,
            description,
            assignedEmployee: isHelper ? null : col.info._id,
            helperColumn: isHelper ? col.id : "none",
            customer: customer ? customer : null,
            customer2: data.customer2 || null,
            icon: template.icon,
            color: template.color,
            specialType: template.specialType || null,
            templateId: template._id || template.id,
            recurrence: data.recurrence,
            collisionBehavior: data.collisionBehavior || "fail",
            checkTaskConflicts: true,
            allowTaskOverlap: finderSelectionMatches && finderSelection.allowTaskOverlap === true,
          }
          if (data.tacBillingRequest) {
            appointmentData.tacBillingRequest = data.tacBillingRequest
          }
          if (data.noAccountData && data.noAccountData.firstName) {
            appointmentData.noAccountData = data.noAccountData
          }
          if (data.noAccountData2 && data.noAccountData2.firstName) {
            appointmentData.noAccountData2 = data.noAccountData2
          }
          if (data.guestData) {
            appointmentData.rawData = { guestData: data.guestData }
          }
          const createdAppointment = await api.shiftManagement.createAppointment(appointmentData)
          if (createdAppointment?.tacWarning) {
            tacCreateWarning = createdAppointment.tacWarning
          }
          if (createdAppointment?.onlineMeeting && createdAppointment.onlineMeeting.status !== "ready") {
            tacCreateWarning = { message: "Online Meeting gespeichert. Den Teams-Link bitte im Termin erneut vorbereiten." }
          }
        }
        if (tacCreateWarning) {
          this.$toast.warning(tacCreateWarning.message || "Der Termin wurde erstellt, der TAC-Status muss aber geprüft werden.")
        } else if (!data.tacBillingRequest) {
          this.$toast.success("Erstellt!")
        } else {
          this.$toast.success(`${data.template?.title || "Termin"} erstellt und 1 Einheit in TAC abgebucht.`)
        }
        this.dropDialog = false
        if (targetDate !== this.currentDate) {
          this.currentDate = targetDate
          this.fetchData(false)
        } else {
          this.fetchData(true)
        }
      } catch (e) {
        if (e?.response?.status === 409 && e.response.data?.collision) {
          this.collisionsList = e.response.data.collidingDates || []
          this.collisionPendingData = data
          this.collisionDialog = true
          return
        }
        console.error(e)
        const msg = e?.response?.data?.message || e.message || "Fehler beim Speichern"
        this.$toast.error(msg)
      } finally {
        this.dropDialogSaving = false
      }
    },
    async resolveCollision(behavior) {
      if (behavior === "cancel") {
        this.collisionDialog = false
        this.collisionsList = []
        this.collisionPendingData = null
        return
      }

      const pendingData = {
        ...this.collisionPendingData,
        collisionBehavior: behavior,
      }

      this.collisionDialog = false
      this.collisionsList = []
      this.collisionPendingData = null

      await this.confirmDrop(pendingData)
    },
    formatDateGerman(dateStr) {
      if (!dateStr) return ""
      return moment(dateStr, "YYYY-MM-DD").format("DD.MM.YYYY")
    },
    async saveEdit(data, saveContext = {}) {
      const notifySaveContext = (name, payload) => {
        const callback = saveContext && typeof saveContext[name] === "function" ? saveContext[name] : null
        if (!callback) return
        try {
          callback(payload)
        } catch (callbackError) {
          console.warn(`Finder-Speicherstatus konnte nicht aktualisiert werden (${name}).`, callbackError)
        }
      }
      const savedFromFinder = saveContext && saveContext.source === "finder"

      const willMoveToNoShow = ["cancelled", "noshow"].includes(String(data.status || ""))
      if (
        !data.isTask &&
        !data.isPause &&
        !willMoveToNoShow &&
        (!data.colId || ["none", "unassigned"].includes(String(data.colId)))
      ) {
        this.$toast.error("Der Termin wurde nicht gespeichert: Bitte einen Mitarbeiter auswählen.")
        notifySaveContext("onCancel")
        return false
      }

      // Validierung (Zeitraum prüfen)
      const targetCol = this.viewColumns.find((c) => String(c.id) === String(data.colId))
      const requestedFinderSelection = data.finderSelection || null
      const requestedFinderSelectionMatches = Boolean(
        requestedFinderSelection &&
          String(requestedFinderSelection.date) === String(data.date) &&
          String(requestedFinderSelection.start) === String(data.start) &&
          String(requestedFinderSelection.colId) === String(data.colId),
      )

      let endStr = data.end
      if (!data.isTask) {
        endStr = moment(data.start, "HH:mm").add(data.duration, "minutes").format("HH:mm")
      } else {
        endStr = moment(data.start, "HH:mm")
          .add(data.duration || 30, "minutes")
          .format("HH:mm")
      }

      if (data.date === this.currentDate && targetCol && !requestedFinderSelectionMatches) {
        const valid = this.isTimeSlotFree(targetCol, data.start, endStr, data.id, data.isTask, data.isPause)
        if (valid !== true) {
          if (this.isHardCalendarCollision(valid, data.isTask)) {
            this.$toast.warning(valid)
            notifySaveContext("onCancel")
            return false
          }
          if (!confirm(`Dieser Eintrag kollidiert (${valid}). Trotzdem speichern?`)) {
            notifySaveContext("onCancel")
            return false
          }
        }
      }

      try {
        if (data.isTask) {
          let checklist = data.checklist || []
          if (data.isDone) {
            checklist = checklist.map((item) => ({ ...item, isDone: true }))
          }
          const isHelper = data.colId === "ma_info" || data.colId === "no_show" || data.colId === "unassigned"
          const payload = {
            title: data.title,
            description: data.description,
            color: data.color,
            time: data.start, // Tasks use time
            date: data.date,
            duration: data.duration || 30,
            assignedEmployee: isHelper ? null : data.colId,
            helperColumn: isHelper ? data.colId : "none",
            checklist: checklist,
            instructions: data.instructions || "",
            isDone: data.isDone || false,
          }
          await api.shiftManagement.updateTask(data.id, payload)
        } else if (data.isPause && data.parentShiftId) {
          // PAUSE UPDATE (ShiftPlan)
          const col = this.columns.find((c) => String(c.info._id) === String(data.colId))
          if (!col) throw new Error("Mitarbeiter nicht gefunden")

          const shift = col.shifts.find((s) => String(s._id) === String(data.parentShiftId))
          if (!shift) throw new Error("Schicht nicht gefunden")

          const newTimes = this.rebuildShiftTimesWithPause(shift, data.start, endStr, data.timeIndex)
          if (!newTimes) {
            this.fetchData(true)
            return
          }

          await this.updateCalendarShift(data.parentShiftId, { times: newTimes, ...this.employeeShiftChangeMetadata() })
        } else {
          // TERMIN UPDATE
          let assignedColId = data.colId
          let finalStatus = data.status

          // Eine bewusste Neuplatzierung aus dem Finder reaktiviert abgesagte
          // bzw. No-Show-Termine. Sonst würde die Statuslogik den Termin trotz
          // gewähltem Mitarbeiter wieder in die Systemspalte verschieben.
          if (
            savedFromFinder &&
            requestedFinderSelectionMatches &&
            !["ma_info", "no_show", "unassigned"].includes(String(assignedColId)) &&
            ["cancelled", "noshow"].includes(finalStatus)
          ) {
            finalStatus = "planned"
          }

          // Automatisch in "Nicht gekommen" (no_show) verschieben, wenn Status abgesagt/noshow ist
          if ((finalStatus === "cancelled" || finalStatus === "noshow") && assignedColId !== "ma_info") {
            assignedColId = "no_show"
          }

          const isHelper = assignedColId === "ma_info" || assignedColId === "no_show" || assignedColId === "unassigned"
          const referenceId = (value) => {
            if (!value) return null
            if (typeof value !== "object") return value
            const nestedUser = value.user
            return value._id || value.id || (typeof nestedUser === "object" ? nestedUser?._id || nestedUser?.id : nestedUser) || null
          }
          const customerPatch = {}
          const originalCustomerId = referenceId(this.editData?.customer)
          const originalCustomer2Id = referenceId(this.editData?.customer2)
          const selectedCustomerId = referenceId(data.customer)
          const selectedCustomer2Id = referenceId(data.customer2)

          if (String(originalCustomerId || "") !== String(selectedCustomerId || "")) {
            customerPatch.customer = selectedCustomerId
            customerPatch.noAccountData = null
          }
          if (String(originalCustomer2Id || "") !== String(selectedCustomer2Id || "")) {
            customerPatch.customer2 = selectedCustomer2Id
            customerPatch.noAccountData2 = null
          }

          const payload = {
            title: data.title,
            description: data.description,
            color: data.color,
            start: data.start,
            end: endStr,
            status: finalStatus,
            isDone: finalStatus === "done",
            assignedEmployee: isHelper ? null : assignedColId,
            helperColumn: isHelper ? assignedColId : "none",
            date: data.date,
            icon: data.icon,
            specialType: data.specialType || null,
            templateId: data.templateId || null,
            ...customerPatch,
          }

          // Wenn der Termin (manuell oder automatisch) in "Nicht gekommen" geschoben wird
          if (assignedColId === "no_show" && this.editData.colId !== "no_show" && this.editData.colId !== "ma_info") {
            payload.originalEmployee = this.editData.colId
            // Falls nur der Mitarbeiter auf "Nicht gekommen" geändert wurde, ohne den Status anzupassen:
            if (payload.status === "planned" || payload.status === "checkedin" || payload.status === "done") {
              payload.status = "noshow"
            }
            payload.isDone = false
          } else if (this.editData.colId === "no_show" && assignedColId !== "no_show" && assignedColId !== "ma_info") {
            // Aus "Nicht gekommen" zurück zu echtem Mitarbeiter verschoben
            payload.originalEmployee = null
            if (payload.status === "noshow") {
              payload.status = "planned"
            }
            payload.isDone = payload.status === "done"
          }

          const finderSelection = requestedFinderSelection
          const finderSelectionMatches = Boolean(
            finderSelection &&
              String(finderSelection.date) === String(data.date) &&
              String(finderSelection.start) === String(data.start) &&
              String(finderSelection.colId) === String(assignedColId),
          )

          if (finderSelectionMatches) {
            const moveResult = await api.shiftManagement.applyAppointmentClipboard({
              action: "move",
              sourceAppointmentId: data.id,
              sourceUpdatedAt: data.sourceUpdatedAt || null,
              target: {
                date: data.date,
                start: data.start,
                end: endStr,
                assignedEmployee: isHelper ? null : assignedColId,
                helperColumn: isHelper ? assignedColId : "none",
                checkTaskConflicts: true,
                allowTaskOverlap: finderSelection.allowTaskOverlap === true,
              },
              appointmentPatch: {
                title: payload.title,
                description: payload.description,
                color: payload.color,
                icon: payload.icon,
                specialType: payload.specialType,
                templateId: payload.templateId,
                status: payload.status,
                isDone: payload.isDone,
                ...customerPatch,
              },
            })
            if (moveResult?.action !== "move" || String(moveResult?.appointment?._id || "") !== String(data.id)) {
              throw new Error("Die Verschiebung wurde vom Server nicht eindeutig bestätigt.")
            }
          } else {
            await api.shiftManagement.updateAppointment(data.id, payload)
          }
        }

        this.$toast.success(savedFromFinder ? "Termin verschoben und gespeichert" : "Gespeichert")
        this.editDialog = false

        // Wenn das Datum geändert wurde, müssen wir evtl. zu dem Tag springen oder einfach neu laden
        if (data.date !== this.currentDate) {
          if (savedFromFinder) {
            this.currentDate = data.date
          } else {
            const switchDate = confirm("Termin wurde auf einen anderen Tag verschoben. Zu diesem Tag springen?")
            if (switchDate) {
              this.currentDate = data.date
            }
          }
        }

        if (savedFromFinder) {
          // Warten, bis die verschobene Termin-ID am Ziel neu geladen wurde.
          // Ein Socket-Refresh kann diesen Request überholen; in diesem Fall
          // laden wir einmal gezielt nach, bevor der Finder Erfolg meldet.
          const refreshed = await this.fetchData(true)
          if (!refreshed) await this.fetchData(true)
        } else {
          this.fetchData(true) // Refresh silent
        }
        notifySaveContext("onSuccess", data)
        return true
      } catch (e) {
        console.error(e)
        const msg = e?.response?.data?.message || e.message || "Fehler beim Speichern"
        this.$toast.error(msg)
        notifySaveContext("onError", e)
        return false
      }
    },
    async deleteItem(data) {
      try {
        const scope = data.deleteScope || "single"
        if (data.isTask) {
          // Automatisch erzeugte Ämtli werden serverseitig als bewusst
          // ausgelassen markiert; manuell erstellte Aufgaben werden gelöscht.
          if (scope === "single" && !data.deleteScope) {
            if (!confirm("Ämtli wirklich löschen? Bei einer normalen Neuplanung wird es nicht automatisch ersetzt.")) return
          }
          await api.shiftManagement.deleteTask(data.id, scope)
        } else if (data.isPause && data.parentShiftId) {
          // PAUSE DELETE (ShiftPlan) - Pausen werden direkt entfernt
          if (!confirm("Pause wirklich löschen?")) return
          const col = this.columns.find((c) => String(c.info._id) === String(data.colId))
          if (!col) throw new Error("Mitarbeiter nicht gefunden")

          const shift = col.shifts.find((s) => String(s._id) === String(data.parentShiftId))
          if (!shift) throw new Error("Schicht nicht gefunden")

          const finalTimes = this.removePauseFromShiftTimes(shift, data.timeIndex)
          if (!finalTimes) return
          await this.updateCalendarShift(data.parentShiftId, { times: finalTimes, ...this.employeeShiftChangeMetadata() })
        } else {
          // Termin: Soft-Delete mit Grund (kommt aus dem Dialog)
          const reason = data.deleteReason || ""
          await api.shiftManagement.deleteAppointment(data.id, reason, scope)
        }
        this.editDialog = false
        this.fetchData(true)
      } catch (e) {
        console.error(e)
        const msg = e?.response?.data?.message || e.message || "Fehler"
        this.$toast.error(msg)
      }
    },
    async markTaskAsDone(payload) {
      try {
        await api.shiftManagement.markTemplateAsDone(payload)
        this.fetchData(true)
      } catch (e) {
        this.$toast.error("Fehler beim Abhaken")
      }
    },
    async unmarkTaskAsDone(completionId) {
      try {
        await api.shiftManagement.removeTemplateDone(completionId)
        this.fetchData(true)
      } catch (e) {
        this.$toast.error("Fehler beim Entfernen")
      }
    },
    getTaskUrgencyLevel(tpl) {
      if (this.isTaskTargetPlanned(tpl)) return 0
      const refDate = this.$moment(this.currentDate)

      if (tpl.frequency === "weekly") {
        const day = refDate.isoWeekday()
        if (day >= 5) return 2 // Friday, Saturday, Sunday = Urgent
        if (day === 4) return 1 // Thursday = Warning
      } else if (tpl.frequency === "monthly") {
        const daysInMonth = refDate.daysInMonth()
        const currentDay = refDate.date()
        const daysLeft = daysInMonth - currentDay
        if (daysLeft <= 3) return 2 // Last 3 days = Urgent
        if (daysLeft <= 7) return 1 // Last 7 days = Warning
      }
      return 0
    },
    getDailyTaskPlanningState(tpl) {
      if (tpl && tpl.isAssigned && tpl.assignedTo) {
        return { label: "Eingeplant", color: "#2196f3", icon: "mdi-calendar-check" }
      }
      if (tpl && tpl.isAssigned) {
        return { label: "Eingeplant · ohne Mitarbeiter", color: "#f59e0b", icon: "mdi-account-alert-outline" }
      }
      return { label: "Noch nicht eingeplant", color: "var(--text-muted)", icon: "mdi-calendar-blank-outline" }
    },
    getTaskCountInfo(tpl, frequency) {
      const target = this.getTaskTargetCount(tpl, frequency)
      if (!this.completions) return { current: 0, planned: 0, open: 0, done: 0, target }
      const templateCompletions = this.completions.filter((c) => String(c.template) === String(tpl._id || tpl.id))
      const refDate = this.$moment(this.currentDate)
      let periodEntries = []
      if (frequency === "weekly") {
        periodEntries = templateCompletions.filter((c) => this.$moment(c.date).isSame(refDate, "isoWeek"))
      } else if (frequency === "monthly") {
        periodEntries = templateCompletions.filter((c) => this.$moment(c.date).isSame(refDate, "month"))
      } else {
        // Tägliche und manuelle/einmalige Vorlagen gelten für genau den
        // ausgewählten Kalendertag.
        periodEntries = templateCompletions.filter((c) => this.$moment(c.date).isSame(refDate, "day"))
      }
      periodEntries = this.getEffectiveTaskProgressEntries(periodEntries)
      const scheduledEntries = periodEntries.filter((entry) => !this.isManualTemplateCompletion(entry))
      // "Eingeplant" means an actual calendar entry. A manual completion is
      // still shown as completion info, but must not cross out the template.
      const planned = scheduledEntries.length
      const done = periodEntries.filter((entry) => this.isTemplateProgressDone(entry)).length
      const open = scheduledEntries.filter((entry) => !this.isTemplateProgressDone(entry)).length
      return { current: planned, planned, open, done, target }
    },
    getEffectiveTaskProgressEntries(entries) {
      const scheduled = (entries || []).filter((entry) => !this.isManualTemplateCompletion(entry))
      const scheduledDates = new Set(scheduled.map((entry) => String(entry.date || "")))
      const manualByDate = new Map()
      ;(entries || [])
        .filter((entry) => this.isManualTemplateCompletion(entry))
        .forEach((entry) => {
          const dateKey = String(entry.date || "")
          if (!scheduledDates.has(dateKey) && !manualByDate.has(dateKey)) manualByDate.set(dateKey, entry)
        })
      return [...scheduled, ...manualByDate.values()]
    },
    getTaskTargetCount(tpl, frequency) {
      const config = (tpl && tpl.frequencyConfig) || {}
      if (config.daySelectionMode === "all") {
        if (frequency === "weekly" && Array.isArray(config.daysOfWeek) && config.daysOfWeek.length) {
          return new Set(config.daysOfWeek.map(Number).filter((day) => day >= 1 && day <= 7)).size || 1
        }
        if (frequency === "monthly" && Array.isArray(config.daysOfMonth) && config.daysOfMonth.length) {
          return new Set(config.daysOfMonth.map(Number).filter((day) => day >= 1 && day <= 31)).size || 1
        }
      }
      return Math.max(1, Number(tpl && tpl.targetCount) || 1)
    },
    getTaskCountString(tpl, frequency) {
      const { planned, target } = this.getTaskCountInfo(tpl, frequency)
      return `${planned}/${target} eingeplant`
    },
    getAllCompletions(tpl) {
      if (!this.completions) return []
      const refDate = this.$moment(this.currentDate)
      const entries = this.completions
        .filter((c) => String(c.template) === String(tpl._id || tpl.id))
        .filter((c) => {
          if (tpl.frequency === "weekly") return this.$moment(c.date).isSame(refDate, "isoWeek")
          if (tpl.frequency === "monthly") return this.$moment(c.date).isSame(refDate, "month")
          return this.$moment(c.date).isSame(refDate, "day")
        })
      return this.getEffectiveTaskProgressEntries(entries)
        .filter((entry) => !this.isManualTemplateCompletion(entry))
        .sort((a, b) => (a.date > b.date ? -1 : 1))
    },
    isTaskDone(tpl) {
      const { done, open, target } = this.getTaskCountInfo(tpl, tpl.frequency)
      return done >= target && open === 0
    },
    isTaskTargetPlanned(tpl) {
      const { planned, target } = this.getTaskCountInfo(tpl, tpl.frequency)
      return planned >= target
    },
    isManualTemplateCompletion(entry) {
      return Boolean(entry && (entry.completedBy || entry.source === "manual"))
    },
    isTemplateProgressDone(entry) {
      if (!entry) return false
      const status = String(entry.status || "").toLowerCase()
      return entry.isDone === true || Boolean(entry.doneAt) || status === "done" || status === "completed" || this.isManualTemplateCompletion(entry)
    },
    getTemplateProgressState(entry) {
      if (entry && entry.assignedEmployee) {
        return { label: "Eingeplant", color: "#2196f3", icon: "mdi-calendar-check" }
      }
      return { label: "Eingeplant · ohne Mitarbeiter", color: "#f59e0b", icon: "mdi-account-alert-outline" }
    },
    toggleTaskCompletion(tpl) {
      if (this.isTaskDone(tpl)) {
        const templateCompletions = this.completions.filter(
          (c) => String(c.template) === String(tpl._id || tpl.id) && this.isManualTemplateCompletion(c),
        )
        const refDate = this.$moment(this.currentDate)
        let toDelete = null
        if (tpl.frequency === "weekly") {
          toDelete = templateCompletions.find((c) => this.$moment(c.date).isSame(refDate, "isoWeek"))
        } else if (tpl.frequency === "monthly") {
          toDelete = templateCompletions.find((c) => this.$moment(c.date).isSame(refDate, "month"))
        }
        if (toDelete) {
          this.unmarkTaskAsDone(toDelete._id)
        } else {
          this.$toast.info("Dieses Ämtli wurde direkt im Kalender erledigt. Öffne den Eintrag, um den Status zu ändern.")
        }
      } else {
        this.markTaskAsDone({ templateId: tpl._id || tpl.id, date: this.$moment().format("YYYY-MM-DD") })
      }
    },
    triggerSpecialAction(data) {
      const type = data.specialType
      const cid = data.customer?._id
      if (!cid) {
        if (type === "new_contract") this.$router.push("/dashboard/contract")
        return
      }
      if (type === "workout") this.$router.push(`/workouts/createnew?userId=${cid}`)
      else if (type === "anamnese") this.$router.push(`/dashboard/user/search/${cid}`)
      else if (type === "upgrade") this.$router.push(`/dashboard/contract?userId=${cid}&upgrade=true`)
      else if (type === "new_contract") this.$router.push(`/dashboard/contract?userId=${cid}`)
    },
    getTaskTimeMode(tpl) {
      if (tpl && Array.isArray(tpl.defaultTimes) && tpl.defaultTimes.some((time) => time && String(time).trim())) return "fixed_times"
      const mode = tpl && tpl.timePreference ? tpl.timePreference.mode : null
      return ["preferred_window", "required_window"].includes(mode) ? mode : "flexible"
    },
    normalizeTaskTimePreference(tpl) {
      const source = tpl && tpl.timePreference && typeof tpl.timePreference === "object" ? tpl.timePreference : {}
      return {
        mode: ["preferred_window", "required_window"].includes(source.mode) ? source.mode : "flexible",
        windowStart: source.windowStart || "09:00",
        windowEnd: source.windowEnd || "12:00",
      }
    },
    setTaskTimeMode(tpl, mode) {
      if (!tpl) return
      const normalizedMode = ["flexible", "preferred_window", "required_window", "fixed_times"].includes(mode) ? mode : "flexible"
      this.$set(tpl, "timePlanningMode", normalizedMode)
      if (!tpl.timePreference) this.$set(tpl, "timePreference", this.normalizeTaskTimePreference(tpl))

      if (normalizedMode === "fixed_times") {
        this.$set(tpl, "hasFixedTimes", true)
        if (!Array.isArray(tpl.defaultTimes)) this.$set(tpl, "defaultTimes", [])
        if (!tpl.defaultTimes.length) tpl.defaultTimes.push("12:00")
        this.$set(tpl.timePreference, "mode", "flexible")
      } else {
        this.$set(tpl, "hasFixedTimes", false)
        this.$set(tpl, "defaultTimes", [])
        this.$set(tpl.timePreference, "mode", normalizedMode)
        if (!tpl.timePreference.windowStart) this.$set(tpl.timePreference, "windowStart", "09:00")
        if (!tpl.timePreference.windowEnd) this.$set(tpl.timePreference, "windowEnd", "12:00")
      }
    },
    getTaskTimeModeHelp(mode) {
      if (mode === "preferred_window")
        return "Das System versucht zuerst das gewählte Zeitfenster. Nur wenn dort nichts frei ist, nimmt es eine andere kollisionsfreie Zeit."
      if (mode === "required_window")
        return "Das Ämtli darf nur im gewählten Zeitfenster liegen. Ist dort nichts frei, bleibt es als unsichtbarer Planungsvorschlag im Ämtli-Manager, bis du es zuweist oder löschst."
      if (mode === "fixed_times") return "Das Ämtli beginnt exakt zu jeder eingetragenen Startzeit. Mehrere Startzeiten erzeugen mehrere Einträge."
      return "Das System wählt innerhalb der Arbeitszeit die beste freie Stelle ohne Termin- oder Ämtli-Kollision."
    },
    validateTaskTimeSettings(tpl) {
      if (!tpl || (tpl.category !== "ämtli" && tpl.category !== "aufgabe" && tpl.type !== "task")) return null
      const mode = tpl.timePlanningMode || this.getTaskTimeMode(tpl)
      if (mode === "fixed_times") {
        const times = (tpl.defaultTimes || []).filter((time) => /^([01]\d|2[0-3]):[0-5]\d$/.test(String(time || "")))
        if (!times.length) return "Bitte mindestens eine gültige feste Startzeit eintragen."
        return null
      }
      if (mode !== "preferred_window" && mode !== "required_window") return null
      const preference = tpl.timePreference || {}
      const start = preference.windowStart
      const end = preference.windowEnd
      if (!/^([01]\d|2[0-3]):[0-5]\d$/.test(String(start || "")) || !/^([01]\d|2[0-3]):[0-5]\d$/.test(String(end || ""))) {
        return "Bitte ein gültiges Zeitfenster eintragen."
      }
      const startMoment = this.$moment(start, "HH:mm")
      const endMoment = this.$moment(end, "HH:mm")
      if (!startMoment.isBefore(endMoment)) return "Das Zeitfenster muss vor seinem Ende beginnen."
      if (endMoment.diff(startMoment, "minutes") < (Number(tpl.duration) || 30)) {
        return "Das Zeitfenster ist kürzer als die Dauer des Ämtlis."
      }
      return null
    },
    hasSelectedTaskDays(tpl) {
      if (!tpl || !tpl.frequencyConfig) return false
      if (tpl.frequency === "weekly") return Array.isArray(tpl.frequencyConfig.daysOfWeek) && tpl.frequencyConfig.daysOfWeek.length > 0
      if (tpl.frequency === "monthly") return Array.isArray(tpl.frequencyConfig.daysOfMonth) && tpl.frequencyConfig.daysOfMonth.length > 0
      return false
    },
    hasSelectedWeekendTaskDay(tpl) {
      if (!tpl || !tpl.frequencyConfig) return false
      if (tpl.frequency === "weekly") {
        return (tpl.frequencyConfig.daysOfWeek || []).some((day) => Number(day) === 6 || Number(day) === 7)
      }
      return tpl.frequency === "monthly" && (tpl.frequencyConfig.daysOfMonth || []).length > 0
    },
    inferTaskDaySelectionMode(tpl, config = null) {
      const frequencyConfig = config || (tpl && tpl.frequencyConfig) || {}
      if (["all", "one"].includes(frequencyConfig.daySelectionMode)) return frequencyConfig.daySelectionMode
      const frequency = tpl && tpl.frequency
      const selectedDays = frequency === "weekly" ? frequencyConfig.daysOfWeek : frequency === "monthly" ? frequencyConfig.daysOfMonth : []
      const legacyCount = frequency === "weekly"
        ? (frequencyConfig.timesPerWeek ?? tpl?.timesPerWeek)
        : (frequencyConfig.timesPerMonth ?? tpl?.timesPerMonth)
      // Legacy counts denote a number of occurrences within the selected
      // days, matching the server's interpretation of the stored settings.
      if (legacyCount !== undefined) return "one"
      return Array.isArray(selectedDays) && selectedDays.length > 0 ? "all" : "one"
    },
    taskFrequencyTargetCount(tpl) {
      const config = tpl?.frequencyConfig || {}
      const configuredMode = ["all", "one"].includes(config.daySelectionMode)
      const legacyCount = tpl?.frequency === "weekly"
        ? (config.timesPerWeek ?? tpl?.timesPerWeek)
        : (config.timesPerMonth ?? tpl?.timesPerMonth)
      const countCandidates = configuredMode ? [tpl.targetCount, legacyCount, 1] : [legacyCount, tpl.targetCount, 1]
      const targetCount = countCandidates.map(Number).find((count) => Number.isFinite(count) && count > 0)
      return Math.max(1, Math.floor(targetCount))
    },
    normalizeTaskFrequencyConfig(tpl) {
      if (!tpl) return
      const targetCount = this.taskFrequencyTargetCount(tpl)
      if (!tpl.frequencyConfig) this.$set(tpl, "frequencyConfig", {})
      if (!Array.isArray(tpl.frequencyConfig.daysOfWeek)) this.$set(tpl.frequencyConfig, "daysOfWeek", [])
      if (!Array.isArray(tpl.frequencyConfig.daysOfMonth)) this.$set(tpl.frequencyConfig, "daysOfMonth", [])
      if (tpl.frequency === "weekly" && tpl.weekendConfig && tpl.weekendConfig.mode === "skip_weekend") {
        this.$set(
          tpl.frequencyConfig,
          "daysOfWeek",
          tpl.frequencyConfig.daysOfWeek.filter((day) => Number(day) >= 1 && Number(day) <= 5),
        )
      }
      if (!["all", "one"].includes(tpl.frequencyConfig.daySelectionMode)) {
        this.$set(tpl.frequencyConfig, "daySelectionMode", this.inferTaskDaySelectionMode(tpl, tpl.frequencyConfig))
      }
      if (!this.hasSelectedTaskDays(tpl)) this.$set(tpl.frequencyConfig, "daySelectionMode", "one")
      this.$set(tpl, "targetCount", targetCount)
    },
    getTemplateTimeSummary(tpl) {
      const mode = (tpl && tpl.timePlanningMode) || this.getTaskTimeMode(tpl)
      if (mode === "fixed_times") return (tpl.defaultTimes || []).join(", ") || "Feste Zeit"
      const preference = (tpl && tpl.timePreference) || {}
      const range = preference.windowStart && preference.windowEnd ? `${preference.windowStart}–${preference.windowEnd}` : "Zeitfenster"
      if (mode === "required_window") return `Nur ${range}`
      if (mode === "preferred_window") return `Bevorzugt ${range}`
      return "Flexibel ohne Kollision"
    },
    getTemplatePlanningDaysSummary(tpl) {
      if (!tpl || tpl.frequency === "once") return "Nur manuell"
      if (tpl.frequency === "daily") return "Jeden Tag"
      if (tpl.frequency === "weekly") {
        const count = tpl.frequencyConfig && Array.isArray(tpl.frequencyConfig.daysOfWeek) ? tpl.frequencyConfig.daysOfWeek.length : 0
        return count ? `${count} Wochentag${count === 1 ? "" : "e"}` : "Flexibel"
      }
      if (tpl.frequency === "monthly") {
        const count = tpl.frequencyConfig && Array.isArray(tpl.frequencyConfig.daysOfMonth) ? tpl.frequencyConfig.daysOfMonth.length : 0
        return count ? `${count} Monatstag${count === 1 ? "" : "e"}` : "Flexibel"
      }
      return "Bitte prüfen"
    },
    getAmtliFrequencyLabel(tpl) {
      const frequency = tpl && tpl.frequency
      if (frequency === "daily") return "Täglich"
      if (frequency === "weekly") return "Wöchentlich"
      if (frequency === "monthly") return "Monatlich"
      if (frequency === "once") return "Nur manuell"
      return "Frequenz prüfen"
    },
    getAmtliTemplateReadiness(tpl) {
      if (!tpl || !String(tpl.title || "").trim()) {
        return { ready: false, label: "Titel fehlt", message: "Bitte einen Titel eintragen.", color: "warning", icon: "mdi-alert-outline" }
      }
      const duration = Number(tpl.duration)
      if (!Number.isFinite(duration) || duration < 1 || duration > 24 * 60) {
        return {
          ready: false,
          label: "Dauer prüfen",
          message: "Bitte eine gültige Dauer eintragen.",
          color: "warning",
          icon: "mdi-timer-alert-outline",
        }
      }
      if (tpl.frequency === "once") {
        return {
          ready: false,
          manualOnly: true,
          label: "Nur manuell",
          message: "Diese Vorlage ist für die Sidebar bereit. Für die Automatik bitte eine Wiederholung wählen.",
          color: "info",
          icon: "mdi-hand-back-right-outline",
        }
      }
      if (!["daily", "weekly", "monthly"].includes(tpl.frequency)) {
        return {
          ready: false,
          label: "Frequenz prüfen",
          message: "Bitte eine gültige Wiederholung wählen.",
          color: "warning",
          icon: "mdi-calendar-alert",
        }
      }
      const targetCount = Number(tpl.targetCount || 1)
      if (!Number.isFinite(targetCount) || targetCount < 1 || targetCount > 31) {
        return {
          ready: false,
          label: "Anzahl prüfen",
          message: "Die Häufigkeit muss zwischen 1 und 31 liegen.",
          color: "warning",
          icon: "mdi-counter",
        }
      }
      const timeError = this.validateTaskTimeSettings(tpl)
      if (timeError) {
        return { ready: false, label: "Zeit prüfen", message: timeError, color: "warning", icon: "mdi-clock-alert-outline" }
      }
      return {
        ready: true,
        label: "Bereit für Automatik",
        message: "Diese Vorlage kann automatisch geplant werden.",
        color: "success",
        icon: "mdi-check-circle-outline",
      }
    },
    getAmtliTemplatePlainSummary(tpl) {
      if (!tpl) return ""
      if (tpl.frequency === "once") {
        return `Bei Bedarf manuell aus der Sidebar einfügen · ${Number(tpl.duration) || 30} Min.`
      }
      const frequencyConfig = tpl.frequencyConfig || {}
      const weekdayLabels = { 1: "Mo", 2: "Di", 3: "Mi", 4: "Do", 5: "Fr", 6: "Sa", 7: "So" }
      const selectedWeekdays = (frequencyConfig.daysOfWeek || []).map((day) => weekdayLabels[Number(day)]).filter(Boolean)
      const selectedMonthDays = (frequencyConfig.daysOfMonth || []).map(Number).filter((day) => day >= 1 && day <= 31)
      const targetCount = Math.max(1, Number(tpl.targetCount) || 1)
      const selectionMode = this.inferTaskDaySelectionMode(tpl, frequencyConfig)
      let frequencyText = "Täglich"

      if (tpl.frequency === "weekly") {
        if (selectedWeekdays.length && selectionMode === "all") frequencyText = `Wöchentlich an ${selectedWeekdays.join(", ")}`
        else if (selectedWeekdays.length) frequencyText = `${targetCount}× pro Woche, bevorzugt ${selectedWeekdays.join(", ")}`
        else frequencyText = `${targetCount}× pro Woche, flexibel verteilt`
      } else if (tpl.frequency === "monthly") {
        if (selectedMonthDays.length && selectionMode === "all") frequencyText = `Monatlich am ${selectedMonthDays.join("., ")}.`
        else if (selectedMonthDays.length) frequencyText = `${targetCount}× pro Monat, bevorzugt Tag ${selectedMonthDays.join(", ")}`
        else frequencyText = `${targetCount}× pro Monat, flexibel verteilt`
      }

      const weekendText = tpl.weekendConfig && tpl.weekendConfig.mode === "skip_weekend" ? "Wochenende ausgeschlossen" : "Wochenende erlaubt"
      let assignmentText = "Alle anwesenden Mitarbeitenden"
      if (tpl.fixedEmployeeId) assignmentText = `Fix: ${this.getEmployeeNameById(tpl.fixedEmployeeId) || "eine Person"}`
      else if ((tpl.assignedEmployees || []).length) assignmentText = `Pool mit ${tpl.assignedEmployees.length} Mitarbeitenden`

      return `${frequencyText} · ${Number(tpl.duration) || 30} Min. · ${this.getTemplateTimeSummary(tpl)} · ${weekendText} · ${assignmentText}`
    },
    setAutoAssignRange(rangeType) {
      const anchor = this.$moment(this.currentDate)
      const target = rangeType === "nextMonth" ? anchor.clone().add(1, "month") : anchor
      this.autoAssignConfig.startDate = target.clone().startOf("month").format("YYYY-MM-DD")
      this.autoAssignConfig.endDate = target.clone().endOf("month").format("YYYY-MM-DD")
    },
    openCreateTemplate() {
      this.editingTemplateId = null
      this.newTemplate = {
        title: "",
        category: "termin",
        frequency: "daily",
        targetCount: 1,
        type: "appointment", // Fallback
        duration: 60,
        color: "#2196F3",
        icon: "mdi-calendar",
        requiresCustomer: false,
        requiresNote: false,
        tacServiceType: null,
        tacActivityTemplateId: "",
        tacAccessPassTemplateIds: [],
        checklist: [],
        instructions: "",
        defaultTimes: [],
        hasFixedTimes: false,
        timePlanningMode: "flexible",
        timePreference: { mode: "flexible", windowStart: "09:00", windowEnd: "12:00" },
        assignedEmployees: [],
        frequencyConfig: { daysOfWeek: [], daysOfMonth: [], daySelectionMode: "one" },
        linkedTemplateId: null,
      }
      this.createTemplateDialog = true
    },
    editTemplate(tpl) {
      if (tpl.parentTemplateId || tpl.variantsConfigured) {
        this.openTemplateConfigDrawer(tpl)
        return
      }
      this.editingTemplateId = tpl._id || tpl.id

      const freqConfig = tpl.frequencyConfig ? JSON.parse(JSON.stringify(tpl.frequencyConfig)) : { daysOfWeek: [], daysOfMonth: [] }
      freqConfig.daySelectionMode = this.inferTaskDaySelectionMode(tpl, freqConfig)

      const timePreference = this.normalizeTaskTimePreference(tpl)
      const timePlanningMode = this.getTaskTimeMode(tpl)

      this.newTemplate = {
        title: tpl.title || "",
        category: tpl.category || (tpl.type === "task" ? "aufgabe" : "termin"),
        frequency: tpl.frequency || "daily",
        targetCount: this.taskFrequencyTargetCount(tpl),
        type: tpl.type || "appointment",
        duration: tpl.duration || 60,
        color: tpl.color || "#2196F3",
        icon: tpl.icon || "mdi-calendar",
        requiresCustomer: !!tpl.requiresCustomer,
        requiresNote: !!tpl.requiresNote,
        tacServiceType:
          tpl.tacServiceType ||
          (/massage/i.test(String(tpl.title || ""))
            ? "massage"
            : /personal[\s-]*training|personaltraining/i.test(String(tpl.title || ""))
            ? "personal_training"
            : null),
        tacActivityTemplateId: tpl.tacActivityTemplateId || "",
        tacAccessPassTemplateIds: Array.isArray(tpl.tacAccessPassTemplateIds) ? [...tpl.tacAccessPassTemplateIds] : [],
        checklist: tpl.checklist ? JSON.parse(JSON.stringify(tpl.checklist)) : [],
        instructions: tpl.instructions || "",
        defaultTimes: tpl.defaultTimes ? [...tpl.defaultTimes] : [],
        hasFixedTimes: !!(tpl.defaultTimes && tpl.defaultTimes.length > 0),
        timePlanningMode,
        timePreference,
        assignedEmployees: tpl.assignedEmployees ? tpl.assignedEmployees.map((emp) => emp._id || emp) : [],
        frequencyConfig: freqConfig,
        linkedTemplateId: tpl.linkedTemplateId || null,
      }
      this.normalizeTaskFrequencyConfig(this.newTemplate)
      this.createTemplateDialog = true
    },
    toggleWeeklyDay(dayValue) {
      if (!this.newTemplate.frequencyConfig) {
        this.$set(this.newTemplate, "frequencyConfig", { daysOfWeek: [], daysOfMonth: [] })
      }
      if (!this.newTemplate.frequencyConfig.daysOfWeek) {
        this.$set(this.newTemplate.frequencyConfig, "daysOfWeek", [])
      }
      const idx = this.newTemplate.frequencyConfig.daysOfWeek.indexOf(dayValue)
      if (idx > -1) {
        this.newTemplate.frequencyConfig.daysOfWeek.splice(idx, 1)
      } else {
        this.newTemplate.frequencyConfig.daysOfWeek.push(dayValue)
      }
    },
    navigatePreviewDate(dir) {
      if (!this.autoAssignResultsGrouped || this.autoAssignResultsGrouped.length === 0) return
      const sorted = [...this.autoAssignResultsGrouped].sort((a, b) => a.date.localeCompare(b.date))
      const idx = sorted.findIndex((g) => g.date === this.selectedPreviewDate)
      if (idx === -1) return
      const newIdx = idx + dir
      if (newIdx >= 0 && newIdx < sorted.length) {
        this.selectedPreviewDate = sorted[newIdx].date
      }
    },
    canNavigatePreviewDate(dir) {
      if (!this.autoAssignResultsGrouped || this.autoAssignResultsGrouped.length === 0 || !this.selectedPreviewDate) return false
      const sorted = [...this.autoAssignResultsGrouped].sort((a, b) => a.date.localeCompare(b.date))
      const idx = sorted.findIndex((g) => g.date === this.selectedPreviewDate)
      if (idx === -1) return false
      const newIdx = idx + dir
      return newIdx >= 0 && newIdx < sorted.length
    },
    async openTasksDashboard() {
      if (!this.hasTaskManagementRight) return
      this.amtliJourneyActive = false
      const now = this.$moment(this.currentDate)
      this.taskDashboardConfig.startDate = now.clone().startOf("month").format("YYYY-MM-DD")
      this.taskDashboardConfig.endDate = now.clone().endOf("month").format("YYYY-MM-DD")
      this.taskDashboardTab = 0
      this.taskDashboardDialog = true
      await this.fetchDashboardData()
    },
    async fetchDashboardData(showLoading = true) {
      if (showLoading) {
        this.taskDashboardLoading = true
      }
      try {
        const res = await api.shiftManagement.fetchTasksDashboard(this.taskDashboardConfig.startDate, this.taskDashboardConfig.endDate)
        if (res && res.data) {
          this.taskDashboardTasks = (res.data.tasks || []).map((t) => {
            if (t.assignedEmployee && typeof t.assignedEmployee === "object") {
              return {
                ...t,
                assignedEmployee: t.assignedEmployee._id || t.assignedEmployee,
              }
            }
            return t
          })
          this.taskDashboardShifts = res.data.shifts || []
          this.taskDashboardAppointments = res.data.appointments || []
        }
      } catch (err) {
        console.error("Fehler beim Laden des Ämtli-Dashboards", err)
        this.$toast.error("Ämtli-Dashboard-Daten konnten nicht geladen werden.")
      } finally {
        this.taskDashboardLoading = false
      }
    },
    async setDashboardRange(rangeType) {
      const now = this.$moment(this.currentDate)
      if (rangeType === "thisWeek") {
        this.taskDashboardConfig.startDate = now.clone().startOf("isoWeek").format("YYYY-MM-DD")
        this.taskDashboardConfig.endDate = now.clone().endOf("isoWeek").format("YYYY-MM-DD")
      } else if (rangeType === "nextWeek") {
        this.taskDashboardConfig.startDate = now.clone().add(1, "week").startOf("isoWeek").format("YYYY-MM-DD")
        this.taskDashboardConfig.endDate = now.clone().add(1, "week").endOf("isoWeek").format("YYYY-MM-DD")
      } else if (rangeType === "thisMonth") {
        this.taskDashboardConfig.startDate = now.clone().startOf("month").format("YYYY-MM-DD")
        this.taskDashboardConfig.endDate = now.clone().endOf("month").format("YYYY-MM-DD")
      } else if (rangeType === "nextMonth") {
        this.taskDashboardConfig.startDate = now.clone().add(1, "month").startOf("month").format("YYYY-MM-DD")
        this.taskDashboardConfig.endDate = now.clone().add(1, "month").endOf("month").format("YYYY-MM-DD")
      }
      await this.fetchDashboardData()
    },
    getEligibleEmployeesForDate(date) {
      const dayShifts = (this.taskDashboardShifts || []).filter((s) => s.date === date)
      const empIds = dayShifts
        .map((s) => {
          const empId = s.assignedEmployee?._id || s.assignedEmployee
          return empId ? String(empId) : null
        })
        .filter(Boolean)
      const workingSet = new Set(empIds)

      // Return ONLY employees scheduled to work on this day
      return this.employeeOptions
        .filter((opt) => workingSet.has(opt.value))
        .map((opt) => {
          return {
            ...opt,
            text: `🟢 ${opt.text} (arbeitet)`,
          }
        })
        .sort((a, b) => a.text.localeCompare(b.text))
    },
    timeToMinutes(timeStr) {
      if (!timeStr) return 0
      const [h, m] = timeStr.split(":").map(Number)
      return h * 60 + m
    },
    getShiftTimes(shift) {
      if (shift && Array.isArray(shift.times) && shift.times.length > 0) {
        return shift.times
      }
      if (shift && shift.shiftTemplate && Array.isArray(shift.shiftTemplate.times) && shift.shiftTemplate.times.length > 0) {
        return shift.shiftTemplate.times
      }
      return []
    },
    getEffectiveShiftFlag(shift, field) {
      if (!shift) return false
      if (shift[field] !== undefined && shift[field] !== null) return Boolean(shift[field])
      return Boolean(shift.shiftTemplate && shift.shiftTemplate[field])
    },
    getShiftWorkMinuteIntervals(shift) {
      const workIntervals = []
      const pauseIntervals = []
      ;(this.getShiftTimes(shift) || []).forEach((entry) => {
        if (!entry || !entry.start || !entry.end) return
        const startMin = this.timeToMinutes(entry.start)
        let endMin = this.timeToMinutes(entry.end)
        if (endMin < startMin) endMin += 1440
        const interval = { startMin, endMin }
        if (!Number.isFinite(interval.startMin) || !Number.isFinite(interval.endMin) || interval.endMin <= interval.startMin) return
        if (String(entry.type || "").trim().toLowerCase() === "pause") pauseIntervals.push(interval)
        else workIntervals.push(interval)
      })
      return this.subtractCalendarMinuteIntervals(workIntervals, pauseIntervals)
    },
    mergeCalendarMinuteIntervals(intervals) {
      return (intervals || [])
        .filter((entry) => Number.isFinite(entry.startMin) && Number.isFinite(entry.endMin) && entry.endMin > entry.startMin)
        .sort((left, right) => left.startMin - right.startMin || left.endMin - right.endMin)
        .reduce((merged, entry) => {
          const previous = merged[merged.length - 1]
          if (!previous || entry.startMin > previous.endMin) {
            merged.push({ startMin: entry.startMin, endMin: entry.endMin })
          } else {
            previous.endMin = Math.max(previous.endMin, entry.endMin)
          }
          return merged
        }, [])
    },
    subtractCalendarMinuteIntervals(intervals, exclusions) {
      let result = this.mergeCalendarMinuteIntervals(intervals)
      this.mergeCalendarMinuteIntervals(exclusions).forEach((exclusion) => {
        result = result.flatMap((interval) => {
          if (exclusion.endMin <= interval.startMin || exclusion.startMin >= interval.endMin) return [interval]
          const remaining = []
          if (exclusion.startMin > interval.startMin) {
            remaining.push({ startMin: interval.startMin, endMin: exclusion.startMin })
          }
          if (exclusion.endMin < interval.endMin) {
            remaining.push({ startMin: exclusion.endMin, endMin: interval.endMin })
          }
          return remaining
        })
      })
      return this.mergeCalendarMinuteIntervals(result)
    },
    getShiftOpeningClosingIntervals(shift) {
      const workIntervals = this.getShiftWorkMinuteIntervals(shift)
      const parsedOffset = Number(this.getStudioOffset(this.selectedStudio))
      const offset = Number.isFinite(parsedOffset) ? Math.max(0, parsedOffset) : 30
      if (!workIntervals.length || !offset) return []

      const systemIntervals = []
      if (this.getEffectiveShiftFlag(shift, "isOpeningShift")) {
        systemIntervals.push({ startMin: workIntervals[0].startMin, endMin: workIntervals[0].startMin + offset })
      }
      if (this.getEffectiveShiftFlag(shift, "isClosingShift")) {
        const lastWorkInterval = workIntervals[workIntervals.length - 1]
        systemIntervals.push({ startMin: lastWorkInterval.endMin - offset, endMin: lastWorkInterval.endMin })
      }

      const clippedIntervals = []
      systemIntervals.forEach((systemInterval) => {
        workIntervals.forEach((workInterval) => {
          const startMin = Math.max(systemInterval.startMin, workInterval.startMin)
          const endMin = Math.min(systemInterval.endMin, workInterval.endMin)
          if (endMin > startMin) clippedIntervals.push({ startMin, endMin })
        })
      })
      return this.mergeCalendarMinuteIntervals(clippedIntervals)
    },
    getOpeningClosingMinutes(shifts, windowStartMin = null, windowEndMin = null) {
      return (shifts || []).reduce((total, shift) => {
        return (
          total +
          this.getShiftOpeningClosingIntervals(shift).reduce((shiftTotal, interval) => {
            const startMin = Number.isFinite(windowStartMin) ? Math.max(interval.startMin, windowStartMin) : interval.startMin
            const endMin = Number.isFinite(windowEndMin) ? Math.min(interval.endMin, windowEndMin) : interval.endMin
            return shiftTotal + Math.max(0, endMin - startMin)
          }, 0)
        )
      }, 0)
    },
    getAssignableTimes(shift, ignoreOpenClose = false) {
      const workTimes = this.getShiftWorkMinuteIntervals(shift)

      if (workTimes.length === 0) return []

      let segments = workTimes.map((wt) => {
        let sMin = wt.startMin
        let eMin = wt.endMin
        if (eMin < sMin) {
          eMin += 1440 // Overnight shift
        }
        return { startMin: sMin, endMin: eMin }
      })

      if (ignoreOpenClose) {
        return segments
      }

      const isOpening = this.getEffectiveShiftFlag(shift, "isOpeningShift")
      const isClosing = this.getEffectiveShiftFlag(shift, "isClosingShift")

      const offset = this.getStudioOffset(this.selectedStudio)

      if (isOpening) {
        const overallMinStart = Math.min(...segments.map((s) => s.startMin))
        segments = segments
          .map((s) => {
            if (s.startMin === overallMinStart) {
              return { startMin: s.startMin + offset, endMin: s.endMin }
            }
            return s
          })
          .filter((s) => s.startMin < s.endMin)
      }

      if (isClosing) {
        const overallMaxEnd = Math.max(...segments.map((s) => s.endMin))
        segments = segments
          .map((s) => {
            if (s.endMin === overallMaxEnd) {
              return { startMin: s.startMin, endMin: s.endMin - offset }
            }
            return s
          })
          .filter((s) => s.startMin < s.endMin)
      }

      return segments
    },
    shiftCoversTime(shift, taskTimeStr, duration = 30, ignoreOpenClose = false) {
      const assignable = this.getAssignableTimes(shift, ignoreOpenClose)
      if (assignable.length === 0) return false

      const taskMinutes = this.timeToMinutes(taskTimeStr)
      const taskEndMinutes = taskMinutes + Number(duration)

      return assignable.some((seg) => taskMinutes >= seg.startMin && taskEndMinutes <= Math.min(seg.endMin, 1440))
    },
    getEligibleEmployeesForTask(task) {
      if (!task || !task.date || !task.time) return []

      const reference = (value) => String(value?._id || value || "")
      const tpl = (this.autoAssignConfig.templates || []).find((t) => reference(t._id || t.id) === reference(task.templateId)) ||
        { _id: reference(task.templateId), title: task.title, type: "task" }
      const allowDuringOpenClose = tpl ? !!tpl.allowDuringOpenClose : false
      const duration = Number(task.duration) || 30
      const start = this.timeToMinutes(task.time)
      const end = start + duration
      if (!Number.isFinite(start) || duration <= 0 || end > 1440) return []
      const overlaps = (intervalStart, intervalEnd) => start < intervalEnd && end > intervalStart

      const dayShifts = (this.taskDashboardShifts || []).filter((s) => s.date === task.date)
      const workingSet = new Set(dayShifts
        .filter((shift) => shift.isSick !== true && this.shiftCoversTime(shift, task.time, duration, allowDuringOpenClose))
        .map((shift) => reference(shift.assignedEmployee)).filter(Boolean))
      const previousDate = this.$moment(task.date, "YYYY-MM-DD").subtract(1, "day").format("YYYY-MM-DD")

      return this.employeeOptions
        .filter((opt) => {
          const employeeId = reference(opt.value)
          if (!workingSet.has(employeeId) || this.appointmentEmployeeRestrictionMessage(tpl, employeeId)) return false
          const pauseConflict = dayShifts.some((shift) => reference(shift.assignedEmployee) === employeeId &&
            this.getShiftTimes(shift).some((entry) => {
              if (String(entry.type || "").trim().toLowerCase() !== "pause") return false
              const pauseStart = this.timeToMinutes(entry.start)
              let pauseEnd = this.timeToMinutes(entry.end)
              if (pauseEnd < pauseStart) pauseEnd += 1440
              return overlaps(pauseStart, pauseEnd)
            }))
          if (pauseConflict) return false
          const taskConflict = (this.taskDashboardTasks || []).some((other) =>
            reference(other._id) !== reference(task._id) && other.suppressed !== true && other.date === task.date &&
            reference(other.assignedEmployee) === employeeId &&
            overlaps(this.timeToMinutes(other.time), this.timeToMinutes(other.time) + (Number(other.duration) || 30)))
          if (taskConflict) return false
          return !(this.taskDashboardAppointments || []).some((appointment) => {
            if (reference(appointment.assignedEmployee) !== employeeId || appointment.deleted === true ||
              ["cancelled", "noshow"].includes(appointment.status) || (appointment.helperColumn && appointment.helperColumn !== "none")) return false
            if (appointment.date !== task.date && appointment.date !== previousDate) return false
            let appointmentStart = this.timeToMinutes(appointment.start)
            let appointmentEnd = this.timeToMinutes(appointment.end)
            if (appointmentEnd < appointmentStart) appointmentEnd += 1440
            if (appointment.date === previousDate) { appointmentStart -= 1440; appointmentEnd -= 1440 }
            return overlaps(appointmentStart, appointmentEnd)
          })
        })
        .map((opt) => {
          return {
            ...opt,
            text: `🟢 ${opt.text} (verfügbar)`,
          }
        })
        .sort((a, b) => a.text.localeCompare(b.text))
    },
    calculateHourlyCapacity(date) {
      const hours = Array.from({ length: 14 }, (_, i) => i + 8) // Hours 8 to 21
      const dayShifts = (this.taskDashboardShifts || []).filter((s) => s.date === date)
      const dayAllTasks = (this.taskDashboardTasks || []).filter((t) => t.date === date && this.isAmtliCategory(t))
      const dayAppts = (this.taskDashboardAppointments || []).filter((a) => a.date === date)

      return hours.map((h) => {
        const slotStart = h * 60
        const slotEnd = (h + 1) * 60

        let slotCapacity = 0
        dayShifts.forEach((s) => {
          this.getShiftWorkMinuteIntervals(s).forEach((interval) => {
            const overlapStart = Math.max(slotStart, interval.startMin)
            const overlapEnd = Math.min(slotEnd, interval.endMin)
            slotCapacity += Math.max(0, overlapEnd - overlapStart)
          })
        })
        const slotOpeningClosingMinutes = this.getOpeningClosingMinutes(dayShifts, slotStart, slotEnd)

        let slotTaskMinutes = 0
        dayAllTasks.forEach((t) => {
          if (t.assignedEmployee && t.time && t.duration) {
            const [sh, sm] = t.time.split(":").map(Number)
            const startMins = sh * 60 + sm
            const endMins = startMins + t.duration

            const overlapStart = Math.max(slotStart, startMins)
            const overlapEnd = Math.min(slotEnd, endMins)
            slotTaskMinutes += Math.max(0, overlapEnd - overlapStart)
          }
        })

        let slotApptMinutes = 0
        dayAppts.forEach((a) => {
          if (a.start && a.end) {
            const [sh, sm] = a.start.split(":").map(Number)
            const [eh, em] = a.end.split(":").map(Number)
            const startMins = sh * 60 + sm
            let endMins = eh * 60 + em
            if (endMins < startMins) endMins += 1440

            const overlapStart = Math.max(slotStart, startMins)
            const overlapEnd = Math.min(slotEnd, endMins)
            slotApptMinutes += Math.max(0, overlapEnd - overlapStart)
          }
        })

        const occupiedMins = slotOpeningClosingMinutes + slotTaskMinutes + slotApptMinutes
        const freeMins = Math.max(0, slotCapacity - occupiedMins)
        const percentFree = slotCapacity > 0 ? Math.round((freeMins / slotCapacity) * 100) : 0

        let color = "#747d8c"
        let label = "Keine Schicht"
        if (slotCapacity > 0) {
          if (percentFree >= 60) {
            color = "#2ed573"
            label = "Gute Kapazität"
          } else if (percentFree >= 30) {
            color = "#ffa502"
            label = "Mittlere Kapazität"
          } else {
            color = "#ff4757"
            label = "Geringe Kapazität"
          }
        }

        return {
          hour: String(h).padStart(2, "0"),
          timeText: `${String(h).padStart(2, "0")}:00 - ${String(h + 1).padStart(2, "0")}:00`,
          capacity: slotCapacity,
          freeMinutes: freeMins,
          percentFree,
          color,
          label,
        }
      })
    },
    async updateTaskAssigneeInDashboard(task) {
      try {
        const empId = task.assignedEmployee?._id || task.assignedEmployee || null
        await api.shiftManagement.updateTask(task._id, {
          assignedEmployee: empId,
          status: empId ? "published" : "draft",
          unassignedReason: empId ? null : task.unassignedReason || null,
        })
        this.$toast.success(empId ? "Ämtli zugewiesen und im Kalender gespeichert." : "Ämtli bleibt als Planungsvorschlag bestehen.")
        await this.fetchDashboardData(false)
        this.fetchData(true)
      } catch (err) {
        console.error("Fehler beim Zuweisen des Ämtlis", err)
        this.$toast.error(err?.response?.data?.message || "Zuweisung konnte nicht gespeichert werden.")
        // Das Select arbeitet per v-model bereits optimistisch. Bei einer
        // abgelehnten Zuweisung (z. B. Kollision) den echten Serverstand laden.
        await this.fetchDashboardData(false)
      }
    },
    async saveTaskTimeInDashboard(task) {
      if (task.isSavingTime) return
      this.$set(task, "isSavingTime", true)
      this.$set(task, "isEditingTime", false)
      try {
        await api.shiftManagement.updateTask(task._id, { time: task.time })
        this.$toast.success("Uhrzeit erfolgreich aktualisiert.")
        await this.fetchDashboardData(false)
        this.fetchData(true)
      } catch (err) {
        console.error("Fehler beim Aktualisieren der Uhrzeit", err)
        this.$toast.error(err?.response?.data?.message || "Aktualisierung fehlgeschlagen.")
        await this.fetchDashboardData(false)
      } finally {
        this.$set(task, "isSavingTime", false)
      }
    },
    async publishSingleTask(taskId) {
      try {
        const task = (this.taskDashboardTasks || []).find((entry) => String(entry._id) === String(taskId))
        if (!task || !task.assignedEmployee) {
          this.$toast.info("Bitte zuerst einen Mitarbeiter zuweisen.")
          return
        }
        await api.shiftManagement.publishTasks({ taskIds: [taskId] })
        this.$toast.success("Ämtli im Kalender gespeichert.")
        await this.fetchDashboardData(false)
        this.fetchData(true)
      } catch (err) {
        console.error("Fehler beim Veröffentlichen des Ämtlis", err)
        this.$toast.error(err?.response?.data?.message || "Freigabe fehlgeschlagen.")
      }
    },
    async publishDayDrafts(date) {
      try {
        const dayTasks = (this.taskDashboardTasks || []).filter((t) => t.date === date && t.status === "draft" && Boolean(t.assignedEmployee))
        const ids = dayTasks.map((t) => t._id)
        if (ids.length === 0) {
          this.$toast.info("An diesem Tag ist noch kein Planungsvorschlag einem Mitarbeiter zugewiesen.")
          return
        }

        await api.shiftManagement.publishTasks({ taskIds: ids })
        this.$toast.success(`${ids.length} zugewiesene Ämtli für den ${this.$moment(date).format("DD.MM.YYYY")} gespeichert.`)
        await this.fetchDashboardData(false)
        this.fetchData(true)
      } catch (err) {
        console.error("Fehler beim Freigeben des Tages", err)
        this.$toast.error(err?.response?.data?.message || "Freigabe fehlgeschlagen.")
      }
    },
    async publishAllDraftsInDashboard() {
      try {
        const ids = this.publishableDraftTasks.map((task) => task._id)
        if (ids.length === 0) {
          this.$toast.info("Es gibt keine zugewiesenen Planungsvorschläge zum Speichern.")
          return
        }
        await api.shiftManagement.publishTasks({ taskIds: ids })
        this.$toast.success(`${ids.length} zugewiesene Planungsvorschläge im Kalender gespeichert.`)
        await this.fetchDashboardData(false)
        this.fetchData(true)
      } catch (err) {
        console.error("Fehler beim Freigeben aller Entwürfe", err)
        this.$toast.error(err?.response?.data?.message || "Massenfreigabe fehlgeschlagen.")
      }
    },
    async deleteDashboardTask(taskId) {
      if (!confirm("Möchtest du dieses Ämtli wirklich löschen? Bei einer normalen Neuplanung wird es nicht automatisch ersetzt.")) return
      try {
        await api.shiftManagement.deleteTask(taskId, "single")
        this.$toast.success("Ämtli gelöscht.")
        await this.fetchDashboardData(false)
        this.fetchData(true)
      } catch (err) {
        console.error("Fehler beim Löschen des Ämtlis", err)
        this.$toast.error("Löschen fehlgeschlagen.")
      }
    },
    async deleteTasksByTemplate(task) {
      if (!task) return
      const templateId = task.templateId
      const title = task.title

      const confirmMsg = `Möchtest du wirklich alle Planungsvorschläge mit dem Namen "${title}" im gewählten Zeitraum löschen? Automatisch erzeugte Einträge werden bei einer normalen Neuplanung nicht ersetzt.`
      if (!confirm(confirmMsg)) return

      try {
        const matchingTasks = (this.taskDashboardTasks || []).filter((t) => {
          const matchesTemplate = templateId && String(t.templateId) === String(templateId)
          const matchesTitle = !templateId && t.title === title
          const isUnassigned = !t.assignedEmployee
          return (matchesTemplate || matchesTitle) && isUnassigned
        })

        if (matchingTasks.length === 0) {
          this.$toast.info("Keine passenden Planungsvorschläge gefunden.")
          return
        }

        let deletedCount = 0
        for (const t of matchingTasks) {
          await api.shiftManagement.deleteTask(t._id, "single")
          deletedCount++
        }

        this.$toast.success(`${deletedCount} Planungsvorschläge "${title}" gelöscht.`)
        await this.fetchDashboardData(false)
        this.fetchData(true)
      } catch (err) {
        console.error("Fehler beim Löschen der Ämtli-Serie", err)
        this.$toast.error("Fehler beim Löschen der Ämtli.")
      }
    },
    async markDashboardTaskAsDone(task) {
      try {
        await api.shiftManagement.updateTask(task._id, { isDone: true })
        this.$toast.success("Ämtli als erledigt markiert.")
        await this.fetchDashboardData(false)
        this.fetchData(true)
      } catch (err) {
        console.error("Fehler beim Erledigen des Ämtlis", err)
        this.$toast.error("Änderung fehlgeschlagen.")
      }
    },
    async rescheduleTaskToToday(task) {
      try {
        const todayStr = this.$moment().format("YYYY-MM-DD")
        await api.shiftManagement.updateTask(task._id, { date: todayStr })
        this.$toast.success("Ämtli auf heute verschoben.")
        await this.fetchDashboardData(false)
        this.fetchData(true)
      } catch (err) {
        console.error("Fehler beim Verschieben des Ämtlis", err)
        this.$toast.error("Verschieben fehlgeschlagen.")
      }
    },
    syncStudioAmtliTemplates() {
      const templates = this.templates.filter((template) => template.enabledInStudio &&
        (template.category === "ämtli" || (!template.category && template.type === "task")))
      this.autoAssignConfig.templates = JSON.parse(JSON.stringify(templates)).map((template) => {
        template.assignedEmployees = (template.assignedEmployees || []).map((employee) => employee._id || employee)
        template.fixedEmployeeId = template.fixedEmployeeId?._id || template.fixedEmployeeId || null
        template.linkedTemplateIds = (template.linkedTemplateIds || []).map((linked) => linked._id || linked)
        template.timePlanningMode = this.getTaskTimeMode(template)
        template.hasFixedTimes = template.timePlanningMode === "fixed_times"
        template.timePreference = this.normalizeTaskTimePreference(template)
        this.normalizeTaskFrequencyConfig(template)
        return template
      })
      const available = new Set(this.autoAssignConfig.templates.filter((template) => this.getAmtliTemplateReadiness(template).ready).map((template) => String(template._id || template.id)))
      this.selectedTemplateIds = this.selectedTemplateIds.filter((id) => available.has(String(id)))
    },
    openNewTaskVariant(source, duplicate = false) {
      if (!source || !source.supportsVariants || this.taskDashboardLoading) return
      const variant = JSON.parse(JSON.stringify(source))
      variant.sourceTemplateId = source._id || source.id
      variant.parentTemplateId = source.variantGroupId || source.parentTemplateId || source._id || source.id
      variant.variantGroupTitle = source.variantGroupTitle || source.title
      variant.isNewStudioVariant = true
      variant.isVariantGroup = false
      this.amtliPoolDialog = false
      variant.enabledInStudio = true
      variant.variantName = duplicate ? `${source.variantName || "Standard"} Kopie`.slice(0, 80) : ""
      variant.title = `${variant.variantGroupTitle} · ${duplicate ? variant.variantName : "Neue Variante"}`
      variant.linkedTemplateId = null
      variant.linkedTemplateIds = []
      delete variant._id
      delete variant.id
      if (!duplicate) {
        // A new task must not inherit assignments hidden in the automation settings.
        // Explicit duplication preserves both the pool and the fixed employee.
        variant.assignedEmployees = []
        variant.fixedEmployeeId = null
        variant.description = ""
        variant.instructions = ""
        variant.checklist = []
        variant.frequency = "once"
        variant.frequencyConfig = { daysOfWeek: [], daysOfMonth: [], daySelectionMode: "one" }
        variant.targetCount = 1
        variant.defaultTimes = []
        variant.timePreference = { mode: "flexible" }
      }
      this.openTemplateConfigDrawer(variant)
    },
    duplicateTaskVariant(source) {
      this.openNewTaskVariant(source, true)
    },
    async toggleTaskVariant(template) {
      if (this.taskDashboardLoading) return
      this.taskDashboardLoading = true
      try {
        const enabledInStudio = !template.enabledInStudio
        const response = await api.shiftManagement.updateTemplate(template._id || template.id, {
          enabledInStudio,
          ...(template.supportsVariants ? { variantName: template.variantName || "Standard" } : {}),
        })
        this.applySavedTaskTemplate(response.data)
        await this.fetchData(true)
        this.syncStudioAmtliTemplates()
        this.$toast.success(enabledInStudio ? "Ämtli aktiviert." : template.supportsVariants
          ? "Variante deaktiviert. Gespeicherte Aufgaben bleiben erhalten."
          : "Ämtli deaktiviert.")
      } catch (error) {
        this.$toast.error(error.response?.data?.message || "Die Aktivierung konnte nicht gespeichert werden.")
      } finally {
        this.taskDashboardLoading = false
      }
    },
    applySavedTaskTemplate(template) {
      const templateId = String(template._id || template.id)
      const index = this.templates.findIndex((candidate) => String(candidate._id || candidate.id) === templateId)
      if (index === -1) this.templates.push(template)
      else this.$set(this.templates, index, template)
    },
    async handleCleaningTasksMerged({ studioId }) {
      if (String(this.studioId) !== studioId) return
      const refreshed = await this.fetchData(true)
      if (String(this.studioId) !== studioId) return
      if (!refreshed) {
        this.$toast.info("Die Reinigungsämtli sind zusammengeführt. Bitte lade den Kalender nochmals neu.")
        return
      }
      this.syncStudioAmtliTemplates()
      this.amtliConfigSearch = ""
      this.amtliConfigFrequencyFilter = "all"
      this.$toast.success("Deine Reinigungsämtli sind jetzt als Varianten unter Reinigen verfügbar.")
    },
    openAmtliConfigDialog() {
      if (!this.hasTaskManagementRight) return
      this.amtliJourneyActive = true
      this.amtliConfigSearch = ""
      this.amtliConfigFrequencyFilter = "all"
      this.overwriteAcknowledged = false
      this.pendingAmtliPoolTemplateId = null
      this.amtliTemplateReturnTarget = "config"
      this.amtliAutomationSettingsOpen = false
      const now = this.$moment(this.currentDate)
      this.autoAssignConfig.startDate = now.clone().startOf("month").format("YYYY-MM-DD")
      this.autoAssignConfig.endDate = now.clone().endOf("month").format("YYYY-MM-DD")
      this.autoAssignConfig.overwriteExisting = false
      this.autoAssignConfig.excludedEmployeeIds = []
      this.autoAssignConfig.preventOverlap = true
      this.autoAssignConfig.linkedTasksSameEmployee = true
      this.autoAssignConfig.preventConsecutiveDays = false
      this.autoAssignConfig.distributionMode = "prefer_pool"

      this.syncStudioAmtliTemplates()

      this.selectedTemplateIds = this.autoAssignConfig.templates
        .filter((template) => this.getAmtliTemplateReadiness(template).ready)
        .map((template) => String(template._id || template.id))
      this.amtliConfigDialog = true
    },
    openAutoAssignDialog() {
      if (!this.hasTaskManagementRight) return
      this.amtliJourneyActive = true
      this.amtliConfigDialog = false
      this.configDrawer = false
      this.amtliPoolDialog = false
      this.showAdvancedAssignSettings = false
      this.autoAssignLogs = []
      this.autoAssignResults = []
      this.autoAssignStats = null
      this.assignStep = 1
      this.autoAssignViewMode = "list"
      this.overwriteAcknowledged = false
      this.$nextTick(() => {
        this.autoAssignTasksDialog = true
      })
    },
    finishAmtliConfiguration() {
      this.amtliConfigDialog = false
      this.amtliJourneyActive = false
      this.fetchData(true)
    },
    returnToAmtliConfiguration() {
      if (this.autoAssignLoading) return
      this.autoAssignTasksDialog = false
      this.$nextTick(() => {
        this.amtliConfigDialog = true
      })
    },
    handleAutoAssignBack() {
      if (this.autoAssignLoading || this.assignStep === 2) {
        this.$toast.info("Die Zuweisung läuft gerade. Bitte warte einen Moment.")
        return
      }
      if (this.assignStep === 1) {
        this.returnToAmtliConfiguration()
        return
      }
      this.assignStep = 1
      this.autoAssignConfig.overwriteExisting = false
      this.overwriteAcknowledged = false
      this.$toast.info("Die eben erstellten Vorschläge bleiben im Ämtli-Manager bestehen.")
    },
    closeAutoAssignJourney() {
      if (this.autoAssignLoading) {
        this.$toast.info("Die Zuweisung läuft gerade. Bitte warte einen Moment.")
        return
      }
      this.autoAssignTasksDialog = false
      this.amtliJourneyActive = false
    },
    returnToAutoAssignSetup() {
      this.taskDashboardDialog = false
      this.$nextTick(() => {
        this.assignStep = 1
        this.autoAssignConfig.overwriteExisting = false
        this.overwriteAcknowledged = false
        this.autoAssignTasksDialog = true
        this.$toast.info("Bereits erstellte Entwürfe bleiben im Ämtli-Manager bestehen.")
      })
    },
    closeAmtliDashboard() {
      this.taskDashboardDialog = false
      this.amtliJourneyActive = false
      this.fetchData(true)
    },
    handleAmtliDashboardBack() {
      if (this.amtliJourneyActive) {
        this.returnToAutoAssignSetup()
      } else {
        this.closeAmtliDashboard()
      }
    },
    goToDashboardFreigabe() {
      this.autoAssignTasksDialog = false
      this.amtliConfigDialog = false
      this.amtliJourneyActive = true
      this.taskDashboardTab = 0
      this.taskDashboardConfig.startDate =
        this.autoAssignConfig.startDate || this.$moment(this.currentDate).clone().startOf("month").format("YYYY-MM-DD")
      this.taskDashboardConfig.endDate = this.autoAssignConfig.endDate || this.$moment(this.currentDate).clone().endOf("month").format("YYYY-MM-DD")
      this.$nextTick(() => {
        this.taskDashboardDialog = true
        this.fetchDashboardData()
      })
    },
    toggleWeeklyDayInConfig(tpl, dayValue) {
      if (!tpl.frequencyConfig) {
        this.$set(tpl, "frequencyConfig", { daysOfWeek: [], daysOfMonth: [] })
      }
      if (!tpl.frequencyConfig.daysOfWeek) {
        this.$set(tpl.frequencyConfig, "daysOfWeek", [])
      }
      const idx = tpl.frequencyConfig.daysOfWeek.indexOf(dayValue)
      if (idx > -1) {
        tpl.frequencyConfig.daysOfWeek.splice(idx, 1)
      } else {
        tpl.frequencyConfig.daysOfWeek.push(dayValue)
      }
    },
    toggleExcludedEmployee(empId) {
      const idx = this.autoAssignConfig.excludedEmployeeIds.indexOf(empId)
      if (idx > -1) {
        this.autoAssignConfig.excludedEmployeeIds.splice(idx, 1)
      } else {
        this.autoAssignConfig.excludedEmployeeIds.push(empId)
      }
    },
    toggleTemplateSelection(tpl) {
      if (!tpl) return
      if (!this.getAmtliTemplateReadiness(tpl).ready) return
      const id = String(tpl._id || tpl.id)
      const idx = this.selectedTemplateIds.indexOf(id)
      if (idx > -1) {
        this.selectedTemplateIds.splice(idx, 1)
      } else {
        this.selectedTemplateIds.push(id)
      }
    },
    getEmployeeNameById(empId) {
      if (!empId) return ""
      const opt = this.employeeOptions.find((o) => String(o.value) === String(empId))
      return opt ? opt.text : "Unbekannt"
    },
    getLinkedParentName(tpl) {
      const tplId = String(tpl._id || tpl.id)
      const parent = this.autoAssignConfig.templates.find((t) => {
        const links = []
        if (t.linkedTemplateId) links.push(String(t.linkedTemplateId))
        if (t.linkedTemplateIds) t.linkedTemplateIds.forEach((id) => links.push(String(id)))
        return links.includes(tplId)
      })
      return parent ? parent.title : null
    },
    getLinkedTemplatesTitles(tpl) {
      if (!tpl.linkedTemplateIds || tpl.linkedTemplateIds.length === 0) return ""
      return tpl.linkedTemplateIds
        .map((id) => {
          const found = this.autoAssignConfig.templates.find((t) => String(t._id || t.id) === String(id))
          return found ? found.title : ""
        })
        .filter(Boolean)
        .join(", ")
    },
    isAmtliAdded(tpl) {
      if (!tpl || tpl.isVariantGroup) return false
      const tplId = String(tpl._id || tpl.id)
      return (this.autoAssignConfig.templates || []).some((t) => String(t._id || t.id) === tplId)
    },
    openTemplateConfigDrawer(tpl, returnTarget = "config") {
      if (!this.hasTaskManagementRight) return
      const source = tpl ? JSON.parse(JSON.stringify(tpl)) : null
      if (source) {
        this.$set(source, "variantName", source.variantName || "")
        this.$set(source, "description", source.description || "")
        this.$set(source, "instructions", source.instructions || "")
        this.$set(source, "checklist", Array.isArray(source.checklist) ? source.checklist.map((item) => ({ text: item.text, isDone: false })) : [])
        if (source.allowDuringOpenClose === undefined) {
          this.$set(source, "allowDuringOpenClose", false)
        }
        if (!source.weekendConfig) {
          this.$set(source, "weekendConfig", { mode: "always", weekendDuration: source.duration || 30, hasWeekendTimes: false, weekendTimes: [] })
        } else {
          if (!source.weekendConfig.mode) {
            this.$set(source.weekendConfig, "mode", "always")
          }
          if (source.weekendConfig.hasWeekendTimes === undefined) {
            this.$set(source.weekendConfig, "hasWeekendTimes", !!(source.weekendConfig.weekendTimes && source.weekendConfig.weekendTimes.length > 0))
          }
          if (!source.weekendConfig.weekendTimes) {
            this.$set(source.weekendConfig, "weekendTimes", [])
          }
        }
        const linkedTemplateIds = Array.isArray(source.linkedTemplateIds) ? source.linkedTemplateIds.map((id) => id._id || id) : []
        const legacyLinkedTemplateId = source.linkedTemplateId?._id || source.linkedTemplateId
        if (legacyLinkedTemplateId && !linkedTemplateIds.some((id) => String(id) === String(legacyLinkedTemplateId))) {
          linkedTemplateIds.push(legacyLinkedTemplateId)
        }
        this.$set(source, "linkedTemplateIds", linkedTemplateIds)
        this.normalizeTaskFrequencyConfig(source)
        this.$set(source, "timePreference", this.normalizeTaskTimePreference(source))
        this.$set(source, "timePlanningMode", this.getTaskTimeMode(source))
        this.$set(source, "hasFixedTimes", source.timePlanningMode === "fixed_times")
      }
      this.amtliTemplateReturnTarget = returnTarget === "auto" ? "auto" : "config"
      this.amtliAutomationSettingsOpen = this.amtliTemplateReturnTarget === "auto"
      this.editingConfigTemplate = source
      this.amtliConfigDialog = false
      this.amtliPoolDialog = false
      this.autoAssignTasksDialog = false
      this.$nextTick(() => {
        this.configDrawer = true
      })
    },
    returnFromTemplateConfig() {
      const returnTarget = this.amtliTemplateReturnTarget
      this.configDrawer = false
      this.editingConfigTemplate = null
      this.pendingAmtliPoolTemplateId = null
      this.amtliTemplateReturnTarget = "config"
      this.amtliAutomationSettingsOpen = false
      this.$nextTick(() => {
        if (returnTarget === "auto") this.autoAssignTasksDialog = true
        else this.amtliConfigDialog = true
      })
    },
    cancelTemplateConfig() {
      if (this.taskDashboardLoading) return
      this.returnFromTemplateConfig()
    },
    async saveTemplateConfig() {
      if (!this.hasTaskManagementRight || !this.editingConfigTemplate || this.taskDashboardLoading) return
      if ((this.editingConfigTemplate.isNewStudioVariant || this.editingConfigTemplate.parentTemplateId) && !String(this.editingConfigTemplate.variantName || "").trim()) {
        this.$toast.warning("Bitte gib der Variante einen Namen.")
        return
      }
      this.normalizeTaskFrequencyConfig(this.editingConfigTemplate)
      const duration = Number(this.editingConfigTemplate.duration)
      const targetCount = Number(this.editingConfigTemplate.targetCount || 1)
      const usesAutomation = ["daily", "weekly", "monthly"].includes(this.editingConfigTemplate.frequency)
      const timeError = usesAutomation ? this.validateTaskTimeSettings(this.editingConfigTemplate) : null
      if (!Number.isFinite(duration) || duration < 1 || duration > 24 * 60) {
        this.$toast.warning("Bitte eine gültige Dauer eintragen.")
        return
      }
      if (usesAutomation && (!Number.isFinite(targetCount) || targetCount < 1 || targetCount > 31)) {
        this.$toast.warning("Die Häufigkeit muss zwischen 1 und 31 liegen.")
        return
      }
      if (timeError) {
        this.$toast.warning(timeError)
        return
      }
      this.taskDashboardLoading = true
      try {
        const id = this.editingConfigTemplate._id || this.editingConfigTemplate.id
        const timeMode = this.editingConfigTemplate.timePlanningMode || this.getTaskTimeMode(this.editingConfigTemplate)
        const timePreference = this.normalizeTaskTimePreference(this.editingConfigTemplate)
        timePreference.mode = ["preferred_window", "required_window"].includes(timeMode) ? timeMode : "flexible"

        const payload = {
          variantName: String(this.editingConfigTemplate.variantName || "").trim(),
          description: this.editingConfigTemplate.description || "",
          instructions: this.editingConfigTemplate.instructions || "",
          frequency: this.editingConfigTemplate.frequency || "daily",
          duration: Number(this.editingConfigTemplate.duration) || 30,
          targetCount: Number(this.editingConfigTemplate.targetCount) || 1,
          defaultTimes: timeMode === "fixed_times" ? (this.editingConfigTemplate.defaultTimes || []).filter(Boolean) : [],
          timePreference,
          checklist: this.editingConfigTemplate.checklist || [],
          assignedEmployees: this.editingConfigTemplate.assignedEmployees || [],
          fixedEmployeeId: this.editingConfigTemplate.fixedEmployeeId || null,
          frequencyConfig: this.editingConfigTemplate.frequencyConfig || { daysOfWeek: [], daysOfMonth: [] },
          linkedTemplateId: null,
          linkedTemplateIds: this.editingConfigTemplate.linkedTemplateIds || [],
          weekendConfig: this.editingConfigTemplate.weekendConfig || { mode: "always", weekendDuration: 30 },
          allowDuringOpenClose: this.editingConfigTemplate.allowDuringOpenClose || false,
          enabledInStudio: this.editingConfigTemplate.enabledInStudio !== undefined ? this.editingConfigTemplate.enabledInStudio : true,
        }

        // Centeröffnung/-schliessung dürfen ohne sm_create weiterhin im Studio
        // konfiguriert werden. Gesperrte globale Frequenzwerte deshalb nicht als
        // vermeintliche Änderung mitsenden.
        if (this.frequencyEditingDisabled) {
          delete payload.frequency
          delete payload.targetCount
          delete payload.frequencyConfig
        }

        const response = this.editingConfigTemplate.isNewStudioVariant
          ? await api.shiftManagement.createTaskVariant(this.editingConfigTemplate.sourceTemplateId, { ...payload, enabledInStudio: true })
          : await api.shiftManagement.updateTemplate(id, payload)
        this.editingConfigTemplate = { ...this.editingConfigTemplate, ...response.data, isNewStudioVariant: false }
        this.applySavedTaskTemplate(response.data)

        this.$toast.success(
          usesAutomation
            ? "Ämtli gespeichert und für die automatische Planung vorbereitet."
            : "Ämtli gespeichert und für die manuelle Sidebar verfügbar.",
        )
        await this.fetchData(true)

        this.syncStudioAmtliTemplates()
        const savedId = String(response.data._id || response.data.id)
        const savedTemplate = this.autoAssignConfig.templates.find((template) => String(template._id || template.id) === savedId)
        if (savedTemplate && this.getAmtliTemplateReadiness(savedTemplate).ready && !this.selectedTemplateIds.includes(savedId)) {
          this.selectedTemplateIds.push(savedId)
        }
        this.returnFromTemplateConfig()
      } catch (err) {
        console.error("Fehler beim Speichern der Ämtli-Konfiguration", err)
        this.$toast.error(err.response?.data?.message || "Speichern fehlgeschlagen.")
      } finally {
        this.taskDashboardLoading = false
      }
    },
    openAmtliPoolDialog() {
      if (!this.hasTaskManagementRight) return
      this.amtliConfigDialog = false
      this.$nextTick(() => {
        this.amtliPoolDialog = true
      })
    },
    closeAmtliPoolDialog() {
      this.amtliPoolDialog = false
      this.$nextTick(() => {
        this.amtliConfigDialog = true
      })
    },
    selectAmtliFromPool(tpl) {
      if (!tpl) return
      if (tpl.isVariantGroup) return this.openNewTaskVariant(tpl)
      const tplId = String(tpl._id || tpl.id)
      let target = this.autoAssignConfig.templates.find((t) => String(t._id || t.id) === tplId)
      if (!target) {
        target = {
          ...JSON.parse(JSON.stringify(tpl)),
          _id: tpl._id,
          id: tpl.id || tpl._id,
          title: tpl.title,
          category: tpl.category || "ämtli",
          type: tpl.type || "task",
          color: tpl.color,
          icon: tpl.icon,
          duration: tpl.duration || 30,
          frequency: tpl.frequency || "daily",
          frequencyConfig: tpl.frequencyConfig
            ? JSON.parse(JSON.stringify(tpl.frequencyConfig))
            : { daysOfWeek: [], daysOfMonth: [], daySelectionMode: "one" },
          hasFixedTimes: tpl.defaultTimes && tpl.defaultTimes.length > 0,
          defaultTimes: tpl.defaultTimes ? [...tpl.defaultTimes] : [],
          timePlanningMode: this.getTaskTimeMode(tpl),
          timePreference: this.normalizeTaskTimePreference(tpl),
          assignedEmployees: tpl.assignedEmployees ? [...tpl.assignedEmployees] : [],
          fixedEmployeeId: tpl.fixedEmployeeId || null,
          linkedTemplateId: tpl.linkedTemplateId || null,
          linkedTemplateIds: tpl.linkedTemplateIds ? [...tpl.linkedTemplateIds] : [],
          weekendConfig: tpl.weekendConfig ? { ...tpl.weekendConfig } : { mode: "always", weekendDuration: tpl.duration || 30 },
          allowDuringOpenClose: tpl.allowDuringOpenClose || false,
        }
        this.normalizeTaskFrequencyConfig(target)
        this.pendingAmtliPoolTemplateId = tplId
      }
      target.enabledInStudio = true
      this.amtliPoolDialog = false
      this.openTemplateConfigDrawer(target)
    },
    async executeAutoAssign() {
      if (this.autoAssignLoading) return
      const selectedTemplates = this.selectedAutoAssignTemplates
      if (this.autoAssignRunIssues.length) {
        this.$toast.warning(this.autoAssignRunIssues[0])
        return
      }
      for (const template of selectedTemplates) {
        this.normalizeTaskFrequencyConfig(template)
      }

      this.autoAssignLoading = true
      this.autoAssignLogs = []
      this.assignStep = 2
      try {
        const payload = {
          startDate: this.autoAssignConfig.startDate,
          endDate: this.autoAssignConfig.endDate,
          overwriteExisting: this.autoAssignConfig.overwriteExisting,
          excludedEmployeeIds: this.autoAssignConfig.excludedEmployeeIds,
          preventOverlap: true,
          linkedTasksSameEmployee: true,
          preventConsecutiveDays: this.autoAssignConfig.preventConsecutiveDays,
          distributionMode: this.autoAssignConfig.distributionMode,
          templates: selectedTemplates,
        }
        const response = await api.shiftManagement.autoAssignTasks(payload)
        if (response && response.data) {
          this.autoAssignLogs = response.data.logs || []
          this.autoAssignStats = response.data.stats || null
          this.autoAssignResults = response.data.assignments || []
          if (this.autoAssignResults.length > 0) {
            const dates = this.autoAssignResults.map((a) => a.date).sort()
            this.selectedPreviewDate = dates[0]
          } else {
            this.selectedPreviewDate = null
          }
          this.fetchData(true)
          if (this.autoAssignResults.length > 0) {
            this.$toast.success(response.data.message || "Zuweisung erfolgreich")
            this.goToDashboardFreigabe()
          } else {
            this.assignStep = 1
            this.$toast.info(response.data.message || "Für diesen Zeitraum wurden keine neuen Zuweisungen erstellt.")
          }
        } else {
          this.$toast.error("Fehler")
          this.assignStep = 1
        }
      } catch (err) {
        console.error(err)
        const errMsg = err.response?.data?.message || err.message || "Fehler"
        this.$toast.error(errMsg)
        this.autoAssignLogs.push({
          date: "-",
          message: `Fehler: ${errMsg}`,
          type: "error",
        })
        this.assignStep = 1
      } finally {
        this.autoAssignLoading = false
      }
    },
    async saveTemplate() {
      try {
        // Fallback for older code logic using 'type'
        if (this.newTemplate.category === "ämtli" || this.newTemplate.category === "aufgabe" || this.newTemplate.category === "info") {
          this.newTemplate.type = "task"
        } else {
          this.newTemplate.type = "appointment"
          this.newTemplate.frequency = "daily" // Reset frequency if not ämtli
        }

        if (this.newTemplate.type === "task") {
          this.newTemplate.tacServiceType = null
          this.newTemplate.tacActivityTemplateId = null
          this.newTemplate.tacAccessPassTemplateIds = []
          const timeMode = this.newTemplate.timePlanningMode || this.getTaskTimeMode(this.newTemplate)
          this.newTemplate.hasFixedTimes = timeMode === "fixed_times"
          if (timeMode !== "fixed_times") {
            this.newTemplate.defaultTimes = []
          } else {
            this.newTemplate.defaultTimes = this.newTemplate.defaultTimes.filter((t) => t && t.trim() !== "")
          }
          const timePreference = this.normalizeTaskTimePreference(this.newTemplate)
          timePreference.mode = ["preferred_window", "required_window"].includes(timeMode) ? timeMode : "flexible"
          this.newTemplate.timePreference = timePreference
          const timeError = this.validateTaskTimeSettings(this.newTemplate)
          if (timeError) {
            this.$toast.warning(timeError)
            return
          }
        }

        if (this.newTemplate.tacServiceType) {
          this.newTemplate.requiresCustomer = true
          this.newTemplate.tacActivityTemplateId = String(this.newTemplate.tacActivityTemplateId || "").trim() || null
          this.newTemplate.tacAccessPassTemplateIds = [
            ...new Set((this.newTemplate.tacAccessPassTemplateIds || []).map((value) => String(value || "").trim()).filter(Boolean)),
          ]
        } else {
          this.newTemplate.tacServiceType = null
          this.newTemplate.tacActivityTemplateId = null
          this.newTemplate.tacAccessPassTemplateIds = []
        }

        // Frequenz-spezifische Konfiguration bereinigen
        if (this.newTemplate.category === "ämtli") {
          if (!this.newTemplate.frequencyConfig) {
            this.newTemplate.frequencyConfig = { daysOfWeek: [], daysOfMonth: [] }
          }
          if (this.newTemplate.frequency === "daily") {
            this.newTemplate.frequencyConfig.daysOfWeek = []
            this.newTemplate.frequencyConfig.daysOfMonth = []
          } else if (this.newTemplate.frequency === "weekly") {
            this.newTemplate.frequencyConfig.daysOfMonth = []
          } else if (this.newTemplate.frequency === "monthly") {
            this.newTemplate.frequencyConfig.daysOfWeek = []
          }
          this.normalizeTaskFrequencyConfig(this.newTemplate)
        } else {
          this.newTemplate.frequencyConfig = { daysOfWeek: [], daysOfMonth: [] }
          this.newTemplate.assignedEmployees = []
        }

        const payload = { ...this.newTemplate }
        delete payload.timePlanningMode
        delete payload.hasFixedTimes

        if (this.editingTemplateId) {
          // UPDATE existing template
          await api.shiftManagement.updateTemplate(this.editingTemplateId, payload)
          this.$toast.success("Vorlage aktualisiert")
        } else {
          // CREATE new template
          await api.shiftManagement.createTemplate(payload)
          this.$toast.success("Vorlage gespeichert")
        }
        this.createTemplateDialog = false
        this.editingTemplateId = null
        this.fetchData(true)
      } catch (e) {
        this.$toast.error("Fehler")
      }
    },
    async deleteTemplate(id) {
      const variant = this.templates.find((template) => String(template._id || template.id) === String(id))
      if (variant && (variant.parentTemplateId || variant.variantsConfigured)) {
        this.openTemplateConfigDrawer(variant)
        return
      }
      if (!confirm("Vorlage wirklich löschen?")) return
      try {
        await api.shiftManagement.deleteTemplate(id)
        this.$toast.success("Vorlage gelöscht")
        this.fetchData(true)
      } catch (e) {
        this.$toast.error("Fehler beim Löschen")
      }
    },
    openNoteDialog(col) {
      this.selectedCol = col
      this.newNoteContent = ""
      this.noteDialog = true
    },
    async createNote() {
      try {
        const date = this.selectedCol.date || this.currentDate
        const targetId = this.selectedCol.info.userId || this.selectedCol.info._id
        const res = await api.shiftManagement.createNote({ date: date, targetEmployee: targetId, content: this.newNoteContent })
        this.$toast.success("Gesendet")
        this.newNoteContent = ""
        // UI sofort aktualisieren
        if (!this.selectedCol.notes) this.$set(this.selectedCol, "notes", [])
        this.selectedCol.notes.push(res.data)
        this.fetchData() // Lade Daten im Hintergrund neu
      } catch (e) {
        this.$toast.error("Fehler")
      }
    },
    async deleteNote(id) {
      if (!confirm("Löschen?")) return
      try {
        await api.shiftManagement.deleteNote(id)
        this.fetchData()
      } catch (e) {}
    },
    // --- Shift Actions ---
    openEditShiftDialog(col) {
      if (!col.shifts || col.shifts.length === 0) return this.$toast.error("Keine Schicht zum Bearbeiten gefunden.")
      this.selectedCol = col
      const shift = col.shifts[0]
      const times = shift.times || []
      if (!times.length) return this.$toast.error("Schicht hat keine Zeiten.")

      const workTimes = times.filter((t) => t.type !== "Pause")
      const sorted = [...workTimes].sort((a, b) => a.start.localeCompare(b.start))
      this.editShiftData = {
        shiftId: shift._id,
        start: sorted[0].start,
        end: sorted[sorted.length - 1].end,
        reason: "",
        selectedReason: "",
        customReason: "",
        times: JSON.parse(JSON.stringify(times)),
        isOpeningShift:
          shift.isOpeningShift !== undefined && shift.isOpeningShift !== null
            ? shift.isOpeningShift
            : shift.shiftTemplate
            ? shift.shiftTemplate.isOpeningShift
            : false,
        isClosingShift:
          shift.isClosingShift !== undefined && shift.isClosingShift !== null
            ? shift.isClosingShift
            : shift.shiftTemplate
            ? shift.shiftTemplate.isClosingShift
            : false,
      }
      this.editShiftDialog = true
    },
    addEditShiftTimeBlock() {
      if (!this.editShiftData.times) {
        this.$set(this.editShiftData, "times", [])
      }
      this.editShiftData.times.push({ type: "Arbeitszeit", start: "08:00", end: "17:00" })
    },
    removeEditShiftTimeBlock(index) {
      if (this.editShiftData.times && this.editShiftData.times.length > 1) {
        this.editShiftData.times.splice(index, 1)
      }
    },
    async saveEditShift() {
      if (!this.editShiftData.selectedReason) {
        return this.$toast.error("Bitte wähle einen Grund aus.")
      }

      let finalReason = ""
      if (this.editShiftData.selectedReason === "Sonstiges (bitte angeben)") {
        if (!this.editShiftData.customReason.trim()) {
          return this.$toast.error("Bitte gib eine Begründung für 'Sonstiges' an.")
        }
        finalReason = this.editShiftData.customReason.trim()
      } else {
        finalReason = this.editShiftData.selectedReason
      }

      if (!this.editShiftData.times || this.editShiftData.times.length === 0) {
        return this.$toast.error("Bitte mindestens einen Zeitblock angeben.")
      }

      for (const t of this.editShiftData.times) {
        if (!t.start || !t.end) {
          return this.$toast.error("Bitte Start- und Endzeit für alle Zeitblöcke angeben.")
        }
      }

      try {
        let times = JSON.parse(JSON.stringify(this.editShiftData.times))
        times.sort((a, b) => a.start.localeCompare(b.start))

        const workTimes = times.filter((t) => t.type !== "Pause")
        const sortedWork = [...workTimes].sort((a, b) => a.start.localeCompare(b.start))
        const totalStart = sortedWork.length > 0 ? sortedWork[0].start : times[0].start
        const totalEnd = sortedWork.length > 0 ? sortedWork[sortedWork.length - 1].end : times[times.length - 1].end

        await this.updateCalendarShift(this.editShiftData.shiftId, {
          times: times,
          isOpeningShift: this.editShiftData.isOpeningShift,
          isClosingShift: this.editShiftData.isClosingShift,
          ...this.employeeShiftChangeMetadata(finalReason),
        })

        const date = this.$moment(this.currentDate).format("YYYY-MM-DD")
        const targetId = this.selectedCol.info._id
        const noteText = `[Schichtzeiten angepasst: ${totalStart}-${totalEnd}]\nGrund: ${finalReason}`
        await api.shiftManagement.createNote({ date: date, targetEmployee: targetId, content: noteText })

        this.$toast.success("Schichtzeiten erfolgreich angepasst.")
        this.editShiftDialog = false
        this.fetchData(true)
      } catch (e) {
        console.error(e)
        const msg = e?.response?.data?.message || e.message || "Fehler beim Anpassen der Schicht."
        this.$toast.error(msg)
      }
    },
    openTransferShiftDialog(col) {
      this.transferOrSwapMode = "transfer"
      this.initTransferOrSwapDialog(col)
    },
    openSwapShiftDialog(col) {
      this.transferOrSwapMode = "swap"
      this.initTransferOrSwapDialog(col)
    },
    async initTransferOrSwapDialog(col) {
      if (!col.shifts || col.shifts.length === 0) return this.$toast.error("Keine Schicht gefunden.")
      this.selectedCol = col
      this.transferShiftData.targetId = null
      this.transferShiftData.reason = "normal"

      try {
        const allEmployees = await api.shiftManagement.fetchShiftManagementEmployee(this.studioId)
        const currentDate = this.$moment(this.currentDate).format("YYYY-MM-DD")

        this.transferableEmployees = allEmployees
          .filter((emp) => {
            // Remove the currently selected employee
            if (String(emp.user) === String(col.info?._id || col.id)) return false

            // Check contract percentage (pensum)
            const studioIdStr = String(this.studioId)
            const mainStudio = emp.studio?.find((s) => String(s._id || s) === studioIdStr)
            return mainStudio && mainStudio.percentage > 0
          })
          .map((emp) => {
            const hasVacation = (emp.holidayEntries || []).some(
              (h) => (!h.status || h.status === "accepted") && (h.dates || []).some((d) => d.date === currentDate),
            )

            const hasFreeWish = (emp.freeWishEntries || []).some((fw) => (!fw.status || fw.status === "accepted") && fw.day === currentDate)

            // Look up target employee schedule in columns
            const targetCol = this.columns.find((c) => c.info && String(c.info._id) === String(emp.user))
            const isSick = targetCol?.shifts?.some((s) => s.isSick) || false
            const shiftRange = targetCol && targetCol.shifts && targetCol.shifts.length > 0 ? this.getShiftRange(targetCol) : ""

            let statusType = "available"
            let statusText = "Verfügbar"
            let sortPriority = 0 // Available first

            if (isSick) {
              statusType = "sick"
              statusText = "Krank gemeldet"
              sortPriority = 4
            } else if (hasVacation) {
              statusType = "vacation"
              statusText = "In den Ferien (Urlaub)"
              sortPriority = 3
            } else if (hasFreeWish) {
              statusType = "freiwunsch"
              statusText = "Freiwunsch für diesen Tag"
              sortPriority = 2
            } else if (shiftRange) {
              statusType = "working"
              statusText = `Arbeitet bereits (${shiftRange})`
              sortPriority = 1
            }

            return {
              id: emp.user,
              name: emp.fullName || "Unbekannter Mitarbeiter",
              picture: emp.picture,
              statusType,
              statusText,
              sortPriority,
            }
          })
          .filter((emp) => {
            if (this.transferOrSwapMode === "swap") {
              // Nur Mitarbeiter, die an diesem Tag arbeiten (Schicht geplant haben)
              return emp.statusType === "working"
            }
            return true
          })
          .sort((a, b) => {
            // Sort by priority: available (0), working (1), freiwunsch (2), holiday (3), sick (4)
            if (a.sortPriority !== b.sortPriority) {
              return a.sortPriority - b.sortPriority
            }
            return a.name.localeCompare(b.name)
          })

        this.transferShiftDialog = true
      } catch (err) {
        console.error(err)
        this.$toast.error("Fehler beim Laden der Mitarbeiterliste.")
      }
    },
    getStatusStyle(type) {
      if (type === "sick" || type === "vacation") return "color: #ff5252 !important; font-weight: 600; font-size: 0.68rem;"
      if (type === "freiwunsch") return "color: #ffb300 !important; font-weight: 600; font-size: 0.68rem;"
      if (type === "working") return "color: #2196F3 !important; font-size: 0.68rem;"
      return "color: #4caf50 !important; font-weight: 600; font-size: 0.68rem;"
    },
    async saveTransferShift() {
      if (!this.transferShiftData.targetId) return this.$toast.error("Bitte einen Ziel-Mitarbeiter wählen.")
      try {
        const date = this.$moment(this.currentDate).format("YYYY-MM-DD")

        if (this.transferOrSwapMode === "transfer") {
          for (const shift of this.selectedCol.shifts) {
            await this.updateCalendarShift(shift._id, { assignedEmployee: this.transferShiftData.targetId, status: "assigned" })
          }
          for (const item of this.selectedCol.items) {
            if (item.isAppt && item.status !== "cancelled") {
              await api.shiftManagement.updateAppointment(item._id, { assignedEmployee: this.transferShiftData.targetId })
            } else if (item.isTask) {
              await api.shiftManagement.updateTask(item._id, { assignedEmployee: this.transferShiftData.targetId })
            }
          }

          let totalSickHours = 0
          if (this.selectedCol.shifts && this.selectedCol.shifts.length > 0) {
            this.selectedCol.shifts.forEach((s) => {
              const totalMinutes = this.getShiftWorkSegments(s).reduce((sum, segment) => {
                const start = this.$moment(segment.start, "HH:mm", true)
                const end = this.$moment(segment.end, "HH:mm", true)
                return start.isValid() && end.isValid() && start.isBefore(end) ? sum + end.diff(start, "minutes") : sum
              }, 0)
              totalSickHours += Math.round((totalMinutes / 60) * 100) / 100
            })
          }

          if (this.transferShiftData.reason === "sick") {
            await this.createCalendarSickFromTransfer({ employee: this.selectedCol.id, date, hours: totalSickHours })
          }
          await api.shiftManagement.createNote({
            date: date,
            targetEmployee: this.transferShiftData.targetId,
            content: `[System] Schicht und Termine von ${this.selectedCol.firstName} übernommen.`,
          })

          this.$toast.success("Schicht und Termine erfolgreich übertragen.")
        } else {
          // Swap Mode
          const targetCol = this.viewColumns.find((c) => String(c.id) === String(this.transferShiftData.targetId))
          const targetShifts = targetCol?.shifts || []
          const targetItems = targetCol?.items || []

          // 1. Assign source shifts & items to target employee
          for (const shift of this.selectedCol.shifts) {
            await this.updateCalendarShift(shift._id, { assignedEmployee: this.transferShiftData.targetId, status: "assigned" })
          }
          for (const item of this.selectedCol.items) {
            if (item.isAppt && item.status !== "cancelled") {
              await api.shiftManagement.updateAppointment(item._id, { assignedEmployee: this.transferShiftData.targetId })
            } else if (item.isTask) {
              await api.shiftManagement.updateTask(item._id, { assignedEmployee: this.transferShiftData.targetId })
            }
          }

          // 2. Assign target shifts & items to source employee
          for (const shift of targetShifts) {
            await this.updateCalendarShift(shift._id, { assignedEmployee: this.selectedCol.id, status: "assigned" })
          }
          for (const item of targetItems) {
            if (item.isAppt && item.status !== "cancelled") {
              await api.shiftManagement.updateAppointment(item._id, { assignedEmployee: this.selectedCol.id })
            } else if (item.isTask) {
              await api.shiftManagement.updateTask(item._id, { assignedEmployee: this.selectedCol.id })
            }
          }

          const targetEmp = this.transferableEmployees.find((emp) => emp.id === this.transferShiftData.targetId)
          const targetName = targetEmp ? targetEmp.name.split(" ")[0] : "Unbekannt"

          // Create notes for both
          await api.shiftManagement.createNote({
            date: date,
            targetEmployee: this.transferShiftData.targetId,
            content: `[System] Schicht mit ${this.selectedCol.firstName} getauscht.`,
          })
          await api.shiftManagement.createNote({
            date: date,
            targetEmployee: this.selectedCol.id,
            content: `[System] Schicht mit ${targetName} getauscht.`,
          })

          this.$toast.success("Schichten erfolgreich getauscht.")
        }
        this.transferShiftDialog = false
        this.fetchData(true)
      } catch (e) {
        console.error(e)
        this.$toast.error(this.transferOrSwapMode === "swap" ? "Fehler beim Tauschen der Schichten." : "Fehler beim Übertragen der Schicht.")
      }
    },
    async toggleSickStatus(col) {
      if (!col.shifts || col.shifts.length === 0) return this.$toast.error("Keine Schicht zum Markieren gefunden.")
      const shift = col.shifts[0]
      const newSickState = !shift.isSick

      if (newSickState) {
        // Open the dialog!
        this.sickDialogCol = col
        this.sickDialogShift = shift
        this.sickDialogPartialType = "complete"
        this.sickDialogPercentage = 100
        this.sickDialogCalculationMode = "time_window"
        this.sickDialogHours = 0
        this.sickDialogTimeRange = ""
        this.sickDialogRange1Start = ""
        this.sickDialogRange1End = ""
        this.sickDialogShowRange2 = false
        this.sickDialogRange2Start = ""
        this.sickDialogRange2End = ""

        // Split-Schichten werden als echte Arbeitssegmente vorbelegt. Die
        // dazwischenliegenden Lücken oder Pausen zählen nie als Krankstunden.
        const workSegments = this.getShiftWorkSegments(shift)
        if (workSegments.length > 0) {
          this.sickDialogShiftTimes = this.formatSickTimeRanges(workSegments, " / ")
          this.setSickDialogRanges(workSegments)
          const totalMinutes = this.getTimeRangesDurationMinutes(workSegments)
          this.sickDialogShiftTotalHours = Math.round((totalMinutes / 60) * 100) / 100
          this.sickDialogHours = this.sickDialogShiftTotalHours
        } else {
          this.sickDialogShiftTimes = ""
          this.sickDialogShiftTotalHours = 0
        }

        this.sickDetailsDialog = true
      } else {
        // Toggle health state (mark healthy)
        try {
          await this.updateCalendarShift(shift._id, {
            isSick: false,
            sickPercentage: 100,
            sickHours: 0,
            sickTimeRange: "",
          })
          this.$toast.success(`${col.firstName} wurde als gesund markiert.`)
          this.fetchData(true)
        } catch (e) {
          this.$toast.error("Fehler beim Ändern des Krankheitsstatus.")
        }
      }
    },
    async saveSickDetails() {
      if (!this.sickDialogShift) return
      const isPartial = this.sickDialogPartialType === "partial"
      const workSegments = this.getShiftWorkSegments(this.sickDialogShift)
      const normalizedRanges = isPartial ? this.clipSickRangesToWorkSegments(this.parseSickTimeRanges(this.sickDialogTimeRange), workSegments) : []
      if (isPartial && !normalizedRanges.length) {
        this.$toast.error("Bitte mindestens ein gültiges Zeitfenster innerhalb der Arbeitszeit wählen.")
        return
      }

      this.sickDialogSaving = true
      try {
        const pct = isPartial ? this.sickDialogPercentage : 100
        const sickMinutes = isPartial ? this.getTimeRangesDurationMinutes(normalizedRanges) : 0
        const hours = isPartial ? Math.round((sickMinutes / 60) * 100) / 100 : 0
        const range = isPartial ? this.formatSickTimeRanges(normalizedRanges) : ""

        await this.updateCalendarShift(this.sickDialogShift._id, {
          isSick: true,
          sickPercentage: pct,
          sickHours: hours,
          sickTimeRange: range,
          absenceCalculationMode: isPartial ? this.sickDialogCalculationMode : "proportional",
          absenceSickLocation: "start",
        })

        this.$toast.success(`${this.sickDialogCol.firstName} wurde als krank markiert.`)
        this.sickDetailsDialog = false
        this.fetchData(true)
      } catch (e) {
        console.error(e)
        this.$toast.error("Fehler beim Speichern des Krankheitsstatus.")
      } finally {
        this.sickDialogSaving = false
      }
    },
    onSickDialogPercentageChange(pct) {
      if (this.sickDialogShiftTotalHours) {
        this.sickDialogHours = Math.round(this.sickDialogShiftTotalHours * (pct / 100) * 100) / 100
        this.updateTimesFromHours(this.sickDialogHours)
      }
    },
    getSickDialogPercentageOptions() {
      return [...new Set([100, 90, 80, 75, 70, 60, 50, 40, 30, 25, 20, 10, Number(this.sickDialogPercentage)])]
        .filter((value) => Number.isFinite(value) && value > 0 && value <= 100)
        .sort((a, b) => b - a)
    },
    toggleRange2() {
      this.sickDialogShowRange2 = !this.sickDialogShowRange2
      if (!this.sickDialogShowRange2) {
        this.sickDialogRange2Start = ""
        this.sickDialogRange2End = ""
      }
      this.recalculateSickHoursFromRanges()
    },
    updateTimesFromHours(hours) {
      if (!this.sickDialogShift) return
      const workSegments = this.getShiftWorkSegments(this.sickDialogShift)
      let remainingMinutes = Math.max(0, Math.round((Number(hours) || 0) * 60))
      const ranges = []

      workSegments.forEach((segment) => {
        if (remainingMinutes <= 0) return
        const start = moment(segment.start, "HH:mm", true)
        const end = moment(segment.end, "HH:mm", true)
        if (!start.isValid() || !end.isValid() || !start.isBefore(end)) return
        const availableMinutes = end.diff(start, "minutes")
        const sickMinutes = Math.min(remainingMinutes, availableMinutes)
        if (sickMinutes > 0) {
          ranges.push({
            start: segment.start,
            end: start.clone().add(sickMinutes, "minutes").format("HH:mm"),
          })
          remainingMinutes -= sickMinutes
        }
      })

      const normalized = this.setSickDialogRanges(ranges)
      this.sickDialogHours = Math.round((this.getTimeRangesDurationMinutes(normalized) / 60) * 100) / 100
    },
    recalculateSickHoursFromRanges() {
      const pendingSecondRange = {
        enabled: this.sickDialogShowRange2,
        start: this.sickDialogRange2Start,
        end: this.sickDialogRange2End,
      }
      const selectedRanges = []
      if (this.sickDialogRange1Start && this.sickDialogRange1End) {
        selectedRanges.push({ start: this.sickDialogRange1Start, end: this.sickDialogRange1End })
      }
      if (this.sickDialogShowRange2 && this.sickDialogRange2Start && this.sickDialogRange2End) {
        selectedRanges.push({ start: this.sickDialogRange2Start, end: this.sickDialogRange2End })
      }
      const workSegments = this.getShiftWorkSegments(this.sickDialogShift)
      const normalizedRanges = this.clipSickRangesToWorkSegments(selectedRanges, workSegments)
      const totalMinutes = this.getTimeRangesDurationMinutes(normalizedRanges)
      this.sickDialogHours = Math.round((totalMinutes / 60) * 100) / 100

      // Update percentage based on the new hours!
      if (this.sickDialogShiftTotalHours > 0) {
        const calculatedPct = Math.round((this.sickDialogHours / this.sickDialogShiftTotalHours) * 100)
        const maximum = this.sickDialogHours < this.sickDialogShiftTotalHours ? 99 : 100
        this.sickDialogPercentage = Math.max(1, Math.min(maximum, calculatedPct))
      }

      // Eingaben, die über eine Split-Lücke reichen, werden als echte,
      // getrennte Arbeitssegmente dargestellt und gespeichert.
      this.setSickDialogRanges(normalizedRanges)
      if (pendingSecondRange.enabled && normalizedRanges.length < 2 && (!pendingSecondRange.start || !pendingSecondRange.end)) {
        this.sickDialogShowRange2 = true
        this.sickDialogRange2Start = pendingSecondRange.start
        this.sickDialogRange2End = pendingSecondRange.end
      }
    },
    toggleEmployeeFocus(col) {
      const colId = String(col.info?._id || col.id)
      if (this.focusedEmployeeId && String(this.focusedEmployeeId) === colId) {
        this.focusedEmployeeId = null
      } else {
        this.focusedEmployeeId = colId
      }
    },
    handleEmptyClick(e, col) {
      // Clipboard mode is deliberately one-tap on touch devices: after
      // copying/cutting an entry the next free slot opens the confirmation
      // menu instead of the create-template sheet.
      if (this.appointmentClipboard) {
        this.showEmptyContextMenu(e, col)
        return
      }
      if (this.$vuetify.breakpoint.mdAndDown) {
        // Berechne die Uhrzeit aus der Klick-Position
        const laneBody = e.target.closest(".lane-body")
        if (laneBody) {
          const rect = laneBody.getBoundingClientRect()
          const relativeY = e.clientY - rect.top
          const clickedMinutes = this.getCalendarStartMinutes + Math.max(0, relativeY / this.hourHeight) * 60
          let hour = Math.floor(clickedMinutes / 60)
          let min = Math.round((clickedMinutes % 60) / 15) * 15
          if (min === 60) {
            min = 0
            hour++
          }
          const clickedTime = moment().hour(hour).minute(min).format("HH:mm")
          this.mobileClickContext = { time: clickedTime, col }
        } else {
          this.mobileClickContext = null
        }
        this.mobileTemplateSheet = true
      } else {
        if (!this.sidebarOpen) {
          this.sidebarOpen = true
        }
        this.$toast.info("Nutze die Vorlagen rechts!")
      }
    },
    getTopFromTime(timeStr) {
      if (!timeStr) return 0
      const [h, m] = timeStr.split(":").map(Number)
      return this.getTop(h, m)
    },
    getTop(hour, minute = 0) {
      const startMin = this.getCalendarStartMinutes
      const currentMin = hour * 60 + minute
      return (Math.max(0, currentMin - startMin) / 60) * this.hourHeight
    },
    hexToRgba(hex, alpha) {
      if (!hex) return `rgba(100,100,100, ${alpha})`
      if (hex.startsWith("#")) {
        const r = parseInt(hex.slice(1, 3), 16)
        const g = parseInt(hex.slice(3, 5), 16)
        const b = parseInt(hex.slice(5, 7), 16)
        return `rgba(${r}, ${g}, ${b}, ${alpha})`
      }
      return hex
    },
    mergeTimeSegments(entries) {
      const sorted = (entries || [])
        .filter((entry) => entry && entry.start && entry.end)
        .filter((entry) => {
          const start = moment(entry.start, "HH:mm")
          const end = moment(entry.end, "HH:mm")
          return start.isValid() && end.isValid() && start.isBefore(end)
        })
        .map((entry) => ({ start: entry.start, end: entry.end }))
        .sort((a, b) => moment(a.start, "HH:mm").diff(moment(b.start, "HH:mm")))

      const merged = []
      sorted.forEach((entry) => {
        const last = merged[merged.length - 1]
        if (!last || moment(entry.start, "HH:mm").isAfter(moment(last.end, "HH:mm"))) {
          merged.push({ ...entry })
          return
        }
        if (moment(entry.end, "HH:mm").isAfter(moment(last.end, "HH:mm"))) last.end = entry.end
      })
      return merged
    },
    getShiftCoverageSegments(shift) {
      // Pausen verbinden die Arbeitsblöcke, die sie unterbrechen. Eine echte
      // Lücke ohne Pause bleibt dagegen ein eigener Split-Schicht-Block.
      return this.mergeTimeSegments(this.getShiftTimes(shift))
    },
    getShiftWorkSegments(shift) {
      const shiftTimes = this.getShiftTimes(shift)
      let workSegments = this.mergeTimeSegments(shiftTimes.filter((entry) => String(entry.type || "").toLowerCase() !== "pause"))
      const pauseSegments = this.mergeTimeSegments(shiftTimes.filter((entry) => String(entry.type || "").toLowerCase() === "pause"))

      pauseSegments.forEach((pause) => {
        const pauseStart = moment(pause.start, "HH:mm", true)
        const pauseEnd = moment(pause.end, "HH:mm", true)
        if (!pauseStart.isValid() || !pauseEnd.isValid() || !pauseStart.isBefore(pauseEnd)) return

        workSegments = workSegments.flatMap((segment) => {
          const segmentStart = moment(segment.start, "HH:mm", true)
          const segmentEnd = moment(segment.end, "HH:mm", true)
          if (!segmentStart.isValid() || !segmentEnd.isValid() || pauseEnd.isSameOrBefore(segmentStart) || pauseStart.isSameOrAfter(segmentEnd)) {
            return [segment]
          }

          const remaining = []
          if (segmentStart.isBefore(pauseStart)) {
            remaining.push({ start: segment.start, end: moment.min(segmentEnd, pauseStart).format("HH:mm") })
          }
          if (segmentEnd.isAfter(pauseEnd)) {
            remaining.push({ start: moment.max(segmentStart, pauseEnd).format("HH:mm"), end: segment.end })
          }
          return remaining
        })
      })

      return this.mergeTimeSegments(workSegments)
    },
    parseSickTimeRanges(value) {
      const source = String(value || "")
      const ranges = []
      const matcher = /(\d{1,2}):(\d{2})\s*[-–—]\s*(\d{1,2}):(\d{2})/g
      let match = null

      while ((match = matcher.exec(source)) !== null) {
        const startHour = Number(match[1])
        const startMinute = Number(match[2])
        const endHour = Number(match[3])
        const endMinute = Number(match[4])
        if (
          startHour < 0 ||
          startHour > 23 ||
          endHour < 0 ||
          endHour > 23 ||
          startMinute < 0 ||
          startMinute > 59 ||
          endMinute < 0 ||
          endMinute > 59
        ) {
          continue
        }

        const start = `${String(startHour).padStart(2, "0")}:${String(startMinute).padStart(2, "0")}`
        const end = `${String(endHour).padStart(2, "0")}:${String(endMinute).padStart(2, "0")}`
        if (moment(start, "HH:mm", true).isBefore(moment(end, "HH:mm", true))) ranges.push({ start, end })
      }

      return this.mergeTimeSegments(ranges)
    },
    formatSickTimeRanges(ranges, separator = ", ") {
      return (ranges || []).map((range) => `${range.start} - ${range.end}`).join(separator)
    },
    clipSickRangesToWorkSegments(ranges, workSegments) {
      const clipped = []
      ;(ranges || []).forEach((range) => {
        const rangeStart = moment(range.start, "HH:mm", true)
        const rangeEnd = moment(range.end, "HH:mm", true)
        if (!rangeStart.isValid() || !rangeEnd.isValid() || !rangeStart.isBefore(rangeEnd)) return
        ;(workSegments || []).forEach((segment) => {
          const segmentStart = moment(segment.start, "HH:mm", true)
          const segmentEnd = moment(segment.end, "HH:mm", true)
          if (!segmentStart.isValid() || !segmentEnd.isValid() || !segmentStart.isBefore(segmentEnd)) return
          const start = moment.max(rangeStart, segmentStart)
          const end = moment.min(rangeEnd, segmentEnd)
          if (start.isBefore(end)) clipped.push({ start: start.format("HH:mm"), end: end.format("HH:mm") })
        })
      })
      return this.mergeTimeSegments(clipped)
    },
    getTimeRangesDurationMinutes(ranges) {
      return (ranges || []).reduce((total, range) => {
        const start = moment(range.start, "HH:mm", true)
        const end = moment(range.end, "HH:mm", true)
        return start.isValid() && end.isValid() && start.isBefore(end) ? total + end.diff(start, "minutes") : total
      }, 0)
    },
    setSickDialogRanges(ranges) {
      const normalized = this.mergeTimeSegments(ranges)
      const first = normalized[0] || null
      const second = normalized[1] || null
      this.sickDialogRange1Start = first ? first.start : ""
      this.sickDialogRange1End = first ? first.end : ""
      this.sickDialogShowRange2 = Boolean(second)
      this.sickDialogRange2Start = second ? second.start : ""
      this.sickDialogRange2End = second ? second.end : ""
      this.sickDialogTimeRange = this.formatSickTimeRanges(normalized)
      return normalized
    },
    getShiftRange(col) {
      if (!col || !Array.isArray(col.shifts) || !col.shifts.length) return "-"
      const segments = this.mergeTimeSegments(col.shifts.flatMap((shift) => this.getShiftWorkSegments(shift)))
      if (!segments.length) return "-"
      return segments.map((segment) => `${segment.start}-${segment.end}`).join(" / ")
    },
    rebuildShiftTimesWithPause(shift, pauseStart, pauseEnd, editPauseIndex = -1) {
      if (!shift || !Array.isArray(shift.times) || !shift.times.length) return null

      const originalTimes = JSON.parse(JSON.stringify(shift.times))
      const coverageSegments = this.getShiftCoverageSegments({ times: originalTimes })
      if (!coverageSegments.length) {
        this.$toast.error("Die Schicht hat keine Arbeitszeiten.")
        return null
      }

      const ps = moment(pauseStart, "HH:mm")
      const pe = moment(pauseEnd, "HH:mm")
      if (!ps.isValid() || !pe.isValid()) {
        this.$toast.error("Ungültige Pausenzeit.")
        return null
      }
      if (ps.isSameOrAfter(pe)) {
        this.$toast.error("Die Pause muss eine positive Dauer haben.")
        return null
      }

      const targetSegmentIndex = coverageSegments.findIndex((segment) => {
        const start = moment(segment.start, "HH:mm")
        const end = moment(segment.end, "HH:mm")
        return ps.isSameOrAfter(start) && pe.isSameOrBefore(end)
      })
      if (targetSegmentIndex === -1) {
        this.$toast.error("Die Pause muss vollständig innerhalb eines Arbeitsblocks liegen.")
        return null
      }

      const targetSegment = coverageSegments[targetSegmentIndex]
      const targetStart = moment(targetSegment.start, "HH:mm")
      const targetEnd = moment(targetSegment.end, "HH:mm")
      if (ps.diff(targetStart, "minutes") < 15) {
        this.$toast.error("Die Pause darf nicht ganz am Anfang des Arbeitsblocks liegen (mind. 15 Min. nach Arbeitsbeginn).")
        return null
      }
      if (targetEnd.diff(pe, "minutes") < 15) {
        this.$toast.error("Die Pause darf nicht ganz am Ende des Arbeitsblocks liegen (mind. 15 Min. vor Arbeitsende).")
        return null
      }

      const editedPause =
        editPauseIndex >= 0 && originalTimes[editPauseIndex] && originalTimes[editPauseIndex].type === "Pause" ? originalTimes[editPauseIndex] : null
      const otherPauses = originalTimes
        .map((entry, index) => ({ entry, index }))
        .filter(({ entry, index }) => entry.type === "Pause" && index !== editPauseIndex)
        .map(({ entry }) => ({ ...entry }))

      for (const other of otherPauses) {
        const otherStart = moment(other.start, "HH:mm")
        const otherEnd = moment(other.end, "HH:mm")
        if (ps.isBefore(otherEnd) && pe.isAfter(otherStart)) {
          this.$toast.error("Kollision mit einer bestehenden Pause.")
          return null
        }
      }

      const allPauses = [...otherPauses, { start: pauseStart, end: pauseEnd, type: "Pause" }].sort((a, b) =>
        moment(a.start, "HH:mm").diff(moment(b.start, "HH:mm")),
      )
      const originalSegmentIndex = editedPause
        ? coverageSegments.findIndex((segment) => {
            const segmentStart = moment(segment.start, "HH:mm")
            const segmentEnd = moment(segment.end, "HH:mm")
            return moment(editedPause.start, "HH:mm").isSameOrAfter(segmentStart) && moment(editedPause.end, "HH:mm").isSameOrBefore(segmentEnd)
          })
        : -1
      const affectedSegments = new Set([targetSegmentIndex])
      if (originalSegmentIndex >= 0) affectedSegments.add(originalSegmentIndex)

      const result = []
      coverageSegments.forEach((segment, segmentIndex) => {
        const segmentStart = moment(segment.start, "HH:mm")
        const segmentEnd = moment(segment.end, "HH:mm")
        const belongsToSegment = (entry) => {
          const entryStart = moment(entry.start, "HH:mm")
          const entryEnd = moment(entry.end, "HH:mm")
          return entryStart.isSameOrAfter(segmentStart) && entryEnd.isSameOrBefore(segmentEnd)
        }

        if (!affectedSegments.has(segmentIndex)) {
          result.push(...originalTimes.filter(belongsToSegment))
          return
        }

        const segmentPauses = allPauses.filter(belongsToSegment)
        const workPrototype = originalTimes.find((entry) => entry.type !== "Pause" && belongsToSegment(entry)) || {
          type: "Arbeitszeit",
        }
        let cursor = segment.start
        segmentPauses.forEach((pause) => {
          if (moment(cursor, "HH:mm").isBefore(moment(pause.start, "HH:mm"))) {
            result.push({ ...workPrototype, start: cursor, end: pause.start, type: workPrototype.type || "Arbeitszeit" })
          }
          result.push({ ...pause, type: "Pause" })
          cursor = pause.end
        })
        if (moment(cursor, "HH:mm").isBefore(segmentEnd)) {
          result.push({ ...workPrototype, start: cursor, end: segment.end, type: workPrototype.type || "Arbeitszeit" })
        }
      })

      return result.sort((a, b) => {
        const byStart = moment(a.start, "HH:mm").diff(moment(b.start, "HH:mm"))
        if (byStart !== 0) return byStart
        return moment(a.end, "HH:mm").diff(moment(b.end, "HH:mm"))
      })
    },
    removePauseFromShiftTimes(shift, pauseIndex) {
      if (!shift || !Array.isArray(shift.times)) return null
      const times = JSON.parse(JSON.stringify(shift.times))
      const pause = times[pauseIndex]
      if (!pause || pause.type !== "Pause") {
        this.$toast.error("Pause nicht gefunden.")
        return null
      }

      const leftIndex = times.findIndex((entry) => entry.type !== "Pause" && entry.end === pause.start)
      const rightIndex = times.findIndex((entry) => entry.type !== "Pause" && entry.start === pause.end)
      times.splice(pauseIndex, 1)

      if (leftIndex >= 0 && rightIndex >= 0) {
        const adjustedLeftIndex = leftIndex > pauseIndex ? leftIndex - 1 : leftIndex
        const adjustedRightIndex = rightIndex > pauseIndex ? rightIndex - 1 : rightIndex
        const left = times[adjustedLeftIndex]
        const right = times[adjustedRightIndex]
        if (left && right && left !== right) {
          left.end = right.end
          times.splice(adjustedRightIndex, 1)
        }
      }

      return times.sort((a, b) => moment(a.start, "HH:mm").diff(moment(b.start, "HH:mm")))
    },
    calculateBackgrounds(col) {
      if (!col.shifts) return []
      return col.shifts.flatMap((shift) => {
        const workSegments = this.getShiftWorkSegments(shift)
        if (!workSegments.length) return []
        const color = shift.shiftTemplate?.color || "#333"

        const buildBlock = (start, end, isSickBlock) => {
          const top = this.getTopFromTime(start)
          const bottom = this.getTopFromTime(end)
          return {
            top,
            height: bottom - top,
            color,
            bgColor: this.hexToRgba(color, 0.1),
            start,
            end,
            isSick: isSickBlock,
            absenceReason: isSickBlock ? col.absenceReason || "sick" : null,
            sickPercentage: shift.sickPercentage !== undefined ? shift.sickPercentage : 100,
            sickHours: shift.sickHours || 0,
            sickTimeRange: shift.sickTimeRange || "",
            shiftId: shift._id,
            times: this.getShiftTimes(shift),
          }
        }

        // Full-day absences are also supplied at column level. Rendering them
        // from that source keeps legacy/full records hatched even if an older
        // shift document has not yet been re-synchronised.
        if (col.isSick && !this.isWorkingDespiteAbsence(col)) {
          return workSegments.map((segment) => buildBlock(segment.start, segment.end, true))
        }

        if (!shift.isSick) {
          return workSegments.map((segment) => buildBlock(segment.start, segment.end, false))
        }

        const blocks = []
        const explicitSickRanges = this.clipSickRangesToWorkSegments(this.parseSickTimeRanges(shift.sickTimeRange), workSegments)

        if (explicitSickRanges.length) {
          workSegments.forEach((segment) => {
            const segmentStart = moment(segment.start, "HH:mm", true)
            const segmentEnd = moment(segment.end, "HH:mm", true)
            const segmentSickRanges = explicitSickRanges.filter((range) => {
              const start = moment(range.start, "HH:mm", true)
              const end = moment(range.end, "HH:mm", true)
              return start.isBefore(segmentEnd) && end.isAfter(segmentStart)
            })
            let cursor = segmentStart.clone()

            segmentSickRanges.forEach((range) => {
              const sickStart = moment.max(segmentStart, moment(range.start, "HH:mm", true))
              const sickEnd = moment.min(segmentEnd, moment(range.end, "HH:mm", true))
              if (!sickStart.isBefore(sickEnd)) return
              if (cursor.isBefore(sickStart)) blocks.push(buildBlock(cursor.format("HH:mm"), sickStart.format("HH:mm"), false))
              blocks.push(buildBlock(sickStart.format("HH:mm"), sickEnd.format("HH:mm"), true))
              if (sickEnd.isAfter(cursor)) cursor = sickEnd.clone()
            })
            if (cursor.isBefore(segmentEnd)) blocks.push(buildBlock(cursor.format("HH:mm"), segment.end, false))
          })
          return blocks
        }

        if (shift.sickPercentage === undefined || Number(shift.sickPercentage) >= 100) {
          return workSegments.map((segment) => buildBlock(segment.start, segment.end, true))
        }

        const totalWorkMinutes = workSegments.reduce(
          (sum, segment) => sum + moment(segment.end, "HH:mm").diff(moment(segment.start, "HH:mm"), "minutes"),
          0,
        )
        let remainingSickMinutes = Math.max(0, Math.min(totalWorkMinutes, Math.round(totalWorkMinutes * (Number(shift.sickPercentage) / 100))))
        workSegments.forEach((segment) => {
          const segmentStart = moment(segment.start, "HH:mm")
          const segmentEnd = moment(segment.end, "HH:mm")
          const segmentMinutes = segmentEnd.diff(segmentStart, "minutes")
          const sickMinutes = Math.min(remainingSickMinutes, segmentMinutes)
          if (sickMinutes > 0) {
            const sickEnd = segmentStart.clone().add(sickMinutes, "minutes")
            blocks.push(buildBlock(segment.start, sickEnd.format("HH:mm"), true))
            if (sickEnd.isBefore(segmentEnd)) blocks.push(buildBlock(sickEnd.format("HH:mm"), segment.end, false))
          } else {
            blocks.push(buildBlock(segment.start, segment.end, false))
          }
          remainingSickMinutes -= sickMinutes
        })
        return blocks
      })
    },
    getStudioOffset(studio) {
      const studioId = studio?._id || studio?.id || this.studioId || ""
      if (!studioId) return 30
      const cached = localStorage.getItem(`wc_studio_offset_${studioId}`)
      if (cached !== null) {
        return parseInt(cached, 10)
      }
      return studio && studio.openingClosingOffset !== undefined ? studio.openingClosingOffset : 30
    },
    calculateItems(col) {
      const offset = this.getStudioOffset(this.selectedStudio)
      let items = []
      col.shifts.forEach((s) => {
        const times = s.times || []
        if (!times.length) return
        times.forEach((t, idx) => {
          if (t.type === "Pause")
            items.push({
              title: "Pause",
              start: t.start,
              end: t.end,
              top: this.getTopFromTime(t.start),
              height: this.getTopFromTime(t.end) - this.getTopFromTime(t.start),
              class: "item-pause", // Spezielle Klasse für Schraffur bleibt
              borderColor: "#FF9800",
              icon: "mdi-coffee",
              iconColor: "orange",
              type: "pause",
              parentShiftId: s._id,
              timeIndex: idx,
            })
        })
        const isOpening =
          s.isOpeningShift !== undefined && s.isOpeningShift !== null ? s.isOpeningShift : s.shiftTemplate ? s.shiftTemplate.isOpeningShift : false
        const isClosing =
          s.isClosingShift !== undefined && s.isClosingShift !== null ? s.isClosingShift : s.shiftTemplate ? s.shiftTemplate.isClosingShift : false
        if (isOpening) {
          const workTimes = times.filter((t) => t.type !== "Pause")
          const sorted = [...workTimes].sort((a, b) => a.start.localeCompare(b.start))
          if (sorted.length > 0) {
            const startStr = sorted[0].start
            const endStr = moment(startStr, "HH:mm").add(offset, "minutes").format("HH:mm")
            const top = this.getTopFromTime(startStr)
            items.push({
              title: "Centeröffnung",
              subtitle: "Fix",
              start: startStr,
              end: endStr,
              top,
              height: this.getTopFromTime(endStr) - top,
              class: "item-system glass-red",
              borderColor: "#D32F2F",
              icon: "mdi-door-open",
              iconColor: "red lighten-3",
              type: "system",
              countsTowardUtilization: true,
              parentShiftId: s._id,
            })
          }
        }
        if (isClosing) {
          const workTimes = times.filter((t) => t.type !== "Pause")
          const sorted = [...workTimes].sort((a, b) => a.start.localeCompare(b.start))
          if (sorted.length > 0) {
            const endStr = sorted[sorted.length - 1].end
            const startStr = moment(endStr, "HH:mm").subtract(offset, "minutes").format("HH:mm")
            const top = this.getTopFromTime(startStr)
            items.push({
              title: "Centerschliessung",
              subtitle: "Fix",
              start: startStr,
              end: endStr,
              top,
              height: this.getTopFromTime(endStr) - top,
              class: "item-system glass-red",
              borderColor: "#D32F2F",
              icon: "mdi-door-closed",
              iconColor: "red lighten-3",
              type: "system",
              countsTowardUtilization: true,
              parentShiftId: s._id,
            })
          }
        }
      })
      col.tasks.forEach((t) => {
        items.push(this.mapTaskToItem(t, col.info?._id))
      })

      // 3. Appointments (Termine) - Jetzt dynamisch!
      col.appointments.forEach((a) => {
        const top = this.getTopFromTime(a.start)
        const color = a.color || "#2196F3" // Default Blau

        items.push({
          _id: a._id,
          seriesId: a.seriesId,
          type: "appointment",
          title: a.title,
          subtitle: a.customer
            ? `${a.customer.vorname} ${a.customer.nachname}${a.customer2 ? ` + ${a.customer2.vorname}` : ""}`
            : a.noAccountData && a.noAccountData.firstName
            ? `${a.noAccountData.firstName} ${a.noAccountData.lastName} (Gast)`
            : a.description,
          start: a.start,
          end: a.end,
          top,
          height: this.getTopFromTime(a.end) - top,

          // HIER IST DIE ÄNDERUNG: Dynamischer Glass-Effekt basierend auf Termin-Farbe
          style: {
            background: `linear-gradient(135deg, ${this.hexToRgba(color, 0.55)} 0%, ${this.hexToRgba(color, 0.35)} 100%)`,
            border: `1px solid ${this.hexToRgba(color, 0.65)}`,
            boxShadow: `0 4px 15px ${this.hexToRgba(color, 0.2)}`,
          },

          description: a.description || "",
          borderColor: color,
          icon: a.icon || "mdi-calendar",
          iconColor: "white",
          isAppt: true,
          rawData: a,
          status: a.status || "planned", // Status durchreichen!
        })
      })
      return items
    },
    getLiveStatus(col) {
      return this.liveAttendanceMap[col.info.employeeId]
        ? { active: true, color: "green accent-4", since: this.liveAttendanceMap[col.info.employeeId].checkin || "Jetzt" }
        : { active: false, color: "grey darken-3", since: "" }
    },
    changeDate(d) {
      this.currentDate = moment(this.currentDate).add(d, "days").format("YYYY-MM-DD")
      this.fetchData()
    },
    selectCalendarDate() {
      this.datePickerMenu = false
      this.fetchData()
    },
    jumpToToday() {
      this.currentDate = moment().format("YYYY-MM-DD")
      this.fetchData()
    },
    syncTimeAxisToScroller(scroller = this.$refs.scroller) {
      const timeAxis = this.$refs.timeAxis || document.querySelector(".time-axis")
      if (timeAxis && scroller) timeAxis.scrollTop = scroller.scrollTop
    },
    syncScroll(e) {
      this.syncTimeAxisToScroller(e && e.target ? e.target : this.$refs.scroller)
    },
    scrollToNow() {
      if (!this.$refs.scroller) return
      const nowMinutes = moment().hours() * 60 + moment().minutes()
      const offset = Math.max(0, nowMinutes - this.getCalendarStartMinutes - 60)
      this.$refs.scroller.scrollTop = (offset / 60) * this.hourHeight
    },
    startClock() {
      this.timerInterval = setInterval(() => {
        this.updateTimeLine()
        this.fetchLiveStatus()
      }, 30000)
    },
    updateTimeLine() {
      if (!this.isToday) {
        this.currentTimeTop = -100
        return
      }
      const now = moment()
      const currentMin = now.hour() * 60 + now.minute()
      const startMin = this.getCalendarStartMinutes
      const endMin = this.getCalendarEndMinutes
      if (currentMin < startMin || currentMin > endMin) {
        this.currentTimeTop = -100
        return
      }
      this.currentTimeTop = this.getTop(now.hour(), now.minute())
    },
    async fetchLiveStatus(studioId = this.studioId) {
      try {
        let data = await api.shiftManagement.fetchAttendanceTrainers(studioId)
        const map = {}
        data.forEach((w) => {
          map[w._id] = w
        })
        if (String(studioId || "") !== String(this.studioId || "")) return false
        this.liveAttendanceMap = map
        return true
      } catch (e) {
        return false
      }
    },
  },
}
</script>

<style scoped>
.wc-studio-switcher-chip {
  cursor: pointer;
  min-width: 0;
  max-width: min(280px, 38vw);
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: var(--text-main);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  user-select: none;
  font-family: inherit;
  backdrop-filter: blur(10px);
}

@media (min-width: 1400px) {
  .wc-studio-switcher-chip {
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
    z-index: 5;
  }
}

.theme--light .wc-studio-switcher-chip {
  background: rgba(0, 0, 0, 0.04);
  border: 1px solid rgba(0, 0, 0, 0.08);
}

.wc-studio-switcher-chip.hovered {
  background: rgba(0, 150, 63, 0.1) !important;
  border-color: rgba(0, 150, 63, 0.4) !important;
  box-shadow: 0 0 12px rgba(0, 150, 63, 0.15);
  transform: translateY(-1px);
}

@media (min-width: 960px) {
  .wc-studio-switcher-chip.hovered {
    transform: translate(-50%, -1px) !important;
  }
}

.wc-studio-switcher-chip.hovered .wc-studio-icon {
  color: #00963f !important;
  transform: scale(1.1);
}

.wc-studio-switcher-chip.hovered .wc-studio-chevron {
  transform: translateY(1px);
}

.wc-studio-icon {
  transition: transform 0.3s ease, color 0.3s ease;
  color: var(--text-muted);
}

.wc-studio-chevron {
  transition: transform 0.3s ease;
  color: var(--text-muted);
}

.wc-studio-name {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 0.85rem;
  letter-spacing: 0.2px;
}

@media (min-width: 960px) and (max-width: 1399px) {
  .wc-studio-switcher-chip {
    position: static;
    left: auto;
    max-width: 230px;
    flex: 0 1 230px;
    transform: none;
  }
  .wc-studio-switcher-chip.hovered {
    transform: translateY(-1px) !important;
  }
}

.series-action-btn {
  font-size: 0.72rem !important;
  font-weight: 700 !important;
  text-transform: none !important;
  letter-spacing: 0.2px !important;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  border-radius: 8px !important;
}
.series-action-btn.extend-btn {
  background: rgba(76, 175, 80, 0.08) !important;
  border: 1px solid rgba(76, 175, 80, 0.2) !important;
  color: #4caf50 !important;
}
.series-action-btn.extend-btn:hover {
  background: rgba(76, 175, 80, 0.16) !important;
  border-color: rgba(76, 175, 80, 0.4) !important;
  transform: translateY(-1px);
  box-shadow: 0 4px 10px rgba(76, 175, 80, 0.12);
}
.series-action-btn.delete-btn {
  background: rgba(244, 67, 54, 0.08) !important;
  border: 1px solid rgba(244, 67, 54, 0.2) !important;
  color: #f44336 !important;
}
.series-action-btn.delete-btn:hover {
  background: rgba(244, 67, 54, 0.16) !important;
  border-color: rgba(244, 67, 54, 0.4) !important;
  transform: translateY(-1px);
  box-shadow: 0 4px 10px rgba(244, 67, 54, 0.12);
}

.occ-row {
  transition: all 0.2s ease;
}
.occ-row:hover {
  transform: translateX(3px);
  background: rgba(120, 120, 120, 0.04) !important;
}
.theme--dark .occ-row:hover {
  background: rgba(255, 255, 255, 0.05) !important;
}

.employee-sort-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 8px;
  max-height: 450px;
  overflow-y: auto;
  padding: 4px;
}
.sort-item {
  cursor: grab;
  user-select: none;
}
.sort-item:active {
  cursor: grabbing;
}
.drag-handle {
  cursor: grab;
  color: var(--text-muted) !important;
}
.drag-handle:active {
  cursor: grabbing;
}

::v-deep .studio-cockpit-wrapper {
  --bg-main: #0a0a0a;
  --bg-header: rgba(12, 12, 12, 0.98);
  --bg-time-axis: rgba(8, 8, 8, 0.95);
  --bg-panel: rgba(255, 255, 255, 0.05);
  /* cards hover, empty state */
  --bg-panel-hover: rgba(255, 255, 255, 0.08);
  --bg-card: #232323;
  --bg-sidebar: rgba(12, 12, 12, 0.98);
  --bg-block: rgba(35, 35, 35, 0.95);
  --bg-skeleton: rgba(255, 255, 255, 0.04);
  --bg-time-line: rgba(255, 255, 255, 0.06);
  --text-main: #ffffff;
  --text-muted: rgba(255, 255, 255, 0.45);
  --text-disabled: rgba(255, 255, 255, 0.25);
  --border-light: rgba(255, 255, 255, 0.04);
  --border-medium: rgba(255, 255, 255, 0.06);
  --border-strong: rgba(255, 255, 255, 0.12);
  --icon-color: rgba(255, 255, 255, 0.5);
  --icon-muted: rgba(255, 255, 255, 0.35);
  --shadow-color: rgba(0, 0, 0, 0.5);

  background: var(--bg-main) !important;
  color: var(--text-main);
  transition: background 0.3s ease, color 0.3s ease;
}

.studio-cockpit-wrapper {
  flex: 1;
  min-height: 0;
  /* Wichtig, damit Flexbox das Scrollen erlaubt */
  overflow: hidden;
  background: var(--bg-main);
  /* Sicherheitshalber: Füllt den letzten Rest mit deiner Dark-Mode Farbe */
}

::v-deep .studio-cockpit-wrapper.theme--light {
  --bg-main: #fafafa;
  --bg-header: #ffffff;
  --bg-time-axis: #fcfcfc;
  --bg-panel: rgba(0, 0, 0, 0.02);
  --bg-panel-hover: rgba(0, 0, 0, 0.04);
  --bg-card: #ffffff;
  --bg-sidebar: #ffffff;
  --bg-block: #ffffff;
  --bg-skeleton: rgba(0, 0, 0, 0.04);
  --bg-time-line: rgba(0, 0, 0, 0.04);
  --text-main: #333333;
  --text-muted: rgba(0, 0, 0, 0.5);
  --text-disabled: rgba(0, 0, 0, 0.3);
  --border-light: rgba(0, 0, 0, 0.03);
  --border-medium: rgba(0, 0, 0, 0.06);
  --border-strong: rgba(0, 0, 0, 0.12);
  --icon-color: rgba(0, 0, 0, 0.45);
  --icon-muted: rgba(0, 0, 0, 0.35);
  --shadow-color: rgba(0, 0, 0, 0.05);
}

.work-calendar {
  display: flex;
  flex-direction: column;
  flex: 1 1 100%;
  width: 100%;
  height: 100%;

  /* DIE NOTBREMSE: Zwingt den Kalender zu stoppen, bevor er zu gross wird! */
  max-height: 100vh;
  max-height: 100dvh;
  /* Modernes CSS für Mobile-Browser (ignoriert Adressleisten) */

  overflow: hidden;
}

.wc-customer-appointment-launcher {
  overflow: hidden;
  color: #f6faf7;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 22px !important;
  background: radial-gradient(circle at 88% 0%, rgba(0, 150, 64, 0.2), transparent 36%), #151a17 !important;
}

.wc-customer-appointment-launcher--light {
  color: #17211b;
  border-color: rgba(15, 45, 28, 0.12);
  background: radial-gradient(circle at 88% 0%, rgba(0, 150, 64, 0.13), transparent 38%), #ffffff !important;
}

.wc-customer-appointment-launcher__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  padding: 24px 26px 18px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.09);
}

.wc-customer-appointment-launcher--light .wc-customer-appointment-launcher__header {
  border-bottom-color: rgba(15, 45, 28, 0.1);
}

.wc-customer-appointment-launcher__identity {
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 15px;
}

.wc-customer-appointment-launcher__icon {
  display: inline-flex;
  width: 52px;
  height: 52px;
  flex: 0 0 52px;
  align-items: center;
  justify-content: center;
  border-radius: 16px;
  background: linear-gradient(135deg, #00ad4b, #007d36);
  box-shadow: 0 12px 28px rgba(0, 150, 64, 0.24);
}

.wc-customer-appointment-launcher__eyebrow {
  display: block;
  margin-bottom: 2px;
  color: #50d486;
  font-size: 12px;
  font-weight: 900;
  letter-spacing: 1.35px;
}

.wc-customer-appointment-launcher__header h2 {
  margin: 0;
  color: inherit;
  font-size: 24px;
  line-height: 1.15;
}

.wc-customer-appointment-launcher__header p {
  margin: 4px 0 0;
  color: rgba(239, 247, 242, 0.65);
  font-size: 14px;
}

.wc-customer-appointment-launcher--light .wc-customer-appointment-launcher__header p {
  color: #6a776f;
}

.wc-customer-appointment-launcher__body {
  padding: 20px 26px 26px !important;
}

.wc-customer-appointment-launcher__hint {
  max-width: 640px;
  margin: 0 0 17px;
  color: rgba(239, 247, 242, 0.66);
  font-size: 13px;
  line-height: 1.5;
}

.wc-customer-appointment-launcher--light .wc-customer-appointment-launcher__hint {
  color: #657168;
}

.wc-customer-appointment-launcher__grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}

.wc-customer-appointment-launcher__template {
  display: grid;
  min-width: 0;
  min-height: 72px;
  grid-template-columns: 44px minmax(0, 1fr) auto;
  align-items: center;
  gap: 12px;
  padding: 12px;
  border: 1px solid rgba(255, 255, 255, 0.09);
  border-radius: 15px;
  color: inherit;
  text-align: left;
  background: rgba(255, 255, 255, 0.045);
  transition: transform 0.16s ease, border-color 0.16s ease, background 0.16s ease;
}

.wc-customer-appointment-launcher__template:hover,
.wc-customer-appointment-launcher__template:focus-visible {
  border-color: rgba(80, 212, 134, 0.55);
  outline: none;
  background: rgba(0, 150, 64, 0.1);
  transform: translateY(-1px);
}

.wc-customer-appointment-launcher--light .wc-customer-appointment-launcher__template {
  border-color: rgba(15, 45, 28, 0.11);
  background: #f8faf9;
}

.wc-customer-appointment-launcher--light .wc-customer-appointment-launcher__template:hover,
.wc-customer-appointment-launcher--light .wc-customer-appointment-launcher__template:focus-visible {
  border-color: rgba(0, 150, 64, 0.42);
  background: #f0faf4;
}

.wc-customer-appointment-launcher__template-icon {
  display: inline-flex;
  width: 44px;
  height: 44px;
  align-items: center;
  justify-content: center;
  border-radius: 12px;
}

.wc-customer-appointment-launcher__template-copy {
  display: flex;
  min-width: 0;
  flex-direction: column;
}

.wc-customer-appointment-launcher__template-copy strong {
  overflow: hidden;
  font-size: 14px;
  line-height: 1.25;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.wc-customer-appointment-launcher__template-copy small {
  margin-top: 3px;
  color: rgba(239, 247, 242, 0.52);
  font-size: 12px;
}

.wc-customer-appointment-launcher--light .wc-customer-appointment-launcher__template-copy small {
  color: #748078;
}

@media (max-width: 600px) {
  .wc-customer-appointment-launcher {
    min-height: 100%;
    border-radius: 0 !important;
  }

  .wc-customer-appointment-launcher__header {
    padding: 18px 16px 14px;
  }

  .wc-customer-appointment-launcher__body {
    padding: 16px !important;
    overflow-y: auto;
  }

  .wc-customer-appointment-launcher__grid {
    grid-template-columns: minmax(0, 1fr);
  }
}

.v-input input,
.v-select__selection {
  color: var(--text-main) !important;
}

.v-icon {
  color: var(--icon-color);
}

/* ===== MOBILE BOTTOM-SHEET DIALOG ===== */
::v-deep .wc-mobile-sheet-dialog {
  align-self: flex-end !important;
  margin-bottom: 0 !important;
  border-radius: 20px 20px 0 0 !important;
  max-height: 85vh !important;
  height: 85vh !important;
}

::v-deep .wc-mobile-sheet-dialog .wc-sheet-header {
  border-radius: 20px 20px 0 0;
}

::v-deep .wc-task-assign-dialog {
  align-self: center !important;
  margin: 24px !important;
  border-radius: 16px !important;
  max-height: 90vh !important;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.6) !important;
  border: 1px solid rgba(255, 255, 255, 0.08) !important;
  background: #121212 !important;
}

.wc-task-assign-card {
  background: #121212 !important;
  border-radius: 16px !important;
  display: flex;
  flex-direction: column;
  max-height: 90vh;
  overflow: hidden;
}

/* Premium Tabs Mobile Sheet */
.wc-premium-tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding: 6px;
  background: rgba(0, 0, 0, 0.2);
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.04);
}

.wc-mobile-guestbook-action {
  width: 100%;
  min-height: 64px;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 11px 13px;
  color: #fff;
  background: linear-gradient(135deg, rgba(0, 169, 79, 0.24), rgba(0, 125, 57, 0.09));
  border: 1px solid rgba(0, 185, 87, 0.38);
  border-radius: 15px;
  cursor: pointer;
  font: inherit;
  text-align: left;
  transition: border-color 0.2s ease, background 0.2s ease, transform 0.2s ease;
  -webkit-tap-highlight-color: transparent;
}
.wc-mobile-guestbook-action:focus-visible {
  outline: 2px solid #66bb6a;
  outline-offset: 2px;
}
.wc-mobile-guestbook-action:active {
  background: linear-gradient(135deg, rgba(0, 169, 79, 0.34), rgba(0, 125, 57, 0.14));
  transform: scale(0.985);
}
@media (hover: hover) {
  .wc-mobile-guestbook-action:hover {
    border-color: rgba(0, 185, 87, 0.58);
    background: linear-gradient(135deg, rgba(0, 169, 79, 0.3), rgba(0, 125, 57, 0.13));
  }
}
.wc-mobile-guestbook-icon {
  width: 40px;
  height: 40px;
  border-radius: 11px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: linear-gradient(135deg, #00ae52, #007c39);
  box-shadow: 0 5px 14px rgba(0, 125, 57, 0.3);
}
.wc-mobile-guestbook-copy {
  min-width: 0;
  display: flex;
  flex: 1;
  flex-direction: column;
  line-height: 1.25;
}
.wc-mobile-guestbook-title {
  font-size: 0.92rem;
  font-weight: 750;
}
.wc-mobile-guestbook-subtitle {
  margin-top: 3px;
  color: rgba(255, 255, 255, 0.56);
  font-size: 0.72rem;
  font-weight: 550;
}
.wc-mobile-guestbook-arrow {
  color: rgba(255, 255, 255, 0.45) !important;
  flex-shrink: 0;
}

.wc-premium-tab {
  flex: 1 1 calc(50% - 6px);
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 10px;
  padding: 10px 14px;
  border-radius: 12px;
  cursor: pointer;
  background: transparent;
  border: 1px solid transparent;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  -webkit-tap-highlight-color: transparent;
}

@media (hover: hover) {
  .wc-premium-tab:hover:not(.active) {
    background: rgba(255, 255, 255, 0.04);
  }
}

.wc-premium-tab.active {
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.03));
  border: 1px solid rgba(255, 255, 255, 0.15);
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
}

.wc-premium-tab-icon {
  color: rgba(255, 255, 255, 0.3) !important;
  transition: all 0.3s ease;
}

.wc-premium-tab.active .wc-premium-tab-icon {
  color: #fff !important;
  transform: scale(1.1);
}

.wc-premium-tab-text {
  font-size: 0.8rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.4);
  transition: all 0.3s ease;
}

.wc-premium-tab.active .wc-premium-tab-text {
  color: #fff;
  text-shadow: 0 0 10px rgba(255, 255, 255, 0.3);
}

::v-deep .wc-mobile-sheet-fullscreen .v-card {
  border-radius: 0 !important;
}

.wc-mobile-sheet-card {
  background: rgba(16, 16, 16, 0.98) !important;
  border: 1px solid rgba(255, 255, 255, 0.06) !important;
  border-radius: 20px 20px 0 0 !important;
  display: flex;
  flex-direction: column;
  max-height: 85vh;
  overflow: hidden;
}

.wc-sheet-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 20px 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  flex-shrink: 0;
}

.wc-sheet-icon-box {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: linear-gradient(135deg, #4caf50, #2e7d32);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.wc-sheet-title {
  font-size: 1.05rem;
  font-weight: 700;
  color: white;
  line-height: 1.2;
}

.wc-sheet-subtitle {
  font-size: 0.7rem;
  color: rgba(255, 255, 255, 0.35);
  margin-top: 2px;
}

.wc-sheet-close {
  color: rgba(255, 255, 255, 0.4) !important;
  background: rgba(255, 255, 255, 0.05) !important;
  border-radius: 10px !important;
}

.wc-sheet-close:hover {
  color: white !important;
  background: rgba(255, 255, 255, 0.1) !important;
}

.wc-sheet-body {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 16px 20px;
  -webkit-overflow-scrolling: touch;
}

.wc-sheet-section {
  margin-bottom: 20px;
}

.wc-sheet-section:last-child {
  margin-bottom: 8px;
}

.wc-sheet-section-label {
  font-size: 0.65rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1.2px;
  color: rgba(255, 255, 255, 0.35);
  margin-bottom: 10px;
  padding-left: 2px;
}

.wc-sheet-grid {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.wc-sheet-card {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 16px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  -webkit-tap-highlight-color: transparent;
  position: relative;
  overflow: hidden;
}

.wc-sheet-card::before {
  content: "";
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 3px;
  background: var(--card-accent);
  border-radius: 0 3px 3px 0;
  opacity: 0;
  transition: opacity 0.2s ease;
}

.wc-sheet-card:active {
  transform: scale(0.98);
  background: rgba(255, 255, 255, 0.06);
}

.wc-sheet-card:active::before {
  opacity: 1;
}

@media (hover: hover) {
  .wc-sheet-card:hover {
    background: rgba(255, 255, 255, 0.06);
    border-color: rgba(255, 255, 255, 0.08);
    transform: translateX(4px);
  }

  .wc-sheet-card:hover::before {
    opacity: 1;
  }
}

.wc-sheet-card-icon {
  width: 38px;
  height: 38px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
}

.wc-sheet-card-info {
  flex: 1;
  min-width: 0;
}

.wc-sheet-card-title {
  font-size: 0.9rem;
  font-weight: 700;
  color: white;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.wc-sheet-card-meta {
  font-size: 0.72rem;
  color: rgba(255, 255, 255, 0.5);
  font-weight: 600;
  margin-top: 2px;
}

.wc-thick-lines .wc-sheet-card-meta {
  font-weight: 800 !important;
  color: var(--text-main) !important;
  font-size: 0.76rem !important;
}
.studio-cockpit-wrapper.theme--light .wc-thick-lines .wc-sheet-card-meta {
  color: #000000 !important;
}

.wc-sheet-card-arrow {
  color: rgba(255, 255, 255, 0.15) !important;
  flex-shrink: 0;
  transition: color 0.2s;
}

.wc-sheet-card:active .wc-sheet-card-arrow {
  color: rgba(255, 255, 255, 0.4) !important;
}

.wc-sheet-footer {
  padding: 12px 20px 20px;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
  flex-shrink: 0;
}

/* Safe area for devices with home indicator */
@supports (padding-bottom: env(safe-area-inset-bottom)) {
  .wc-sheet-footer {
    padding-bottom: calc(20px + env(safe-area-inset-bottom));
  }
}

.wc-sheet-create-btn {
  background: rgba(76, 175, 80, 0.12) !important;
  color: #66bb6a !important;
  border: 1px solid rgba(76, 175, 80, 0.2) !important;
  border-radius: 12px !important;
  text-transform: none !important;
  font-weight: 600 !important;
  font-size: 0.85rem !important;
  letter-spacing: 0 !important;
  height: 46px !important;
  transition: all 0.2s ease;
}

.wc-sheet-create-btn:active {
  background: rgba(76, 175, 80, 0.2) !important;
  transform: scale(0.98);
}

/* ===== ICON-ON-TOP GRID TILES ===== */
.wc-sheet-grid-icons {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
}

.wc-time-input input[type="time"]::-webkit-calendar-picker-indicator {
  display: none;
  -webkit-appearance: none;
}

.wc-sheet-tile {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 16px 8px 14px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 16px;
  cursor: pointer;
  transition: all 0.2s ease;
  -webkit-tap-highlight-color: transparent;
  position: relative;
}

.wc-task-highlight {
  border: 1px solid #66bb6a;
  background: rgba(102, 187, 106, 0.05);
}

.wc-sheet-tile:active {
  transform: scale(0.95);
  background: rgba(255, 255, 255, 0.08);
}

@media (hover: hover) {
  .wc-sheet-tile:hover {
    background: rgba(255, 255, 255, 0.06);
    border-color: var(--tile-color, rgba(255, 255, 255, 0.1));
    transform: translateY(-2px);
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.3);
  }
}

@keyframes wc-pulse-urgent {
  0%,
  100% {
    border-color: rgba(229, 57, 53, 0.8);
    opacity: 1;
  }
  50% {
    border-color: rgba(255, 255, 255, 0.05);
    opacity: 0.8;
  }
}

.wc-sheet-tile-urgent {
  animation: wc-pulse-urgent 2.5s ease-in-out infinite;
  border-color: rgba(229, 57, 53, 0.8);
}

@media (prefers-reduced-motion: reduce) {
  .wc-sheet-tile-urgent {
    animation: none !important;
  }
}

.wc-sheet-tile-warning {
  border-color: #fb8c00 !important;
}

.wc-sheet-urgency-badge {
  font-size: 0.55rem;
  font-weight: 800;
  padding: 2px 4px;
  border-radius: 4px;
  letter-spacing: 0.5px;
  position: absolute;
  top: 6px;
  right: 6px;
  z-index: 2;
}

.wc-sheet-urgency-badge.urgent {
  background: rgba(229, 57, 53, 0.2);
  color: #ef5350;
  border: 1px solid rgba(229, 57, 53, 0.4);
}

.wc-sheet-urgency-badge.warning {
  background: rgba(251, 140, 0, 0.2);
  color: #ffb74d;
  border: 1px solid rgba(251, 140, 0, 0.4);
}

.wc-sheet-tile-icon {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 10px;
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.35);
  flex-shrink: 0;
}

.wc-sheet-tile-title {
  font-size: 0.72rem;
  font-weight: 700;
  color: white;
  line-height: 1.25;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.wc-sheet-tile-group {
  font-weight: 500;
}
.wc-sheet-tile-meta {
  font-size: 0.6rem;
  color: rgba(255, 255, 255, 0.3);
  margin-top: 3px;
}

.wc-sheet-tile-completion {
  font-size: 0.55rem;
  color: var(--text-muted);
  margin-top: 2px;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  column-gap: 2px;
  line-height: 1.2;
}

/* ===== ÄMTLI SMART-ASSIGNMENT DIALOG ===== */
.wc-task-time-picker {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 20px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  background: rgba(255, 255, 255, 0.02);
  flex-shrink: 0;
}

.wc-task-time-label {
  font-size: 0.7rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.4);
  display: flex;
  align-items: center;
}

.wc-task-time-input {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 10px;
  color: white;
  font-size: 1.2rem;
  font-weight: 700;
  text-align: center;
  padding: 8px 12px;
  width: 100%;
  height: 44px;
  outline: none;
  font-variant-numeric: tabular-nums;
  box-sizing: border-box;
  transition: all 0.2s ease;
}

.wc-task-time-input:hover {
  background: rgba(255, 255, 255, 0.08);
  border-color: rgba(255, 255, 255, 0.15);
}

.wc-task-time-input:focus {
  background: rgba(255, 255, 255, 0.1);
  border-color: var(--v-primary-base);
  box-shadow: 0 0 0 3px rgba(156, 39, 176, 0.25);
}

.wc-task-time-input::-webkit-calendar-picker-indicator {
  filter: invert(1);
  opacity: 0.6;
  cursor: pointer;
  transition: opacity 0.2s;
}

.wc-task-time-input::-webkit-calendar-picker-indicator:hover {
  opacity: 0.9;
}

.wc-task-time-btn {
  background: rgba(255, 255, 255, 0.06) !important;
  color: rgba(255, 255, 255, 0.5) !important;
  border-radius: 10px !important;
}

.wc-task-time-btn:active {
  background: rgba(255, 255, 255, 0.12) !important;
}

/* Employee Availability Cards */
.wc-task-emp-card {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  margin-bottom: 8px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 14px;
  cursor: pointer;
  transition: all 0.15s ease;
  -webkit-tap-highlight-color: transparent;
}

.wc-task-emp-free {
  border-color: rgba(76, 175, 80, 0.2);
}

.wc-task-emp-free:active {
  background: rgba(76, 175, 80, 0.1);
  transform: scale(0.98);
}

.wc-task-emp-busy {
  border-color: rgba(255, 152, 0, 0.15);
}

.wc-task-emp-busy:active {
  background: rgba(255, 152, 0, 0.08);
  transform: scale(0.98);
}

.wc-task-emp-noshift {
  opacity: 0.35;
  cursor: not-allowed;
  pointer-events: none;
}

.wc-task-emp-restricted {
  border-color: rgba(239, 83, 80, 0.22);
}

.disabled-card {
  opacity: 0.6;
  cursor: not-allowed !important;
}

.wc-task-emp-avatar {
  border: 2px solid rgba(255, 255, 255, 0.08) !important;
  flex-shrink: 0;
}

.wc-task-emp-info {
  flex: 1;
  min-width: 0;
}

.wc-task-emp-name {
  font-size: 0.9rem;
  font-weight: 700;
  color: white;
}

.wc-task-emp-shift {
  font-size: 0.7rem;
  color: rgba(255, 255, 255, 0.35);
  display: flex;
  align-items: center;
  margin-top: 2px;
}

.wc-task-emp-status {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
  flex-shrink: 0;
}

.wc-task-badge {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 0.65rem;
  font-weight: 700;
  padding: 3px 8px;
  border-radius: 6px;
}

.wc-task-badge-free {
  background: rgba(76, 175, 80, 0.2);
  color: #66bb6a;
}

.wc-task-badge-busy {
  background: rgba(255, 152, 0, 0.15);
  color: #ffa726;
}

.wc-task-badge-off {
  background: rgba(255, 255, 255, 0.06);
  color: rgba(255, 255, 255, 0.3);
  font-size: 0.6rem;
}

.wc-task-badge-restricted {
  background: rgba(239, 83, 80, 0.16);
  color: #ef9a9a;
}

.wc-task-alt {
  font-size: 0.6rem;
  color: #66bb6a;
  font-weight: 600;
}

/* ===== PREMIUM HEADER (GRID RESPONSIVE) ===== */
.wc-header {
  position: relative;
  flex-shrink: 0;
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: center;
  gap: 8px 12px;
  padding: 10px 16px;
  background: var(--bg-header);
  border-bottom: 1px solid var(--border-medium);
  z-index: 20;
}

.wc-header-left {
  grid-column: 1;
  grid-row: 1;
  display: flex;
  align-items: center;
  min-width: 0;
}

.wc-trainer-hub-wrapper {
  grid-column: 2;
  grid-row: 1;
  display: flex;
  justify-content: flex-end;
  align-items: center;
}

.wc-header-center-controls {
  grid-column: 1 / span 2;
  grid-row: 2;
  min-width: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-top: 1px solid var(--border-light);
  padding-top: 8px;
}

@media (min-width: 1400px) {
  .wc-header {
    grid-template-columns: 1fr auto auto;
    grid-template-rows: 1fr;
    gap: 12px;
    padding: 10px 24px;
  }

  .wc-header-left {
    grid-column: 1;
    grid-row: 1;
  }

  .wc-header-center-controls {
    grid-column: 2;
    grid-row: 1;
    border-top: none;
    padding-top: 0;
    justify-content: flex-end;
    gap: 8px;
  }

  .wc-trainer-hub-wrapper {
    grid-column: 3;
    grid-row: 1;
  }
}

/* ===== TRAINER HUB TRIGGER & DROPDOWN CARD ===== */
.wc-trainer-hub-trigger {
  cursor: pointer;
  padding: 4px 12px;
  border-radius: 30px;
  background: rgba(var(--rgb-color), 0.05);
  border: 1px solid rgba(var(--rgb-color), 0.08);
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  user-select: none;
}
.theme--dark .wc-trainer-hub-trigger {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
}
.wc-trainer-hub-trigger:hover {
  background: rgba(var(--rgb-color), 0.1);
  border-color: rgba(var(--rgb-color), 0.18);
  box-shadow: 0 2px 10px rgba(var(--rgb-color), 0.05);
}
.theme--dark .wc-trainer-hub-trigger:hover {
  background: rgba(255, 255, 255, 0.08);
  border-color: rgba(255, 255, 255, 0.2);
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
}

.wc-trainer-hub-trigger-mobile {
  cursor: pointer;
  padding: 2px;
  border-radius: 50%;
  background: rgba(var(--rgb-color), 0.05);
  border: 1px solid rgba(var(--rgb-color), 0.08);
  transition: all 0.25s ease;
}
.theme--dark .wc-trainer-hub-trigger-mobile {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
}
.wc-trainer-hub-trigger-mobile:active {
  transform: scale(0.92);
}

/* Card and options styles moved to global style block at the end of the file to support hoisted dark mode menu rendering */

.wc-header-left {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  row-gap: 6px;
}

.wc-back-btn {
  flex: 0 0 auto;
  background: var(--bg-panel) !important;
  border-radius: 8px !important;
}

.wc-back-btn .v-icon {
  color: var(--text-main) !important;
}

.wc-date-block {
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  white-space: nowrap;
  word-break: normal;
  overflow-wrap: normal;
}

.wc-day-name {
  font-size: 1.5rem;
  font-weight: 800;
  color: var(--text-main);
  line-height: 1.2;
  text-transform: capitalize;
}

.wc-full-date {
  font-size: 0.85rem;
  color: var(--text-muted);
  font-weight: 500;
}

.wc-today-badge {
  flex: 0 0 auto;
  white-space: nowrap;
  word-break: normal;
  overflow-wrap: normal;
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 2px 8px;
  border-radius: 4px;
  background: linear-gradient(135deg, #4caf50, #2e7d32);
  color: white;
}

.wc-focus-chip {
  min-width: 0;
  max-width: 220px;
  flex: 0 1 auto;
}
.wc-focus-chip ::v-deep .v-chip__content {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.wc-header-center {
  display: flex;
  align-items: center;
  gap: 16px;
}

.wc-stat {
  display: flex;
  align-items: center;
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--text-muted);
}

.wc-header-right {
  display: flex;
  align-items: center;
  gap: 4px;
}

.wc-nav-group {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  background: var(--bg-panel);
  border-radius: 8px;
  padding: 2px;
  margin-right: 4px;
}

.wc-nav-btn {
  color: var(--icon-muted) !important;
}

.wc-nav-btn:hover {
  color: var(--text-main) !important;
}

.wc-today-btn {
  color: var(--text-muted) !important;
  text-transform: none !important;
  font-weight: 600 !important;
  font-size: 0.7rem !important;
  min-width: 0 !important;
  padding: 0 8px !important;
  border-radius: 6px !important;
}

.wc-today-btn.is-today {
  background: rgba(76, 175, 80, 0.15) !important;
  color: #66bb6a !important;
}

.wc-icon-btn {
  flex: 0 0 auto;
  color: var(--icon-muted) !important;
  transition: all 0.2s;
}

.wc-clipboard-chip {
  min-width: 0;
  max-width: 220px;
  flex: 0 1 220px;
}
.wc-clipboard-chip ::v-deep .v-chip__content {
  min-width: 0;
}
.wc-clipboard-mode {
  flex: 0 0 auto;
}
.wc-clipboard-title {
  min-width: 0;
  margin-left: 4px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

@media (max-width: 1199px) {
  .wc-clipboard-chip {
    max-width: 150px;
    flex-basis: 150px;
  }
  .wc-clipboard-title {
    display: none;
  }
}

@media (max-width: 1399px) {
  .wc-header-center-controls {
    justify-content: flex-start;
    gap: 4px;
    white-space: nowrap;
  }
  .wc-studio-switcher-chip {
    margin-left: 8px !important;
  }
}

.wc-icon-btn:hover,
.wc-icon-btn.active {
  color: var(--text-main) !important;
  background: var(--bg-panel-hover) !important;
}

.wc-view-toggle {
  background: rgba(76, 175, 80, 0.1) !important;
  color: #66bb6a !important;
  text-transform: none !important;
  font-weight: 600 !important;
  font-size: 0.7rem !important;
  border-radius: 6px !important;
  padding: 0 10px !important;
  min-width: 0 !important;
  height: 26px !important;
  letter-spacing: 0;
  border: 1px solid rgba(76, 175, 80, 0.15) !important;
}

.wc-view-toggle:hover {
  background: rgba(76, 175, 80, 0.2) !important;
}

.wc-active-employee {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 20px;
  background: rgba(var(--rgb-color), 0.05);
  border: 1px solid rgba(var(--rgb-color), 0.08);
  transition: all 0.2s ease;
}
.wc-active-employee.clickable {
  cursor: pointer;
}
.wc-active-employee.clickable:hover {
  background: rgba(var(--rgb-color), 0.1);
  border-color: rgba(var(--rgb-color), 0.15);
}
.wc-apps-btn {
  background: rgba(var(--rgb-color), 0.05) !important;
  border: 1px solid rgba(var(--rgb-color), 0.08) !important;
  color: var(--text-main) !important;
  text-transform: none !important;
  font-weight: 600 !important;
  font-size: 0.75rem !important;
  border-radius: 8px !important;
  height: 30px !important;
}
.wc-apps-btn:hover {
  background: rgba(var(--rgb-color), 0.1) !important;
}
.wc-shortcut-tile {
  cursor: pointer;
  border: 1px solid var(--border-color) !important;
  background: var(--bg-panel) !important;
  transition: all 0.2s ease;
}
.wc-shortcut-tile:hover {
  background: var(--bg-panel-hover) !important;
  border-color: var(--border-color-hover) !important;
}

/* ===== EXISTING UTILITIES ===== */
.glass-panel,
.glass-btn,
.glass-panel-light {
  background: var(--bg-panel) !important;
  border: 1px solid var(--border-color) !important;
}

.glass-purple {
  background: linear-gradient(135deg, rgba(0, 150, 63, 0.15), rgba(0, 150, 63, 0.05));
  border: 1px solid rgba(0, 150, 63, 0.3);
}

.border-right {
  border-right: 1px solid var(--border-light);
}

.time-slot-label {
  position: relative;
  /* Die Linie zeichnet .wc-hour-tick absolut, damit kein Slot an Höhe gewinnt. */
  border-bottom: none;
}

.wc-hour-label {
  position: absolute;
  top: 0;
  transform: translateY(-50%);
  /* Zentriert die Zahl millimetergenau auf der Linie */
  left: 6px;
  right: auto;
  font-size: 0.8rem;
  font-weight: 500;
  color: var(--text-disabled);
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

.studio-cockpit-wrapper.theme--light .wc-hour-label {
  color: #000000 !important;
  font-weight: 600;
}

.wc-thick-lines .wc-hour-tick {
  height: 1.2px !important;
  background-color: rgba(0, 0, 0, 0.08) !important;
}
.theme--dark .wc-thick-lines .wc-hour-tick {
  background-color: rgba(255, 255, 255, 0.12) !important;
}
.wc-thick-lines .wc-hour-label {
  font-weight: 750 !important;
  font-size: 0.85rem !important;
  color: var(--text-main) !important;
}

.wc-hour-tick {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 1px;
  background: var(--bg-time-line);
  /* Ersetzt den alten Rahmen, ohne die Höhe zu beeinflussen */
}

/* ===== DAS NEUE CURRENT TIME DESIGN ===== */
.time-axis-current-badge {
  position: absolute;
  right: -1px;
  /* Liegt perfekt an der Begrenzungslinie an */
  transform: translateY(-50%);
  /* Exakt auf der Linie zentriert */
  background: #ff5252;
  color: white;
  padding: 3px 5px;
  border-radius: 4px 0 0 4px;
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.5px;
  box-shadow: 0 2px 6px rgba(255, 82, 82, 0.4);
}

.current-time-line {
  position: absolute;
  height: var(--current-time-line-width, 2px);
  transform: translateY(-50%);
  background: #ef3340;
  box-shadow: 0 -1px 0 rgba(255, 255, 255, 0.72), 0 1px 0 rgba(255, 255, 255, 0.72), 0 0 7px rgba(239, 51, 64, 0.62);
  pointer-events: none;
  z-index: 4;
}

.past-time-overlay {
  position: absolute;
  background: #334155;
  pointer-events: none;
  z-index: 80;
}

/* Der leuchtende, pulsierende Punkt links am Beginn der roten Linie */
.current-time-line::after {
  content: "";
  position: absolute;
  left: -2px;
  top: 50%;
  width: 10px;
  height: 10px;
  transform: translateY(-50%);
  background: #ef3340;
  border-radius: 50%;
  border: 1px solid rgba(255, 255, 255, 0.9);
  box-shadow: 0 0 8px 2px rgba(239, 51, 64, 0.62);
  animation: pulse-glow 2s infinite;
}

@keyframes pulse-glow {
  0% {
    box-shadow: 0 0 0 0 rgba(255, 82, 82, 0.4);
  }
  70% {
    box-shadow: 0 0 0 6px rgba(255, 82, 82, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(255, 82, 82, 0);
  }
}

/* ===== SCROLLBAR ===== */
.custom-scrollbar::-webkit-scrollbar {
  width: 5px;
  height: 5px;
}

.custom-scrollbar::-webkit-scrollbar-track {
  background: transparent;
}

.custom-scrollbar::-webkit-scrollbar-thumb {
  background: var(--border-medium);
  border-radius: 3px;
}

.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: var(--border-strong);
}

/* Scroller for columns */
.wc-scroller {
  display: flex;
  flex: 1 1 auto;
  overflow: auto;
  position: relative;
  -webkit-overflow-scrolling: touch;
  isolation: isolate;
  /* Eigener Stacking-Context: z-index Werte bleiben im Scroller und laufen nicht über Sidebar/Overlays */
}

/* iPad & Touch */
@media (max-width: 1024px) {
  .wc-header {
    padding: 8px 12px;
  }

  .wc-day-name {
    font-size: 1.2rem;
  }
}

@media (hover: none) and (pointer: coarse) {
  .wc-scroller {
    -webkit-overflow-scrolling: touch;
  }

  .employee-column {
    min-width: 200px !important;
  }
}

/* Template Dialog */
.wc-color-dot {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  cursor: pointer;
  opacity: 0.5;
  border: 2px solid transparent;
  transition: all 0.15s;
}

.wc-color-dot:hover {
  opacity: 0.8;
  transform: scale(1.1);
}

.wc-color-dot.active {
  opacity: 1;
  transform: scale(1.2);
  border-color: white;
  box-shadow: 0 0 10px currentColor;
}

.wc-icon-pick {
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  cursor: pointer;
  background: var(--bg-panel);
  border: 1px solid transparent;
  transition: all 0.15s;
}

.wc-icon-pick:hover {
  background: var(--bg-panel-hover);
}

.wc-icon-pick.active {
  background: rgba(76, 175, 80, 0.15);
  border-color: #4caf50;
}

/* ===== LOADING SKELETON ===== */
.wc-skeleton-col {
  min-width: 240px;
  flex: 1 1 240px;
  border-right: 1px solid rgba(255, 255, 255, 0.04);
  display: flex;
  flex-direction: column;
}

.wc-skeleton-header {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 14px;
  background: var(--bg-header);
  border-bottom: 1px solid var(--border-medium);
}

.wc-skeleton-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  flex-shrink: 0;
}

.wc-skeleton-line {
  border-radius: 4px;
}

.wc-skeleton-body {
  position: relative;
  height: 600px;
}

.wc-skeleton-block {
  position: absolute;
  left: 8px;
  right: 8px;
  border-radius: 10px;
}

/* Pulse Animation */
.wc-pulse {
  background: var(--bg-skeleton);
  animation: wcPulse 1.5s ease-in-out infinite;
}

@keyframes wcPulse {
  0%,
  100% {
    opacity: 1;
  }

  50% {
    opacity: 0.4;
  }
}

/* Empty State */
.wc-empty-icon {
  width: 80px;
  height: 80px;
  border-radius: 20px;
  background: var(--bg-panel);
  border: 1px dashed var(--border-strong);
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Premium Segmented Control */
.wc-seg-control {
  display: flex;
  position: relative;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 12px;
  padding: 3px;
  gap: 0;
}

.wc-seg-slider {
  position: absolute;
  top: 3px;
  left: 3px;
  width: calc(50% - 3px);
  height: calc(100% - 6px);
  background: linear-gradient(135deg, rgba(76, 175, 80, 0.35), rgba(46, 125, 50, 0.25));
  border: 1px solid rgba(76, 175, 80, 0.3);
  border-radius: 10px;
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 2px 8px rgba(76, 175, 80, 0.15);
  z-index: 0;
}

.wc-seg-btn {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px 12px;
  font-size: 0.8rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.4);
  cursor: pointer;
  z-index: 1;
  position: relative;
  transition: color 0.25s ease;
  letter-spacing: 0.3px;
  user-select: none;
  border-radius: 10px;
}

.wc-seg-btn.active {
  color: white;
}

.wc-seg-btn:hover:not(.active) {
  color: rgba(255, 255, 255, 0.6);
}

/* Mobile Search Input */
.wc-mobile-search >>> .v-input__slot {
  min-height: 38px !important;
  border-radius: 10px !important;
  border: 1px solid var(--border-medium) !important;
}
.wc-mobile-search >>> input {
  font-size: 0.9rem;
}
.wc-mobile-search >>> .v-icon {
  font-size: 18px !important;
  color: var(--text-muted) !important;
}

/* Transfer Dialog Styles */
.wc-transfer-card {
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 12px;
  padding: 10px 12px;
  cursor: pointer;
  transition: all 0.2s ease;
}
.wc-transfer-card:hover {
  background: rgba(255, 255, 255, 0.06);
  border-color: rgba(255, 255, 255, 0.12);
}
.wc-transfer-card.active {
  background: rgba(156, 39, 176, 0.15) !important;
  border-color: rgba(156, 39, 176, 0.5) !important;
  box-shadow: 0 4px 12px rgba(156, 39, 176, 0.1);
}
.wc-transfer-avatar {
  border: 1.5px solid rgba(255, 255, 255, 0.08) !important;
  background: rgba(255, 255, 255, 0.05);
}
.wc-transfer-card.active .wc-transfer-avatar {
  border-color: rgba(156, 39, 176, 0.5) !important;
}
.wc-transfer-name {
  font-size: 0.85rem;
  font-weight: 700;
  color: white;
  line-height: 1.2;
}
.wc-transfer-status {
  line-height: 1.2;
  margin-top: 1px;
}
.wc-transfer-checkbox {
  flex-shrink: 0;
  display: flex;
  align-items: center;
}
.wc-focus-select {
  width: 100%;
  height: 36px;
  padding: 0 32px 0 12px;
  border-radius: 6px;
  font-size: 0.85rem;
  font-weight: 600;
  outline: none;
  cursor: pointer;
  appearance: none;
  background-image: url("data:image/svg+xml;charset=UTF-8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 8px center;
  background-size: 16px;
}
.wc-dashboard-row {
  transition: background-color 0.2s ease;
}
.wc-dashboard-row:hover {
  background-color: rgba(33, 150, 243, 0.04) !important;
}
.theme--dark .wc-dashboard-row:hover {
  background-color: rgba(255, 255, 255, 0.02) !important;
}
@media (max-width: 599px) {
  .wc-header {
    padding-left: 10px;
    padding-right: 10px;
  }
  .wc-header-center-controls {
    gap: 1px;
  }
  .wc-header-center-controls ::v-deep .v-btn--icon,
  .wc-header-center-controls .v-btn--icon {
    width: 34px !important;
    min-width: 34px !important;
    height: 34px !important;
  }
  .wc-header-center-controls .mr-2 {
    margin-right: 2px !important;
  }
  .wc-nav-group {
    margin-right: 2px;
    padding: 1px;
  }
  .wc-today-btn {
    padding: 0 5px !important;
  }
}
</style>

<style>
.wc-date-picker-menu {
  z-index: 10000 !important;
}

@media (any-pointer: coarse), (hover: none) {
  .work-calendar .wc-header-center-controls .wc-icon-btn {
    width: 44px !important;
    min-width: 44px !important;
    height: 44px !important;
  }
  .wc-calendar-context-menu .v-list-item {
    min-height: 48px !important;
  }
}

/* ===== GLOBAL OVERRIDES FOR TRAINER CONTROL CENTER HOISTED DIALOGS & MENUS ===== */
.wc-control-center-card {
  max-height: calc(100vh - 120px) !important;
  max-height: calc(100dvh - 120px) !important;
  overflow-y: auto !important;
  display: flex !important;
  flex-direction: column !important;
}
.wc-control-center-card.theme--light {
  background: #ffffff !important;
  border-color: rgba(0, 0, 0, 0.08) !important;
}
.wc-control-center-card.theme--dark {
  background: #1e1e1e !important;
  border-color: rgba(255, 255, 255, 0.08) !important;
  box-shadow: 0 16px 36px rgba(0, 0, 0, 0.4) !important;
}

.wc-control-center-card.theme--dark .caption.grey--text {
  color: rgba(255, 255, 255, 0.5) !important;
}
.wc-control-center-card.theme--light .caption.grey--text {
  color: rgba(0, 0, 0, 0.5) !important;
}

/* Action Tiles for Blitz-Aktionen */
.wc-action-tile {
  cursor: pointer;
  background: rgba(0, 0, 0, 0.03);
  border: 1px solid rgba(0, 0, 0, 0.05);
  border-radius: 12px;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  user-select: none;
}
.wc-control-center-card.theme--dark .wc-action-tile {
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.05);
}
.wc-action-tile:hover {
  transform: translateY(-1px);
  background: rgba(0, 0, 0, 0.05);
  border-color: rgba(0, 0, 0, 0.08);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
}
.wc-control-center-card.theme--dark .wc-action-tile:hover {
  background: rgba(255, 255, 255, 0.06);
  border-color: rgba(255, 255, 255, 0.08);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}
.wc-action-tile:active {
  transform: scale(0.97);
}
.wc-action-icon-box {
  width: 28px;
  height: 28px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
/* Theme colors for icon boxes */
.wc-action-icon-box.blue-theme {
  background: rgba(33, 150, 243, 0.12);
}
.wc-action-icon-box.orange-theme {
  background: rgba(255, 152, 0, 0.12);
}
.wc-action-icon-box.purple-theme {
  background: rgba(156, 39, 176, 0.12);
}
.wc-action-icon-box.red-theme {
  background: rgba(244, 67, 54, 0.12);
}
.wc-action-icon-box.grey-theme {
  background: rgba(158, 158, 158, 0.12);
}
.wc-action-text {
  font-size: 0.72rem;
  font-weight: 700;
}
.wc-control-center-card.theme--dark .wc-action-text {
  color: #ffffff !important;
}
.wc-control-center-card.theme--light .wc-action-text {
  color: #333333 !important;
}

/* Custom Dropdown select */
.wc-focus-select {
  appearance: none;
  background-image: url("data:image/svg+xml;charset=UTF-8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='%23888888'%3E%3Cpath d='M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 10px center;
  background-size: 16px;
  padding: 6px 32px 6px 12px !important;
  border: 1px solid rgba(var(--rgb-color), 0.1);
  border-radius: 12px;
  font-weight: 600;
  transition: all 0.2s ease;
  font-size: 0.8rem;
  outline: none;
}
.wc-control-center-card.theme--light .wc-focus-select {
  background-color: rgba(0, 0, 0, 0.03);
  color: #333333;
}
.wc-control-center-card.theme--dark .wc-focus-select {
  background-color: rgba(255, 255, 255, 0.05);
  color: #ffffff;
}
.wc-control-center-card.theme--dark .wc-focus-select option {
  background: #1e1e1e;
  color: #ffffff;
}
.wc-control-center-card.theme--light .wc-focus-select option {
  background: #ffffff;
  color: #333333;
}
.wc-focus-select:focus {
  border-color: rgba(var(--rgb-color), 0.3);
  box-shadow: 0 0 0 3px rgba(var(--rgb-color), 0.15);
}
.wc-theme-option-disabled.v-list-item--disabled {
  opacity: 0.72 !important;
}
.wc-theme-option-note {
  margin-top: 1px;
  color: #fb8c00 !important;
  font-size: 0.62rem !important;
  line-height: 1.2 !important;
}

/* Monatsplanung Wizard Template Item Styles */
.assign-template-item {
  display: grid;
  grid-template-columns: 40px 220px 1fr 1fr 1fr 1fr auto;
  align-items: center;
  gap: 14px;
  width: 100%;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  user-select: none;
}
.assign-template-item:hover {
  transform: translateY(-1px);
}
.theme--dark .assign-template-item:hover {
  background: rgba(255, 255, 255, 0.05) !important;
  border-color: rgba(255, 255, 255, 0.1) !important;
}
.theme--light .assign-template-item:hover {
  background: #f1f5f9 !important;
  border-color: rgba(0, 0, 0, 0.12) !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.03) !important;
}
@media (max-width: 960px) {
  .assign-template-item {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }
}

/* Guided Ämtli journey */
.amtli-journey-card,
.amtli-template-editor-card,
.amtli-template-editor-shell {
  height: 100vh;
  height: 100dvh;
}

.amtli-journey-body {
  min-height: 0;
}

.amtli-help-button {
  width: 44px;
  height: 44px;
  flex: 0 0 44px;
}

.amtli-help-card {
  width: min(360px, calc(100vw - 24px));
  border-radius: 16px !important;
}

.amtli-help-row {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  color: #687585;
  font-size: 0.78rem;
  line-height: 1.4;
}

.amtli-help-row + .amtli-help-row {
  margin-top: 10px;
}

.amtli-run-issues {
  padding-left: 20px;
  line-height: 1.45;
}

.amtli-run-issues li + li {
  margin-top: 4px;
}

.amtli-config-layout,
.amtli-plan-layout {
  height: 100%;
  min-height: 0;
  flex-wrap: nowrap;
}

.amtli-config-filterbar {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 12px;
  border: 1px solid rgba(128, 128, 128, 0.16);
  border-radius: 14px;
  background: rgba(128, 128, 128, 0.035);
}

.amtli-config-search {
  flex: 1 1 280px;
  max-width: 420px;
}

.amtli-config-filters {
  flex: 1 1 auto;
  min-width: 0;
  overflow-x: auto;
}

.amtli-template-summary {
  max-width: 100%;
  margin-top: 5px;
  color: #7b8794;
  font-size: 0.66rem;
  line-height: 1.25;
}

.amtli-editor-guide {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.amtli-editor-guide > div {
  display: flex;
  align-items: center;
  min-height: 52px;
  padding: 11px 14px;
  border: 1px solid rgba(128, 128, 128, 0.15);
  border-radius: 13px;
  background: rgba(128, 128, 128, 0.035);
  color: #667382;
  font-size: 0.76rem;
  line-height: 1.4;
}

.amtli-automation-toggle {
  min-height: 52px;
}

.amtli-loader-steps {
  display: grid;
  grid-template-columns: repeat(4, minmax(120px, 1fr));
  gap: 10px;
  width: min(720px, calc(100% - 40px));
}

.amtli-loader-steps > div {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px;
  border: 1px solid rgba(19, 164, 90, 0.18);
  border-radius: 12px;
  background: rgba(19, 164, 90, 0.07);
  font-size: 0.74rem;
}

.amtli-loader-steps span {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  flex: 0 0 24px;
  border-radius: 50%;
  color: #fff;
  background: #13a45a;
  font-weight: 800;
}

.amtli-review-card {
  display: flex;
  flex-direction: column;
  height: 100vh;
  height: 100dvh;
  overflow: hidden;
}

.amtli-review-rangebar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  padding: 10px 20px;
  border-bottom: 1px solid rgba(128, 128, 128, 0.14);
  background: rgba(128, 128, 128, 0.035);
}

.amtli-review-presets,
.amtli-review-dates {
  display: flex;
  align-items: center;
  gap: 8px;
}

.amtli-review-dates {
  width: min(360px, 100%);
}

.amtli-review-scroll {
  min-height: 0;
  flex: 1 1 auto;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}

.amtli-review-callout {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 14px 16px;
  border: 1px solid;
  border-radius: 14px;
}

.amtli-review-callout--ready {
  border-color: rgba(19, 164, 90, 0.24);
  background: rgba(19, 164, 90, 0.08);
}

.amtli-review-callout--warning {
  border-color: rgba(251, 140, 0, 0.28);
  background: rgba(251, 140, 0, 0.08);
}

.amtli-review-callout__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  flex: 0 0 42px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.55);
}

@media (max-width: 1264px) {
  .amtli-config-layout,
  .amtli-plan-layout {
    display: block;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
  }

  .amtli-config-aside,
  .amtli-config-main,
  .amtli-plan-aside,
  .amtli-plan-main {
    width: 100% !important;
    max-width: 100% !important;
    flex: 0 0 100% !important;
    height: auto !important;
    overflow: visible !important;
  }

  .amtli-config-aside,
  .amtli-plan-aside {
    border-right: 0 !important;
    border-bottom: 1px solid rgba(128, 128, 128, 0.16);
  }

  .assign-template-item {
    display: grid !important;
    grid-template-columns: repeat(3, minmax(0, 1fr)) !important;
    align-items: stretch;
  }

  .assign-template-item > div {
    min-width: 0;
  }

  .amtli-auto-template-item {
    grid-template-columns: 40px repeat(3, minmax(0, 1fr)) !important;
  }

  .amtli-auto-template-check {
    grid-column: 1;
    grid-row: 1;
  }

  .amtli-auto-template-main {
    grid-column: 2 / 5;
    grid-row: 1;
  }

  .amtli-auto-template-detail:nth-child(3) {
    grid-column: 2;
    grid-row: 2;
  }

  .amtli-auto-template-detail:nth-child(4) {
    grid-column: 3;
    grid-row: 2;
  }

  .amtli-auto-template-detail:nth-child(5) {
    grid-column: 4;
    grid-row: 2;
  }

  .amtli-review-rangebar {
    align-items: stretch;
    flex-direction: column;
  }

  .amtli-review-presets {
    overflow-x: auto;
    padding-bottom: 2px;
  }

  .amtli-review-presets .v-btn,
  .amtli-journey-body .v-btn {
    min-height: 44px;
  }

  .amtli-proposal-table thead {
    display: none;
  }

  .amtli-proposal-table table,
  .amtli-proposal-table tbody {
    display: block;
  }

  .amtli-proposal-table tbody tr {
    display: grid;
    grid-template-columns: 100px minmax(160px, 1fr) minmax(240px, 1.25fr) 100px;
    align-items: center;
    margin-bottom: 10px;
    border: 1px solid rgba(128, 128, 128, 0.16);
    border-radius: 12px;
    overflow: hidden;
  }

  .amtli-proposal-table tbody td {
    height: auto !important;
    padding: 10px !important;
    border-bottom: 0 !important;
  }
}

@media (max-width: 760px) {
  .amtli-config-filterbar,
  .amtli-review-callout,
  .amtli-review-dates {
    align-items: stretch;
    flex-direction: column;
    max-width: none;
    width: 100%;
  }

  .amtli-config-search {
    max-width: none;
  }

  .assign-template-item {
    display: flex !important;
    flex-direction: column;
    align-items: stretch;
  }

  .amtli-auto-template-check {
    align-self: flex-start;
  }

  .amtli-loader-steps {
    grid-template-columns: 1fr 1fr;
  }

  .amtli-editor-guide {
    grid-template-columns: 1fr;
  }

  .amtli-proposal-table tbody tr {
    grid-template-columns: 1fr;
  }
}

/* ===== TERMIN-ZUGRIFF ===== */
.wc-restrictions-dialog {
  border-radius: 22px !important;
  overflow: hidden;
}

.wc-restrictions-card {
  --restriction-bg: #ffffff;
  --restriction-surface: #f8fafc;
  --restriction-surface-hover: #f1f5f9;
  --restriction-text: #18212f;
  --restriction-muted: #667085;
  --restriction-border: #e5eaf0;
  max-height: calc(100vh - 80px);
  max-height: calc(100dvh - 80px);
  display: flex !important;
  flex-direction: column;
  overflow: hidden;
  border: 1px solid var(--restriction-border);
  border-radius: 22px !important;
  background: var(--restriction-bg) !important;
  color: var(--restriction-text) !important;
  box-shadow: 0 26px 80px rgba(15, 23, 42, 0.24) !important;
}

.wc-restrictions-card--dark {
  --restriction-bg: #171a1f;
  --restriction-surface: #20242b;
  --restriction-surface-hover: #272c34;
  --restriction-text: #f7f8fa;
  --restriction-muted: #9ca5b4;
  --restriction-border: rgba(255, 255, 255, 0.1);
}

.wc-restrictions-header {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 21px 22px 19px;
  border-bottom: 1px solid var(--restriction-border);
}

.wc-restrictions-header-icon {
  width: 44px;
  height: 44px;
  flex: 0 0 44px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 13px;
  background: linear-gradient(145deg, #14a34a, #087a34);
  box-shadow: 0 8px 18px rgba(9, 150, 64, 0.22);
}

.wc-restrictions-heading {
  min-width: 0;
  flex: 1;
}

.wc-restrictions-eyebrow {
  margin-bottom: 2px;
  color: #159447;
  font-size: 0.65rem;
  font-weight: 850;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.wc-restrictions-title {
  color: var(--restriction-text);
  font-size: 1.18rem;
  font-weight: 800;
  letter-spacing: -0.02em;
  line-height: 1.25;
}

.wc-restrictions-subtitle {
  margin-top: 4px;
  color: var(--restriction-muted);
  font-size: 0.78rem;
  line-height: 1.35;
}

.wc-restrictions-close {
  align-self: flex-start;
  color: var(--restriction-muted) !important;
  background: var(--restriction-surface) !important;
  border: 1px solid var(--restriction-border);
}

.wc-restrictions-body {
  min-height: 0;
  padding: 20px 22px 18px;
  overflow-y: auto;
}

.wc-restrictions-section + .wc-restrictions-section {
  margin-top: 20px;
}

.wc-restrictions-section-label {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 0 9px 2px;
  color: var(--restriction-text);
  font-size: 0.72rem;
  font-weight: 800;
  letter-spacing: 0.015em;
}

.wc-restrictions-section-label > span {
  width: 20px;
  height: 20px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 7px;
  color: #ffffff;
  background: #159447;
  font-size: 0.65rem;
}

.wc-restrictions-card .v-text-field--outlined > .v-input__control > .v-input__slot {
  min-height: 44px !important;
  border-radius: 11px !important;
  background: var(--restriction-surface) !important;
}

.wc-restrictions-card .v-text-field--outlined fieldset {
  border-color: var(--restriction-border) !important;
}

.wc-restrictions-card .v-text-field--outlined.v-input--is-focused fieldset {
  border-color: var(--v-primary-base, #159447) !important;
}

.wc-restrictions-card .v-input input,
.wc-restrictions-card .v-select__selection {
  color: var(--restriction-text) !important;
  font-size: 0.86rem;
}

.wc-restrictions-card .v-input__icon .v-icon {
  color: var(--restriction-muted) !important;
}

.wc-restrictions-rules {
  overflow: hidden;
  border: 1px solid var(--restriction-border);
  border-radius: 14px;
  background: var(--restriction-surface);
}

.wc-restrictions-rule {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 13px 14px;
}

.wc-restrictions-rule + .wc-restrictions-rule {
  border-top: 1px solid var(--restriction-border);
}

.wc-restrictions-rule--employees {
  display: block;
}

.wc-restrictions-rule-heading {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 11px;
}

.wc-restrictions-rule-icon {
  width: 34px;
  height: 34px;
  flex: 0 0 34px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  color: #159447;
  background: rgba(21, 148, 71, 0.11);
}

.wc-restrictions-rule-icon .v-icon {
  color: inherit !important;
}

.wc-restrictions-rule-copy {
  min-width: 0;
  flex: 1;
  display: flex;
  flex-direction: column;
}

.wc-restrictions-rule-copy strong {
  color: var(--restriction-text);
  font-size: 0.82rem;
  font-weight: 750;
  line-height: 1.25;
}

.wc-restrictions-rule-copy small {
  margin-top: 3px;
  color: var(--restriction-muted);
  font-size: 0.7rem;
  line-height: 1.35;
}

.wc-restrictions-switch {
  flex: 0 0 auto;
}

.wc-restrictions-count {
  flex: 0 0 auto;
  padding: 4px 7px;
  border-radius: 999px;
  color: #12823e;
  background: rgba(21, 148, 71, 0.11);
  font-size: 0.65rem;
  font-weight: 800;
}

.wc-restrictions-card--dark .wc-restrictions-count {
  color: #6fda92;
}

.wc-restrictions-card .v-chip {
  border: 1px solid rgba(21, 148, 71, 0.18) !important;
  background: rgba(21, 148, 71, 0.09) !important;
  color: var(--restriction-text) !important;
}

.wc-restrictions-note {
  display: flex;
  align-items: flex-start;
  gap: 9px;
  margin-top: 14px;
  padding: 10px 12px;
  color: var(--restriction-muted);
  background: rgba(33, 150, 243, 0.07);
  border-radius: 10px;
  font-size: 0.7rem;
  line-height: 1.45;
}

.wc-restrictions-note .v-icon {
  margin-top: 1px;
  color: #2488d8 !important;
}

.wc-restrictions-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 14px 22px;
  border-top: 1px solid var(--restriction-border);
  background: var(--restriction-surface);
}

.wc-restrictions-footer-hint {
  color: var(--restriction-muted);
  font-size: 0.68rem;
}

@media (max-width: 600px) {
  .wc-restrictions-dialog {
    margin: 12px !important;
  }
  .wc-restrictions-card {
    max-height: calc(100vh - 24px);
    max-height: calc(100dvh - 24px);
    border-radius: 18px !important;
  }
  .wc-restrictions-header {
    padding: 17px 16px 15px;
  }
  .wc-restrictions-header-icon {
    width: 40px;
    height: 40px;
    flex-basis: 40px;
  }
  .wc-restrictions-title {
    font-size: 1rem;
  }
  .wc-restrictions-subtitle,
  .wc-restrictions-footer-hint {
    display: none;
  }
  .wc-restrictions-body {
    padding: 17px 16px 15px;
  }
  .wc-restrictions-rule {
    padding: 12px;
  }
  .wc-restrictions-footer {
    justify-content: flex-end;
    padding: 12px 16px;
  }
}
</style>
